import { useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  OverlayView,
  OverlayViewF,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import Header from "../Header/Header";
import Footer from "../Footer";
import { useLanguage } from "../contexts/LanguageContext";
import { img } from "../utils/cloudinaryImage";
import { spots, baseMapSpots } from "../data/oceanGuidePlaces.mjs";
import { getOceanPlacePath, oceanPlaces } from "../utils/oceanPlacePages.mjs";
import { getJapaneseMeteorShowerCopy } from "../utils/meteorShowerCopy.mjs";
import { InstagramEmbed } from "../components/BlogArticle/InstagramEmbed";
import { ShopCard } from "../components/BlogArticle/ShopArticleTemplate";
import { blogArticles } from "../data/blogArticles";
import {
  localizeArticle,
  localizeShop,
  withArticleTranslations,
} from "../utils/localizedContent";
import {
  trackCtaClick,
  trackEvent,
  trackInstagramBookingClick,
  trackPhoneBookingClick,
  trackRestaurantMapClick,
} from "../utils/analytics";
import { LINE_URL } from "../config/socialLinks";
import { db } from "../firebase";
import {
  defaultOceanGuideSettings,
  getOceanGuideAdminText,
  getSpotOverrideId,
  getTodaySpotOverride,
  subscribeOceanGuideSettings,
  subscribeOceanGuideSpotOverrides,
} from "../services/oceanGuideSettings";
import * as SunCalc from "suncalc";
import s from "./OceanGuidePage.module.scss";
import blogArticleStyles from "./BlogArticle.module.scss";
import OceanOperator from "../OceanOperator/OceanOperator";
import { getAutomaticMarineConditionType } from "../utils/oceanMarineCondition.mjs";
import { isTyphoonPointNearMiyako, TYPHOON_MAX_CENTER_KM } from "../utils/typhoonRelevance.mjs";
import { getAutomaticBeachCondition, hasStrongWindSafetyStop, applySpotConditionOverride } from "../utils/oceanSpotCondition.mjs";
import OceanGuideSearchLinks from "../components/OceanGuideSearchLinks/OceanGuideSearchLinks";

const t = {
  JP: {
    lang: "JP",
    appName: "Miyako Ocean Guide",
    pageHeroSubtitle: "宮古島の旅行中に見たい情報を ひとつにまとめたアプリです。",
    mapButton: "マップを見る",
    rainButton: "雨の日画面を見る",
    heroTitle: "{day}の宮古島は海遊び日和です！",
    heroSub: "風や波の変化を確認しながら、安全に海を楽しみましょう。",
    heroBadTitle: "{day}は海況がやや不安定です",
    heroBadSub: "安全なビーチを選んで無理せず楽しみましょう",
    heroWindTitle: "{day}は強風で海が大しけです",
    heroWindSub: "海遊びは中止も検討し、室内やカフェでゆったり過ごすのがおすすめです",
    heroRainTitle: "{day}はあいにくの雨模様です",
    heroRainSub: "海遊びよりカフェ巡りや室内体験でゆったり過ごしましょう",
    heroHighRainTitle: "{day}の宮古島は雨に注意して遊びましょう！",
    heroHighRainSub: "今日は降水確率が高めです。雨の合間や場所を見ながら楽しみましょう。",
    heroByCategory: {
      snorkel: {
        title: "{day}の宮古島は海遊び日和です！",
        sub: "風や波の変化を確認しながら、安全に海を楽しみましょう。",
      },
      stars: {
        title: "今夜の宮古島で星空を楽しむ",
        sub: "雲・風・月明かりを見ながら、星空撮影に合う場所を探せます",
      },
      sunset: {
        title: "今日の宮古島で夕日を見るなら",
        sub: "日の入り時間と風を見ながら、きれいに見えるサンセットスポットを探せます",
      },
      yabiji: {
        title: "宮古島の秘境・八重干瀬（やびじ）",
        sub: "ツアー船でしか行けない幻の巨大サンゴ礁。",
      },
      rainy: {
        title: "雨の日でも楽しめる宮古島の室内体験",
        sub: "レジンアートやシーサー作りなど、雨の日にぴったりの体験をチェック",
      },
    },
    tabs: {
      snorkel: "シュノーケル",
      stars: "星空",
      sunset: "サンセット",
      beach: "ビーチル",
      cafe: "ランチ・カフェ",
      lunch: "ランチ・カフェ",
      dinner: "ディナー",
      hotel: "ホテル",
      voices: "みんなの声",
      nightlife: "夜遊び",
      lateNightMeal: "〆ご飯",
      yabiji: "やびじ",
      rainy: "雨の日",
    },
    foodCrowdFilters: {
      all: "すべて",
      easy: "入りやすい",
      busy: "混雑しやすい",
      reservation: "予約推奨",
    },
    yabijiNotice:
      "今シーズンの八重干瀬ツアーは、もうすぐ終わりです。来年の3月ごろからまた船が出るので、各ツアー会社のSNSをチェックしてください。",
    yabijiTourTitle: "おすすめのツアー",
    yabijiTourLead: "現地の方から人気のおすすめツアーです。最新の催行状況はDMなどで直接ご確認ください。",
    scoreLabels: {
      snorkel: "シュノーケル",
      marine: "マリンアクティビティ",
      photo: "撮影",
      dronePhoto: "ドローン撮影",
      underwaterPhoto: "水中撮影",
      groundPhoto: "地上撮影",
      starPhoto: "星空撮影",
      sunsetPhoto: "サンセット指数",
      beginner: "初心者",
      turtle: "ウミガメ遭遇率",
      crowd: "混み具合",
    },
    starSpotLabels: {
      photoScore: "撮影の評価",
      bestTime: "おすすめの時間帯",
      darknessScore: "暗さの評価",
      driveTimeLabel: "市街地から車で",
      milkyWayScore: "天の川評価",
      milkyWayDirectionLabel: "天の川の向き",
      milkyWayDirectionTonight: "南の空",
      milkyWayVisibleLabel: "天の川",
      milkyWayVisibleYes: "今すぐ見えます",
      milkyWayVisibleAt: "{time}に見えます",
      defaultBestTime: "21:00〜深夜",
      milkyWayDirectionFitLabel: "天の川の見え方",
      milkyWayDirectionGood: "見えやすい",
      milkyWayDirectionPartial: "やや見えにくい",
      milkyWayDirectionPoor: "見えにくい",
      monthlyInfoTitle: "今月の星空情報（今日の情報ではありません）",
      newMoonTitle: "新月",
      monthlyBestTitle: "今月のおすすめ撮影日",
      moonNote: "新月前後は月明かりが少なく、天の川や星空撮影に向いています。",
      starConditionsTitle: "美しい星空を楽しめる条件",
      starConditionsItems: [
        ["新月の前後", "空が暗いため、星がよりはっきりと見える"],
        ["大雨の翌日", "地表に水蒸気がたまり、上空に雲が少ない"],
        ["風のない日", "大気の動きがない方が、星の光がまっすぐ届く"],
      ],
      mapConditionBest: "とても良い",
      mapConditionGood: "良い",
      mapConditionCaution: "見えにくい",
      mapConditionBad: "おすすめしない",
      tonightRatingTitle: "今日の夜の総合評価",
      weatherFactorLabel: "天気",
      cloudFactorLabel: "夜の雲量",
      windFactorLabel: "風",
      moonFactorLabel: "月明かり",
      windCalm: "穏やか",
      windModerate: "やや風あり",
      windStrong: "風が強め",
      windVeryStrong: "非常に強い",
      moonDark: "とても暗い",
      moonSlightlyDark: "暗め",
      moonSlightlyBright: "やや明るい",
      moonBright: "明るい",
      moonAgeLabel: "月齢",
      sunsetLabel: "日の入り",
      moonriseLabel: "月の出",
      moonsetLabel: "月の入り",
      moonRecBrightUp:
        "月の光がなくなる月の入り（{time}）以降がおすすめです。",
      moonRecBrightDown:
        "月の光がない月の出（{time}）までがおすすめです。",
      moonRecDark: "月明かりが少ないため、星空観測におすすめです。",
      moonRecBelowHorizon:
        "今夜は月が出ていない時間が長いため、月明かりの影響を受けにくく、星空観測におすすめです。",
      moonRecBrightAllNight:
        "今夜は月明かりが強いままなので、星空観測にはあまり向いていません。",
      moonBestTimeUntilMoonrise: "{start}〜{time}",
      moonBestTimeAfterMoonset: "{time}〜深夜",
    },
    sunsetSpotLabels: {
      waveTitle: "夕方の波",
      windTitle: "風",
      sunsetTitle: "日の入り",
      sunsetScore: "夕日評価",
      bestTime: "おすすめの時間帯",
      defaultBestTime: "日の入り30分前〜日没後",
      mapConditionBest: "とても良い",
      mapConditionGood: "良い",
      mapConditionCaution: "天気を確認",
      mapConditionBad: "微妙",
      weatherLabel: "天気",
      ratingLabel: "評価",
      crowdLabel: "混み具合",
      crowdPrivate: "貸切",
      crowdQuiet: "空いている",
      crowdModerate: "やや混雑",
      crowdBusy: "混雑しやすい",
      burnLevels: {
        high: "🔥 空が焼けそう",
        medium: "空が焼けるかも",
      },
    },
    recommend: "{day}のおすすめスポット",
    snorkelConditionTitle: "{day}の総合評価",
    snorkelConditionBest: "とても良い",
    snorkelConditionGood: "良い",
    snorkelConditionCaution: "注意",
    snorkelConditionBad: "おすすめしない",
    shelteredSpotsTitle: "{day}は安全なビーチで楽しみましょう",
    windResistantSpotsTitle: "{day}は風の影響を受けにくいビーチがおすすめです",
    dayToggleToday: "今日",
    dayToggleTomorrow: "明日",
    snorkelConditionCardTitle: "{day}の海況情報",
    weatherLabel: "天気",
    todayWeatherHourlyLabel: "天気",
    todayWaveLabel: "波",
    windLabel: "風",
    tideLabel: "満潮・干潮",
    tideHighShort: "満潮",
    tideLowShort: "干潮",
    windImpactLabel: "風の影響",
    waveImpactLabel: "波の影響",
    windImpactStrong: "{direction}風を受けやすい",
    windImpactOffshore: "{direction}の風で陸風になり穏やか",
    windImpactUnknown: "--",
    waveImpactHigh: "受けやすい",
    waveImpactCalm: "受けにくい",
    waveImpactUnknown: "--",
    starBest: "今日の星空ベストスポット",
    starTour: {
      label: "おすすめツアー",
      title: "宮古島 星空撮影ツアー",
      notice: "星空のコンディションについてやツアーの予約は直接DMでお問い合わせください！",
      description:
        "宮古島で星空撮影といえばこの男。星空に特化したカメラマンが、その日ベストな場所で、クリエイティブに富んだ特別な思い出を残します。色味や画角にもこだわり、宮古島の夜を一生見返したくなる写真に。",
      points: [
        "Photo World 2026コンテスト受賞",
        "累計3000組以上撮影",
        "星空に特化したカメラマン",
        "その日ベストな星空スポットへ",
      ],
      button: "Instagramで見る",
    },
    mapStarTour: {
      label: "星空撮影",
      title: "宮古島の星空を特別な写真に",
      notice: "星空のコンディションについてやツアーの予約は直接DMでお問い合わせください！",
      description:
        "宮古島で活動する映像クリエイターが、星空スポットを知り尽くした経験をもとに、その日ベストな場所で星空撮影。映像クリエイターならではの色味や画角にこだわり、特別な写真を撮影します。",
      points: ["星空スポットを知り尽くした案内", "その日ベストな場所で撮影", "色味や画角にこだわった一枚"],
      button: "投稿を見る",
    },
    nav: ["ホーム", "マップ", "予報", "ガイド", "メニュー"],
    mapTitle: "現在地付近のベストスポット",
    mapSelectAriaLabel: "{name}を選択",
    mapAreaLabel: "エリア",
    mapRecommendLabel: "おすすめ",
    mapPhoneLabel: "電話",
    mapHoursLabel: "時間",
    mapLegendTitle: "コンディションの見方",
    mapConditionBest: "とても良い",
    mapConditionGood: "良い",
    mapConditionCaution: "注意",
    mapConditionBad: "危険",
    beachLoungeBest: "とても良い",
    beachLoungeGood: "良い",
    beachLoungeCaution: "良くない",
    beachLoungeBad: "やめときましょう",
    mapTapHintText: "📍 地図上の場所をタップすると詳細がポップアップ表示されます",
    mapClickToInteract: "タップして地図を操作",
    oceanClarityLabel: "海の透明度",
    overallCautionBeforeMap: "今日は注意が必要な海況です。",
    overallCautionAfterMap: "を確認し、無理のない場所と時間を選びましょう。",
    overallAssessment: {
      best: "今日は海況がとても良く、海遊びを楽しみやすい一日です。",
      good: "今日は比較的海遊びを楽しみやすい一日です。風や波の変化を確認しながら行動しましょう。",
      caution: "今日は注意が必要な海況です。風・波・雨雲を確認し、無理のない場所と時間を選びましょう。",
      bad: "今日は海遊びに適さない海況です。無理に海へ入らず、別の過ごし方を選びましょう。",
    },
    clarityLabel: "透明度",
    clarityNormal: "普通",
    clarityBest: "最高",
    clarityHigh: "高い",
    claritySlightlyLow: "やや低い",
    clarityLow: "低い",
    overallRatingTitle: "{day}の海遊び",
    todayBeachLoungeLabel: "{day}のビーチル",
    todayConditionLabel: "{day}のコンディション",
    beachInfoLabel: "ビーチ情報",
    crowdScoreVeryBusy: "かなり混む",
    crowdScoreSlightlyBusy: "少し混む",
    crowdScoreFew: "空いてる",
    crowdScorePrivate: "ほぼプライベート",
    parkingLabel: "駐車場",
    facilitiesLabel: "設備・サービス",
    tourLinkLabel: "ツアーを見る 〉",
    sunriseLabel: "日の出",
    sunsetLabel: "日の入り",
    highTideLabel: "満潮",
    lowTideLabel: "干潮",
    hourlyRainGuideTitle: "時間ごとの雨の目安",
    hourlyRainNote: "Yahoo天気APIの現在雨量をもとに表示しています。風は下のOpen-Meteo予報で確認できます。",
    tideSectionTitle: "{day}の満潮・干潮",
    rainProbabilitySectionTitle: "{day}の時間ごとの降水確率",
    windForecastLoading: "読み込み中",
    fetchingStatusLabel: "取得中",
    windForecastLoadingNote: "時間別の風速予報を読み込んでいます。",
    guideIntroTitle: "海遊びを120%楽しむために",
    guideIntroSub: "宮古島の海を安全に、思いっきり楽しむためのガイドです。",
    guideActivitySectionTitle: "アクティビティガイド",
    photoTourLinkLabel: "撮影付きツアーを見る",
    termsTitle: "利用規約",
    termsText:
      "本アプリで表示している海況・天気などの情報は、各種気象データおよび統計に基づいて算出した参考値です。宮古島の天気は予報が外れることも多く、非常に変わりやすい特性があります。表示内容を100％信用せず、あくまで宮古島でお過ごしいただく際の参考情報としてご活用ください。万が一、本アプリの情報をもとにした行動により事故・トラブル等が発生した場合でも、当方は一切の責任を負いかねますので、あらかじめご了承の上でご利用ください。",
    aboutWhyTitle: "なぜ作ったのか",
    aboutWhyText:
      "宮古島に移住し、ツアーガイドとして仕事をする中で、多くの観光客の方が共通の悩みを抱えていることに気づきました。「この日程の天気はどうか」「今日はどこの海がいいか」「ご飯のおすすめ」「台風の影響で海況はどうなるか」——知りたい情報は共通しているのに、それらが様々な場所に散らばっており、天気予報サイトも複数あってどれを信じればいいか分からないという声も多く耳にしました。そこで、宮古島を訪れる方が迷わず、もっと宮古島を楽しめるように、必要な情報を一つにまとめたアプリを作ろうと決め開発しました。宮古島旅行はこのアプリを見れば間違いないという最も信頼できるものを作っていきます。今後はおすすめのツアーの紹介等もしていく予定です。掲載する情報やツアーは私が本当にいいと思った厳選したものだけを掲載しています。",
    aboutFeedbackTitle: "ご要望・情報について",
    aboutFeedbackText:
      "「データが間違っている」「こんな機能が欲しい」といったお声をお待ちしております。実際にご利用いただいた方々の声をもとに、このアプリは日々進化していきます。現在は私1人で開発、運営しているため、対応にお時間をいただく場合がございますが、価値あるご意見は積極的に取り入れ、改善を重ねてまいりますので、どうぞよろしくお願いいたします。まずはwebでの公開になりますが、今後需要があればアプリとしてリリースを予定しております。",
    developerCredit: "開発者：Premium Tour Miyakoisland 代表 藤江 廉",
    backButtonLabel: "戻る",
    closeButtonLabel: "閉じる",
    droneTourDriveTime: "宮古島市街地から車で約{minutes}分",
    photogenicScoreTitle: "映える指数（総合評価）",
    spotInfoTitle: "スポット情報",
    approxMinutesTemplate: "約{minutes}分",
    todayWaveFactLabel: "今日の波",
    windStrengthLabel: "風の強さ",
    beginnerSuitabilityLabel: "初心者適性",
    crowdLevelLabel: "混雑度",
    priceRangeLabel: "価格帯",
    menuFoodDrinkTemplate: "ご飯：{food} / ドリンク：{drink}",
    menuFoodOnlyTemplate: "ご飯：{food}",
    menuDrinkOnlyTemplate: "ドリンク：{drink}",
    hotelViewLabel: "オーシャンビュー",
    hotelGuideEmbedLabel: "宮古島のホテルをもっと見る",
    highTempLabel: "最高",
    lowTempLabel: "最低",
    rainHeroTitle1: "今日の宮古島は雨模様",
    rainHeroTitle2: "雨の日でも楽しめる体験をチェック！",
    rainHeroSub: "雨の日限定のおすすめ広告・体験",
    outdoorActivityLabel: "屋外アクティビティは",
    outdoorActivityLevelLow: "控えめ",
    rainyRecommendTitle: "雨の日のおすすめ体験",
    viewOnMapLabel: "📍 地図で見る",
    menuHeroTitle: "宮古島をもっと楽しむ",
    menuHeroSub: "海況・予報・ガイドを見ながら、今日の過ごし方を選べます。",
    menuItems: {
      map: { title: "マップを見る", description: "今行きやすい海遊びスポットを地図で確認" },
      forecast: { title: "今日の予報を見る", description: "天気・降水確率・波・月の出入りを確認" },
      guide: { title: "海遊びガイド", description: "初めてでも安心して楽しむための基本" },
      rain: { title: "雨の日でも楽しむ", description: "カフェや室内体験など雨の日の過ごし方" },
    },
    forecastTitle: "日別おすすめ度",
    guideTitle: "はじめての宮古島",
    detailTitle: "映えるスポット詳細",
    condition: "現在のコンディション",
    hourlyTitle: "時間ごとの予報",
    bestTimeTitle: "今日のベストタイム",
    hourlyWindTitle: "時間ごとの風と安全目安",
    hourlyWindNote:
      "Open-Meteoの時間別予報をもとに表示しています。5m/s前後から流されやすくなるため、初心者は無理せず風下へ流されない場所を選んでください。",
    windAvoidanceLabel: "避けたい場所",
    forecastScoreTitle: "総合評価",
    windGustLabel: "最大",
    hourlyWindItems: [
      ["6:00", "南", "3m/s", "windSafe", "○", "安全寄り", "流されにくい"],
      ["9:00", "南", "4m/s", "windSafe", "○", "比較的安全", "沖向きの風に注意"],
      ["12:00", "南", "5m/s", "windCaution", "△", "注意", "流されやすい"],
      ["15:00", "南東", "5m/s", "windCaution", "△", "注意", "風下へ流されやすい"],
      ["18:00", "南東", "4m/s", "windSafe", "○", "比較的安全", "サンセット撮影向き"],
      ["21:00", "南東", "3m/s", "windSafe", "○", "安全寄り", "夜は足元に注意"],
    ],
    bestTimes: [
      [
        "ドローン撮影",
        "朝 / 15時ごろ / サンセット",
        "太陽が真上に来る正午前後を避け、斜めから光が入る朝や夕方がおすすめです。",
      ],
      ["シュノーケル", "9:00〜12:00", "透明度を見ながら午前中に行くのがおすすめ"],
    ],
    bestTimeBad: "場所を選んで遊びましょう",
    bestTimePhotoBad: "天気や風の状況を見ながら、落ち着く時間を選びましょう。",
    bestTimeSnorkelBad: "波・うねり・風の影響で流されやすくなるため、初心者のシュノーケルはおすすめしません。",
    bestTimeSnorkelMorning: "波や風の影響が比較的少ない午前中がおすすめです。",
    bestTimeMarineBad: "おすすめしません。キャンセルになる可能性があります。",
    bestTimeMarineGood: "波と風が落ち着いている午前中を選ぶと、マリンアクティビティを楽しみやすいです。",
    safetyTitle: "安全ガイド",
    natureTitle: "自然・生き物ガイド",
    rainLead: "雨の日でも無理なく楽しめる過ごし方をまとめました。",
    rainRadarTitle: "宮古島の雨雲レーダー",
    rainRadarLead: "色が濃いエリアほど雨が強い可能性があります。宮古島の上空だけでなく、風上側の雨雲の動きも見ておくと予定を立てやすいです。",
    rainRadarAdvice: "雨雲が近づく時間帯は、海遊びよりカフェ・室内体験・ホテルでゆっくり過ごす予定に切り替えるのがおすすめです。",
    rainRadarButton: "詳しい雨雲レーダーを見る",
    rainRadarNearAlert: "{name} 周辺で雨雲に注意",
    rainRadarDayAlert: "{day}は雨の時間帯に注意",
    rainRadarSelectedDayFallback: "選択日",
    rainRadarLowSummary: "宮古島周辺に強い雨雲は少なめ",
    rainRadarCurrentNote: "現在の目安は{rainfall}mm/hです。海遊び前に最新レーダーを確認してください。",
    rainRadarFutureHighNote: "時間帯別の降水確率が高めです。出発前に最新の雨雲レーダーを確認してください。",
    rainRadarLowNote: "今のところ雨は弱めです。海況と風を見ながら、午前中から動くのがおすすめです。",
    rainProbabilityLabel: "降水確率",
    rainMovementLink: "雨の動きを確認",
    typhoonTitle: "台風の発生状況",
    typhoonLead:
      "海遊びや観光に影響がありそうかを、気象庁の最新情報からわかりやすく表示します。",
    typhoonNone: "現在、宮古島への影響が予想される台風・熱帯低気圧はありません。",
    typhoonOfficial: "気象庁で進路を確認",
    typhoonDistance: "宮古島から約{distance}km",
    typhoonTrackLabel: "進路の目安",
    typhoonUpdated: "発表",
    typhoonImpactLabel: "宮古島への影響",
    typhoonFlightImpactLabel: "航空便への影響",
    typhoonPeriodLabel: "影響が予想される期間",
    typhoonPeriodRange: "{start} 〜 {end}",
    typhoonPeriodCaveat: "※現在発表されている予報に基づく目安です。進路や速度の変化により前後する可能性があります。",
    typhoonAfterPassNote: "台風が過ぎた後もしばらくは波やうねりが残り、海況が荒れた状態が続くことがあります。",
    typhoonAvoidTitle: "避けたい場所",
    typhoonAvoidSeparator: "、",
    typhoonAvoidMapPrefix: "比較的安全な場所はこちらの",
    typhoonAvoidMapButtonLabel: "マップ",
    typhoonAvoidMapSuffix: "をチェック",
    typhoonMovementLabel: "動き",
    typhoonWeatherSummary:
      "台風のうねりや風の影響があります。海は流されやすいのでお気をつけください。",
    waveHeightLabel: "波の高さ",
    swellLabel: "うねり",
    waveMeaning:
      "波の高さは、海面がどれくらい荒れているかの目安です。目安として0.6m以上でやや不安定になりやすく、1m以上でシュノーケルをする場合は十分お気をつけください。",
    swellMeaning:
      "うねりは、遠くの台風や低気圧から届く大きな揺れです。目安として0.5m以上で影響が出始め、1m以上は晴れていても急に波が強くなることがあります。",
    tideRecommendTitle: "潮位に合わせたおすすめ",
    tideConditionCaution: "潮位は良い時間ですが、波・うねり・風が強めです。行く場合は海況を見て無理しないでください。",
    tideDayParts: {
      morning: "午前中",
      afternoon: "午後",
      daytime: "日中",
    },
    tideTimeLabel: "{time}ごろ（{part}）",
    tideRecommendations: [
      ["満潮に近い時間", "ユニの浜ツアーがおすすめ", "ユニの浜でドローン撮影するなら満潮で午前中がおすすめ。"],
      ["干潮に近い時間", "17ENDがおすすめ", "干潮時に白い砂浜が出現し、宮古ブルーとのコントラストがとても綺麗に見えます。"],
    ],
    book: "ツアーを予約する",
    navigate: "ここに行く",
    viewDetails: "詳細を見る",
    caution: "注意が必要",
    good: "とても良い",
    ok: "初心者OK",
  },
  EN: {
    lang: "EN",
    appName: "Miyako Ocean Guide",
    pageHeroSubtitle:
      "An app that brings together everything you want to check during your trip to Miyakojima.",
    mapButton: "View Map",
    rainButton: "View Rainy Day Screen",
    heroTitle: "{day} is perfect for ocean activities.",
    heroSub: "Keep an eye on changing wind and waves, and enjoy the ocean safely.",
    heroBadTitle: "{day}, sea conditions are a bit rough",
    heroBadSub: "Choose a safer beach and enjoy the ocean without pushing it.",
    heroWindTitle: "{day}, strong winds are making the sea very rough",
    heroWindSub: "Consider skipping ocean activities and spend the day indoors or at a cafe instead",
    heroRainTitle: "{day} is a rainy day",
    heroRainSub: "Enjoy cafe-hopping or indoor experiences instead of ocean activities",
    heroHighRainTitle: "Watch for rain while enjoying Miyakojima {day}!",
    heroHighRainSub: "Plan around the showers and enjoy the day at a comfortable pace.",
    heroByCategory: {
      snorkel: {
        title: "{day} is perfect for ocean activities.",
        sub: "Keep an eye on changing wind and waves, and enjoy the ocean safely.",
      },
      stars: {
        title: "Enjoy Miyakojima's Starry Sky Tonight",
        sub: "Check clouds, wind, and moonlight to find a good place for night-sky photos.",
      },
      sunset: {
        title: "Where to Watch Sunset in Miyakojima Today",
        sub: "Use sunset time and wind conditions to choose a beautiful sunset spot.",
      },
      yabiji: {
        title: "Yabiji: Miyakojima's Hidden Reef",
        sub: "A vast coral reef only reachable by tour boat. Trips depend heavily on sea conditions.",
      },
      rainy: {
        title: "Indoor Experiences in Miyakojima for Rainy Days",
        sub: "Check out resin art, Shisa making, and other great rainy-day activities.",
      },
    },
    tabs: {
      snorkel: "Snorkel",
      stars: "Stars",
      sunset: "Sunset",
      beach: "Beach Chill",
      cafe: "Lunch & Cafe",
      lunch: "Lunch & Cafe",
      dinner: "Dinner",
      hotel: "Hotels",
      voices: "Live updates",
      nightlife: "Nightlife",
      lateNightMeal: "Late-Night Meal",
      yabiji: "Yabiji",
      rainy: "Rainy Day",
    },
    foodCrowdFilters: {
      all: "All",
      easy: "Easy to enter",
      busy: "Often busy",
      reservation: "Reservation",
    },
    yabijiNotice:
      "Yabiji tours for this season are ending soon. Boats are expected to resume around March next year, so please check each operator's social media.",
    yabijiTourTitle: "Recommended Tours",
    yabijiTourLead: "Popular tours among locals. Please confirm the latest operating status directly by DM.",
    scoreLabels: {
      snorkel: "Snorkel",
      marine: "Marine Activities",
      photo: "Photography",
      dronePhoto: "Drone Photography",
      underwaterPhoto: "Underwater Photography",
      groundPhoto: "Ground Photography",
      starPhoto: "Star Photography",
      sunsetPhoto: "Sunset Score",
      beginner: "Beginner Friendly",
      turtle: "Turtle Encounter Rate",
      crowd: "Crowd Level",
    },
    starSpotLabels: {
      photoScore: "Photo Rating",
      bestTime: "Best Time",
      darknessScore: "Darkness Rating",
      driveTimeLabel: "From downtown",
      milkyWayScore: "Milky Way Rating",
      milkyWayDirectionLabel: "Milky Way Direction",
      milkyWayDirectionTonight: "Southern sky",
      milkyWayVisibleLabel: "Milky Way",
      milkyWayVisibleYes: "Visible now",
      milkyWayVisibleAt: "Visible {time}",
      defaultBestTime: "21:00-late night",
      milkyWayDirectionFitLabel: "Milky Way Visibility",
      milkyWayDirectionGood: "Good view",
      milkyWayDirectionPartial: "Partially blocked",
      milkyWayDirectionPoor: "Hard to see",
      monthlyInfoTitle: "This month's star info (not today's forecast)",
      newMoonTitle: "New Moon",
      monthlyBestTitle: "Best Shooting Dates This Month",
      moonNote: "Around the new moon, moonlight is low, making it better for Milky Way and starry-sky photos.",
      starConditionsTitle: "Conditions for a Beautiful Starry Sky",
      starConditionsItems: [
        ["Around the new moon", "The sky is darker, so stars stand out more clearly"],
        ["The day after heavy rain", "Moisture settles near the ground, leaving fewer clouds above"],
        ["Windless nights", "Calmer air lets starlight travel straight without shimmering"],
      ],
      mapConditionBest: "Excellent",
      mapConditionGood: "Good",
      mapConditionCaution: "Hard to see",
      mapConditionBad: "Not recommended",
      tonightRatingTitle: "Tonight's Overall Rating",
      weatherFactorLabel: "Weather",
      cloudFactorLabel: "Night cloud cover",
      windFactorLabel: "Wind",
      moonFactorLabel: "Moonlight",
      windCalm: "Calm",
      windModerate: "Slightly windy",
      windStrong: "Fairly strong",
      windVeryStrong: "Very strong",
      moonDark: "Very dark",
      moonSlightlyDark: "Fairly dark",
      moonSlightlyBright: "Somewhat bright",
      moonBright: "Bright",
      moonAgeLabel: "Moon Age",
      sunsetLabel: "Sunset",
      moonriseLabel: "Moonrise",
      moonsetLabel: "Moonset",
      moonRecBrightUp:
        "Best after moonset ({time}), once the moonlight is gone.",
      moonRecBrightDown:
        "Best until moonrise ({time}), while there's no moonlight.",
      moonRecDark: "Low moonlight makes tonight good for stargazing.",
      moonRecBelowHorizon: "The moon stays below the horizon for much of tonight, making conditions favorable for stargazing.",
      moonRecBrightAllNight:
        "The moon stays bright tonight, so it's not an ideal night for stargazing.",
      moonBestTimeUntilMoonrise: "{start}-{time}",
      moonBestTimeAfterMoonset: "{time}-late night",
    },
    sunsetSpotLabels: {
      waveTitle: "Evening Waves",
      windTitle: "Wind",
      sunsetTitle: "Sunset",
      sunsetScore: "Sunset Rating",
      bestTime: "Best Time",
      defaultBestTime: "30 min before sunset-after sunset",
      mapConditionBest: "Excellent",
      mapConditionGood: "Good",
      mapConditionCaution: "Check the weather",
      mapConditionBad: "So-so",
      weatherLabel: "Weather",
      ratingLabel: "Rating",
      crowdLabel: "Crowd Level",
      crowdPrivate: "Private",
      crowdQuiet: "Quiet",
      crowdModerate: "Somewhat busy",
      crowdBusy: "Often crowded",
      burnLevels: {
        high: "🔥 Fiery sunset likely",
        medium: "Sunset may be vivid",
      },
    },
    recommend: "{day}'s Recommended Spots",
    snorkelConditionTitle: "{day}'s Overall Rating",
    snorkelConditionBest: "Excellent",
    snorkelConditionGood: "Good",
    snorkelConditionCaution: "Caution",
    snorkelConditionBad: "Not recommended",
    shelteredSpotsTitle: "{day}: safe beaches to enjoy",
    windResistantSpotsTitle: "{day}: beaches less affected by the wind are recommended",
    dayToggleToday: "Today",
    dayToggleTomorrow: "Tomorrow",
    snorkelConditionCardTitle: "{day}'s Sea Conditions",
    weatherLabel: "Weather",
    todayWeatherHourlyLabel: "Weather",
    todayWaveLabel: "Waves",
    windLabel: "Wind",
    tideLabel: "High / Low Tide",
    tideHighShort: "High",
    tideLowShort: "Low",
    windImpactLabel: "Wind Impact",
    waveImpactLabel: "Wave Impact",
    windImpactStrong: "Easily affected by {direction} wind",
    windImpactOffshore: "Calm ({direction} offshore wind)",
    windImpactUnknown: "--",
    waveImpactHigh: "Easily affected",
    waveImpactCalm: "Not easily affected",
    waveImpactUnknown: "--",
    starBest: "Best Starry Sky Spot",
    starTour: {
      label: "Recommended Tour",
      title: "Miyakojima Starry Sky Photo Tour",
      notice: "Please DM directly to ask about current starry-sky conditions or to book a tour.",
      description:
        "When it comes to starry sky photography in Miyakojima, this is the photographer to know. A specialist in night-sky shoots chooses the best location of the day and creates creative, memorable photos with careful attention to color and framing.",
      points: [
        "Photo World 2026 award winner",
        "Over 3,000 shoots completed",
        "Starry sky photography specialist",
        "Best star spot for the day",
      ],
      button: "View on Instagram",
    },
    mapStarTour: {
      label: "Star Photography",
      title: "Turn Miyakojima's Stars into a Special Photo",
      notice: "Please DM directly to ask about current starry-sky conditions or to book a tour.",
      description:
        "A Miyakojima-based videographer who knows the island's starry sky spots chooses the best location of the day and captures special photos with careful attention to color, framing, and atmosphere.",
      points: ["Guided by someone who knows the star spots", "Shot at the best location of the day", "Color and framing with a creative eye"],
      button: "View Post",
    },
    nav: ["Home", "Map", "Forecast", "Guide", "Menu"],
    mapTitle: "Best spots near you",
    mapSelectAriaLabel: "Select {name}",
    mapAreaLabel: "Area",
    mapRecommendLabel: "Recommended",
    mapPhoneLabel: "Phone",
    mapHoursLabel: "Hours",
    mapLegendTitle: "How to read conditions",
    mapConditionBest: "Excellent",
    mapConditionGood: "Good",
    mapConditionCaution: "Caution",
    mapConditionBad: "Dangerous",
    beachLoungeBest: "Great",
    beachLoungeGood: "Good",
    beachLoungeCaution: "Not great",
    beachLoungeBad: "Best to avoid",
    mapTapHintText: "📍 Tap a place on the map to see details in a popup",
    mapClickToInteract: "Tap to interact with the map",
    oceanClarityLabel: "Water Clarity",
    overallAssessment: {
      best: "Sea conditions are excellent today, making it a great day for ocean activities.",
      good: "Conditions are fairly good for ocean activities today. Keep checking for changes in wind and waves.",
      caution: "Use caution with ocean activities today. Check the wind, waves, and rain radar, and choose a safe time and place.",
      bad: "Conditions are unsuitable for ocean activities today. Avoid entering the water and choose another activity.",
    },
    clarityLabel: "Clarity",
    clarityNormal: "Normal",
    clarityBest: "Excellent",
    clarityHigh: "High",
    claritySlightlyLow: "Slightly Low",
    clarityLow: "Low",
    overallRatingTitle: "{day}'s Ocean Play",
    todayBeachLoungeLabel: "{day}'s Beach Chill",
    todayConditionLabel: "{day}'s Condition",
    beachInfoLabel: "Beach Info",
    crowdScoreVeryBusy: "Quite Crowded",
    crowdScoreSlightlyBusy: "Somewhat Crowded",
    crowdScoreFew: "Uncrowded",
    crowdScorePrivate: "Almost Private",
    parkingLabel: "Parking",
    facilitiesLabel: "Facilities & Services",
    tourLinkLabel: "View Tours 〉",
    sunriseLabel: "Sunrise",
    sunsetLabel: "Sunset",
    highTideLabel: "High Tide",
    lowTideLabel: "Low Tide",
    hourlyRainGuideTitle: "Hourly Rain Guide",
    hourlyRainNote: "Based on current rainfall from the Yahoo Weather API. Check wind in the Open-Meteo forecast below.",
    tideSectionTitle: "High/Low Tide for {day}",
    rainProbabilitySectionTitle: "Hourly Rain Chance for {day}",
    windForecastLoading: "Loading",
    fetchingStatusLabel: "Fetching",
    windForecastLoadingNote: "Loading the hourly wind forecast.",
    guideIntroTitle: "Enjoy the Ocean 120%",
    guideIntroSub: "A guide to enjoying Miyakojima's ocean safely and to the fullest.",
    guideActivitySectionTitle: "Activity Guide",
    photoTourLinkLabel: "View Tours with Photography",
    termsTitle: "Terms of Use",
    termsText:
      "The ocean and weather data shown in this app are reference values calculated from various meteorological data and statistics. Weather in Miyakojima is highly changeable, and forecasts are often inaccurate. Please don't rely on this information 100% — use it only as a reference for enjoying your time in Miyakojima. We cannot be held responsible for any accidents or issues arising from actions based on this app's information, so please use it with that understanding.",
    aboutWhyTitle: "Why I Made This",
    aboutWhyText:
      "After moving to Miyakojima and working as a tour guide, I noticed that visitors kept running into the same questions: What's the weather like on this day? Which beach is good today? Any food recommendations? Is the sea safe, or should I cancel because of a typhoon? Everyone wanted the same information, but it was scattered across many different sources — and with so many weather forecasts out there, people didn't know which one to trust. So I decided to build an app that brings everything together in one place, to help visitors enjoy Miyakojima without the guesswork. I'll keep building this into the most trustworthy resource out there — the one place you can check and know your Miyakojima trip will go smoothly. Going forward, I also plan to introduce recommended tours. The information and tours featured here are only ever carefully selected ones that I personally believe are genuinely worth it.",
    aboutFeedbackTitle: "Feedback & Information",
    aboutFeedbackText:
      "I welcome feedback — whether it's about incorrect data or features you'd like to see. This app keeps evolving based on input from people who actually use it. Since I currently run this entirely on my own, replies may take some time, but I read every message and keep improving the app with any feedback that adds real value. Thank you for your understanding. This is starting out as a web app, but if there's enough demand, I plan to release it as a dedicated app in the future.",
    developerCredit: "Developer: Premium Tour Miyakoisland — Representative Ren Fujie",
    backButtonLabel: "Back",
    closeButtonLabel: "Close",
    droneTourDriveTime: "About {minutes} min by car from Miyako city center",
    photogenicScoreTitle: "Photogenic Score (Overall)",
    spotInfoTitle: "Spot Info",
    approxMinutesTemplate: "About {minutes} min",
    todayWaveFactLabel: "Today's Waves",
    windStrengthLabel: "Wind Strength",
    beginnerSuitabilityLabel: "Beginner Suitability",
    crowdLevelLabel: "Crowd Level",
    priceRangeLabel: "Price Range",
    menuFoodDrinkTemplate: "Food: {food} / Drink: {drink}",
    menuFoodOnlyTemplate: "Food: {food}",
    menuDrinkOnlyTemplate: "Drink: {drink}",
    hotelViewLabel: "Ocean View",
    hotelGuideEmbedLabel: "See more Miyakojima hotels",
    highTempLabel: "High",
    lowTempLabel: "Low",
    rainHeroTitle1: "It looks rainy in Miyakojima today",
    rainHeroTitle2: "Check out experiences you can enjoy on rainy days!",
    rainHeroSub: "Recommended rainy-day-only picks & experiences",
    outdoorActivityLabel: "Outdoor activities:",
    outdoorActivityLevelLow: "Limited",
    rainyRecommendTitle: "Recommended Rainy Day Experiences",
    viewOnMapLabel: "📍 View on map",
    menuHeroTitle: "Enjoy Miyakojima Even More",
    menuHeroSub: "Check ocean conditions, forecasts, and guides to plan your day.",
    menuItems: {
      map: { title: "View Map", description: "Check easy-to-reach ocean spots on the map" },
      forecast: { title: "View Today's Forecast", description: "Check weather, rain chance, waves, and moon times" },
      guide: { title: "Ocean Guide", description: "The basics for enjoying the ocean safely, even for beginners" },
      rain: { title: "Enjoy Rainy Days", description: "Cafes, indoor experiences, and other rainy-day plans" },
    },
    forecastTitle: "Daily recommendation",
    guideTitle: "First time in Miyakojima",
    detailTitle: "Photogenic Spot Detail",
    condition: "Current Conditions",
    hourlyTitle: "Hourly Forecast",
    bestTimeTitle: "Best Times Today",
    hourlyWindTitle: "Hourly Wind and Safety",
    hourlyWindNote:
      "Based on Open-Meteo hourly forecasts. Around 5 m/s, beginners can drift more easily, so choose sheltered spots and avoid offshore wind.",
    windAvoidanceLabel: "Avoid",
    forecastScoreTitle: "Overall Ratings",
    windGustLabel: "gust",
    hourlyWindItems: [
      ["6:00", "S", "3m/s", "windSafe", "○", "Safer", "Low drift risk"],
      ["9:00", "S", "4m/s", "windSafe", "○", "Mostly safe", "Watch offshore wind"],
      ["12:00", "S", "5m/s", "windCaution", "△", "Caution", "Easy to drift"],
      ["15:00", "SE", "5m/s", "windCaution", "△", "Caution", "Can drift downwind"],
      ["18:00", "SE", "4m/s", "windSafe", "○", "Mostly safe", "Good for sunset photos"],
      ["21:00", "SE", "3m/s", "windSafe", "○", "Safer", "Watch your footing at night"],
    ],
    bestTimes: [
      [
        "Drone Shooting",
        "Morning / Afternoon / Sunset",
        "When the sun is directly overhead, the ocean can look washed out. Morning side light or later afternoon light is better. Choose the east side in the morning and the west side in the afternoon depending on the sun position. Sunset silhouettes with the sun behind you are also beautiful.",
      ],
      ["Snorkeling", "9:00-12:00", "Morning is recommended while checking the water clarity."],
    ],
    bestTimeBad: "Pick a sheltered spot",
    bestTimePhotoBad: "Check the weather and wind, and choose a calmer window.",
    bestTimeSnorkelBad: "Waves, swell, or wind can make drifting easier, so snorkeling is not recommended for beginners.",
    bestTimeSnorkelMorning: "Morning is recommended, when waves and wind tend to have less impact.",
    bestTimeMarineBad: "If conditions are poor, some tours may be cancelled.",
    bestTimeMarineGood: "Choose calmer wave and wind windows for safer ocean activities.",
    safetyTitle: "Safety Guide",
    natureTitle: "Nature Guide",
    rainLead: "Easy indoor ideas for rainy days in Miyakojima.",
    rainRadarTitle: "Rain Radar for Miyakojima",
    rainRadarLead: "Darker colored areas may mean stronger rain. Check not only Miyakojima itself, but also the clouds approaching from the windward side.",
    rainRadarAdvice: "If rain clouds are moving in, switch ocean plans to cafes, indoor activities, or a relaxed hotel stay.",
    rainRadarButton: "Open detailed rain radar",
    rainRadarNearAlert: "Watch for rain clouds near {name}",
    rainRadarDayAlert: "Watch for rain on {day}",
    rainRadarSelectedDayFallback: "the selected day",
    rainRadarLowSummary: "Few strong rain clouds around Miyakojima",
    rainRadarCurrentNote: "The current estimate is {rainfall}mm/h. Check the latest radar before ocean activities.",
    rainRadarFutureHighNote: "Rain chances are elevated for some hours. Check the latest rain radar before you head out.",
    rainRadarLowNote: "Rain is light for now. Keep an eye on the sea and wind, and it's a good idea to head out in the morning.",
    rainProbabilityLabel: "Rain chance",
    rainMovementLink: "Check rain movement",
    typhoonTitle: "Typhoon Status",
    typhoonLead:
      "Shows whether typhoons may affect ocean activities or travel, based on the latest JMA data.",
    typhoonNone: "No typhoon or tropical depression is currently expected to affect Miyakojima.",
    typhoonOfficial: "Check track on JMA",
    typhoonDistance: "About {distance} km from Miyakojima",
    typhoonTrackLabel: "Track outlook",
    typhoonUpdated: "Issued",
    typhoonImpactLabel: "Impact on Miyakojima",
    typhoonFlightImpactLabel: "Impact on Flights",
    typhoonPeriodLabel: "Expected Impact Period",
    typhoonPeriodRange: "{start} - {end}",
    typhoonPeriodCaveat: "Based on the currently published forecast; timing may shift if the track or speed changes.",
    typhoonAfterPassNote: "Even after the typhoon passes, waves and swell can remain for a while, keeping the sea rough.",
    typhoonAvoidTitle: "Places to Avoid",
    typhoonAvoidSeparator: ": ",
    typhoonAvoidMapPrefix: "Check this ",
    typhoonAvoidMapButtonLabel: "map",
    typhoonAvoidMapSuffix: " for relatively safe spots",
    typhoonMovementLabel: "Movement",
    typhoonWeatherSummary:
      "Typhoon swell and wind may affect the sea. It can be easier to drift, so please be careful.",
    waveHeightLabel: "Wave Height",
    swellLabel: "Swell",
    waveMeaning:
      "Wave height shows how rough the sea surface may be. As a guide, 0.6m or more starts to feel unstable, and 1m or more is not suitable for SUP or snorkeling.",
    swellMeaning:
      "Swell is a larger rolling motion from distant storms or low pressure. As a guide, effects start around 0.5m, and 1m or more can bring sudden strong waves even on sunny days.",
    tideRecommendTitle: "Best Spots by Tide",
    tideConditionCaution: "The tide timing is good, but waves, swell, or wind may be strong. Check conditions and avoid pushing it.",
    tideDayParts: {
      morning: "morning",
      afternoon: "afternoon",
      daytime: "daytime",
    },
    tideTimeLabel: "Around {time} ({part})",
    tideRecommendations: [
      ["Near high tide", "Yuni Beach is recommended", "It is easier to enjoy the floating sandbar scenery. For drone shots, high tide looks especially beautiful."],
      ["Near low tide", "17END is recommended", "At low tide, white sand appears and creates a beautiful contrast with Miyako blue."],
    ],
    book: "Book a Tour",
    navigate: "Go here",
    viewDetails: "View Details",
    caution: "Caution",
    good: "Excellent",
    ok: "Beginner OK",
  },
  中文: {
    lang: "中文",
    appName: "Miyako Ocean Guide",
    pageHeroSubtitle: "这是一款汇集宫古岛旅行中所需信息的应用。",
    mapButton: "查看地图",
    rainButton: "查看雨天页面",
    heroTitle: "{day}的宫古岛很适合海上活动！",
    heroSub: "请留意风浪变化，安全地享受大海。",
    heroBadTitle: "{day}海况不太稳定",
    heroBadSub: "请选择相对安全的海滩，不要勉强，安心享受。",
    heroWindTitle: "{day}风大浪急，海况非常恶劣",
    heroWindSub: "建议考虑取消海上活动，选择室内或咖啡店悠闲度过",
    heroRainTitle: "{day}不巧是雨天",
    heroRainSub: "比起海上活动，不如去咖啡店或室内体验悠闲度过",
    heroHighRainTitle: "{day}在宫古岛游玩时请注意降雨！",
    heroHighRainSub: "留意降雨间隙，合理安排行程，轻松享受旅程。",
    heroByCategory: {
      snorkel: {
        title: "{day}的宫古岛很适合海上活动！",
        sub: "请留意风浪变化，安全地享受大海。",
      },
      stars: {
        title: "今晚在宫古岛欣赏星空",
        sub: "查看云量、风和月光，寻找适合星空拍摄的地点。",
      },
      sunset: {
        title: "今天在宫古岛看日落的话",
        sub: "根据日落时间和风况，选择更适合欣赏夕阳的地点。",
      },
      yabiji: {
        title: "宫古岛的秘境・八重干瀬（Yabiji）",
        sub: "只能乘船前往的巨大梦幻珊瑚礁，是否出船很大程度取决于海况。",
      },
      rainy: {
        title: "宫古岛雨天也能享受的室内体验",
        sub: "树脂艺术、风狮爷制作等，一起看看适合雨天的体验吧",
      },
    },
    tabs: {
      snorkel: "浮潜",
      stars: "星空",
      sunset: "日落",
      beach: "海滩发呆",
      cafe: "午餐・咖啡",
      lunch: "午餐・咖啡",
      dinner: "晚餐",
      hotel: "酒店",
      voices: "大家动态",
      nightlife: "夜生活",
      lateNightMeal: "宵夜",
      yabiji: "八重干瀬",
      rainy: "雨天",
    },
    foodCrowdFilters: {
      all: "全部",
      easy: "较容易入店",
      busy: "容易拥挤",
      reservation: "建议预约",
    },
    yabijiNotice:
      "本季八重干濑行程即将结束。预计明年3月左右恢复出船，请关注各家行程公司的社交媒体。",
    yabijiTourTitle: "推荐行程",
    yabijiTourLead: "深受当地人欢迎的推荐行程，请直接通过私信确认最新出船情况。",
    scoreLabels: {
      snorkel: "浮潜",
      marine: "海上活动",
      photo: "拍摄",
      dronePhoto: "无人机航拍",
      underwaterPhoto: "水下摄影",
      groundPhoto: "地面摄影",
      starPhoto: "星空摄影",
      sunsetPhoto: "日落指数",
      beginner: "初学者友好度",
      turtle: "海龟遭遇率",
      crowd: "拥挤程度",
    },
    starSpotLabels: {
      photoScore: "拍摄评分",
      bestTime: "推荐时间",
      darknessScore: "暗度评分",
      driveTimeLabel: "距市区车程",
      milkyWayScore: "银河评分",
      milkyWayDirectionLabel: "银河方向",
      milkyWayDirectionTonight: "南方天空",
      milkyWayVisibleLabel: "银河",
      milkyWayVisibleYes: "现在可见",
      milkyWayVisibleAt: "{time}可见",
      defaultBestTime: "21:00〜深夜",
      milkyWayDirectionFitLabel: "银河可见度",
      milkyWayDirectionGood: "容易看到",
      milkyWayDirectionPartial: "视野稍受限",
      milkyWayDirectionPoor: "不易看到",
      monthlyInfoTitle: "本月星空信息（非今日实况）",
      newMoonTitle: "新月",
      monthlyBestTitle: "本月推荐拍摄日期",
      moonNote: "新月前后月光较少，更适合拍摄银河和星空。",
      starConditionsTitle: "欣赏美丽星空的条件",
      starConditionsItems: [
        ["新月前后", "天空更暗，星星看起来更清晰"],
        ["大雨过后的第二天", "地表聚集水汽，高空云量较少"],
        ["无风的夜晚", "大气稳定，星光可以直线到达"],
      ],
      mapConditionBest: "非常好",
      mapConditionGood: "良好",
      mapConditionCaution: "较难看到",
      mapConditionBad: "不推荐",
      tonightRatingTitle: "今晚综合评分",
      weatherFactorLabel: "天气",
      cloudFactorLabel: "夜间云量",
      windFactorLabel: "风",
      moonFactorLabel: "月光",
      windCalm: "平稳",
      windModerate: "略有风",
      windStrong: "风较强",
      windVeryStrong: "风非常强",
      moonDark: "非常暗",
      moonSlightlyDark: "较暗",
      moonSlightlyBright: "略亮",
      moonBright: "明亮",
      moonAgeLabel: "月龄",
      sunsetLabel: "日落",
      moonriseLabel: "月出",
      moonsetLabel: "月落",
      moonRecBrightUp: "建议在月光消失的月落（{time}）之后再观星。",
      moonRecBrightDown: "建议在没有月光的月出（{time}）之前观赏。",
      moonRecDark: "今晚月光较少，适合观星。",
      moonRecBelowHorizon: "今晚月亮长时间位于地平线以下，受月光影响较小，适合观星。",
      moonRecBrightAllNight: "今晚月光会持续较亮，不太适合观星。",
      moonBestTimeUntilMoonrise: "{start}〜{time}",
      moonBestTimeAfterMoonset: "{time}〜深夜",
    },
    sunsetSpotLabels: {
      waveTitle: "傍晚浪况",
      windTitle: "风",
      sunsetTitle: "日落",
      sunsetScore: "日落评分",
      bestTime: "推荐时间",
      defaultBestTime: "日落前30分钟〜日落后",
      mapConditionBest: "非常好",
      mapConditionGood: "良好",
      mapConditionCaution: "请确认天气",
      mapConditionBad: "一般",
      weatherLabel: "天气",
      ratingLabel: "评分",
      crowdLabel: "拥挤程度",
      crowdPrivate: "包场",
      crowdQuiet: "人少",
      crowdModerate: "略拥挤",
      crowdBusy: "容易拥挤",
      burnLevels: {
        high: "🔥 今天晚霞可能很美",
        medium: "晚霞可能会比较美",
      },
    },
    recommend: "{day}推荐地点",
    snorkelConditionTitle: "{day}综合评分",
    snorkelConditionBest: "非常好",
    snorkelConditionGood: "良好",
    snorkelConditionCaution: "需注意",
    snorkelConditionBad: "不推荐",
    shelteredSpotsTitle: "{day}就去安全的海滩玩吧",
    windResistantSpotsTitle: "{day}推荐不易受风影响的海滩",
    dayToggleToday: "今天",
    dayToggleTomorrow: "明天",
    snorkelConditionCardTitle: "{day}的海况信息",
    weatherLabel: "天气",
    todayWeatherHourlyLabel: "天气",
    todayWaveLabel: "浪高",
    windLabel: "风",
    tideLabel: "满潮・干潮",
    tideHighShort: "满潮",
    tideLowShort: "干潮",
    windImpactLabel: "风的影响",
    waveImpactLabel: "浪的影响",
    windImpactStrong: "容易受{direction}影响",
    windImpactOffshore: "{direction}风为离岸风，较平稳",
    windImpactUnknown: "--",
    waveImpactHigh: "容易受影响",
    waveImpactCalm: "不易受影响",
    waveImpactUnknown: "--",
    starBest: "今日最佳星空地点",
    starTour: {
      label: "推荐行程",
      title: "宫古岛星空摄影体验",
      notice: "星空拍摄条件及行程预约请直接通过DM咨询。",
      description:
        "说到宫古岛星空摄影，推荐这位摄影师。专注星空拍摄的摄影师会根据当天条件选择最佳地点，以创意、色调和构图，为您留下特别的旅行回忆。",
      points: [
        "Photo World 2026摄影比赛获奖",
        "累计拍摄超过3000组客人",
        "专注星空摄影的摄影师",
        "前往当天最佳星空地点",
      ],
      button: "在Instagram查看",
    },
    mapStarTour: {
      label: "星空摄影",
      title: "把宫古岛星空留下成特别照片",
      notice: "星空拍摄条件及行程预约请直接通过DM咨询。",
      description:
        "在宫古岛活动的影像创作者，会根据对星空地点的熟悉程度选择当天最佳地点。以影像创作者的色调、构图和氛围感，为您拍摄特别的星空照片。",
      points: ["熟悉宫古岛星空地点", "在当天最佳地点拍摄", "讲究色调与构图"],
      button: "查看帖子",
    },
    nav: ["首页", "地图", "预报", "指南", "菜单"],
    mapTitle: "附近最佳地点",
    mapSelectAriaLabel: "选择{name}",
    mapAreaLabel: "区域",
    mapRecommendLabel: "推荐",
    mapPhoneLabel: "电话",
    mapHoursLabel: "营业时间",
    mapLegendTitle: "海况判读方式",
    mapConditionBest: "非常好",
    mapConditionGood: "好",
    mapConditionCaution: "注意",
    mapConditionBad: "危险",
    beachLoungeBest: "非常好",
    beachLoungeGood: "好",
    beachLoungeCaution: "不太好",
    beachLoungeBad: "建议避免",
    mapTapHintText: "📍 点击地图上的地点即可弹出查看详情",
    mapClickToInteract: "点击以操作地图",
    oceanClarityLabel: "海水透明度",
    overallAssessment: {
      best: "今天海况非常好，适合享受海上活动。",
      good: "今天的海况较适合海上活动，请随时留意风浪变化。",
      caution: "今天进行海上活动需要谨慎。请确认风浪和雨云，选择安全的地点与时间。",
      bad: "今天的海况不适合海上活动。请勿勉强下海，建议选择其他活动。",
    },
    clarityLabel: "透明度",
    clarityNormal: "普通",
    clarityBest: "最佳",
    clarityHigh: "高",
    claritySlightlyLow: "较低",
    clarityLow: "低",
    overallRatingTitle: "{day}海边玩乐",
    todayBeachLoungeLabel: "{day}海滩发呆",
    todayConditionLabel: "{day}的状况",
    beachInfoLabel: "海滩信息",
    crowdScoreVeryBusy: "非常拥挤",
    crowdScoreSlightlyBusy: "有点拥挤",
    crowdScoreFew: "人不多",
    crowdScorePrivate: "几乎私享",
    parkingLabel: "停车场",
    facilitiesLabel: "设施・服务",
    tourLinkLabel: "查看行程 〉",
    sunriseLabel: "日出",
    sunsetLabel: "日落",
    highTideLabel: "满潮",
    lowTideLabel: "干潮",
    hourlyRainGuideTitle: "时段降雨参考",
    hourlyRainNote: "基于Yahoo天气API的当前降雨量显示。风力请查看下方Open-Meteo预报。",
    tideSectionTitle: "{day}的满潮・干潮",
    rainProbabilitySectionTitle: "{day}的逐时降水概率",
    windForecastLoading: "加载中",
    fetchingStatusLabel: "获取中",
    windForecastLoadingNote: "正在加载逐时风速预报。",
    guideIntroTitle: "120%尽享海洋乐趣",
    guideIntroSub: "安全尽情享受宫古岛海洋的指南。",
    guideActivitySectionTitle: "活动指南",
    photoTourLinkLabel: "查看含摄影的行程",
    termsTitle: "使用条款",
    termsText:
      "本应用中显示的海况、天气等信息，均基于各类气象数据及统计资料计算得出，仅供参考。宫古岛的天气变化多端，天气预报也经常不准。请不要100%依赖这些信息，仅将其作为您在宫古岛游玩时的参考。若因参考本应用信息而采取行动导致任何事故或纠纷，本人概不负责，敬请理解后再使用。",
    aboutWhyTitle: "为什么制作这个应用",
    aboutWhyText:
      "移居宫古岛并从事导游工作后，我发现许多游客都面临着相同的困扰：这几天天气如何、今天哪片海域好、有什么美食推荐、台风来临时该不该取消行程、海况是否安全……大家想知道的信息其实都差不多，却分散在各处，而且天气预报网站众多，让人不知该相信哪一个。于是我决定开发一款应用，把这些必要的信息整合到一处，希望能让来宫古岛游玩的朋友们不再迷茫，更好地享受宫古岛。我会持续打磨这款应用，做到「宫古岛旅行只要看这个应用就不会出错」的最值得信赖的程度。今后也计划介绍一些推荐行程。所刊登的信息和行程，都只是我自己真心认为足够优质、经过精心筛选的内容。",
    aboutFeedbackTitle: "关于建议与信息",
    aboutFeedbackText:
      "欢迎大家提出意见，无论是数据有误还是希望增加的功能，我们都非常期待。这款应用会根据实际使用者的反馈不断进化。目前应用由我一人开发运营，回复可能会有延迟，但只要是有价值的建议，我都会虚心采纳并持续改进，请多多包涵。目前先以网页版形式上线，今后如有需求，也计划推出正式的应用程序。",
    developerCredit: "开发者：Premium Tour Miyakoisland 代表 藤江廉",
    backButtonLabel: "返回",
    closeButtonLabel: "关闭",
    droneTourDriveTime: "距宫古岛市区约{minutes}分钟车程",
    photogenicScoreTitle: "上镜指数（综合评价）",
    spotInfoTitle: "地点信息",
    approxMinutesTemplate: "约{minutes}分钟",
    todayWaveFactLabel: "今日浪高",
    windStrengthLabel: "风力",
    beginnerSuitabilityLabel: "初学者适应度",
    crowdLevelLabel: "拥挤程度",
    priceRangeLabel: "价格区间",
    menuFoodDrinkTemplate: "餐食：{food} / 饮品：{drink}",
    menuFoodOnlyTemplate: "餐食：{food}",
    menuDrinkOnlyTemplate: "饮品：{drink}",
    hotelViewLabel: "海景房",
    hotelGuideEmbedLabel: "查看更多宫古岛酒店",
    highTempLabel: "最高",
    lowTempLabel: "最低",
    rainHeroTitle1: "今天宫古岛是雨天",
    rainHeroTitle2: "来看看雨天也能享受的体验吧！",
    rainHeroSub: "雨天限定推荐广告・体验",
    outdoorActivityLabel: "户外活动",
    outdoorActivityLevelLow: "较少",
    rainyRecommendTitle: "雨天推荐体验",
    viewOnMapLabel: "📍 查看地图",
    menuHeroTitle: "更尽兴地享受宫古岛",
    menuHeroSub: "查看海况・预报・指南，规划今天的行程。",
    menuItems: {
      map: { title: "查看地图", description: "在地图上确认现在容易前往的海边景点" },
      forecast: { title: "查看今日预报", description: "确认天气・降水概率・浪高・日出日落" },
      guide: { title: "海洋游玩指南", description: "初次体验也能安心游玩的基础知识" },
      rain: { title: "雨天也能尽兴", description: "咖啡店、室内体验等雨天的度过方式" },
    },
    forecastTitle: "每日推荐度",
    guideTitle: "第一次来宫古岛",
    detailTitle: "拍照地点详情",
    condition: "当前状况",
    hourlyTitle: "分时预报",
    bestTimeTitle: "今日最佳时间",
    hourlyWindTitle: "分时风速与安全提示",
    hourlyWindNote:
      "基于 Open-Meteo 分时预报显示。风速接近5m/s时，初学者更容易被风带走，建议选择避风地点并避免离岸风。",
    windAvoidanceLabel: "建议避开",
    forecastScoreTitle: "综合评分",
    windGustLabel: "阵风",
    hourlyWindItems: [
      ["6:00", "南风", "3m/s", "windSafe", "○", "较安全", "不太容易漂走"],
      ["9:00", "南风", "4m/s", "windSafe", "○", "相对安全", "注意离岸风"],
      ["12:00", "南风", "5m/s", "windCaution", "△", "注意", "容易被风带走"],
      ["15:00", "东南风", "5m/s", "windCaution", "△", "注意", "容易顺风漂走"],
      ["18:00", "东南风", "4m/s", "windSafe", "○", "相对安全", "适合日落拍摄"],
      ["21:00", "东南风", "3m/s", "windSafe", "○", "较安全", "夜间注意脚下"],
    ],
    bestTimes: [
      [
        "无人机拍摄",
        "早晨 / 午后 / 日落",
        "太阳升到正上方时，海的颜色容易显得发白。更推荐早晨的斜射光或午后光线。上午选择东侧、下午选择西侧等，根据太阳位置选择地点会更好看。日落时也很适合拍摄以太阳为背景的剪影。",
      ],
      ["浮潜", "9:00〜12:00", "建议上午根据透明度选择合适时间。"],
    ],
    bestTimeBad: "请挑选合适的地点游玩",
    bestTimePhotoBad: "请查看天气和风况，选择较平稳的时间。",
    bestTimeSnorkelBad: "受浪、涌浪或风影响，容易被带走，不推荐初学者浮潜。",
    bestTimeSnorkelMorning: "建议选择浪和风影响相对较小的上午。",
    bestTimeMarineBad: "如果海况较差，部分行程可能会取消。",
    bestTimeMarineGood: "选择浪和风较平稳的时间，更适合海上活动。",
    safetyTitle: "安全指南",
    natureTitle: "自然・生物指南",
    rainLead: "雨天也可以轻松享受的宫古岛玩法。",
    rainRadarTitle: "宫古岛雨云雷达",
    rainRadarLead: "颜色越深，降雨可能越强。建议同时查看宫古岛上空和上风方向的雨云移动。",
    rainRadarAdvice: "如果雨云接近，建议把海上活动改为咖啡店、室内体验或酒店休息。",
    rainRadarButton: "查看详细雨云雷达",
    rainRadarNearAlert: "{name}周边请留意雨云",
    rainRadarDayAlert: "{day}请留意降雨时段",
    rainRadarSelectedDayFallback: "所选日期",
    rainRadarLowSummary: "宫古岛周边强雨云较少",
    rainRadarCurrentNote: "目前的参考值为{rainfall}mm/h。海上活动前请确认最新雷达。",
    rainRadarFutureHighNote: "部分时段降雨概率较高。出发前请确认最新雨云雷达。",
    rainRadarLowNote: "目前雨势较弱。请留意海况和风力，建议上午出发行动。",
    rainProbabilityLabel: "降水概率",
    rainMovementLink: "查看降雨移动",
    typhoonTitle: "台风发生状况",
    typhoonLead:
      "根据日本气象厅最新信息，简单显示台风是否可能影响海上活动和旅行。",
    typhoonNone: "目前没有预计会影响宫古岛的台风或热带低压。",
    typhoonOfficial: "在日本气象厅确认路径",
    typhoonDistance: "距离宫古岛约 {distance} km",
    typhoonTrackLabel: "路径参考",
    typhoonUpdated: "发布",
    typhoonImpactLabel: "对宫古岛的影响",
    typhoonFlightImpactLabel: "对航班的影响",
    typhoonPeriodLabel: "预计受影响的时间段",
    typhoonPeriodRange: "{start} 〜 {end}",
    typhoonPeriodCaveat: "此为根据当前发布预报的参考时间，实际可能因路径或速度变化而有所前后。",
    typhoonAfterPassNote: "台风过境后，海上仍可能残留波浪和涌浪，海况持续不稳定一段时间。",
    typhoonAvoidTitle: "建议避开的地点",
    typhoonAvoidSeparator: "，",
    typhoonAvoidMapPrefix: "相对安全的地点请查看这份",
    typhoonAvoidMapButtonLabel: "地图",
    typhoonAvoidMapSuffix: "",
    typhoonMovementLabel: "移动",
    typhoonWeatherSummary:
      "可能受到台风涌浪和风的影响。海上更容易被带走，请多加小心。",
    waveHeightLabel: "浪高",
    swellLabel: "涌浪",
    waveMeaning:
      "浪高表示海面有多不平稳。作为参考，0.6米以上开始变得不稳定，1米以上则不适合SUP和浮潜。",
    swellMeaning:
      "涌浪是远处台风或低气压带来的大幅度海浪。作为参考，0.5米以上开始有影响，1米以上即使晴天也可能突然出现较强海浪。",
    tideRecommendTitle: "根据潮位推荐",
    tideConditionCaution: "潮位时间不错，但浪、涌浪或风可能偏强。请确认海况，不要勉强前往。",
    tideDayParts: {
      morning: "上午",
      afternoon: "下午",
      daytime: "白天",
    },
    tideTimeLabel: "{time} 左右（{part}）",
    tideRecommendations: [
      ["接近满潮", "推荐 Yuni 海滩", "更容易欣赏像漂浮在海上的幻之沙洲景色。无人机拍摄的话，满潮时会更好看。"],
      ["接近干潮", "推荐 17END", "干潮时白色沙滩会出现，与宫古蓝形成非常美的对比。"],
    ],
    book: "预约行程",
    navigate: "去这里",
    viewDetails: "查看详情",
    caution: "需要注意",
    good: "非常好",
    ok: "初学者OK",
  },
  한국어: {
    lang: "한국어",
    appName: "Miyako Ocean Guide",
    pageHeroSubtitle: "미야코지마 여행 중 보고 싶은 정보를 하나로 모은 앱입니다.",
    mapButton: "지도 보기",
    rainButton: "비 오는 날 화면 보기",
    heroTitle: "{day} 미야코지마는 바다놀이 하기 좋은 날입니다.",
    heroSub: "바람과 파도 변화를 확인하면서 안전하게 바다를 즐기세요.",
    heroBadTitle: "{day}은 바다 상태가 다소 불안정해요",
    heroBadSub: "안전한 비치를 선택해 무리하지 않고 즐겨보세요.",
    heroWindTitle: "{day}은 강풍으로 바다가 매우 거칠어요",
    heroWindSub: "바다 활동은 취소를 고려하고 실내나 카페에서 여유롭게 보내는 것을 추천합니다",
    heroRainTitle: "{day}은 아쉽게도 비가 내려요",
    heroRainSub: "바다 활동 대신 카페 투어나 실내 체험으로 여유롭게 보내세요",
    heroHighRainTitle: "{day} 미야코지마에서는 비를 조심하며 즐기세요!",
    heroHighRainSub: "비가 그치는 시간을 보면서 무리 없는 일정으로 즐기세요.",
    heroByCategory: {
      snorkel: {
        title: "{day} 미야코지마는 바다놀이 하기 좋은 날입니다.",
        sub: "바람과 파도 변화를 확인하면서 안전하게 바다를 즐기세요.",
      },
      stars: {
        title: "오늘 밤 미야코지마의 별하늘을 즐기기",
        sub: "구름, 바람, 달빛을 보면서 별하늘 촬영에 좋은 장소를 찾을 수 있어요.",
      },
      sunset: {
        title: "오늘 미야코지마에서 선셋을 본다면",
        sub: "일몰 시간과 바람을 확인하며 예쁜 선셋 스팟을 고를 수 있어요.",
      },
      yabiji: {
        title: "미야코지마의 비경・야비지(八重干瀬)",
        sub: "투어 배로만 갈 수 있는 거대한 산호초. 해황에 따라 운항 여부가 크게 좌우돼요.",
      },
      rainy: {
        title: "비 오는 날에도 즐길 수 있는 미야코지마 실내 체험",
        sub: "레진아트, 시사 만들기 등 비 오는 날에 딱 맞는 체험을 확인해보세요",
      },
    },
    tabs: {
      snorkel: "스노클",
      stars: "별",
      sunset: "선셋",
      beach: "비치 힐링",
      cafe: "점심・카페",
      lunch: "점심・카페",
      dinner: "저녁",
      hotel: "호텔",
      voices: "현장 소식",
      nightlife: "밤놀이",
      lateNightMeal: "해장 음식",
      yabiji: "야비지",
      rainy: "비 오는 날",
    },
    foodCrowdFilters: {
      all: "전체",
      easy: "비교적 여유",
      busy: "혼잡하기 쉬움",
      reservation: "예약 추천",
    },
    yabijiNotice:
      "이번 시즌 야비지 투어는 곧 종료됩니다. 내년 3월경 다시 출항할 예정이니 각 투어 업체의 SNS를 확인해 주세요.",
    yabijiTourTitle: "추천 투어",
    yabijiTourLead: "현지에서도 인기 있는 추천 투어예요. 최신 운항 상황은 DM으로 직접 확인해 주세요.",
    scoreLabels: {
      snorkel: "스노클",
      marine: "해양 액티비티",
      photo: "촬영",
      dronePhoto: "드론 촬영",
      underwaterPhoto: "수중 촬영",
      groundPhoto: "지상 촬영",
      starPhoto: "별하늘 촬영",
      sunsetPhoto: "선셋 지수",
      beginner: "초보자 적합도",
      turtle: "바다거북 조우율",
      crowd: "혼잡도",
    },
    starSpotLabels: {
      photoScore: "촬영 평가",
      bestTime: "추천 시간대",
      darknessScore: "어두움 평가",
      driveTimeLabel: "시내에서 차로",
      milkyWayScore: "은하수 평가",
      milkyWayDirectionLabel: "은하수 방향",
      milkyWayDirectionTonight: "남쪽 하늘",
      milkyWayVisibleLabel: "은하수",
      milkyWayVisibleYes: "지금 보여요",
      milkyWayVisibleAt: "{time}에 보여요",
      defaultBestTime: "21:00〜심야",
      milkyWayDirectionFitLabel: "은하수 가시성",
      milkyWayDirectionGood: "잘 보여요",
      milkyWayDirectionPartial: "다소 가려짐",
      milkyWayDirectionPoor: "보기 어려워요",
      monthlyInfoTitle: "이번 달 별하늘 정보 (오늘 정보 아님)",
      newMoonTitle: "신월",
      monthlyBestTitle: "이번 달 추천 촬영일",
      moonNote: "신월 전후에는 달빛이 적어 은하수와 별하늘 촬영에 좋습니다.",
      starConditionsTitle: "아름다운 별하늘을 즐기기 좋은 조건",
      starConditionsItems: [
        ["신월 전후", "하늘이 어두워 별이 더 또렷하게 보여요"],
        ["폭우가 내린 다음 날", "지표에 수증기가 모여 상공의 구름이 적어져요"],
        ["바람이 없는 밤", "대기 움직임이 적어 별빛이 곧게 도달해요"],
      ],
      mapConditionBest: "매우 좋음",
      mapConditionGood: "좋음",
      mapConditionCaution: "보기 어려움",
      mapConditionBad: "추천하지 않음",
      tonightRatingTitle: "오늘 밤 종합 평가",
      weatherFactorLabel: "날씨",
      cloudFactorLabel: "밤 구름량",
      windFactorLabel: "바람",
      moonFactorLabel: "달빛",
      windCalm: "잔잔함",
      windModerate: "약간 바람",
      windStrong: "바람이 강함",
      windVeryStrong: "매우 강함",
      moonDark: "매우 어두움",
      moonSlightlyDark: "어두운 편",
      moonSlightlyBright: "약간 밝음",
      moonBright: "밝음",
      moonAgeLabel: "월령",
      sunsetLabel: "일몰",
      moonriseLabel: "달뜨는 시간",
      moonsetLabel: "달지는 시간",
      moonRecBrightUp: "달빛이 사라지는 달이 지는 시간({time}) 이후를 추천해요.",
      moonRecBrightDown: "달빛이 없는 달이 뜨는 시간({time})까지를 추천해요.",
      moonRecDark: "오늘 밤은 달빛이 적어 별을 보기 좋아요.",
      moonRecBelowHorizon: "오늘 밤은 달이 지평선 아래에 있는 시간이 길어 달빛 영향이 적고 별 보기 좋아요.",
      moonRecBrightAllNight: "오늘 밤은 달이 계속 밝아 별 보기에는 좋지 않아요.",
      moonBestTimeUntilMoonrise: "{start}〜{time}",
      moonBestTimeAfterMoonset: "{time}〜심야",
    },
    sunsetSpotLabels: {
      waveTitle: "저녁 파도",
      windTitle: "바람",
      sunsetTitle: "일몰",
      sunsetScore: "선셋 평가",
      bestTime: "추천 시간대",
      defaultBestTime: "일몰 30분 전〜일몰 후",
      mapConditionBest: "매우 좋음",
      mapConditionGood: "좋음",
      mapConditionCaution: "날씨 확인",
      mapConditionBad: "애매함",
      weatherLabel: "날씨",
      ratingLabel: "평가",
      crowdLabel: "혼잡도",
      crowdPrivate: "프라이빗",
      crowdQuiet: "한산함",
      crowdModerate: "약간 혼잡",
      crowdBusy: "혼잡하기 쉬움",
      burnLevels: {
        high: "🔥 오늘은 노을이 붉게 물들 것 같아요",
        medium: "노을이 아름다울 수도 있어요",
      },
    },
    recommend: "{day}의 추천 스팟",
    snorkelConditionTitle: "{day}의 종합 평가",
    snorkelConditionBest: "매우 좋음",
    snorkelConditionGood: "좋음",
    snorkelConditionCaution: "주의",
    snorkelConditionBad: "추천하지 않음",
    shelteredSpotsTitle: "{day}은 안전한 비치에서 즐겨보세요",
    windResistantSpotsTitle: "{day}은 바람의 영향을 덜 받는 해변을 추천합니다",
    dayToggleToday: "오늘",
    dayToggleTomorrow: "내일",
    snorkelConditionCardTitle: "{day}의 해황 정보",
    weatherLabel: "날씨",
    todayWeatherHourlyLabel: "날씨",
    todayWaveLabel: "파도",
    windLabel: "바람",
    tideLabel: "만조・간조",
    tideHighShort: "만조",
    tideLowShort: "간조",
    windImpactLabel: "바람의 영향",
    waveImpactLabel: "파도의 영향",
    windImpactStrong: "{direction}의 영향을 받기 쉬움",
    windImpactOffshore: "{direction} 바람이 육풍이라 잔잔함",
    windImpactUnknown: "--",
    waveImpactHigh: "영향을 받기 쉬움",
    waveImpactCalm: "영향을 받기 어려움",
    waveImpactUnknown: "--",
    starBest: "오늘의 별 보기 베스트",
    starTour: {
      label: "추천 투어",
      title: "미야코지마 별하늘 촬영 투어",
      notice: "별하늘 컨디션이나 투어 예약은 DM으로 직접 문의해 주세요.",
      description:
        "미야코지마 별하늘 촬영이라면 이 사람. 별하늘 촬영에 특화된 포토그래퍼가 그날 가장 좋은 장소를 골라, 창의적인 감성과 색감, 구도까지 신경 쓴 특별한 추억을 남겨드립니다.",
      points: [
        "Photo World 2026 콘테스트 수상",
        "누적 3000팀 이상 촬영",
        "별하늘 촬영 전문 포토그래퍼",
        "그날 가장 좋은 별하늘 스팟",
      ],
      button: "Instagram에서 보기",
    },
    mapStarTour: {
      label: "별하늘 촬영",
      title: "미야코지마의 별을 특별한 사진으로",
      notice: "별하늘 컨디션이나 투어 예약은 DM으로 직접 문의해 주세요.",
      description:
        "미야코지마에서 활동하는 영상 크리에이터가 별하늘 스팟을 잘 아는 경험을 바탕으로, 그날 가장 좋은 장소에서 촬영합니다. 영상 크리에이터다운 색감과 구도에 신경 써 특별한 사진을 남겨드립니다.",
      points: ["별하늘 스팟을 잘 아는 안내", "그날 가장 좋은 장소에서 촬영", "색감과 구도에 신경 쓴 한 장"],
      button: "게시물 보기",
    },
    nav: ["홈", "지도", "예보", "가이드", "메뉴"],
    mapTitle: "현재 위치 근처 베스트 스팟",
    mapSelectAriaLabel: "{name} 선택",
    mapAreaLabel: "지역",
    mapRecommendLabel: "추천",
    mapPhoneLabel: "전화",
    mapHoursLabel: "영업시간",
    mapLegendTitle: "상태 보는 법",
    mapConditionBest: "매우 좋음",
    mapConditionGood: "좋음",
    mapConditionCaution: "주의",
    mapConditionBad: "위험",
    beachLoungeBest: "매우 좋음",
    beachLoungeGood: "좋음",
    beachLoungeCaution: "별로임",
    beachLoungeBad: "피하는 게 좋음",
    mapTapHintText: "📍 지도의 장소를 탭하면 팝업으로 상세 정보가 표시됩니다",
    mapClickToInteract: "탭하여 지도 조작하기",
    oceanClarityLabel: "바닷물 투명도",
    overallAssessment: {
      best: "오늘은 바다 상태가 매우 좋아 해양 활동을 즐기기 좋은 날입니다.",
      good: "오늘은 비교적 해양 활동을 즐기기 좋습니다. 바람과 파도 변화를 확인하세요.",
      caution: "오늘은 해양 활동에 주의가 필요합니다. 바람·파도·비구름을 확인하고 안전한 시간과 장소를 선택하세요.",
      bad: "오늘은 해양 활동에 적합하지 않습니다. 무리해서 입수하지 말고 다른 활동을 선택하세요.",
    },
    clarityLabel: "투명도",
    clarityNormal: "보통",
    clarityBest: "최고",
    clarityHigh: "높음",
    claritySlightlyLow: "약간 낮음",
    clarityLow: "낮음",
    overallRatingTitle: "{day}의 바다 놀이",
    todayBeachLoungeLabel: "{day}의 비치 힐링",
    todayConditionLabel: "{day}의 컨디션",
    beachInfoLabel: "비치 정보",
    crowdScoreVeryBusy: "꽤 붐빔",
    crowdScoreSlightlyBusy: "약간 붐빔",
    crowdScoreFew: "한적함",
    crowdScorePrivate: "거의 프라이빗",
    parkingLabel: "주차장",
    facilitiesLabel: "시설・서비스",
    tourLinkLabel: "투어 보기 〉",
    sunriseLabel: "일출",
    sunsetLabel: "일몰",
    highTideLabel: "만조",
    lowTideLabel: "간조",
    hourlyRainGuideTitle: "시간대별 비 예상",
    hourlyRainNote: "Yahoo 날씨 API의 현재 강수량을 기준으로 표시합니다. 바람은 아래 Open-Meteo 예보에서 확인할 수 있습니다.",
    tideSectionTitle: "{day}의 만조・간조",
    rainProbabilitySectionTitle: "{day}의 시간대별 강수 확률",
    windForecastLoading: "불러오는 중",
    fetchingStatusLabel: "가져오는 중",
    windForecastLoadingNote: "시간별 풍속 예보를 불러오고 있습니다.",
    guideIntroTitle: "바다를 120% 즐기기 위해",
    guideIntroSub: "미야코지마의 바다를 안전하고 마음껏 즐기기 위한 가이드입니다.",
    guideActivitySectionTitle: "액티비티 가이드",
    photoTourLinkLabel: "촬영 포함 투어 보기",
    termsTitle: "이용약관",
    termsText:
      "본 앱에서 표시하는 해황・날씨 등의 정보는 다양한 기상 데이터와 통계를 바탕으로 산출한 참고용 수치입니다. 미야코지마의 날씨는 예보가 자주 빗나갈 만큼 변화가 심합니다. 표시된 정보를 100% 신뢰하지 마시고, 어디까지나 미야코지마에서 즐거운 시간을 보내기 위한 참고 자료로 활용해 주세요. 본 앱의 정보를 바탕으로 한 행동으로 인해 사고나 문제가 발생하더라도 당사는 일체 책임을 지지 않으니, 이 점 양해 부탁드립니다.",
    aboutWhyTitle: "만든 이유",
    aboutWhyText:
      "미야코지마로 이주해 투어 가이드로 일하면서, 많은 관광객분들이 공통된 고민을 갖고 있다는 것을 알게 되었습니다. 이 날짜 날씨는 어떤지, 오늘은 어느 바다가 좋은지, 맛집 추천, 태풍 때문에 취소해야 할지 바다 상황은 어떤지 등 궁금해하시는 내용은 비슷한데 정보는 여기저기 흩어져 있고, 일기예보도 여러 곳이라 어떤 걸 믿어야 할지 모르겠다는 목소리도 많았습니다. 그래서 여행객분들이 헤매지 않고 미야코지마를 더 즐길 수 있도록 필요한 정보를 한곳에 모은 앱을 만들기로 결심하고 개발했습니다. '미야코지마 여행은 이 앱만 보면 틀림없다'고 할 수 있는, 가장 신뢰할 수 있는 앱으로 계속 만들어 나가겠습니다. 앞으로는 추천 투어 소개 등도 해나갈 예정입니다. 게재하는 정보와 투어는 제가 정말 좋다고 생각한, 엄선된 것만 다루고 있습니다.",
    aboutFeedbackTitle: "요청・정보에 대해",
    aboutFeedbackText:
      "데이터가 잘못되었거나 이런 기능이 있었으면 좋겠다는 의견을 기다리고 있습니다. 실제로 이용해 주신 분들의 의견을 바탕으로 이 앱은 계속 발전해 나갑니다. 현재는 저 혼자 개발・운영하고 있어 답변이 늦어질 수 있지만, 가치 있는 의견은 적극적으로 받아들여 개선해 나가겠습니다. 잘 부탁드립니다. 우선은 웹으로 공개하지만, 앞으로 수요가 있으면 앱으로도 출시할 예정입니다.",
    developerCredit: "개발자: Premium Tour Miyakoisland 대표 후지에 렌",
    backButtonLabel: "뒤로",
    closeButtonLabel: "닫기",
    droneTourDriveTime: "미야코 시내에서 차로 약 {minutes}분",
    photogenicScoreTitle: "포토제닉 지수(종합 평가)",
    spotInfoTitle: "스팟 정보",
    approxMinutesTemplate: "약 {minutes}분",
    todayWaveFactLabel: "오늘의 파도",
    windStrengthLabel: "바람 세기",
    beginnerSuitabilityLabel: "초보자 적합도",
    crowdLevelLabel: "혼잡도",
    priceRangeLabel: "가격대",
    menuFoodDrinkTemplate: "식사: {food} / 음료: {drink}",
    menuFoodOnlyTemplate: "식사: {food}",
    menuDrinkOnlyTemplate: "음료: {drink}",
    hotelViewLabel: "오션뷰",
    hotelGuideEmbedLabel: "미야코지마 호텔 더 보기",
    highTempLabel: "최고",
    lowTempLabel: "최저",
    rainHeroTitle1: "오늘 미야코지마는 비 소식",
    rainHeroTitle2: "비 오는 날에도 즐길 수 있는 체험을 확인하세요!",
    rainHeroSub: "비 오는 날 한정 추천 광고・체험",
    outdoorActivityLabel: "야외 활동은",
    outdoorActivityLevelLow: "자제",
    rainyRecommendTitle: "비 오는 날 추천 체험",
    viewOnMapLabel: "📍 지도에서 보기",
    menuHeroTitle: "미야코지마를 더 즐기기",
    menuHeroSub: "해황・예보・가이드를 보면서 오늘 일정을 선택할 수 있습니다.",
    menuItems: {
      map: { title: "지도 보기", description: "지금 가기 좋은 바다 스팟을 지도에서 확인" },
      forecast: { title: "오늘의 예보 보기", description: "날씨・강수 확률・파도・달의 출몰 확인" },
      guide: { title: "바다 놀이 가이드", description: "처음이어도 안심하고 즐기기 위한 기본 정보" },
      rain: { title: "비 오는 날도 즐기기", description: "카페나 실내 체험 등 비 오는 날 보내는 방법" },
    },
    forecastTitle: "일별 추천도",
    guideTitle: "처음 오는 미야코지마",
    detailTitle: "사진 명소 상세",
    condition: "현재 컨디션",
    hourlyTitle: "시간대별 예보",
    bestTimeTitle: "오늘의 베스트 타임",
    hourlyWindTitle: "시간대별 바람과 안전 기준",
    hourlyWindNote:
      "Open-Meteo 시간대별 예보를 기준으로 표시합니다. 5m/s 전후부터 초보자는 떠밀리기 쉬우니 바람을 피할 수 있는 장소를 선택하세요.",
    windAvoidanceLabel: "피하면 좋은 곳",
    forecastScoreTitle: "종합 평가",
    windGustLabel: "최대",
    hourlyWindItems: [
      ["6:00", "남풍", "3m/s", "windSafe", "○", "안전한 편", "떠밀릴 위험 낮음"],
      ["9:00", "남풍", "4m/s", "windSafe", "○", "비교적 안전", "바다 쪽 바람 주의"],
      ["12:00", "남풍", "5m/s", "windCaution", "△", "주의", "떠밀리기 쉬움"],
      ["15:00", "남동풍", "5m/s", "windCaution", "△", "주의", "바람 아래로 밀릴 수 있음"],
      ["18:00", "남동풍", "4m/s", "windSafe", "○", "비교적 안전", "선셋 촬영에 좋음"],
      ["21:00", "남동풍", "3m/s", "windSafe", "○", "안전한 편", "밤에는 발밑 주의"],
    ],
    bestTimes: [
      [
        "드론 촬영",
        "아침 / 오후 / 선셋",
        "해가 머리 위에 있는 시간은 바다색이 하얗게 날아 보이기 쉽습니다. 아침의 사선 빛이나 오후 이후가 더 좋습니다. 오전에는 동쪽, 오후에는 서쪽처럼 태양 위치를 보고 장소를 고르면 더 예쁘게 찍힙니다. 선셋에는 태양을 배경으로 한 실루엣 촬영도 추천합니다.",
      ],
      ["스노클링", "9:00〜12:00", "투명도를 확인하면서 오전에 가는 것을 추천합니다."],
    ],
    bestTimeBad: "장소를 골라서 즐기세요",
    bestTimePhotoBad: "날씨와 바람을 확인하고 비교적 잔잔한 시간을 선택하세요.",
    bestTimeSnorkelBad: "파도, 너울, 바람 때문에 떠밀리기 쉬워 초보자 스노클링은 추천하지 않습니다.",
    bestTimeSnorkelMorning: "파도와 바람의 영향이 비교적 적은 오전을 추천합니다.",
    bestTimeMarineBad: "컨디션이 좋지 않으면 투어에 따라 취소될 수 있습니다.",
    bestTimeMarineGood: "파도와 바람이 잔잔한 시간을 고르면 해양 활동을 즐기기 쉽습니다.",
    safetyTitle: "안전 가이드",
    natureTitle: "자연・생물 가이드",
    rainLead: "비 오는 날에도 편하게 즐길 수 있는 코스를 정리했어요.",
    rainRadarTitle: "미야코지마 비구름 레이더",
    rainRadarLead: "색이 진할수록 비가 강할 가능성이 있습니다. 미야코지마 위뿐 아니라 바람이 불어오는 쪽의 구름 흐름도 함께 확인하세요.",
    rainRadarAdvice: "비구름이 가까워지는 시간대에는 바다 일정 대신 카페, 실내 체험, 호텔 휴식을 추천합니다.",
    rainRadarButton: "자세한 비구름 레이더 보기",
    rainRadarNearAlert: "{name} 주변 비구름 주의",
    rainRadarDayAlert: "{day}은(는) 비 오는 시간대 주의",
    rainRadarSelectedDayFallback: "선택한 날",
    rainRadarLowSummary: "미야코지마 주변에 강한 비구름은 적음",
    rainRadarCurrentNote: "현재 기준은 {rainfall}mm/h입니다. 바다 일정 전 최신 레이더를 확인하세요.",
    rainRadarFutureHighNote: "시간대별 강수 확률이 높은 편입니다. 출발 전 최신 비구름 레이더를 확인하세요.",
    rainRadarLowNote: "지금은 비가 약한 편입니다. 바다 상태와 바람을 보면서 오전부터 움직이는 것을 추천합니다.",
    rainProbabilityLabel: "강수 확률",
    rainMovementLink: "비 이동 확인",
    typhoonTitle: "태풍 발생 상황",
    typhoonLead:
      "일본 기상청 최신 정보를 바탕으로 태풍이 해양 활동과 여행에 영향을 줄지 쉽게 보여줍니다.",
    typhoonNone: "현재 미야코지마에 영향을 줄 것으로 예상되는 태풍이나 열대저압부는 없습니다.",
    typhoonOfficial: "일본 기상청에서 진로 확인",
    typhoonDistance: "미야코지마에서 약 {distance}km",
    typhoonTrackLabel: "진로 예상",
    typhoonUpdated: "발표",
    typhoonImpactLabel: "미야코지마 영향",
    typhoonFlightImpactLabel: "항공편 영향",
    typhoonPeriodLabel: "영향이 예상되는 기간",
    typhoonPeriodRange: "{start} 〜 {end}",
    typhoonPeriodCaveat: "현재 발표된 예보를 기준으로 한 참고 시간이에요. 진로나 속도 변화에 따라 앞뒤로 바뀔 수 있어요.",
    typhoonAfterPassNote: "태풍이 지나간 후에도 한동안 파도와 너울이 남아 바다가 거친 상태가 이어질 수 있습니다.",
    typhoonAvoidTitle: "피해야 할 장소",
    typhoonAvoidSeparator: ": ",
    typhoonAvoidMapPrefix: "비교적 안전한 장소는 이 ",
    typhoonAvoidMapButtonLabel: "지도",
    typhoonAvoidMapSuffix: "를 확인하세요",
    typhoonMovementLabel: "움직임",
    typhoonWeatherSummary:
      "태풍 너울과 바람의 영향이 있을 수 있습니다. 바다에서는 떠밀리기 쉬우니 주의해 주세요.",
    waveHeightLabel: "파도 높이",
    swellLabel: "너울",
    waveMeaning:
      "파도 높이는 해수면이 얼마나 거친지 보는 기준입니다. 기준으로 0.6m 이상부터 불안정해지기 쉽고, 1m 이상은 SUP와 스노클링에 적합하지 않습니다.",
    swellMeaning:
      "너울은 멀리 있는 태풍이나 저기압에서 오는 큰 흔들림입니다. 기준으로 0.5m 이상부터 영향이 있고, 1m 이상이면 맑은 날에도 갑자기 파도가 강해질 수 있습니다.",
    tideRecommendTitle: "조위에 맞춘 추천",
    tideConditionCaution: "조위 시간은 좋지만 파도, 너울, 바람이 강할 수 있습니다. 해황을 보고 무리하지 마세요.",
    tideDayParts: {
      morning: "오전",
      afternoon: "오후",
      daytime: "낮",
    },
    tideTimeLabel: "{time}쯤（{part}）",
    tideRecommendations: [
      ["만조에 가까운 시간", "유니 해변 추천", "바다 위에 떠 있는 모래섬 같은 풍경을 보기 좋은 시간대입니다. 드론 촬영은 만조 시간이 특히 예쁩니다."],
      ["간조에 가까운 시간", "17END 추천", "간조 때 하얀 모래사장이 드러나 미야코 블루와의 대비가 아름답게 보입니다."],
    ],
    book: "투어 예약",
    navigate: "여기로 가기",
    viewDetails: "자세히 보기",
    caution: "주의 필요",
    good: "매우 좋음",
    ok: "초보자 OK",
  },
};

const heroImage = img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp", {
  width: 1800,
  height: 1180,
});
const oceanGuideHeroYoutubeId = "mRwcLL6hzM8";
const heroYoutubeIdsByCategory = {
  snorkel: oceanGuideHeroYoutubeId,
  sunset: "tWbh4c5IioY",
};

const heroImagesByCategory = {
  snorkel: heroImage,
  stars: "https://res.cloudinary.com/drxijtp8g/image/upload/v1786120675/DSC00888_tk8uhe.webp",
  sunset: img("v1785774919/DJI_20260801191604_0048_D_t2h5kf.webp", {
    width: 1800,
    height: 1180,
  }),
  yabiji: img("v1784531285/IMG_1066_sd05qe.webp", {
    width: 1800,
    height: 1180,
  }),
  rainy: img(
    "v1788261035/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-09-01_20.09.37_a4hlqg.png",
    { width: 1800, height: 1180 },
  ),
};

const yabijiTourAccounts = [
  {
    profileUrl: "https://www.instagram.com/sashiba_nohane/",
    postUrl: "https://www.instagram.com/reel/DcOIfFBxOxb/",
  },
  {
    profileUrl: "https://www.instagram.com/saltyfeet_fumispi23/",
    postUrl: "https://www.instagram.com/p/DLMiM-ixUZ_/",
  },
  {
    profileUrl: "https://www.instagram.com/irie_marine.miyakojima/",
    postUrl: "https://www.instagram.com/p/Db7-Myvh77y/",
  },
  {
    profileUrl: "https://www.instagram.com/irabujima.tour.makana/",
    postUrl: "https://www.instagram.com/reel/DHqeK1tv5M1/",
  },
];

const getInstagramHandle = (url) => {
  const match = String(url).match(/instagram\.com\/([^/?]+)/);
  return match ? `@${match[1]}` : url;
};

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyBGMjAtOf13SZ5FDNjmt7XVlcSRcurUbBE";
const MIYAKO_MAP_CENTER = { lat: 24.82, lng: 125.265 };
const nightlifeMapCenter = { lat: 24.8035, lng: 125.2815 };
const MIYAKO_MAP_BOUNDS = {
  north: 24.98,
  south: 24.66,
  east: 125.5,
  west: 125.08,
};
const currentOceanCondition = {
  weather: "取得中",
  windDirection: "--",
  windSpeed: "",
  windGust: "",
  windDegree: null,
};
const oceanGuideMapStyle = {
  width: "100%",
  height: "100%",
};
const oceanGuideMapOptions = {
  mapTypeId: "roadmap",
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
  styles: [
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#aee8f7" }],
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [{ color: "#eef5df" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }],
    },
  ],
};

const spotBeginnerTranslations = {
  "とても安心": { EN: "Very reassuring", 中文: "非常安心", 한국어: "매우 안심" },
  "やや注意": { EN: "Some caution needed", 中文: "需稍加注意", 한국어: "다소 주의 필요" },
  "かなりいい": { EN: "Quite good", 中文: "相当不错", 한국어: "꽤 좋음" },
  "安心": { EN: "Reassuring", 中文: "安心", 한국어: "안심" },
  "波に注意": { EN: "Watch for waves", 中文: "注意浪高", 한국어: "파도 주의" },
  "足元に注意": { EN: "Watch your footing", 中文: "注意脚下", 한국어: "발밑 주의" },
  "潮位に注意": { EN: "Watch the tide level", 中文: "注意潮位", 한국어: "조수 수위 주의" },
  "初心者にもおすすめ": { EN: "Recommended for beginners too", 中文: "也推荐初学者", 한국어: "초보자에게도 추천" },
  "波が穏やかで安心": { EN: "Calm waves, reassuring", 中文: "浪平稳，安心", 한국어: "파도가 잔잔해 안심" },
  "北風の日は注意": { EN: "Caution on north-wind days", 中文: "刮北风时需注意", 한국어: "북풍이 부는 날은 주의" },
  "岩場のため上級者向け": { EN: "Rocky, for advanced swimmers", 中文: "多岩石，适合高阶玩家", 한국어: "암초 지대라 상급자용" },
  "風に注意": { EN: "Watch the wind", 中文: "注意风力", 한국어: "바람 주의" },
};

const spotParkingTranslations = {
  "無料駐車場：繁忙期は午前中には満車になることも。有料駐車場：無料駐車場がいっぱいになった場合、料金は1回2,000円です。繁忙期は10時を過ぎると駐車場の空き待ちで坂の上まで大渋滞が発生し、1時間ほど待つこともあります。": {
    EN: "Free parking is available, though it can fill up by morning during peak season. If the free lot is full, there is a paid lot at ¥2,000 per visit. During peak season, waiting for a space after 10am can cause heavy traffic up the hill, sometimes requiring about an hour's wait.",
    中文: "设有免费停车场，旺季上午就可能停满。若免费停车场已满，付费停车场每次2,000日元。旺季过了10点后常因等待车位在坡道上发生严重拥堵，有时需等待约1小时。",
    한국어: "무료 주차장이 있지만 성수기에는 오전 중에 만차가 되기도 합니다. 무료 주차장이 가득 찬 경우 유료 주차장은 1회 2,000엔입니다. 성수기에는 10시가 지나면 주차 공간을 기다리며 언덕 위까지 심한 정체가 발생해 1시간 정도 기다리기도 합니다.",
  },
  "なし": { EN: "None", 中文: "无", 한국어: "없음" },
  "有（1,000円・100台）": { EN: "Available (¥1,000, 100 spaces)", 中文: "有（1,000日元・100个车位）", 한국어: "있음(1,000엔・100대)" },
  "10台程駐車できる、無料の駐車場があります。繁忙期は満車になることも。": {
    EN: "There is a free parking lot with space for about 10 cars. It can fill up during peak season.",
    中文: "设有可停约10辆车的免费停车场。旺季可能停满。",
    한국어: "약 10대를 세울 수 있는 무료 주차장이 있습니다. 성수기에는 만차가 되기도 합니다.",
  },
  "あり": { EN: "Available", 中文: "有", 한국어: "있음" },
};

const spotFacilitiesTranslations = {
  "トイレ：あり　シャワー：あり": { EN: "Restroom: Yes / Shower: Yes", 中文: "洗手间：有　淋浴：有", 한국어: "화장실: 있음  샤워장: 있음" },
  "トイレ：なし　シャワー：なし": { EN: "Restroom: No / Shower: No", 中文: "洗手间：无　淋浴：无", 한국어: "화장실: 없음  샤워장: 없음" },
  "トイレ・シャワー：なし": { EN: "Restroom/Shower: None", 中文: "洗手间・淋浴：无", 한국어: "화장실・샤워장: 없음" },
  "トイレ：あり　シャワー：なし": { EN: "Restroom: Yes / Shower: No", 中文: "洗手间：有　淋浴：无", 한국어: "화장실: 있음  샤워장: 없음" },
};

const findSpotByName = (name) => spots.find((candidate) => candidate.name === name);
const localizeSpotName = (spot, lang) => {
  const source = spot.i18n ? spot : findSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.name || spot.name;
};
const localizeSpotDescription = (spot, lang) => {
  const source = spot.i18n ? spot : findSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.description || spot.description;
};
const localizeSpotBeginner = (spot, lang) =>
  spotBeginnerTranslations[spot.beginner]?.[lang] || spot.beginner;
const localizeSpotClarity = (spot, labels) =>
  spot.clarity === "高い" ? labels.clarityHigh : spot.clarity;
const getRatingStatus = (type, labels) => ({
  best: labels.snorkelConditionBest,
  good: labels.snorkelConditionGood,
  caution: labels.snorkelConditionCaution,
  bad: labels.snorkelConditionBad,
}[type]);

const getClarityLabel = (type, labels) => ({
  best: labels.clarityBest,
  good: labels.clarityHigh,
  caution: labels.claritySlightlyLow,
  bad: labels.clarityLow,
}[type]);

const getManualOceanRating = (selectedDay, labels, settings = defaultOceanGuideSettings) => {
  const type = selectedDay === 2
    ? settings.dayAfterTomorrowRating
    : selectedDay === 1
      ? settings.tomorrowRating
      : settings.todayRating;
  if (!type || type === "auto") return null;
  return { type, status: getRatingStatus(type, labels) };
};

const getManualOceanClarity = (selectedDay, labels, settings = defaultOceanGuideSettings) => {
  const type = selectedDay === 2
    ? settings.dayAfterTomorrowClarity
    : selectedDay === 1
      ? settings.tomorrowClarity
      : settings.todayClarity;
  if (!type || type === "auto") return null;
  return { type, label: getClarityLabel(type, labels) };
};

const getSpotTodayClarity = (spot, ratingType, labels, selectedDay = 0, rainProbability = 0, guideSettings) => {
  const manualClarity = getManualOceanClarity(selectedDay, labels, guideSettings);
  if (manualClarity) return manualClarity.label;
  if (rainProbability >= 50) return labels.claritySlightlyLow;

  if (ratingType === "bad" || ratingType === "caution") return labels.clarityLow;
  return localizeSpotClarity(spot, labels);
};
const localizeSpotParking = (spot, lang) =>
  spotParkingTranslations[spot.parking]?.[lang] || spot.parking;
const localizeSpotFacilities = (spot, lang) =>
  spotFacilitiesTranslations[spot.facilities]?.[lang] || spot.facilities;
const foodAreaTranslations = {
  "伊良部島": { EN: "Irabu Island", 中文: "伊良部岛", 한국어: "이라부섬" },
  "池間島": { EN: "Ikema Island", 中文: "池间岛", 한국어: "이케마섬" },
  "平良": { EN: "Hirara", 中文: "平良", 한국어: "히라라" },
  "来間島": { EN: "Kurima Island", 中文: "来间岛", 한국어: "구리마섬" },
  "上野": { EN: "Ueno", 中文: "上野", 한국어: "우에노" },
  "宮古島": { EN: "Miyakojima", 中文: "宫古岛", 한국어: "미야코지마" },
};

const foodFieldTranslations = {
  "海カフェ": { EN: "Ocean Cafe", 中文: "海景咖啡厅", 한국어: "바다 카페" },
  "ランチ": { EN: "Lunch", 中文: "午餐", 한국어: "런치" },
  "カフェ": { EN: "Cafe", 中文: "咖啡厅", 한국어: "카페" },
  "スイーツ": { EN: "Sweets", 中文: "甜点", 한국어: "디저트" },
  "映えカフェ": { EN: "Photogenic Cafe", 中文: "网红咖啡厅", 한국어: "인생샷 카페" },
  "夜ご飯": { EN: "Dinner", 中文: "晚餐", 한국어: "저녁 식사" },
  "混雑しやすい": { EN: "Tends to be crowded", 中文: "容易拥挤", 한국어: "혼잡하기 쉬움" },
  "比較的入りやすい": { EN: "Relatively easy to get a seat", 中文: "相对容易进入", 한국어: "비교적 들어가기 쉬움" },
  "予約推奨": { EN: "Reservation recommended", 中文: "建议预约", 한국어: "예약 권장" },
  "予約必須": { EN: "Reservation required", 中文: "需预约", 한국어: "예약 필수" },
  "予約不要": { EN: "No reservation needed", 中文: "无需预约", 한국어: "예약 불필요" },
  "あり": { EN: "Available", 中文: "有", 한국어: "있음" },
  "近隣駐車場を確認": { EN: "Check nearby parking", 中文: "请确认附近停车场", 한국어: "인근 주차장 확인" },
  "要確認": { EN: "Please check", 中文: "请确认", 한국어: "확인 필요" },
  "不定休": { EN: "Irregular holidays", 中文: "不定期休息", 한국어: "비정기 휴무" },
  "木曜日": { EN: "Thursdays", 中文: "周四", 한국어: "목요일" },
  "なし": { EN: "None", 中文: "无", 한국어: "없음" },
};

const localizeFoodArea = (area, lang) => foodAreaTranslations[area]?.[lang] || area;
const localizeFoodField = (value, lang) => foodFieldTranslations[value]?.[lang] || value;
const findFoodSpotByName = (name) => mapSpots.find((candidate) => candidate.name === name);
const localizeFoodName = (spot, lang) => {
  const source = spot.i18n ? spot : findFoodSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.name || spot.name;
};
const localizeFoodGenre = (spot, lang) => {
  const source = spot.i18n ? spot : findFoodSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.genre || spot.genre;
};
const localizeFoodDetail = (spot, lang) => {
  const source = spot.i18n ? spot : findFoodSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.detail || spot.detail;
};
const localizeFoodMenuFood = (spot, lang) => {
  const source = spot.i18n ? spot : findFoodSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.menuFood || spot.menu?.food;
};
const localizeFoodMenuDrink = (spot, lang) => {
  const source = spot.i18n ? spot : findFoodSpotByName(spot.name) || spot;
  return source.i18n?.[lang]?.menuDrink || spot.menu?.drink;
};
const getFoodMenuText = (spot, labels) => {
  if (!spot.menu) return localizeFoodGenre(spot, labels.lang);

  const food = localizeFoodMenuFood(spot, labels.lang);
  const drink = localizeFoodMenuDrink(spot, labels.lang);

  if (food && drink) {
    return labels.menuFoodDrinkTemplate
      .replace("{food}", food)
      .replace("{drink}", drink);
  }
  if (drink) return labels.menuDrinkOnlyTemplate.replace("{drink}", drink);
  if (food) return labels.menuFoodOnlyTemplate.replace("{food}", food);
  return localizeFoodGenre(spot, labels.lang);
};

const getTelHref = (phone = "") => `tel:${phone.replace(/[^\d+]/g, "")}`;

const localizeFoodAsShop = (spot, labels) => {
  const lang = labels.lang;
  const rows = [];

  if (spot.priceRange) rows.push([labels.priceRangeLabel, spot.priceRange]);
  if (spot.crowd) {
    rows.push([labels.crowdLevelLabel, localizeFoodField(spot.crowd, lang)]);
  }
  if (spot.phone) {
    rows.push([
      labels.mapPhoneLabel,
      <a href={getTelHref(spot.phone)}>{spot.phone}</a>,
    ]);
  }
  if (spot.parking && !spot.categories?.includes("dinner")) {
    rows.push([labels.parkingLabel, localizeFoodField(spot.parking, lang)]);
  }

  return {
    name: localizeFoodName(spot, lang),
    area: localizeFoodArea(spot.area, lang),
    genre: localizeFoodField(spot.genre, lang),
    text: localizeFoodDetail(spot, lang),
    rows,
    recommendedMenu: getFoodMenuText(spot, labels),
    businessHours: localizeFoodField(spot.hours, lang),
    closedDays: localizeFoodField(spot.closed, lang),
    instagramPostUrl: spot.instaPostUrl,
    images: spot.images,
    mapUrl: spot.mapUrl || mapUrl(`${spot.name} ${spot.area || "宮古島"}`),
  };
};

const trackSpotView = (spot, extra = {}) => {
  if (!spot?.name) return;

  trackEvent("spot_view", { spot_name: spot.name, ...extra });

  if (spot.categories?.includes("beach")) {
    trackEvent("beach_select", { spot_name: spot.name, ...extra });
  }
};

const trackConditionDetailView = (spot, extra = {}) => {
  if (!spot?.name) return;

  trackEvent("condition_detail_view", { spot_name: spot.name, ...extra });
};

const hotelGuideArticle = withArticleTranslations(
  blogArticles["miyako-hotel-guide-by-area"],
);

const hotelAreaLocations = {
  "伊良部島": { lat: 24.832, lng: 125.176 },
  "伊良部島 佐和田": { lat: 24.838, lng: 125.15 },
  "伊良部島 渡口の浜周辺": { lat: 24.813, lng: 125.178 },
  "シギラ": { lat: 24.722, lng: 125.337 },
  "シギラ / インギャー": { lat: 24.725, lng: 125.353 },
  "インギャー": { lat: 24.725, lng: 125.356 },
  "上野": { lat: 24.724, lng: 125.329 },
  "宮古島北部（大浦湾）": { lat: 24.856, lng: 125.318 },
  "平良（サンセットビーチ隣接）": { lat: 24.799, lng: 125.259 },
  "宮古島北部（荷川取）": { lat: 24.83, lng: 125.3 },
  "来間島": { lat: 24.724, lng: 125.245 },
  "与那覇湾周辺": { lat: 24.75, lng: 125.27 },
  "宮古島南部": { lat: 24.735, lng: 125.315 },
};

const resolveJpText = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value.JP || value.EN || value["中文"] || value["한국어"] || "";
  }
  return value || "";
};

const getHotelRowValue = (hotel, label) =>
  resolveJpText(
    hotel.rows?.find(([rowLabel]) => rowLabel.includes(label))?.[1],
  );

const getHotelAirportAccess = (hotel) => {
  if (hotel.nearestAirport) return hotel.nearestAirport;

  const textMatch = resolveJpText(hotel.text).match(
    /(?:宮古|下地島)空港から車で約[0-9０-９]+分/,
  );
  if (textMatch) return textMatch[0];

  const area = hotel.area || "";
  if (area.includes("伊良部") || area.includes("下地島")) {
    return "宮古空港から車で約20分 / 下地島空港から車で約10〜15分";
  }
  if (area.includes("シギラ") || area.includes("上野") || area.includes("インギャー")) {
    return "宮古空港から車で約15〜20分";
  }
  if (area.includes("来間")) return "宮古空港から車で約20分";
  if (area.includes("平良") || area.includes("北部")) return "宮古空港から車で約15〜25分";

  return "宮古空港から車で約15〜25分";
};

const getHotelFallbackLocation = (area = "", index = 0) => {
  const base =
    hotelAreaLocations[area] ||
    Object.entries(hotelAreaLocations).find(([key]) => area.includes(key))?.[1] ||
    MIYAKO_MAP_CENTER;

  return {
    lat: base.lat + ((index % 3) - 1) * 0.004,
    lng: base.lng + (Math.floor(index / 3) - 1) * 0.005,
  };
};

const hotelMapSpots =
  hotelGuideArticle?.points?.flatMap((section) =>
    (section.shops || []).map((hotel, index) => ({
      ...hotel,
      type: "hotel",
      kind: "hotel",
      status: section.title,
      detail: hotel.text,
      priceRange: getHotelRowValue(hotel, "一泊あたりの目安"),
      oceanView: getHotelRowValue(hotel, "オーシャンビュー"),
      airportAccess: getHotelAirportAccess(hotel),
      categories: ["hotel"],
      location: getHotelFallbackLocation(hotel.area || section.title, index),
      mapQuery: `${hotel.name} ${hotel.area || "宮古島"}`,
    })),
  ) || [];


const mapSpots = [...baseMapSpots, ...hotelMapSpots];

const getDirectionGroup = (facing) => {
  if (!Number.isFinite(facing)) return "方角不明";
  if (facing >= 315 || facing < 45) return "北向き";
  if (facing < 135) return "東向き";
  if (facing < 225) return "南向き";
  return "西向き";
};

export const oceanGuideAdminSpots = mapSpots
  .filter((spot) => spot.kind !== "food" && spot.kind !== "hotel")
  .filter((spot) => spot.categories?.some((category) => ["snorkel", "beach"].includes(category)))
  .map((spot) => ({
    id: getSpotOverrideId(spot.name),
    name: spot.name,
    direction: getDirectionGroup(spot.facing),
  }));

const forecastStatuses = [
  ["取得中", "good", "--", "--"],
  ["取得中", "good", "--", "--"],
  ["取得中", "good", "--", "--"],
  ["取得中", "good", "--", "--"],
  ["取得中", "good", "--", "--"],
];

const weekDays = ["日", "月", "火", "水", "木", "金", "土"];

const weatherIcons = {
  晴れ: "☀",
  晴時々曇: "🌤",
  晴れのち曇: "🌤",
  晴れのち雨: "🌦",
  晴れ時々雨: "🌦",
  曇時々晴: "⛅",
  曇のち晴れ: "⛅",
  曇のち雨: "🌦",
  曇: "☁",
  曇時々雨: "🌦",
  雨のち晴れ: "🌦",
  雨のち曇: "🌦",
  雨時々晴れ: "🌦",
  雨時々曇: "🌦",
  雨: "☔",
  雨なし: "🌤",
  取得中: "…",
};

const weatherCodeLabels = {
  0: "晴れ",
  1: "晴時々曇",
  2: "晴時々曇",
  3: "曇",
  45: "曇",
  48: "曇",
  51: "曇時々雨",
  53: "曇時々雨",
  55: "雨",
  56: "雨",
  57: "雨",
  61: "雨",
  63: "雨",
  65: "雨",
  66: "雨",
  67: "雨",
  80: "曇時々雨",
  81: "雨",
  82: "雨",
  95: "雨",
  96: "雨",
  99: "雨",
};

const weatherLabelTranslations = {
  "晴れ": { EN: "Sunny", 中文: "晴", 한국어: "맑음" },
  "晴時々曇": { EN: "Sunny, Occasionally Cloudy", 中文: "晴间多云", 한국어: "맑음, 가끔 흐림" },
  "晴れのち曇": { EN: "Sunny Later Cloudy", 中文: "晴转多云", 한국어: "맑다가 흐림" },
  "晴れのち雨": { EN: "Sunny Later Rain", 中文: "晴转雨", 한국어: "맑다가 비" },
  "晴れ時々雨": { EN: "Sunny, Occasionally Rain", 中文: "晴间有雨", 한국어: "맑음, 가끔 비" },
  "曇時々晴": { EN: "Cloudy, Occasionally Sunny", 中文: "多云间晴", 한국어: "흐림, 가끔 맑음" },
  "曇のち晴れ": { EN: "Cloudy Later Sunny", 中文: "多云转晴", 한국어: "흐리다가 맑음" },
  "曇のち雨": { EN: "Cloudy Later Rain", 中文: "多云转雨", 한국어: "흐리다가 비" },
  "曇": { EN: "Cloudy", 中文: "多云", 한국어: "흐림" },
  "曇時々雨": { EN: "Cloudy, Occasionally Rain", 中文: "多云间雨", 한국어: "흐림, 가끔 비" },
  "雨のち晴れ": { EN: "Rain Later Sunny", 中文: "雨转晴", 한국어: "비 오다가 맑음" },
  "雨のち曇": { EN: "Rain Later Cloudy", 中文: "雨转多云", 한국어: "비 오다가 흐림" },
  "雨時々晴れ": { EN: "Rain, Occasionally Sunny", 中文: "雨间晴", 한국어: "비, 가끔 맑음" },
  "雨時々曇": { EN: "Rain, Occasionally Cloudy", 中文: "雨间多云", 한국어: "비, 가끔 흐림" },
  "雨": { EN: "Rain", 中文: "雨", 한국어: "비" },
  "雨なし": { EN: "No Rain", 中文: "无雨", 한국어: "비 없음" },
  "取得中": { EN: "Fetching...", 中文: "获取中...", 한국어: "가져오는 중..." },
};
const translateWeatherLabel = (weather, lang) =>
  weatherLabelTranslations[weather]?.[lang] || weather;

const defaultWeatherForecast = {
  updated: "Open-Meteo 読み込み中",
  current: {
    weather: "取得中",
    high: "--",
    low: "--",
    summary: "Open-Meteoから今日の実データを読み込んでいます。",
    sunrise: "--",
    sunset: "--",
    windDirection: "--",
    windSpeed: "",
    hourlyWeather: [],
  },
  days: [],
  dailyDetails: [],
  rainByTime: [],
};

const weekDayTranslations = {
  日: { EN: "Sun", 中文: "周日", 한국어: "일" },
  月: { EN: "Mon", 中文: "周一", 한국어: "월" },
  火: { EN: "Tue", 中文: "周二", 한국어: "화" },
  水: { EN: "Wed", 中文: "周三", 한국어: "수" },
  木: { EN: "Thu", 中文: "周四", 한국어: "목" },
  金: { EN: "Fri", 中文: "周五", 한국어: "금" },
  土: { EN: "Sat", 中文: "周六", 한국어: "토" },
};

const getWeekDayLabel = (date, lang) => {
  const jpDay = weekDays[date.getDay()];
  return weekDayTranslations[jpDay]?.[lang] || jpDay;
};

const formatForecastDate = (date, lang) =>
  `${date.getMonth() + 1}/${date.getDate()}(${getWeekDayLabel(date, lang)})`;

const buildForecastDays = (lang) => {
  const today = new Date();

  return forecastStatuses.map(([label, status, high, low], index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index);

    const dayLabel = getForecastDayLabel(index, date, lang);
    const dateLabel = formatForecastDate(date, lang);

    return [dayLabel, dateLabel, label, status, high, low];
  });
};

const forecastDayLabelTranslations = {
  "今日": { EN: "Today", 中文: "今天", 한국어: "오늘" },
  "明日": { EN: "Tomorrow", 中文: "明天", 한국어: "내일" },
  "あさって": { EN: "Day after tomorrow", 中文: "后天", 한국어: "모레" },
};

const getForecastDayLabel = (index, date, lang) => {
  const jpLabel = index === 0 ? "今日" : index === 1 ? "明日" : index === 2 ? "あさって" : null;
  if (!jpLabel) return formatForecastDate(date, lang);
  return forecastDayLabelTranslations[jpLabel]?.[lang] || jpLabel;
};

const getTideHour = (time) => Number(String(time).split(":")[0]);

const getTideDayPart = (hour, labels) => {
  if (hour < 12) return labels.tideDayParts.morning;
  if (hour < 18) return labels.tideDayParts.afternoon;
  return labels.tideDayParts.daytime;
};

// The actual tide extremum can land in the very early morning (before sunrise, or
// before most visitors are up and moving), which isn't a realistic time to suggest
// heading out. Floor the displayed recommendation time to 8:00 rather than showing
// an impractically early hour — the tide condition described still holds through
// the morning window either way.
const getDisplayTideTime = (time) => (getTideHour(time) < 8 ? "8:00" : time);

const formatTideRecommendationTime = (time, labels) => {
  const displayTime = getDisplayTideTime(time);
  return labels.tideTimeLabel
    .replace("{time}", displayTime)
    .replace("{part}", getTideDayPart(getTideHour(displayTime), labels));
};

const getTideRecommendationCondition = (weather, marine, windItems) => {
  const wave = getNumericValue(marine.wave);
  const swell = getNumericValue(marine.swell);
  const windSpeeds = (windItems || []).map((item) => getNumericValue(item.speed));
  const maxWind = windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0;
  const windGusts = (windItems || []).map((item) => getNumericValue(item.gust));
  const maxGust = windGusts.length > 0 ? Math.max(...windGusts) : 0;
  const maxRain = Math.max(
    0,
    ...(weather.rainByTime || []).map(([, rain]) => getNumericValue(rain)),
  );
  const rainy = isRainyWeather(weather.weather);

  if (
    wave >= 1.8 ||
    swell >= 1.6 ||
    maxWind >= 8 ||
    maxGust >= 11 ||
    maxRain >= 70 ||
    rainy
  ) {
    return "hide";
  }

  if (wave >= 1.2 || swell >= 1 || maxWind >= 5 || maxGust >= 8 || maxRain >= 40) {
    return "caution";
  }

  return "good";
};

const is17EndAffectedByWind = (time, windItems) => {
  const targetHour = getTideHour(getDisplayTideTime(time));
  const nearestWind = (windItems || [])
    .filter((item) => Number.isFinite(item.degree))
    .reduce((nearest, item) => {
      const distance = Math.abs(getTideHour(item.time) - targetHour);
      return !nearest || distance < nearest.distance ? { item, distance } : nearest;
    }, null)?.item;

  // 17END faces west. Easterly winds blow from behind the beach and therefore
  // should not trigger the generic rough-condition warning shown for exposed coasts.
  return nearestWind ? !isWindSheltered({ facing: 270 }, nearestWind.degree) : true;
};

const buildVisibleTideRecommendations = ({
  labels,
  tides = defaultDailyMarineForecast.tides,
  weather = defaultWeatherForecast.current,
  marine = defaultDailyMarineForecast,
  windItems = [],
}) => {
  const condition = getTideRecommendationCondition(weather, marine, windItems);
  if (condition === "hide") return [];

  const [highRule, lowRule] = labels.tideRecommendations;
  const highRecommendations = (tides.high || [])
    .filter(([time]) => {
      const hour = getTideHour(time);
      return hour >= 5 && hour < 12;
    })
    .map(([time]) => ({
      timing: formatTideRecommendationTime(time, labels),
      spot: highRule[1],
      description: highRule[2],
      condition: "good",
    }));
  const lowRecommendations = (tides.low || [])
    .filter(([time]) => {
      const hour = getTideHour(time);
      return hour >= 10 && hour < 18;
    })
    .map(([time]) => {
      const showCaution = condition === "caution" && is17EndAffectedByWind(time, windItems);
      return {
        timing: formatTideRecommendationTime(time, labels),
        spot: lowRule[1],
        description: showCaution ? labels.tideConditionCaution : lowRule[2],
        condition: showCaution ? "caution" : "good",
      };
    });

  return [...highRecommendations, ...lowRecommendations];
};

const guideContentTranslations = {
  "海の危険サインを知ろう": {
    EN: {
      title: "Know the Warning Signs of the Sea",
      description: "Watch out for rip currents (strong currents pulling you out to sea). Areas where the wave color differs from the surroundings, or where foam is flowing straight offshore, are signs of a rip current. If caught in one, don't swim straight back to shore — first swim parallel to the shore to escape the current. Also watch for sudden weather changes and tide shifts. Even on a sunny day, strong swell can sweep you away. On days with bad conditions, use a tour or avoid going out too far.",
    },
    中文: {
      title: "了解海洋危险信号",
      description: "请注意离岸流（将人拖向外海的强劲水流）。与周围颜色不同的海域，或泡沫笔直流向外海的地方，都是离岸流的信号。若被卷入，不要直线游向岸边，应先沿与海岸平行的方向游动脱离水流。也请留意天气骤变和潮位变化。即使天晴，涌浪强时也会被冲走。天气不好的日子请利用团体行程，或避免游到远海。",
    },
    한국어: {
      title: "바다의 위험 신호 알아두기",
      description: "이안류(먼바다 쪽으로 끌려가는 강한 흐름)에 주의하세요. 주변과 파도 색이 다른 곳이나 거품이 곧장 먼바다로 흘러가는 곳은 이안류의 신호입니다. 휩쓸렸을 때는 곧바로 해안으로 향하지 말고, 우선 해안과 평행하게 헤엄쳐 흐름에서 벗어나세요. 날씨 급변이나 조수 변화에도 주의하세요. 맑은 날이라도 너울이 강하면 떠내려갈 수 있습니다. 날씨가 나쁜 날은 투어를 이용하거나 먼바다까지 나가지 않도록 주의하세요.",
    },
  },
  "ライフジャケットのすすめ": {
    EN: {
      title: "We Recommend a Life Jacket",
      description: "Even confident swimmers should wear a life jacket for snorkeling, SUP, and other ocean activities. It keeps you buoyant without using energy, which is a safety measure for when you're tired or caught in a current. Most tours and rental shops have them available.",
    },
    中文: {
      title: "推荐穿救生衣",
      description: "即使是游泳有信心的人，在浮潜、SUP等海上活动中也建议穿救生衣。无需耗费体力就能保持浮力，是疲劳或被冲走时的安全保障。大多数团体行程和租赁店都可以借到。",
    },
    한국어: {
      title: "구명조끼 착용을 권장합니다",
      description: "수영에 자신 있는 분이라도 스노클링이나 SUP 등 바다 놀이에서는 구명조끼 착용을 추천합니다. 체력을 쓰지 않고 부력을 유지할 수 있어 지쳤을 때나 떠내려갈 때 안전 대책이 됩니다. 대부분의 투어나 렌탈숍에서 빌릴 수 있습니다.",
    },
  },
  "緊急時の連絡先": {
    EN: {
      title: "Emergency Contacts",
      description: "For sea accidents (drowning, boat accidents, etc.), call 118 (Japan Coast Guard). For general emergencies such as injury or sudden illness, call 119 (Fire/Ambulance). To stay calm in an emergency, it helps to check your accommodation's address and your current location in advance.",
    },
    中文: {
      title: "紧急联络方式",
      description: "海上事故（溺水、船只事故等）请拨打118（海上保安厅）。受伤或突发疾病等一般紧急情况请拨打119（消防・救护）。为避免紧急时慌乱，建议事先确认住宿地址和当前所在位置。",
    },
    한국어: {
      title: "긴급 연락처",
      description: "바다 사고(익수・선박 사고 등)는 118번(해상보안청)으로 연락하세요. 부상이나 급병 등 일반적인 긴급 상황은 119번(소방・구급)으로 연락하세요. 만일의 상황에 당황하지 않도록 숙소 주소와 현재 위치를 미리 확인해두면 안심입니다.",
    },
  },
  "ベストシーズン": {
    EN: {
      title: "Best Season",
      description: "The best season for ocean activities is April–October. May–June, around the end of the rainy season, has especially clear water, while July–September offers warm air and water for comfortable swimming. Note that July–October is also typhoon season, so be sure to check the forecast right before you go.",
    },
    中文: {
      title: "最佳季节",
      description: "海上活动的最佳季节是4月至10月。5-6月梅雨结束前后海水透明度尤其高，7-9月气温水温都高，游泳很舒适。不过7-10月也是台风季节，出发前请务必确认天气预报。",
    },
    한국어: {
      title: "베스트 시즌",
      description: "바다 놀이의 베스트 시즌은 4월~10월. 5~6월은 장마가 끝날 무렵으로 바다 투명도가 특히 높고, 7~9월은 기온・수온이 모두 높아 쾌적하게 수영할 수 있습니다. 다만 7~10월은 태풍 시즌이기도 하니 출발 직전 일기예보 확인을 잊지 마세요.",
    },
  },
  "持ち物リスト": {
    EN: {
      title: "Packing List",
      description: "Swimsuit, rash guard, marine shoes, sunscreen (a reef-safe, non-chemical type is recommended), towel, hat, sunglasses, a waterproof case for your phone, and any regular medication. Most items can also be rented or bought at local shops, so it's fine to travel light.",
    },
    中文: {
      title: "随身物品清单",
      description: "泳衣、防晒衣、溯溪鞋、防晒霜（推荐对珊瑚友好的无化学成分类型）、毛巾、帽子、太阳镜、手机防水壳、常备药。大部分物品在当地商店也可以租借或购买，轻装出行也不用担心。",
    },
    한국어: {
      title: "준비물 리스트",
      description: "수영복・래시가드・마린슈즈・선크림(산호에 순한 논케미컬 타입 추천)・수건・모자・선글라스・스마트폰 방수 케이스・상비약. 대부분의 아이템은 현지 매장에서도 대여・구매할 수 있어 가볍게 여행해도 안심입니다.",
    },
  },
  "服装ガイド": {
    EN: {
      title: "What to Wear",
      description: "From April to October, short sleeves and shorts are comfortable, but the sun is very strong, so a UV-cut cover-up is reassuring to have. From November to March, strong north winds can make it feel colder on some days, so bring a light jacket. Indoor spaces are also often air-conditioned.",
    },
    中文: {
      title: "穿衣指南",
      description: "4-10月穿短袖短裤即可舒适度过，但日照非常强烈，建议准备防晒外套。11-3月北风较强，体感温度有时会下降，建议带一件薄外套。室内也常常开着冷气。",
    },
    한국어: {
      title: "복장 가이드",
      description: "4~10월은 반팔・반바지로 편하게 지낼 수 있지만 햇볕이 매우 강하므로 UV 차단 소재의 겉옷이 있으면 안심입니다. 11~3월은 북풍이 강해 체감 온도가 낮아지는 날도 있으니 얇은 겉옷을 하나 챙기세요. 실내는 냉방이 강한 경우도 많습니다.",
    },
  },
  "海亀シュノーケル": {
    EN: { title: "Sea Turtle Snorkeling", description: "Meet sea turtles and colorful tropical fish in the Miyako-blue sea. Many shops rent full gear sets, so you can enjoy it easily even empty-handed.", tag: "Beginner OK" },
    中文: { title: "海龟浮潜", description: "在宫古蓝的海中邂逅海龟与色彩缤纷的热带鱼。许多店铺可租借全套装备，空手前往也能轻松享受。", tag: "初学者OK" },
    한국어: { title: "바다거북 스노클링", description: "미야코블루의 바다에서 바다거북과 형형색색의 열대어를 만날 수 있습니다. 장비 일체를 대여할 수 있는 매장도 많아 빈손으로도 부담 없이 즐길 수 있습니다.", tag: "초보자 OK" },
  },
  "秘境SUP": {
    EN: { title: "Secret SUP", description: "A tour visiting hidden beaches only reachable by SUP. Have Miyakojima's sea all to yourself, with drone photography too. Recommended for those seeking the ultimate extraordinary experience, or wanting video and photos worth sharing on social media.", tag: "Beginner OK" },
    中文: { title: "秘境SUP", description: "只能乘SUP前往的秘境海滩巡游行程。独享宫古岛的海，还可进行无人机航拍。推荐给追求极致非日常体验、想留下适合发布社交媒体的照片和视频的人。", tag: "初学者OK" },
    한국어: { title: "비경 SUP", description: "SUP로만 갈 수 있는 비경 해변을 둘러보는 투어. 미야코지마의 바다를 독차지하며 드론 촬영도 가능합니다. 궁극의 특별한 경험을 원하는 분, SNS에 올리고 싶은 영상과 사진을 남기고 싶은 분에게 추천합니다.", tag: "초보자 OK" },
  },
  "サンセットSUP": {
    EN: { title: "Sunset SUP", description: "An evening-only SUP tour where you watch the sun sink into the horizon from the water. The calm, glassy sea reflects the colors of the sky.", tag: "Beginner OK" },
    中文: { title: "日落SUP", description: "从海上眺望夕阳沉入水平线的傍晚限定SUP行程。风平浪静的海面倒映着天空的色彩。", tag: "初学者OK" },
    한국어: { title: "선셋 SUP", description: "수평선으로 지는 석양을 바다 위에서 바라보는 저녁 한정 SUP 투어. 잔잔한 수면에 하늘의 색이 비칩니다.", tag: "초보자 OK" },
  },
  "サーフィン体験": {
    EN: { title: "Surfing Experience", description: "Try surfing in exceptionally clear water — an experience unique to Miyakojima. Use a beginner-friendly school and feel the island's great nature.", tag: "Beginner OK" },
    中文: { title: "冲浪体验", description: "在透明度极佳的海中体验冲浪，这是只有宫古岛才能拥有的体验。可选择初学者也能安心的冲浪学校，感受宫古岛的大自然。", tag: "初学者OK" },
    한국어: { title: "서핑 체험", description: "투명도가 뛰어난 바다에서 즐기는 서핑 체험. 미야코지마에서만 할 수 있는 경험입니다. 초보자도 안심할 수 있는 스쿨을 이용해 미야코지마의 대자연을 느껴보세요.", tag: "초보자 OK" },
  },
  "やびじツアー": {
    EN: { title: "Yabiji Tour", description: "A boat tour around Yabiji, Miyakojima's largest coral reef system. This is the most beautiful sea in Miyakojima.", tag: "Subject to sea conditions" },
    中文: { title: "八重干瀬之旅", description: "乘船巡游宫古岛最大的珊瑚礁群——八重干瀬的行程。这里是宫古岛最美的海域。", tag: "根据海况决定是否出行" },
    한국어: { title: "야비지 투어", description: "미야코지마 최대의 산호초 군락・야비지를 배로 둘러보는 투어. 미야코지마에서 가장 아름다운 바다가 바로 이곳입니다.", tag: "해황에 따라 운항 여부 결정" },
  },
  "星空撮影": {
    EN: { title: "Starry Sky Photography", description: "Have your photo taken against a sky full of stars and the Milky Way. Tour prices are also reasonable, at around ¥5,000–7,000 per person.", tag: "" },
    中文: { title: "星空摄影", description: "以满天繁星和银河为背景拍摄星空写真。行程费用也不贵，每人约5,000~7,000日元。", tag: "" },
    한국어: { title: "별하늘 촬영", description: "가득한 별과 은하수를 배경으로 별하늘 사진을 촬영합니다. 투어 요금도 1인 5,000~7,000엔 정도로 부담스럽지 않습니다.", tag: "" },
  },
  "撮影ツアー": {
    EN: { title: "Photo Tour", description: "A small-group private tour visiting photo spots and scenic viewpoints, shooting with drones and DSLR cameras. For those who want photography that makes the most of Miyakojima's potential.", tag: "" },
    中文: { title: "摄影之旅", description: "小团体限定的私人行程，巡游摄影点和绝景点，用无人机和单反相机拍摄。适合追求充分展现宫古岛魅力的摄影爱好者。", tag: "" },
    한국어: { title: "촬영 투어", description: "소수 인원 한정 프라이빗 투어로 촬영 포인트와 절경 포인트를 둘러보며 드론과 DSLR 카메라로 촬영합니다. 미야코지마의 매력을 최대한 살린 촬영을 원하는 분에게 추천합니다.", tag: "" },
  },
  "ウミガメ": {
    EN: { title: "Sea Turtles", description: "Shallow beaches with seagrass beds, such as Aragusuku Beach and Wai Wai Beach, have a higher chance of encounters. Early morning to late morning, when the sea is calm, is the recommended time. If you spot one, don't chase or touch it — just watch quietly." },
    中文: { title: "海龟", description: "新城海岸、Wai Wai海滩等远浅且有海藻床的海滩，遇见几率较高。推荐在海况平稳的清晨到上午时段前往。即使发现海龟，也请不要追逐或触摸，安静守望即可。" },
    한국어: { title: "바다거북", description: "신구스쿠 해안이나 와이와이 비치처럼 완만하고 해초밭이 있는 해변은 만날 확률이 높은 편입니다. 바다가 잔잔한 이른 아침~오전 중이 추천 시간대입니다. 발견하더라도 쫓아가거나 만지지 말고 조용히 지켜봐 주세요." },
  },
  "サンゴ礁": {
    EN: { title: "Coral Reefs", description: "Miyakojima's beautiful sea is supported by its coral reefs. Choose a reef-safe, non-chemical sunscreen, and be mindful of where you stand or enter the water so you don't step on or kick the coral." },
    中文: { title: "珊瑚礁", description: "宫古岛美丽的大海是由珊瑚礁支撑的。请选择对海洋友好的无化学成分防晒霜，并注意站立和入水位置，避免踩踏或踢到珊瑚。" },
    한국어: { title: "산호초", description: "미야코지마의 아름다운 바다는 산호초가 지탱하고 있습니다. 선크림은 바다에 순한 논케미컬 타입을 선택하고, 산호를 밟거나 차지 않도록 서 있는 위치와 입수 지점에도 주의하세요." },
  },
  "星空・天の川": {
    EN: { title: "Stars & Milky Way", description: "Nights around the new moon with few clouds are the best time for observation. Spots with little city light, such as Higashi-Henna Cape and Higa Road Park, are popular viewing locations." },
    中文: { title: "星空・银河", description: "新月前后、云量少的夜晚是观测的最佳时机。灯光稀少的东平安名崎、比嘉路公园等是人气观测点。" },
    한국어: { title: "별하늘・은하수", description: "신월 전후, 구름이 적은 밤이 관측의 베스트 타이밍입니다. 불빛이 적은 히가시헨나자키나 히가 로드파크 등이 인기 관측 스팟입니다." },
  },
  "クラゲ": {
    EN: { title: "Jellyfish", description: "From summer to autumn, venomous jellyfish such as the Portuguese man o' war can drift in. Wear a rash guard, and if stung, don't rub the area — rinse it with seawater and then consult staff or a medical facility." },
    中文: { title: "水母", description: "夏季至秋季，可能会漂来僧帽水母等有毒水母。请穿着防晒衣，若被蜇伤请勿揉搓，先用海水冲洗后再咨询工作人员或就医。" },
    한국어: { title: "해파리", description: "여름부터 가을에 걸쳐 파문어리(전기해파리) 등 독이 있는 해파리가 떠다니는 경우가 있습니다. 래시가드를 착용하고, 쏘였을 경우 문지르지 말고 바닷물로 씻어낸 후 스태프나 의료기관에 상담하세요." },
  },
};

const localizeGuideTitle = (title, lang) => guideContentTranslations[title]?.[lang]?.title || title;
const localizeGuideDescription = (title, description, lang) =>
  guideContentTranslations[title]?.[lang]?.description || description;
const localizeGuideTag = (title, tag, lang) => guideContentTranslations[title]?.[lang]?.tag ?? tag;

const safetyGuides = [
  [
    "海の危険サインを知ろう",
    "離岸流（沖に向かって流される強い流れ）に注意。周囲と波の色が違う場所や、泡がまっすぐ沖へ流れている場所は離岸流のサインです。流された場合は岸に向かって直進せず、まず岸と平行に泳いで流れから抜けましょう。天候の急変や潮位の変化にも注意してください。晴れてるからといってうねりが強ければ流されます。天気が悪い日はツアーを利用するか沖まで出ないように注意してください。",
  ],
  [
    "ライフジャケットのすすめ",
    "泳ぎに自信がある人でも、シュノーケルやSUPなどの海遊びではライフジャケットの着用がおすすめです。体力を使わず浮力を保てるので、疲れたときや流された際の安全対策になります。多くのツアーやレンタルショップで借りられます。",
  ],
  [
    "緊急時の連絡先",
    "海の事故（水難事故・船の事故など）は118番（海上保安庁）へ。ケガや急病など一般的な緊急時は119番（消防・救急）へ連絡してください。もしもの時に慌てないよう、宿泊先の住所や現在地を事前に確認しておくと安心です。",
  ],
];

const guideItems = [
  [
    "☀",
    "ベストシーズン",
    "海遊びのベストシーズンは4月〜10月。5〜6月は梅雨明け前後で海の透明度が特に高く、7〜9月は気温・水温ともに高く快適に泳げます。ただし7〜10月は台風シーズンでもあるため、出発直前の天気予報チェックを忘れずに。",
  ],
  [
    "🎒",
    "持ち物リスト",
    "水着・ラッシュガード・マリンシューズ・日焼け止め（サンゴに優しいノンケミカルタイプがおすすめ）・タオル・帽子・サングラス・スマホ用防水ケース・常備薬。ほとんどのアイテムは現地のショップでもレンタル・購入できるので、身軽な旅行でも安心です。",
  ],
  [
    "👕",
    "服装ガイド",
    "4〜10月は半袖・短パンで快適に過ごせますが、日差しがとても強いのでUVカット素材の羽織りものがあると安心です。11〜3月は北風が強く体感温度が下がる日もあるため、薄手の上着を1枚持っておきましょう。屋内は冷房が効いていることも多いです。",
  ],
];

const activities = [
  [
    "海亀シュノーケル",
    "宮古ブルーの海でウミガメやカラフルな熱帯魚に出会える。道具一式レンタルできるショップも多く、手ぶらでも気軽に楽しめます。",
    "初心者OK",
  ],
  [
    "秘境SUP",
    "SUPでしか行けない秘境ビーチを巡るツアー。宮古島の海を独り占めしてドローン撮影も。究極の非日常体験を求める方、SNSにあげたくなる動画や写真を残したい方におすすめです。",
    "初心者OK",
  ],
  [
    "サンセットSUP",
    "水平線に沈む夕日を海の上から眺める、夕方限定のSUPツアー。穏やかな凪の海面に空の色が映り込みます。",
    "初心者OK",
  ],
  [
    "サーフィン体験",
    "透明度抜群の海でサーフィン体験。宮古島でしかできない体験です。初心者でも安心なスクールを利用し、宮古島の大自然を感じてください。",
    "初心者OK",
    "/tours/surfing",
  ],
  [
    "やびじツアー",
    "宮古島最大のサンゴ礁群・八重干瀬を船で巡るツアー。宮古島で最も綺麗な海はここです。",
    "海況により催行判断",
  ],
  [
    "星空撮影",
    "満天の星と天の川をバックに星空撮影。ツアーの料金も1人5,000〜7,000円ぐらいで高くありません。",
    "",
  ],
  [
    "撮影ツアー",
    "少人数限定のプライベートツアーで撮影ポイント、絶景ポイントをめぐりドローン撮影や一眼カメラで撮影。宮古島のポテンシャルを最大限に生かした撮影にこだわりたい方へ。",
    "",
    "/tours/one-day-photogenic-tour",
  ],
];

const activityImages = {
  海亀シュノーケル: () => img("v1784531285/IMG_1066_sd05qe.webp", { width: 900, height: 520 }),
  秘境SUP: () => img("v1785005393/DJI_20260722152001_0021_D_small_odgfk0.jpg", { width: 900, height: 520 }),
  サンセットSUP: () => heroImagesByCategory.sunset,
  サーフィン体験: () => img("v1784350658/DJI_20260706094707_0054_D_t0d3pv.webp", { width: 900, height: 520 }),
  やびじツアー: () => img("v1784531283/IMG_1043_jmvmax.webp", { width: 900, height: 520 }),
  星空撮影: () => img("v1784940531/DSC00400_ftuhtb.jpg", { width: 900, height: 520 }),
  撮影ツアー: () => img("v1788372062/DSC00525_necd00.jpg", { width: 900, height: 520 }),
};

const rainyDayPlan = blogArticles["rainy-day-plan"];

const rainPointIcons = {
  筋トレで解決: "💪",
  レジンアート体験: "🎨",
  シーサー作り: "🦁",
  アクセサリー作り: "💍",
};

const rainRowIcons = {
  住所: "📍",
  アクセス: "🚗",
  電話: "📞",
  営業時間: "🕐",
  定休日: "🚫",
  所要時間: "⏱",
  対象: "👪",
  予約: "📝",
};

const starImage = img(
  "v1784354836/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-07-18_15.06.59_noluwe.png",
  {
    width: 900,
    height: 520,
  },
);

const natureGuides = [
  [
    "ウミガメ",
    "新城海岸やワイワイビーチなど、遠浅で藻場のあるビーチは遭遇率が高め。海が穏やかな早朝〜午前中がおすすめの時間帯です。見つけても追いかけたり触れたりせず、そっと見守りましょう。",
    img("https://res.cloudinary.com/drxijtp8g/image/upload/v1784531285/IMG_1066_sd05qe.webp"),
  ],
  [
    "サンゴ礁",
    "宮古島の美しい海はサンゴ礁が支えています。日焼け止めは海に優しいノンケミカルタイプを選び、サンゴを踏んだり蹴ったりしないよう、立ち位置や着水にも気をつけましょう。",
    img("https://res.cloudinary.com/drxijtp8g/image/upload/v1784531283/IMG_1043_jmvmax.webp"),
  ],
  [
    "星空・天の川",
    "新月前後、雲の少ない夜が観測のベストタイミング。街灯りの少ない東平安名崎や比嘉ロードパークなどが人気の観測スポットです。",
    img("https://res.cloudinary.com/drxijtp8g/image/upload/v1784940531/DSC00400_ftuhtb.jpg"),
  ],
  [
    "クラゲ",
    "夏〜秋にかけてカツオノエボシなど毒のあるクラゲが漂うことがあります。ラッシュガードを着用し、刺された場合はこすらず海水で洗い流してからスタッフや医療機関に相談しましょう。",
    img("https://res.cloudinary.com/drxijtp8g/image/upload/v1789048380/67E3CZTVOFIMJDRPCN4IADMOEM_w2ywpc.jpg"),
  ],
];

const rainRadarPoints = [
  { name: "宮古島市街地", lat: 24.805, lng: 125.281 },
  { name: "新城海岸", lat: 24.7599, lng: 125.4249 },
  { name: "与那覇前浜", lat: 24.735, lng: 125.265 },
  { name: "池間島", lat: 24.933, lng: 125.247 },
  { name: "来間島", lat: 24.7045, lng: 125.238 },
  { name: "東平安名崎", lat: 24.7195, lng: 125.4684 },
];

const defaultRainRadarSnapshot = {
  updated: "",
  points: rainRadarPoints.map((point) => ({
    ...point,
    rainfall: 0,
    level: "none",
    label: "雨なし",
  })),
};

const mapUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const normalizePlaceSearch = (value = "") =>
  String(value).normalize("NFKC").toLowerCase().replace(/\s/g, "");

const SYNODIC_MONTH_DAYS = 29.530588853;
const MOON_BRIGHT_THRESHOLD = 0.35;
const NIGHT_START_TIME = "20:00";
const SUN_DARK_ALTITUDE = (-6 * Math.PI) / 180;

const formatMoonTime = (date) => {
  if (!date) return null;
  return date.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Tokyo",
  });
};

// Meeus' lunar phase calculation keeps the monthly new-moon information
// current without maintaining a hard-coded calendar. Times are returned in UTC
// and formatted for Miyakojima (JST) below.
const calculateNewMoon = (lunationNumber) => {
  const k = lunationNumber;
  const T = k / 1236.85;
  const T2 = T * T;
  const T3 = T2 * T;
  const T4 = T3 * T;
  const toRad = (degree) => (degree * Math.PI) / 180;
  const sin = (degree) => Math.sin(toRad(degree));
  const E = 1 - 0.002516 * T - 0.0000074 * T2;
  const M = 2.5534 + 29.1053567 * k - 0.0000014 * T2 - 0.00000011 * T3;
  const moonM = 201.5643 + 385.81693528 * k + 0.0107582 * T2
    + 0.00001238 * T3 - 0.000000058 * T4;
  const F = 160.7108 + 390.67050284 * k - 0.0016118 * T2
    - 0.00000227 * T3 + 0.000000011 * T4;
  const omega = 124.7746 - 1.5637558 * k + 0.0020672 * T2 + 0.00000215 * T3;
  let julianDay = 2451550.09765 + 29.530588853 * k + 0.0001337 * T2
    - 0.00000015 * T3 + 0.00000000073 * T4;

  julianDay += -0.4072 * sin(moonM) + 0.17241 * E * sin(M)
    + 0.01608 * sin(2 * moonM) + 0.01039 * sin(2 * F)
    + 0.00739 * E * sin(moonM - M) - 0.00514 * E * sin(moonM + M)
    + 0.00208 * E * E * sin(2 * M) - 0.00111 * sin(moonM - 2 * F)
    - 0.00057 * sin(moonM + 2 * F) + 0.00056 * E * sin(2 * moonM + M)
    - 0.00042 * sin(3 * moonM) + 0.00042 * E * sin(M + 2 * F)
    + 0.00038 * E * sin(M - 2 * F) - 0.00024 * E * sin(2 * moonM - M)
    - 0.00017 * sin(omega) - 0.00007 * sin(moonM + 2 * M)
    + 0.00004 * sin(2 * moonM - 2 * F) + 0.00004 * sin(3 * M)
    + 0.00003 * sin(moonM + M - 2 * F) + 0.00003 * sin(2 * moonM + 2 * F)
    - 0.00003 * sin(moonM + M + 2 * F) + 0.00003 * sin(moonM - M + 2 * F)
    - 0.00002 * sin(moonM - M - 2 * F) - 0.00002 * sin(3 * moonM + M)
    + 0.00002 * sin(4 * moonM);

  const planetaryAngles = [
    299.77 + 0.107408 * k - 0.009173 * T2,
    251.88 + 0.016321 * k,
    251.83 + 26.651886 * k,
    349.42 + 36.412478 * k,
    84.66 + 18.206239 * k,
    141.74 + 53.303771 * k,
    207.14 + 2.453732 * k,
    154.84 + 7.30686 * k,
    34.52 + 27.261239 * k,
    207.19 + 0.121824 * k,
    291.34 + 1.844379 * k,
    161.72 + 24.198154 * k,
    239.56 + 25.513099 * k,
    331.55 + 3.592518 * k,
  ];
  const planetaryWeights = [325, 165, 164, 126, 110, 62, 60, 56, 47, 42, 40, 37, 35, 23];
  julianDay += planetaryAngles.reduce(
    (sum, angle, index) => sum + planetaryWeights[index] * 0.000001 * sin(angle),
    0,
  );

  // The phase formula uses terrestrial time. 69.184 seconds is the current
  // TT−UTC offset and is sufficiently precise for this visitor-facing display.
  return new Date((julianDay - 2440587.5) * 86400000 - 69184);
};

const getJstCalendarParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type) => parts.find((part) => part.type === type)?.value || "";
  return {
    year: Number(value("year")),
    month: Number(value("month")),
    day: Number(value("day")),
    hour: Number(value("hour")),
    minute: value("minute"),
  };
};

// Major annual meteor showers only. Display for the peak night and the evening
// either side, as many showers peak around midnight in Japan.
const METEOR_SHOWERS = [
  { month: 1, day: 4, names: { JP: "しぶんぎ座流星群", EN: "Quadrantids", 中文: "象限儀座流星雨", 한국어: "사분의자리 유성우" } },
  { month: 4, day: 22, names: { JP: "こと座流星群", EN: "Lyrids", 中文: "天琴座流星雨", 한국어: "거문고자리 유성우" } },
  { month: 5, day: 6, names: { JP: "みずがめ座η流星群", EN: "Eta Aquariids", 中文: "寶瓶座η流星雨", 한국어: "물병자리 에타 유성우" } },
  { month: 8, day: 13, names: { JP: "ペルセウス座流星群", EN: "Perseids", 中文: "英仙座流星雨", 한국어: "페르세우스자리 유성우" } },
  { month: 10, day: 22, names: { JP: "オリオン座流星群", EN: "Orionids", 中文: "獵戶座流星雨", 한국어: "오리온자리 유성우" } },
  { month: 11, day: 18, names: { JP: "しし座流星群", EN: "Leonids", 中文: "獅子座流星雨", 한국어: "사자자리 유성우" } },
  { month: 12, day: 14, names: { JP: "ふたご座流星群", EN: "Geminids", 中文: "雙子座流星雨", 한국어: "쌍둥이자리 유성우" } },
  { month: 12, day: 23, names: { JP: "こぐま座流星群", EN: "Ursids", 中文: "小熊座流星雨", 한국어: "작은곰자리 유성우" } },
];

const getMeteorShowerNotice = (date, lang = "JP", { weatherCaution = false } = {}) => {
  const { year, month, day } = getJstCalendarParts(date);
  const target = Date.UTC(year, month - 1, day);
  const shower = METEOR_SHOWERS
    .map((item) => ({
      ...item,
      distance: Math.round((target - Date.UTC(year, item.month - 1, item.day)) / 86400000),
    }))
    .find((item) => item.distance >= -1 && item.distance <= 0);
  if (!shower) return null;

  const name = shower.names[lang] || shower.names.JP;
  if (lang === "EN") {
    return {
      title: shower.distance === 0 ? `${name} is best viewed tonight` : `${name} is best viewed tomorrow night`,
      detail: "If the sky is clear, look up from a safe, open location.",
    };
  }
  if (lang === "中文") {
    return {
      title: shower.distance === 0 ? `今晚是${name}的觀賞期` : `${name}預計明晚進入觀賞期`,
      detail: "天氣允許時，請在安全、視野開闊的地方抬頭看看夜空。",
    };
  }
  if (lang === "한국어") {
    return {
      title: shower.distance === 0 ? `오늘 밤 ${name} 관측 적기` : `${name}는 내일 밤이 관측 적기입니다`,
      detail: "하늘이 맑다면 안전하고 시야가 트인 곳에서 밤하늘을 올려다보세요.",
    };
  }
  return getJapaneseMeteorShowerCopy(name, shower.distance, { weatherCaution });
};

const getMonthlyNewMoon = (referenceDate = new Date()) => {
  const reference = getJstCalendarParts(referenceDate);
  const decimalYear = reference.year + (reference.month - 0.5) / 12;
  const approximateK = Math.round((decimalYear - 2000) * 12.3685);
  const candidates = [-1, 0, 1].map((offset) => calculateNewMoon(approximateK + offset));
  return candidates.find((date) => {
    const parts = getJstCalendarParts(date);
    return parts.year === reference.year && parts.month === reference.month;
  }) || candidates[1];
};

const formatMonthlyMoonDate = (date, lang, includeTime = false) => {
  const { month, day, hour, minute } = getJstCalendarParts(date);
  const locale = { JP: "ja-JP", EN: "en-US", 中文: "zh-TW", 한국어: "ko-KR" }[lang] || "ja-JP";
  const weekday = new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Tokyo",
    weekday: "short",
  }).format(date);
  const time = includeTime ? ` ${hour}:${minute}` : "";
  if (lang === "EN") {
    const monthName = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Tokyo", month: "short" }).format(date);
    return `${monthName} ${day} (${weekday})${time}`;
  }
  if (lang === "한국어") return `${month}월 ${day}일(${weekday})${time}`;
  return `${month}月${day}日（${weekday}）${time}`;
};

const getMonthlyMoonDisplay = (lang, referenceDate = new Date()) => {
  const newMoon = getMonthlyNewMoon(referenceDate);
  const bestStart = new Date(newMoon.getTime() - 2 * 86400000);
  const bestEnd = new Date(newMoon.getTime() + 4 * 86400000);
  return {
    newMoonDate: formatMonthlyMoonDate(newMoon, lang, true),
    bestDate: `${formatMonthlyMoonDate(bestStart, lang)}〜${formatMonthlyMoonDate(bestEnd, lang)}`,
  };
};

const shiftTimeString = (timeStr, minutesDelta) => {
  const [hours, minutes] = String(timeStr).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return timeStr;
  const total = (((hours * 60 + minutes + minutesDelta) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const getSunsetBestTimeRange = (sunsetTime) => {
  if (!sunsetTime || sunsetTime === "--") return null;
  return `${shiftTimeString(sunsetTime, -30)}〜${shiftTimeString(sunsetTime, 30)}`;
};

const getMoonPhasePath = (phase, size) => {
  const r = size / 2 - 2;
  const c = size / 2;
  const theta = phase * 2 * Math.PI;
  const rx = Math.max(Math.abs(Math.cos(theta)) * r, 0.01);
  const limbSweep = phase < 0.5 ? 1 : 0;
  let termSweep;
  if (phase < 0.25) termSweep = 0;
  else if (phase < 0.5) termSweep = 1;
  else if (phase < 0.75) termSweep = 0;
  else termSweep = 1;
  const top = `${c},${(c - r).toFixed(2)}`;
  const bottom = `${c},${(c + r).toFixed(2)}`;
  return `M ${top} A ${r} ${r} 0 0 ${limbSweep} ${bottom} A ${rx.toFixed(2)} ${r} 0 0 ${termSweep} ${top} Z`;
};

const buildMoonGuideData = (starLabels, oceanCondition, labels, weatherForecast, selectedDay = 0) => {
  const now = new Date(Date.now() + selectedDay * 24 * 60 * 60 * 1000);
  const { lat, lng } = MIYAKO_MAP_CENTER;
  const illumination = SunCalc.getMoonIllumination(now);
  const position = SunCalc.getMoonPosition(now, lat, lng);
  const isUp = position.altitude > 0;
  const sunPosition = SunCalc.getPosition(now, lat, lng);
  const isDarkEnough = sunPosition.altitude <= SUN_DARK_ALTITUDE;

  const todayTimes = SunCalc.getMoonTimes(now, lat, lng);
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowTimes = SunCalc.getMoonTimes(tomorrow, lat, lng);

  // Recommendations are for the coming night, not for the moment the visitor
  // opens the page. A daytime moonrise still means the moon is already up when
  // stargazing begins, so the useful dark window starts after that night's set.
  const nightStart = new Date(now);
  nightStart.setHours(Number(NIGHT_START_TIME.split(":")[0]), 0, 0, 0);
  const nightEnd = new Date(nightStart);
  nightEnd.setDate(nightEnd.getDate() + 1);
  // Visitor-facing recommendations run from 20:00 through 02:59.
  // Moon events after that can still be displayed, but must not become the
  // recommended viewing time (for example, "5時ごろがおすすめ").
  nightEnd.setHours(2, 59, 59, 999);

  const events = [
    todayTimes.rise && { type: "rise", time: todayTimes.rise },
    todayTimes.set && { type: "set", time: todayTimes.set },
    tomorrowTimes.rise && { type: "rise", time: tomorrowTimes.rise },
    tomorrowTimes.set && { type: "set", time: tomorrowTimes.set },
  ].filter(Boolean);

  const upcoming = events
    .filter((event) => event.time > now)
    .sort((a, b) => a.time - b.time);
  const nextSet = upcoming.find((event) => event.type === "set");
  const nextRise = upcoming.find((event) => event.type === "rise");
  const tonightEvents = events
    .filter((event) => event.time >= nightStart && event.time <= nightEnd)
    .sort((a, b) => a.time - b.time);
  const tonightSet = tonightEvents.find((event) => event.type === "set");
  const tonightRise = tonightEvents.find((event) => event.type === "rise");
  const moonIsUpAtNightStart = SunCalc.getMoonPosition(nightStart, lat, lng).altitude > 0;

  const isBright = illumination.fraction >= MOON_BRIGHT_THRESHOLD;
  let recommendation = starLabels.moonRecDark;
  let bestTime = null;
  if (isBright) {
    if (moonIsUpAtNightStart && tonightSet) {
      const setTime = formatMoonTime(tonightSet.time);
      recommendation = starLabels.moonRecBrightUp.replace("{time}", setTime);
      bestTime = starLabels.moonBestTimeAfterMoonset.replace("{time}", setTime);
    } else if (!moonIsUpAtNightStart && tonightRise) {
      const riseTime = formatMoonTime(tonightRise.time);
      recommendation = starLabels.moonRecBrightDown.replace("{time}", riseTime);
      bestTime = starLabels.moonBestTimeUntilMoonrise
        .replace("{start}", NIGHT_START_TIME)
        .replace("{time}", riseTime);
    } else if (!moonIsUpAtNightStart) {
      recommendation = starLabels.moonRecBelowHorizon;
    } else {
      recommendation = starLabels.moonRecBrightAllNight;
    }
  }

  const milkyWayVisibleNow = isDarkEnough && (!isBright || !isUp);
  // There's no separately-computed "night weather" for days other than today, so
  // fall back to that day's general daytime label rather than silently reusing
  // tonight's — still a same-day signal instead of a stale one from today.
  const nightWeatherText =
    selectedDay === 0
      ? weatherForecast?.current?.nightWeather
      : weatherForecast?.dailyDetails?.[selectedDay]?.weather;
  const tonightRating = buildTonightStarRating(
    oceanCondition,
    illumination.fraction,
    labels,
    nightWeatherText,
    weatherForecast?.dailyDetails?.[selectedDay]?.nightCloudCover,
    getNightRainProbability(
      weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current,
    ),
  );
  const meteorWeatherCaution =
    tonightRating.type === "bad" || getNumericValue(oceanCondition?.windSpeed) >= 8;

  return {
    phase: illumination.phase,
    ageDays: Math.round(illumination.phase * SYNODIC_MONTH_DAYS * 10) / 10,
    moonrise: formatMoonTime(nextRise?.time || todayTimes.rise || tomorrowTimes.rise),
    moonset: formatMoonTime(nextSet?.time || todayTimes.set || tomorrowTimes.set),
    recommendation,
    bestTime,
    milkyWayVisibleNow,
    tonightRating,
    meteorShower: getMeteorShowerNotice(now, labels.lang, { weatherCaution: meteorWeatherCaution }),
  };
};
const rainRadarUrl =
  "https://embed.windy.com/embed2.html?lat=24.805&lon=125.281&detailLat=24.805&detailLon=125.281&width=650&height=450&zoom=10&level=surface&overlay=rain&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=m%2Fs&metricTemp=%C2%B0C&radarRange=-1";
const yahooRainRadarUrl =
  "https://weather.yahoo.co.jp/weather/zoomradar/?lat=24.805&lon=125.281&z=10";
const jmaTyphoonUrl = "https://www.jma.go.jp/bosai/map.html#contents=typhoon";

const typhoonCategoryLabels = {
  JP: {
    TD: "熱帯低気圧",
    LOW: "低気圧",
    TS: "台風",
    STS: "強い台風",
    TY: "非常に強い台風",
    VTY: "猛烈な台風",
  },
  EN: {
    TD: "Tropical Depression",
    LOW: "Low Pressure",
    TS: "Typhoon",
    STS: "Severe Tropical Storm",
    TY: "Typhoon",
    VTY: "Violent Typhoon",
  },
  中文: {
    TD: "热带低压",
    LOW: "低气压",
    TS: "台风",
    STS: "强台风",
    TY: "强台风",
    VTY: "猛烈台风",
  },
  한국어: {
    TD: "열대저압부",
    LOW: "저기압",
    TS: "태풍",
    STS: "강한 태풍",
    TY: "매우 강한 태풍",
    VTY: "맹렬한 태풍",
  },
};

const parseYahooTimestamp = (timestamp) => {
  if (typeof timestamp === "number") return timestamp;
  if (!timestamp) return Number.NaN;

  const value = String(timestamp);
  const compactMatch = value.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/);
  if (compactMatch) {
    const [, year, month, day, hour, minute] = compactMatch;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
    ).getTime();
  }

  return new Date(value).getTime();
};

const getClosestIndex = (timestamps = []) => {
  const now = Date.now();
  const normalized = timestamps.map(parseYahooTimestamp);
  const futureIndex = normalized.findIndex((timestamp) => timestamp >= now);
  if (futureIndex >= 0) return futureIndex;
  return Math.max(normalized.length - 1, 0);
};

const getRainLevel = (rainfall) => {
  if (rainfall >= 10) return ["strong", "強い雨"];
  if (rainfall >= 3) return ["moderate", "雨"];
  if (rainfall > 0) return ["light", "小雨"];
  return ["none", "雨なし"];
};

const formatYahooWeatherTime = (timestamp) => {
  if (!timestamp) return "Yahoo天気API 更新";
  const parsed = parseYahooTimestamp(timestamp);
  const date = new Date(parsed);
  if (Number.isNaN(date.getTime())) return "Yahoo天気API 更新";
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")} 更新`;
};

const formatYahooForecastTime = (timestamp) => {
  const parsed = parseYahooTimestamp(timestamp);
  const date = new Date(parsed);
  if (Number.isNaN(date.getTime())) return "--:--";
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const defaultHourlyRainForecast = {
  updated: "Yahoo天気API 読み込み中",
  items: [
    ["0-6", "--"],
    ["6-12", "--"],
    ["12-18", "--"],
    ["18-24", "--"],
  ].map(([time, rain]) => ({
    time,
    rainfall: rain,
    level: "none",
    label: "目安",
  })),
};

const getWindDirectionLabel = (degree, lang) => {
  if (typeof degree !== "number" || Number.isNaN(degree)) return "--";

  const directions = {
    JP: [
      "北", "北北東", "北東", "東北東",
      "東", "東南東", "南東", "南南東",
      "南", "南南西", "南西", "西南西",
      "西", "西北西", "北西", "北北西",
    ],
    EN: [
      "N", "NNE", "NE", "ENE",
      "E", "ESE", "SE", "SSE",
      "S", "SSW", "SW", "WSW",
      "W", "WNW", "NW", "NNW",
    ],
    中文: [
      "北风", "北北东风", "东北风", "东北东风",
      "东风", "东南东风", "东南风", "南南东风",
      "南风", "南南西风", "西南风", "西南西风",
      "西风", "西北西风", "西北风", "北北西风",
    ],
    한국어: [
      "북풍", "북북동풍", "북동풍", "동북동풍",
      "동풍", "동남동풍", "남동풍", "남남동풍",
      "남풍", "남남서풍", "남서풍", "서남서풍",
      "서풍", "서북서풍", "북서풍", "북북서풍",
    ],
  };
  const list = directions[lang] || directions.JP;
  const index = Math.round(degree / 22.5) % 16;
  return list[index];
};

const getAngleDiff = (a, b) => {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
};

// A wind angled steeply across a beach (not dead-on, but not dead-offshore either)
// still wraps into a lot of real Miyako coves and points — treating anything past a
// bare 90° as "sheltered" was calling glancing-angle wind calm when it isn't.
// Only count a spot as genuinely wind-sheltered once the wind is coming from
// meaningfully behind it.
const WIND_SHELTER_ANGLE = 110;

const isWindSheltered = (spot, windDegree) =>
  typeof spot.facing === "number" &&
  typeof windDegree === "number" &&
  !Number.isNaN(windDegree) &&
  getAngleDiff(windDegree, spot.facing) > WIND_SHELTER_ANGLE;

const conditionRank = { best: 3, good: 2, caution: 1, bad: 0 };
const worseCondition = (a, b) => (conditionRank[a] <= conditionRank[b] ? a : b);

const waveConditionStatus = {
  best: "とても良い",
  good: "良い",
  caution: "注意",
  bad: "危険",
};

const getWaveMapSpotCondition = (spot, oceanCondition, dailyMarineForecast, maxWindSpeed = 0) => {
  const type = getAutomaticMarineConditionType(spot, {
    windDegree: oceanCondition.windDegree,
    windSpeed: getNumericValue(oceanCondition.windSpeed),
    windGust: getNumericValue(oceanCondition.windGust),
    maxWindSpeed,
    windItems: oceanCondition.windItems,
    wave: getNumericValue(dailyMarineForecast?.wave),
    swell: getNumericValue(dailyMarineForecast?.swell),
    rainy: isRainyWeather(oceanCondition.weather),
  });
  return { type, status: waveConditionStatus[type], safetyStop: hasStrongWindSafetyStop({ ...oceanCondition, maxWindSpeed }) };
};

const getWindWaveImpactTexts = (spot, oceanCondition, dailyMarineForecast, labels) => {
  const windDegree = oceanCondition.windDegree;

  if (hasStrongWindSafetyStop(oceanCondition)) {
    const warning = {
      JP: ["強風に注意", "波・うねりにも注意"],
      EN: ["Strong wind caution", "Watch for waves and swell"],
      中文: ["注意強風", "注意海浪與湧浪"],
      한국어: ["강풍 주의", "파도와 너울도 주의"],
    }[labels.lang] || ["Strong wind caution", "Watch for waves and swell"];
    return { windImpact: warning[0], waveImpact: warning[1], onshore: false };
  }

  if (typeof spot.facing !== "number" || typeof windDegree !== "number" || Number.isNaN(windDegree)) {
    return {
      windImpact: labels.windImpactUnknown,
      waveImpact: labels.waveImpactUnknown,
      onshore: false,
    };
  }

  const windAngle = getAngleDiff(windDegree, spot.facing);
  const onshore = windAngle <= 60;
  const currentWindDirectionLabel = getWindDirectionLabel(windDegree, labels.lang);
  const crosswindText = {
    JP: `${currentWindDirectionLabel}が横から入りやすい`,
    EN: `${currentWindDirectionLabel} crosswind`,
    中文: `${currentWindDirectionLabel}横风影响`,
    한국어: `${currentWindDirectionLabel}이 옆바람으로 불기 쉬움`,
  };
  const windImpact = onshore
    ? labels.windImpactStrong.replace("{direction}", currentWindDirectionLabel)
    : windAngle <= WIND_SHELTER_ANGLE
      ? (crosswindText[labels.lang] || crosswindText.EN)
      : labels.windImpactOffshore.replace("{direction}", currentWindDirectionLabel);

  const waveImpact = onshore ? labels.waveImpactHigh : labels.waveImpactCalm;

  return { windImpact, waveImpact, onshore };
};

const getWeatherCondition = (weatherText, labelSet) => {
  if (!weatherText) {
    return { type: "bad", status: labelSet.mapConditionBad };
  }
  // "雨" or a rain-led forecast ("雨のち...") means it's actually raining or about
  // to — that alone rules out stargazing. A "...時々雨"/"...のち雨" forecast is just
  // an occasional-shower risk layered on an otherwise clear/cloudy night, so it
  // shouldn't hard-block the rating the way active rain does — let it fall through
  // and be weighed alongside wind and moonlight instead.
  if (weatherText === "雨" || weatherText.startsWith("雨")) {
    return { type: "bad", status: labelSet.mapConditionBad };
  }
  if (weatherText === "晴れ" || weatherText.startsWith("晴")) {
    return weatherText === "晴れ"
      ? { type: "best", status: labelSet.mapConditionBest }
      : { type: "good", status: labelSet.mapConditionGood };
  }
  return { type: "caution", status: labelSet.mapConditionCaution };
};

const getStarMapSpotCondition = (weatherText, labels) => {
  const starLabels = labels?.starSpotLabels || t.JP.starSpotLabels;
  return getWeatherCondition(weatherText, starLabels);
};

const getSunsetWeatherCondition = (weatherText, labelSet) => {
  if (!weatherText || weatherText.includes("雨")) {
    return { type: "bad", status: labelSet.mapConditionBad };
  }
  if (weatherText === "晴れ" || weatherText.startsWith("晴")) {
    return { type: "best", status: labelSet.mapConditionBest };
  }
  // Sunsets over the ocean are often visible through gaps near the horizon even on
  // a mostly cloudy day, so cloud-only (no rain) still counts as a good chance.
  return { type: "good", status: labelSet.mapConditionGood };
};

const getSunsetMapSpotCondition = (weatherText, labels) => {
  const sunsetLabels = labels?.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  return getSunsetWeatherCondition(weatherText, sunsetLabels);
};

const getSunsetWeatherForDay = (weatherForecast, selectedDay = 0) => {
  const dayWeather = weatherForecast?.dailyDetails?.[selectedDay];
  if (dayWeather?.sunsetWeather) return dayWeather.sunsetWeather;
  if (selectedDay === 0 && weatherForecast?.current?.sunsetWeather) {
    return weatherForecast.current.sunsetWeather;
  }
  return dayWeather?.weather || weatherForecast?.current?.weather;
};

const getSunsetConditionForSpot = (
  spot,
  labels,
  weatherForecast,
  selectedDay = 0,
  guideSettings,
) => {
  const sunsetLabels = labels?.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  const weatherText = getSunsetWeatherForDay(weatherForecast, selectedDay);
  const weatherCondition = getSunsetMapSpotCondition(weatherText, labels);
  const baseScore = clampScore(spot?.sunsetScore || 5);
  const rainProbability = getAverageRainProbability(
    weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current,
  );
  const manualType = selectedDay === 1
    ? guideSettings?.tomorrowSunsetRating
    : guideSettings?.todaySunsetRating;
  if (manualType && manualType !== "auto") {
    const statusByType = {
      best: sunsetLabels.mapConditionBest,
      good: sunsetLabels.mapConditionGood,
      caution: sunsetLabels.mapConditionCaution,
      bad: sunsetLabels.mapConditionBad,
    };
    return {
      type: manualType,
      status: statusByType[manualType],
      weatherText,
      score: { best: 5, good: 4, caution: 3, bad: 1 }[manualType],
    };
  }

  if (rainProbability >= 50 && weatherCondition.type !== "bad") {
    return {
      type: "caution",
      status: sunsetLabels.mapConditionCaution,
      weatherText,
      score: Math.min(baseScore, 3),
    };
  }

  if (weatherCondition.type === "bad") {
    return {
      ...weatherCondition,
      weatherText,
      score: Math.min(baseScore, 2),
    };
  }

  if (weatherCondition.type === "best") {
    return {
      type: "best",
      status: sunsetLabels.mapConditionBest,
      weatherText,
      score: 5,
    };
  }

  if (weatherCondition.type === "good") {
    return {
      type: baseScore >= 4 ? "best" : "good",
      status: baseScore >= 4
        ? sunsetLabels.mapConditionBest
        : sunsetLabels.mapConditionGood,
      weatherText,
      score: Math.min(5, Math.max(baseScore, 4)),
    };
  }

  return {
    ...weatherCondition,
    weatherText,
    score: baseScore,
  };
};

const getBeachLoungeCondition = (spot, oceanCondition, labels) => {
  const automatic = getAutomaticBeachCondition(spot, oceanCondition);
  const statusByType = { best: labels.beachLoungeBest, good: labels.beachLoungeGood, caution: labels.beachLoungeCaution, bad: labels.beachLoungeBad };
  return { ...automatic, status: statusByType[automatic.type] };
};

const getMapSpotCondition = (spot, activeMapCategory, oceanCondition, labels, weatherForecast, dailyMarineForecast, maxWindSpeed, selectedDay = 0, guideSettings, spotOverrides = {}) => {
  if (spot.kind === "food") {
    return {};
  }
  if (activeMapCategory === "stars") {
    const manualStarType = selectedDay === 1
      ? guideSettings?.tomorrowStarRating
      : guideSettings?.todayStarRating;
    if (manualStarType && manualStarType !== "auto") {
      const starLabels = labels?.starSpotLabels || t.JP.starSpotLabels;
      return {
        type: manualStarType,
        status: {
          best: starLabels.mapConditionBest,
          good: starLabels.mapConditionGood,
          caution: starLabels.mapConditionCaution,
          bad: starLabels.mapConditionBad,
        }[manualStarType],
      };
    }
    const weatherText = weatherForecast?.current?.nightWeather || oceanCondition.weather;
    const starCondition = getStarMapSpotCondition(weatherText, labels);
    const nightRainProbability = getNightRainProbability(
      weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current,
    );
    return nightRainProbability >= 50 && ["best", "good"].includes(starCondition.type)
      ? {
          type: "caution",
          status: (labels?.starSpotLabels || t.JP.starSpotLabels).mapConditionCaution,
        }
      : starCondition;
  }
  if (activeMapCategory === "sunset") {
    return getSunsetConditionForSpot(spot, labels, weatherForecast, selectedDay, guideSettings);
  }
  const spotOverride = selectedDay === 0
    ? getTodaySpotOverride(spotOverrides[getSpotOverrideId(spot.name)])
    : null;
  const overrideType = activeMapCategory === "beach"
    ? spotOverride?.beachRating
    : spotOverride?.marineRating;
  const automatic = activeMapCategory === "beach"
    ? getBeachLoungeCondition(spot, oceanCondition, labels)
    : getWaveMapSpotCondition(spot, oceanCondition, dailyMarineForecast, maxWindSpeed);
  const condition = applySpotConditionOverride(automatic, overrideType, spotOverride?.showCaution);
  if (condition === automatic) return automatic;
  return {
    ...condition,
    status: { best: labels.mapConditionBest, good: labels.mapConditionGood, caution: labels.mapConditionCaution, bad: labels.mapConditionBad }[condition.type],
  };
};

const getSnorkelCondition = (dailyMarineForecast, oceanCondition, labels, maxWindSpeed = 0) => {
  const wave = getNumericValue(dailyMarineForecast.wave);
  const swell = getNumericValue(dailyMarineForecast.swell);
  const windSpeed = Math.max(getNumericValue(oceanCondition.windSpeed), maxWindSpeed || 0);
  const rainy = isRainyWeather(oceanCondition.weather);

  let type = "best";
  if (rainy || wave >= 1.5 || swell >= 1.3 || windSpeed >= 10) {
    type = "bad";
  } else if (wave >= 1.0 || swell >= 0.8 || windSpeed >= 7) {
    type = "caution";
  } else if (wave >= 0.6 || swell >= 0.5 || windSpeed >= 5) {
    type = "good";
  }

  const statusByType = {
    best: labels.snorkelConditionBest,
    good: labels.snorkelConditionGood,
    caution: labels.snorkelConditionCaution,
    bad: labels.snorkelConditionBad,
  };

  const windExtreme = windSpeed >= 10;

  return {
    type,
    status: statusByType[type],
    windExtreme,
    rainy,
  };
};

const conditionScores = { best: 3, good: 2, caution: 1, bad: 0 };
const conditionTypeByScore = ["bad", "caution", "good", "best"];

const getWindFactor = (windSpeed, starLabels) => {
  if (windSpeed >= 12) return { type: "bad", label: starLabels.windVeryStrong };
  if (windSpeed >= 8) return { type: "caution", label: starLabels.windStrong };
  if (windSpeed >= 5) return { type: "good", label: starLabels.windModerate };
  return { type: "best", label: starLabels.windCalm };
};

const getMoonFactor = (fraction, starLabels) => {
  if (fraction >= 0.7) return { type: "bad", label: starLabels.moonBright };
  if (fraction >= MOON_BRIGHT_THRESHOLD) return { type: "caution", label: starLabels.moonSlightlyBright };
  if (fraction >= 0.2) return { type: "good", label: starLabels.moonSlightlyDark };
  return { type: "best", label: starLabels.moonDark };
};

const getCloudFactor = (cloudCover, starLabels) => {
  if (!Number.isFinite(cloudCover)) return null;
  if (cloudCover >= 90) return { type: "bad", label: starLabels.mapConditionBad };
  if (cloudCover >= 70) return { type: "caution", label: starLabels.mapConditionCaution };
  // 雲量70%未満は、雲の切れ間が期待できるため総合評価を下げない。
  if (cloudCover > 40) return { type: "best", label: starLabels.mapConditionGood };
  return { type: "best", label: starLabels.mapConditionBest };
};

const buildTonightStarRating = (
  oceanCondition,
  moonFraction,
  labels,
  nightWeatherText,
  nightCloudCover,
  rainProbability = 0,
) => {
  const starLabels = labels?.starSpotLabels || t.JP.starSpotLabels;
  const resolvedWeatherText = nightWeatherText || oceanCondition.weather;
  const weather = {
    ...getStarMapSpotCondition(resolvedWeatherText, labels),
    text: resolvedWeatherText,
  };
  const wind = getWindFactor(getNumericValue(oceanCondition.windSpeed), starLabels);
  const moon = getMoonFactor(moonFraction, starLabels);
  const cloud = getCloudFactor(nightCloudCover, starLabels);
  const skyType = weather.type === "bad" ? "bad" : cloud?.type || weather.type;

  const score =
    (conditionScores[skyType] * 2 +
      conditionScores[wind.type] +
      conditionScores[moon.type] * 1.5) /
    4.5;
  const weightedType =
    skyType === "bad"
      ? "bad"
      : conditionTypeByScore[Math.max(0, Math.min(3, Math.round(score)))];
  const weakestVisibleFactor = [skyType, wind.type, moon.type]
    .reduce((weakest, factorType) => worseCondition(weakest, factorType), "best");
  const calculatedType = worseCondition(weightedType, weakestVisibleFactor);
  const type =
    rainProbability >= 50 && ["best", "good"].includes(calculatedType)
      ? "caution"
      : calculatedType;
  const statusByType = {
    best: starLabels.mapConditionBest,
    good: starLabels.mapConditionGood,
    caution: starLabels.mapConditionCaution,
    bad: starLabels.mapConditionBad,
  };

  return { type, status: statusByType[type], weather, wind, moon, cloud, nightCloudCover };
};

const getWindSafety = (speed, lang, gust = 0) => {
  const safeText = {
    JP: ["○", "安全寄り", "流されにくい"],
    EN: ["○", "Safer", "Low drift risk"],
    中文: ["○", "较安全", "不太容易漂走"],
    한국어: ["○", "안전한 편", "떠밀릴 위험 낮음"],
  };
  const cautionText = {
    JP: ["△", "注意", "流されやすい"],
    EN: ["△", "Caution", "Easy to drift"],
    中文: ["△", "注意", "容易被风带走"],
    한국어: ["△", "주의", "떠밀리기 쉬움"],
  };
  const dangerText = {
    JP: ["×", "海遊び注意", ""],
    EN: ["×", "High caution", "Consider cancelling"],
    中文: ["×", "海上活动需谨慎", "建议不要勉强下海"],
    한국어: ["×", "해양 활동 주의", "무리하지 말고 취소 판단"],
  };

  if (speed >= 7 || gust >= 11) {
    return ["windDanger", ...(dangerText[lang] || dangerText.JP)];
  }

  if (speed >= 5 || gust >= 8) {
    return ["windCaution", ...(cautionText[lang] || cautionText.JP)];
  }

  return ["windSafe", ...(safeText[lang] || safeText.JP)];
};

const getWindAvoidanceAdvice = (level, lang) => {
  if (level === "windSafe") return "";

  const cautionAdvice = {
    JP: "外海側・橋周辺・沖へ出るSUPやシュノーケルは避けるのがおすすめです。",
    EN: "Avoid exposed outer coasts, bridge areas, SUP, and snorkeling far from shore.",
    中文: "建议避开外海侧、桥周边以及离岸较远的SUP或浮潜。",
    한국어: "외해 쪽, 다리 주변, 먼 바다로 나가는 SUP와 스노클링은 피하는 것이 좋아요.",
  };
  const dangerAdvice = {
    JP: "海遊びは無理せず中止判断。ビーチ散歩やカフェなど陸の予定に切り替えるのがおすすめです。",
    EN: "Consider cancelling ocean activities and switch to beach walks, cafes, or land plans.",
    中文: "建议不要勉强下海，可改为海边散步、咖啡店等陆上行程。",
    한국어: "해양 액티비티는 무리하지 말고 취소를 고려하고, 산책이나 카페 일정으로 바꾸는 것이 좋아요.",
  };

  return level === "windDanger"
    ? (dangerAdvice[lang] || dangerAdvice.JP)
    : (cautionAdvice[lang] || cautionAdvice.JP);
};

const formatAppUpdatedTime = (lang, updatedAt) => {
  const date = updatedAt || new Date();
  const time = `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (lang === "EN") return `Updated ${time}`;
  if (lang === "한국어") return `${time} 업데이트`;
  return `${time} 更新`;
};

const formatAppUpdatedDateTime = (lang, updatedAt) => {
  const date = updatedAt || new Date();
  const dateLabel = formatForecastDate(date, lang);
  const time = `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (lang === "EN") return `${dateLabel} Updated ${time}`;
  if (lang === "한국어") return `${dateLabel} ${time} 업데이트`;
  return `${dateLabel}${time} 更新`;
};

const formatOpenMeteoUpdated = (lang, updatedAt = new Date()) => {
  const date = new Date(updatedAt);
  const time = `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (lang === "EN") return `Open-Meteo updated ${time}`;
  if (lang === "中文") return `Open-Meteo ${time} 更新`;
  if (lang === "한국어") return `Open-Meteo ${time} 업데이트`;
  return `Open-Meteo ${time} 更新`;
};

const defaultHourlyWindForecast = {
  updated: "Open-Meteo 読み込み中",
  source: "Open-Meteo",
  items: [],
  days: [],
};

const defaultTyphoonInfo = {
  updated: "",
  items: [],
  status: "loading",
};

const defaultDailyMarineForecast = {
  updated: "Open-Meteo Marine 読み込み中",
  wave: "--",
  waveLabel: "今日の波",
  swell: "--",
  swellLabel: "取得中",
  period: "--",
  nightWaves: [],
  daytimeWaves: [],
  daytimeSwells: [],
  tides: {
    high: [],
    low: [],
  },
  days: [],
};

const windDisplayHours = [6, 9, 12, 15, 18];

const pickCurrentHourItem = (items) => {
  if (!items || items.length === 0) return null;
  const currentHour = new Date().getHours();
  const past = items.filter((item) => Number.parseInt(item.time, 10) <= currentHour);
  return past.length > 0 ? past[past.length - 1] : items[0];
};

const formatOpenMeteoWeatherUpdated = (lang, updatedAt = new Date()) => {
  const date = new Date(updatedAt);
  const time = `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (lang === "EN") return `Open-Meteo updated ${time}`;
  if (lang === "中文") return `Open-Meteo ${time} 更新`;
  if (lang === "한국어") return `Open-Meteo ${time} 업데이트`;
  return `Open-Meteo ${time} 更新`;
};

const formatApiTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const formatApiDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const getDistanceKm = (from, to) => {
  if (!from || !to) return null;
  const toRad = (degree) => (degree * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return Math.round(earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const getMovementDirectionLabel = (from, to, lang) => {
  if (!from || !to) return "--";
  const dLat = to.lat - from.lat;
  const dLng = to.lng - from.lng;
  const angle = (Math.atan2(dLng, dLat) * 180) / Math.PI;
  const normalized = (angle + 360) % 360;
  const directionSets = {
    JP: ["北", "北東", "東", "南東", "南", "南西", "西", "北西"],
    EN: ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"],
    中文: ["北", "东北", "东", "东南", "南", "西南", "西", "西北"],
    한국어: ["북쪽", "북동쪽", "동쪽", "남동쪽", "남쪽", "남서쪽", "서쪽", "북서쪽"],
  };
  const directions = directionSets[lang] || directionSets.JP;
  return directions[Math.round(normalized / 45) % 8];
};

const getTyphoonMovementSummary = (track, lang) => {
  const coordinates = track.filter((point) => point.coordinate);
  const current = coordinates[0];
  const future = coordinates[coordinates.length - 1];
  if (!current || !future) return "--";

  const direction = getMovementDirectionLabel(current.coordinate, future.coordinate, lang);
  const currentDistance = current.distance;
  const futureDistance = future.distance;
  const distanceChange = futureDistance - currentDistance;
  const movementTexts = {
    JP: {
      approach: `${direction}方向へ進み、宮古島に近づく予想です`,
      away: `${direction}方向へ進み、宮古島から離れる予想です`,
      side: `${direction}方向へ進み、宮古島の周辺を通る可能性があります`,
    },
    EN: {
      approach: `Moving ${direction}, expected to move closer to Miyakojima`,
      away: `Moving ${direction}, expected to move away from Miyakojima`,
      side: `Moving ${direction}, may pass near Miyakojima`,
    },
    中文: {
      approach: `向${direction}移动，预计接近宫古岛`,
      away: `向${direction}移动，预计远离宫古岛`,
      side: `向${direction}移动，可能经过宫古岛附近`,
    },
    한국어: {
      approach: `${direction}으로 이동하며 미야코지마에 가까워질 예상입니다`,
      away: `${direction}으로 이동하며 미야코지마에서 멀어질 예상입니다`,
      side: `${direction}으로 이동하며 미야코지마 주변을 지날 가능성이 있습니다`,
    },
  };
  const texts = movementTexts[lang] || movementTexts.JP;

  if (distanceChange <= -80) return texts.approach;
  if (distanceChange >= 80) return texts.away;
  return texts.side;
};

const warningAreaTexts = {
  JP: {
    storm: "現在、宮古島は暴風域（風速25m/s以上の暴風が吹いている、または吹く可能性がある範囲）に入っています。",
    gale: "現在、宮古島は強風域（風速15m/s以上の強い風が吹いている、または吹く可能性がある範囲）に入っています。",
  },
  EN: {
    storm: "Miyakojima is currently inside the storm warning area (winds of 25 m/s or more expected).",
    gale: "Miyakojima is currently inside the gale warning area (winds of 15 m/s or more expected).",
  },
  中文: {
    storm: "宫古岛目前位于暴风圈内（风速达到或可能达到25米/秒以上）。",
    gale: "宫古岛目前位于强风圈内（风速达到或可能达到15米/秒以上）。",
  },
  한국어: {
    storm: "미야코지마는 현재 폭풍역(풍속 25m/s 이상의 폭풍이 불고 있거나 불 가능성이 있는 범위) 안에 있어요.",
    gale: "미야코지마는 현재 강풍역(풍속 15m/s 이상의 강한 바람이 불고 있거나 불 가능성이 있는 범위) 안에 있어요.",
  },
};

const getWarningAreaStatus = (currentPoint, lang) => {
  if (!currentPoint) return null;
  const texts = warningAreaTexts[lang] || warningAreaTexts.JP;

  const checkArea = (area) => {
    if (!area?.center || !Number.isFinite(area?.radius)) return null;
    const distance = getDistanceKm(MIYAKO_MAP_CENTER, {
      lat: area.center[0],
      lng: area.center[1],
    });
    return Number.isFinite(distance) ? distance <= area.radius / 1000 : null;
  };

  const inStormArea = checkArea(
    currentPoint.stormWarningArea || currentPoint.stormArea,
  );
  const inGaleArea = checkArea(currentPoint.galeWarningArea);

  if (inStormArea) return { level: "storm", text: texts.storm };
  if (inGaleArea) return { level: "gale", text: texts.gale };
  return null;
};

const stormAreaProbabilityTexts = {
  JP: {
    label: "宮古島が暴風域に入る確率（概算）",
    value: "約{percent}%",
    none: "現在、暴風域は設定されていません（入る可能性は低い見込み）。",
    noneWhileGale: "現在は強風域に入っていますが、暴風域（風速25m/s以上）は設定されていません（発達する可能性は低い見込み）。",
  },
  EN: {
    label: "Estimated chance of entering the storm-force wind area",
    value: "About {percent}%",
    none: "No storm-force wind area is currently defined (the chance is considered low).",
    noneWhileGale: "Miyakojima is currently in the gale warning area, but no storm-force area (25 m/s+) is defined (unlikely to develop into one).",
  },
  中文: {
    label: "进入暴风圈的概率（估算）",
    value: "约{percent}%",
    none: "目前未设定暴风圈（预计进入的可能性较低）。",
    noneWhileGale: "目前位于强风圈内，但尚未设定暴风圈（风速25米/秒以上，预计发展为暴风圈的可能性较低）。",
  },
  한국어: {
    label: "미야코지마가 폭풍역에 들어갈 확률(추정)",
    value: "약 {percent}%",
    none: "현재 폭풍역이 설정되어 있지 않습니다(들어갈 가능성은 낮을 것으로 예상).",
    noneWhileGale: "현재 강풍역 안에 있지만, 폭풍역(풍속 25m/s 이상)은 설정되어 있지 않습니다(발달할 가능성은 낮을 것으로 예상).",
  },
};

const getStormAreaProbability = (track, currentPoint, lang, warningAreaStatus = null) => {
  const texts = stormAreaProbabilityTexts[lang] || stormAreaProbabilityTexts.JP;
  const stormArea = currentPoint?.stormWarningArea || currentPoint?.stormArea;
  const windRadiusKm = Number.isFinite(stormArea?.radius) ? stormArea.radius / 1000 : 0;

  if (!windRadiusKm) {
    const noneText = warningAreaStatus?.level === "gale" ? texts.noneWhileGale : texts.none;
    return { percent: 0, hasArea: false, label: texts.label, text: noneText, caveat: texts.caveat };
  }

  const relevant = track.filter(
    (point) =>
      Number.isFinite(point.distance) && Number.isFinite(point.probabilityCircleRadiusKm),
  );

  const percents = relevant.map((point) => {
    if (point.distance <= windRadiusKm) return 100;
    const spread = windRadiusKm + point.probabilityCircleRadiusKm - point.distance;
    if (spread <= 0) return 0;
    return Math.min(100, (spread / point.probabilityCircleRadiusKm) * 100);
  });

  const maxPercent =
    percents.length > 0 ? Math.round(Math.max(...percents) / 5) * 5 : 0;

  return {
    percent: maxPercent,
    hasArea: true,
    label: texts.label,
    text: texts.value.replace("{percent}", maxPercent),
    caveat: texts.caveat,
  };
};

const getTyphoonImpactPeriod = (track) => {
  const relevant = track.filter(
    (point) =>
      isTyphoonPointNearMiyako(point.distance, point.probabilityCircleRadiusKm) &&
      point.time &&
      point.time !== "--",
  );

  if (relevant.length === 0) return null;

  return { start: relevant[0].time, end: relevant[relevant.length - 1].time };
};

const getTyphoonImpact = (track, category, lang) => {
  const distances = track
    .map((point) => point.distance)
    .filter((distance) => Number.isFinite(distance));
  const closestDistance = distances.length > 0 ? Math.min(...distances) : null;
  const isDeveloping = ["熱帯低気圧", "Tropical Depression", "热带低压", "열대저압부"].includes(category);
  const impactTexts = {
    JP: {
      high: "宮古島にかなり近い予想です。海は荒れやすく、ツアーや船は中止・欠航になる可能性があります。",
      caution: "宮古島周辺の海に影響が出る可能性があります。SUPやシュノーケルは無理せず、前日と当日に確認してください。",
      watch: "まだ離れていますが、うねりや風だけ先に強まることがあります。海遊び前に最新情報を確認してください。",
      low: isDeveloping
        ? "今のところ影響は小さめです。ただし発達すると進路が変わることがあります。"
        : "今のところ宮古島への影響は小さめです。",
    },
    EN: {
      high: "It is forecast to be very close to Miyakojima. Seas may become rough and tours or ferries may be cancelled.",
      caution: "Marine conditions around Miyakojima may be affected. Check updates before SUP or snorkeling.",
      watch: "It is still far away, but swell and wind may arrive first. Check updates before ocean activities.",
      low: isDeveloping
        ? "Impact is currently low, but the track can change if it develops."
        : "Impact on Miyakojima is currently low.",
    },
    中文: {
      high: "预计会非常接近宫古岛。海况容易变差，行程或船只可能取消。",
      caution: "宫古岛周边海况可能受影响。SUP和浮潜前请确认最新信息。",
      watch: "距离还较远，但涌浪和风可能先增强。海上活动前请确认最新信息。",
      low: isDeveloping
        ? "目前影响较小，但如果增强，路径可能改变。"
        : "目前对宫古岛影响较小。",
    },
    한국어: {
      high: "미야코지마에 매우 가까워질 예상입니다. 바다가 거칠어지고 투어나 배가 취소될 수 있습니다.",
      caution: "미야코지마 주변 바다에 영향이 있을 수 있습니다. SUP와 스노클 전 최신 정보를 확인하세요.",
      watch: "아직 멀리 있지만 너울과 바람이 먼저 강해질 수 있습니다. 바다 일정 전 최신 정보를 확인하세요.",
      low: isDeveloping
        ? "현재 영향은 작지만 발달하면 진로가 바뀔 수 있습니다."
        : "현재 미야코지마 영향은 작습니다.",
    },
  };
  const texts = impactTexts[lang] || impactTexts.JP;

  const flightImpactTexts = {
    JP: {
      high: "宮古空港発着便に欠航や大幅な遅延が出る可能性が高い状況です。搭乗前に運航状況を必ず確認してください。",
      caution: "風や雨の影響で、宮古空港発着便に遅延や一部欠航が出る可能性があります。搭乗前に運航状況を確認してください。",
      watch: "現時点で欠航の可能性は低めですが、進路次第では影響が出ることがあります。念のため運航状況を確認してください。",
      low: "現時点で宮古空港発着便への影響は小さいと考えられます。",
    },
    EN: {
      high: "Flights to/from Miyako Airport are likely to face cancellations or major delays. Please check flight status before departure.",
      caution: "Wind and rain may cause delays or some cancellations for Miyako Airport flights. Please check flight status before departure.",
      watch: "Cancellations are unlikely for now, but this could change depending on the track. Check flight status just in case.",
      low: "Impact on Miyako Airport flights is currently expected to be small.",
    },
    中文: {
      high: "宫古机场航班很可能出现取消或大幅延误，请在出发前务必确认航班状态。",
      caution: "受风雨影响，宫古机场航班可能出现延误或部分取消，请在出发前确认航班状态。",
      watch: "目前取消航班的可能性较低，但依路径变化可能会受到影响，请以防万一确认航班状态。",
      low: "目前预计对宫古机场航班的影响较小。",
    },
    한국어: {
      high: "미야코 공항 항공편은 결항이나 큰 지연이 발생할 가능성이 높습니다. 탑승 전 반드시 운항 상황을 확인하세요.",
      caution: "바람과 비의 영향으로 미야코 공항 항공편에 지연이나 일부 결항이 발생할 수 있습니다. 탑승 전 운항 상황을 확인하세요.",
      watch: "현재로서는 결항 가능성이 낮지만 진로에 따라 영향이 있을 수 있습니다. 만약을 위해 운항 상황을 확인하세요.",
      low: "현재 미야코 공항 항공편에 대한 영향은 작을 것으로 예상됩니다.",
    },
  };
  const flightTexts = flightImpactTexts[lang] || flightImpactTexts.JP;

  const buildResult = (level) => ({
    level,
    text: texts[level],
    flightImpact: { level, text: flightTexts[level] },
  });

  if (closestDistance === null) return buildResult("watch");
  if (closestDistance <= 300) return buildResult("high");
  if (closestDistance <= 600) return buildResult("caution");
  if (closestDistance <= TYPHOON_MAX_CENTER_KM) return buildResult("watch");
  return buildResult("low");
};

const typhoonDowngradedNoticeTemplates = {
  JP: "台風{number}号は熱帯低気圧に変わりました。",
  EN: "Typhoon #{number} has weakened into a tropical depression.",
  中文: "台风{number}号已减弱为热带低压。",
  한국어: "태풍 {number}호가 열대저압부로 약화되었습니다.",
};

const formatTyphoonDisplayName = (item) => {
  if (!item.number || item.number === "a" || item.number === "b") {
    return item.category;
  }

  const match = String(item.number).match(/(\d{1,2})$/);
  return `台風${match ? match[1] : item.number}号`;
};

const getDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const getTodayDateKey = () => getDateKey(new Date());

const getTyphoonPeriodDateKey = (value, selectedDateKey) => {
  const match = String(value || "").match(/(\d{1,2})\/(\d{1,2})/);
  if (!match || !selectedDateKey) return "";
  const selectedYear = Number(selectedDateKey.slice(0, 4));
  const selectedMonth = Number(selectedDateKey.slice(5, 7));
  const month = Number(match[1]);
  const year = selectedMonth === 12 && month === 1 ? selectedYear + 1
    : selectedMonth === 1 && month === 12 ? selectedYear - 1
      : selectedYear;
  return `${year}-${String(month).padStart(2, "0")}-${String(match[2]).padStart(2, "0")}`;
};

const isTyphoonInfluenceForecastDay = (typhoonInfo, selectedDateKey) =>
  (typhoonInfo?.items || []).some((item) => {
    if (!["high", "caution", "watch"].includes(item.impact?.level)) return false;
    const start = getTyphoonPeriodDateKey(item.impactPeriod?.start, selectedDateKey);
    const end = getTyphoonPeriodDateKey(item.impactPeriod?.end, selectedDateKey);
    if (start && end) return selectedDateKey >= start && selectedDateKey <= end;
    return selectedDateKey === getTodayDateKey();
  });

const getJstDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const isCurrentJstDaytimeSnapshot = (snapshot) => Boolean(
  snapshot?.fetchedAt
  && getJstDateKey(snapshot.fetchedAt) === getJstDateKey(new Date()),
);

const getForecastStartIndex = (dailyTimes = []) => {
  const todayKey = getTodayDateKey();
  const exactIndex = dailyTimes.findIndex((time) => getDateKey(time) === todayKey);
  if (exactIndex >= 0) return exactIndex;

  const nextIndex = dailyTimes.findIndex((time) => getDateKey(time) > todayKey);
  return nextIndex >= 0 ? nextIndex : 0;
};

const getWeatherLabel = (code) =>
  weatherCodeLabels[Number(code)] || "取得中";

const getWeatherGroup = (label) => {
  if (!label) return "unknown";
  // Any mention of rain wins outright (safety-first for a "should I go out" signal).
  if (label.includes("雨")) return "rain";
  // For a mixed sun/cloud label ("晴時々曇" vs "曇時々晴"), whichever character comes
  // first is the dominant condition — a plain "contains 曇" check was classifying
  // "mostly sunny, sometimes cloudy" labels as cloudy just because they mention
  // cloud at all, which is exactly backwards.
  const sunIndex = label.indexOf("晴");
  const cloudIndex = label.indexOf("曇");
  if (sunIndex === -1 && cloudIndex === -1) return "unknown";
  if (sunIndex === -1) return "cloud";
  if (cloudIndex === -1) return "sun";
  return sunIndex < cloudIndex ? "sun" : "cloud";
};

const getHourlyWeatherAtHour = (hourlyTimes, hourlyCodes, dateKey, targetHour) => {
  let bestIndex = -1;
  let bestDiff = Infinity;

  hourlyTimes.forEach((time, index) => {
    if (getDateKey(time) !== dateKey) return;
    const diff = Math.abs(new Date(time).getHours() - targetHour);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = index;
    }
  });

  return bestIndex >= 0 ? getWeatherLabel(hourlyCodes?.[bestIndex]) : null;
};

const getWeatherGroupLabel = (group) => {
  if (group === "rain") return "雨";
  if (group === "cloud") return "曇";
  if (group === "sun") return "晴れ";
  return "取得中";
};

const getDominantWeatherGroup = (times, weatherCodes, rainProbabilities, start, end) => {
  const counts = { sun: 0, cloud: 0, rain: 0, unknown: 0 };
  const rainValues = [];

  times.forEach((time, index) => {
    const date = new Date(time);
    const hour = date.getHours();
    if (Number.isNaN(date.getTime()) || hour < start || hour >= end) return;

    const group = getWeatherGroup(getWeatherLabel(weatherCodes[index]));
    const rainProbability = Number(rainProbabilities[index]);
    if (Number.isFinite(rainProbability)) {
      rainValues.push(rainProbability);
    }

    if (group === "rain" && Number.isFinite(rainProbability) && rainProbability < 45) {
      counts.cloud += 1;
      return;
    }

    counts[group] += 1;
  });

  const maxRain = rainValues.length > 0 ? Math.max(...rainValues) : 0;
  const hasSun = counts.sun > 0;
  const hasCloud = counts.cloud > 0;

  if (counts.rain > 0 && maxRain < 45) {
    if (hasSun) return "sun";
    if (hasCloud) return "cloud";
  }

  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "unknown";
};

const hasWeatherGroupInTimeRange = (times, weatherCodes, start, end, groupName) =>
  times.some((time, index) => {
    const date = new Date(time);
    const hour = date.getHours();
    if (Number.isNaN(date.getTime()) || hour < start || hour >= end) return false;
    return getWeatherGroup(getWeatherLabel(weatherCodes[index])) === groupName;
  });

const getWeatherLabelInHourRange = (
  times,
  weatherCodes,
  rainProbabilities,
  start,
  end,
  fallbackLabel,
) => {
  const items = times
    .map((time, index) => {
      const date = new Date(time);
      const hour = date.getHours();
      if (Number.isNaN(date.getTime()) || hour < start || hour >= end) return null;
      const weather = getWeatherLabel(weatherCodes[index]);
      return {
        group: getWeatherGroup(weather),
        rain: Number(rainProbabilities[index]),
      };
    })
    .filter(Boolean);

  if (items.length === 0) return fallbackLabel;

  const maxRain = Math.max(0, ...items.map((item) => (
    Number.isFinite(item.rain) ? item.rain : 0
  )));
  const hasSun = items.some((item) => item.group === "sun");
  const hasCloud = items.some((item) => item.group === "cloud");
  const hasRain = items.some((item) => item.group === "rain") || maxRain >= 60;

  if (hasSun && (hasCloud || hasRain || maxRain >= 35)) return "晴れのち曇";
  if (hasSun) return "晴れ";
  if (hasRain) return "雨";
  if (hasCloud) return "曇";
  return fallbackLabel;
};

const getDaytimeWeatherLabel = (times, weatherCodes, rainProbabilities, fallbackLabel) => {
  const morning = getDominantWeatherGroup(times, weatherCodes, rainProbabilities, 6, 12);
  const afternoon = getDominantWeatherGroup(times, weatherCodes, rainProbabilities, 12, 18);
  const hasDaytimeSun = hasWeatherGroupInTimeRange(times, weatherCodes, 6, 18, "sun");
  const hasDaytimeCloud = hasWeatherGroupInTimeRange(times, weatherCodes, 6, 18, "cloud");
  const hasDaytimeRain = hasWeatherGroupInTimeRange(times, weatherCodes, 6, 18, "rain");

  if (morning === "unknown" || afternoon === "unknown") return fallbackLabel;
  if (hasDaytimeSun && (hasDaytimeCloud || hasDaytimeRain)) return "晴れのち曇";
  if (morning === afternoon) {
    if (morning === "sun" && hasDaytimeCloud) return "晴時々曇";
    return getWeatherGroupLabel(morning);
  }
  if (morning === "sun" && afternoon === "cloud") return "晴れのち曇";
  if (morning === "cloud" && afternoon === "sun") return "曇のち晴れ";

  return `${getWeatherGroupLabel(morning)}のち${getWeatherGroupLabel(afternoon)}`;
};

const getForecastStatus = (weatherLabel, rainProbability) => {
  if (/雨/.test(weatherLabel) || rainProbability >= 60) return "caution";
  if (weatherLabel === "曇時々雨" || rainProbability >= 40) return "caution";
  return "good";
};

const getRainByPeriod = (times, rainProbabilities) => {
  const periods = [
    ["0-6", 0, 6],
    ["6-12", 6, 12],
    ["12-18", 12, 18],
    ["18-24", 18, 24],
  ];

  return periods.map(([label, start, end]) => {
    const values = times
      .map((time, index) => ({
        date: new Date(time),
        value: Number(rainProbabilities[index]),
      }))
      .filter(({ date, value }) =>
        !Number.isNaN(date.getTime()) &&
        Number.isFinite(value) &&
        date.getHours() >= start &&
        date.getHours() < end
      )
      .map(({ value }) => value);

    const average = values.length > 0
      ? values.reduce((total, value) => total + value, 0) / values.length
      : 0;
    return [label, `${Math.round(average)}%`];
  });
};

const buildWeatherSummaryText = (current, rainByTime, lang, dayLabel = "今日") => {
  const rainValues = rainByTime
    .map(([, value]) => Number.parseInt(value, 10))
    .filter((value) => Number.isFinite(value));
  const averageRain = rainValues.length
    ? Math.round(rainValues.reduce((sum, value) => sum + value, 0) / rainValues.length)
    : null;
  const weather = translateWeatherLabel(current.weather, lang);
  const rainLevel = averageRain === null ? "unknown" : averageRain < 50 ? "low" : averageRain < 70 ? "medium" : "high";
  if (lang === "EN") {
    const advice = rainLevel === "low"
      ? "The weather looks good throughout the day."
      : rainLevel === "medium"
        ? "The weather may change, so keep an eye on the sky while enjoying your plans."
        : rainLevel === "high"
          ? "Rain is more likely, so plan around breaks in the rain and local conditions."
          : "Check the latest weather before heading out.";
    return `${dayLabel} in Miyakojima is ${weather}. High ${current.high}°C / low ${current.low}°C. ${advice}`;
  }
  if (lang === "中文") {
    const advice = rainLevel === "low"
      ? "全天看起来天气不错。"
      : rainLevel === "medium"
        ? "天气可能有变化，请留意天空状况安排行程。"
        : rainLevel === "high"
          ? "降雨概率较高，请避开降雨时段并根据地点灵活安排行程。"
          : "出发前请查看最新天气。";
    return `${dayLabel}宫古岛天气为${weather}。最高${current.high}℃ / 最低${current.low}℃。${advice}`;
  }
  if (lang === "한국어") {
    const advice = rainLevel === "low"
      ? "하루 종일 날씨가 좋을 것으로 보입니다."
      : rainLevel === "medium"
        ? "날씨가 변할 수 있으니 하늘을 살피며 일정을 즐기세요."
        : rainLevel === "high"
          ? "강수 확률이 높으니 비가 그친 시간과 장소를 보며 일정을 조정하세요."
          : "외출 전에 최신 날씨를 확인하세요.";
    return `${dayLabel} 미야코지마는 ${weather}입니다. 최고 ${current.high}℃ / 최저 ${current.low}℃. ${advice}`;
  }
  const advice = rainLevel === "low"
    ? `${dayLabel}は一日天気が良さそうです。`
    : rainLevel === "medium"
      ? `${dayLabel}は天気が変わる可能性があります。空模様を見ながら楽しみましょう。`
      : rainLevel === "high"
        ? `${dayLabel}は降水確率が高めです。雨の合間や場所を見ながら楽しみましょう。`
        : "出発前に最新の天気を確認しましょう。";
  return `${dayLabel}の宮古島は${weather}です。最高${current.high}℃ / 最低${current.low}℃。${advice}`;
};

// Averages an hourly cloud-cover array over the hours within `windowHours` of
// `targetHour`, restricted to the given day's `hourlyIndexes`. Returns null when
// no matching hours have a numeric value (e.g. cloud_cover_low/mid/high aren't
// present yet in an older cached data file).
const getAverageCloudCoverNear = (hourlyIndexes, hourlyTimes, cloudArray, targetHour, windowHours = 1) => {
  const values = hourlyIndexes
    .filter((hourIndex) => {
      const hour = new Date(hourlyTimes[hourIndex]).getHours();
      return Math.abs(hour - targetHour) <= windowHours;
    })
    .map((hourIndex) => Number(cloudArray?.[hourIndex]))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) return null;

  return Math.round(values.reduce((total, value) => total + value, 0) / values.length);
};

// A vivid, "burning" sunset needs the western horizon itself to be clear (low
// cloud blocks the sun before it ever reaches the horizon) while mid/high cloud
// catches and scatters that low-angle light into color. Fully clear sky at every
// level is fine but tends to fade out plain pink/orange rather than a deep red.
const getSunsetBurnJudgment = (dayWeather, labels) => {
  const { sunsetLowCloud, sunsetMidCloud, sunsetHighCloud, sunsetRainProbability } = dayWeather || {};
  const hasBandData = [sunsetLowCloud, sunsetMidCloud, sunsetHighCloud].some(
    (value) => value !== null && value !== undefined,
  );

  if (!hasBandData) return null;

  const low = sunsetLowCloud ?? 0;
  const midHigh = Math.max(sunsetMidCloud ?? 0, sunsetHighCloud ?? 0);
  const rainy = Number(sunsetRainProbability) >= 50;

  let level = "low";
  if (!rainy && low <= 40 && midHigh >= 20 && midHigh <= 80) {
    level = "high";
  } else if (!rainy && low <= 60 && midHigh >= 10) {
    level = "medium";
  }

  return {
    level,
    label: labels?.burnLevels?.[level] || "",
  };
};

const getOpenMeteoWeatherForecast = (lang, data, updatedAt) => {
  const dailyTimes = data.daily?.time || [];
  const hourlyTimes = data.hourly?.time || [];
  const startIndex = getForecastStartIndex(dailyTimes);
  const forecastDailyTimes = dailyTimes.slice(startIndex);
  const todayDateKey = getDateKey(forecastDailyTimes[0] || hourlyTimes[0]);
  const apiCurrentDateKey = getDateKey(data.current?.time);
  const canUseApiCurrent = apiCurrentDateKey === todayDateKey;
  const apiCurrentWeather = getWeatherLabel(data.current?.weather_code);
  const dailyDetails = forecastDailyTimes.map((time, relativeIndex) => {
    const index = startIndex + relativeIndex;
    const date = new Date(time);
    const dateKey = getDateKey(date);
    const hourlyIndexes = hourlyTimes
      .map((hourTime, hourIndex) => [hourTime, hourIndex])
      .filter(([hourTime]) => getDateKey(hourTime) === dateKey)
      .map(([, hourIndex]) => hourIndex);
    const dayHourlyTimes = hourlyIndexes.map((hourIndex) => hourlyTimes[hourIndex]);
    const dayRainProbabilities = hourlyIndexes.map((hourIndex) =>
      data.hourly?.precipitation_probability?.[hourIndex]
    );
    const rainByTime = getRainByPeriod(dayHourlyTimes, dayRainProbabilities);
    const fallbackLabel = getWeatherLabel(data.daily?.weather_code?.[index]);
    const dayHourlyWeatherCodes = hourlyIndexes.map((hourIndex) =>
      data.hourly?.weather_code?.[hourIndex]
    );
    const nightCloudValues = hourlyIndexes
      .filter((hourIndex) => new Date(hourlyTimes[hourIndex]).getHours() >= 19)
      .map((hourIndex) => Number(data.hourly?.cloud_cover?.[hourIndex]))
      .filter((value) => Number.isFinite(value));
    const nightCloudCover = nightCloudValues.length > 0
      ? Math.round(
          nightCloudValues.reduce((total, value) => total + value, 0) /
            nightCloudValues.length,
        )
      : null;
    const label = getDaytimeWeatherLabel(
      dayHourlyTimes,
      dayHourlyWeatherCodes,
      dayRainProbabilities,
      fallbackLabel,
    );
    const sunsetHour = data.daily?.sunset?.[index]
      ? new Date(data.daily.sunset[index]).getHours()
      : 18;
    const sunsetLowCloud = getAverageCloudCoverNear(
      hourlyIndexes,
      hourlyTimes,
      data.hourly?.cloud_cover_low,
      sunsetHour,
    );
    const sunsetMidCloud = getAverageCloudCoverNear(
      hourlyIndexes,
      hourlyTimes,
      data.hourly?.cloud_cover_mid,
      sunsetHour,
    );
    const sunsetHighCloud = getAverageCloudCoverNear(
      hourlyIndexes,
      hourlyTimes,
      data.hourly?.cloud_cover_high,
      sunsetHour,
    );
    const sunsetRainProbability = getAverageCloudCoverNear(
      hourlyIndexes,
      hourlyTimes,
      data.hourly?.precipitation_probability,
      sunsetHour,
    );
    const rawSunsetWeather = getWeatherLabelInHourRange(
      dayHourlyTimes,
      dayHourlyWeatherCodes,
      dayRainProbabilities,
      Math.max(0, sunsetHour - 1),
      Math.min(24, sunsetHour + 2),
      getHourlyWeatherAtHour(
        hourlyTimes,
        data.hourly?.weather_code,
        dateKey,
        sunsetHour,
      ) || label,
    );
    const sunsetWeather = sunsetRainProbability >= 70
      ? "曇"
      : rawSunsetWeather.startsWith("晴")
        ? rawSunsetWeather
        : label.startsWith("晴")
          ? label
          : "晴れのち曇";
    const dayLabel = getForecastDayLabel(relativeIndex, date, lang);
    const dayHigh = Math.round(Number(data.daily?.temperature_2m_max?.[index]));
    const dayLow = Math.round(Number(data.daily?.temperature_2m_min?.[index]));
    const dayHourlyWeather = windDisplayHours.map((hour) => ({
      time: `${hour}:00`,
      weather:
        getHourlyWeatherAtHour(
          hourlyTimes,
          data.hourly?.weather_code,
          dateKey,
          hour,
        ) || label,
    }));
    const daytimeWindIndex = hourlyIndexes.find((hourIndex) =>
      new Date(hourlyTimes[hourIndex]).getHours() >= 9
    );
    const windIndex = daytimeWindIndex ?? hourlyIndexes[0];
    const windSpeed = Number(data.hourly?.wind_speed_10m?.[windIndex]);
    const windDirection = Number(data.hourly?.wind_direction_10m?.[windIndex]);
    const detail = {
      dateKey,
      dayLabel,
      dateLabel: formatForecastDate(date, lang),
      weather: label,
      high: Number.isFinite(dayHigh) ? dayHigh : "--",
      low: Number.isFinite(dayLow) ? dayLow : "--",
      sunrise: formatApiTime(data.daily?.sunrise?.[index]),
      sunset: formatApiTime(data.daily?.sunset?.[index]),
      sunsetWeather,
      sunsetLowCloud,
      sunsetMidCloud,
      sunsetHighCloud,
      sunsetRainProbability,
      nightCloudCover,
      windDirection: relativeIndex === 0 && canUseApiCurrent
        ? getWindDirectionLabel(Number(data.current?.wind_direction_10m), lang)
        : getWindDirectionLabel(windDirection, lang),
      windSpeed: relativeIndex === 0 && canUseApiCurrent && Number.isFinite(Number(data.current?.wind_speed_10m))
        ? `${Number(data.current.wind_speed_10m).toFixed(1)}m/s`
        : Number.isFinite(windSpeed)
          ? `${windSpeed.toFixed(1)}m/s`
          : "",
      rainByTime,
      rainProbability: Number(data.daily?.precipitation_probability_max?.[index]) || 0,
    };

    return {
      ...detail,
      hourlyWeather: dayHourlyWeather,
      summary: buildWeatherSummaryText(detail, rainByTime, lang, dayLabel),
    };
  });
  const rainByTime = dailyDetails[0]?.rainByTime || defaultWeatherForecast.rainByTime;
  const currentWeather = dailyDetails[0]?.weather || apiCurrentWeather;
  const high = Math.round(Number(data.daily?.temperature_2m_max?.[startIndex]));
  const low = Math.round(Number(data.daily?.temperature_2m_min?.[startIndex]));
  const sunsetHour = data.daily?.sunset?.[startIndex]
    ? new Date(data.daily.sunset[startIndex]).getHours()
    : 18;
  const nightWeather =
    getHourlyWeatherAtHour(
      hourlyTimes,
      data.hourly?.weather_code,
      todayDateKey,
      Number(NIGHT_START_TIME.split(":")[0]),
    ) || currentWeather;
  const sunsetWeather =
    getHourlyWeatherAtHour(
      hourlyTimes,
      data.hourly?.weather_code,
      todayDateKey,
      sunsetHour,
    ) || currentWeather;
  const hourlyWeather = windDisplayHours.map((hour) => ({
    time: `${hour}:00`,
    weather:
      getHourlyWeatherAtHour(
        hourlyTimes,
        data.hourly?.weather_code,
        todayDateKey,
        hour,
      ) || currentWeather,
  }));
  const current = {
    weather: currentWeather,
    nightWeather,
    sunsetWeather,
    hourlyWeather,
    high: Number.isFinite(high) ? high : "--",
    low: Number.isFinite(low) ? low : "--",
    sunrise: formatApiTime(data.daily?.sunrise?.[startIndex]),
    sunset: formatApiTime(data.daily?.sunset?.[startIndex]),
    windDirection: canUseApiCurrent
      ? getWindDirectionLabel(Number(data.current?.wind_direction_10m), lang)
      : dailyDetails[0]?.windDirection || "",
    windDegree: canUseApiCurrent && Number.isFinite(Number(data.current?.wind_direction_10m))
      ? Number(data.current.wind_direction_10m)
      : null,
    windSpeed: canUseApiCurrent && Number.isFinite(Number(data.current?.wind_speed_10m))
      ? `${Number(data.current.wind_speed_10m).toFixed(1)}m/s`
      : dailyDetails[0]?.windSpeed || "",
  };

  return {
    updated: formatOpenMeteoWeatherUpdated(lang, updatedAt),
    current: {
      ...current,
      ...(dailyDetails[0] || {}),
      summary: buildWeatherSummaryText(current, rainByTime, lang),
    },
    days: forecastDailyTimes.map((time, relativeIndex) => {
      const index = startIndex + relativeIndex;
      const date = new Date(time);
      const dateKey = getDateKey(date);
      const hourlyIndexes = hourlyTimes
        .map((hourTime, hourIndex) => [hourTime, hourIndex])
        .filter(([hourTime]) => getDateKey(hourTime) === dateKey)
        .map(([, hourIndex]) => hourIndex);
      const dayHourlyTimes = hourlyIndexes.map((hourIndex) => hourlyTimes[hourIndex]);
      const dayHourlyWeatherCodes = hourlyIndexes.map((hourIndex) =>
        data.hourly?.weather_code?.[hourIndex]
      );
      const dayRainProbabilities = hourlyIndexes.map((hourIndex) =>
        data.hourly?.precipitation_probability?.[hourIndex]
      );
      const fallbackLabel = getWeatherLabel(data.daily?.weather_code?.[index]);
      const label = getDaytimeWeatherLabel(
        dayHourlyTimes,
        dayHourlyWeatherCodes,
        dayRainProbabilities,
        fallbackLabel,
      );
      const rainProbability = Number(data.daily?.precipitation_probability_max?.[index]) || 0;
      const dayLabel = getForecastDayLabel(relativeIndex, date, lang);
      return [
        dayLabel,
        formatForecastDate(date, lang),
        label,
        getForecastStatus(label, rainProbability),
        Math.round(Number(data.daily?.temperature_2m_max?.[index])),
        Math.round(Number(data.daily?.temperature_2m_min?.[index])),
      ];
    }),
    dailyDetails,
    rainByTime,
  };
};

const formatOpenMeteoMarineUpdated = (lang, updatedAt = new Date()) => {
  const date = new Date(updatedAt);
  const time = `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (lang === "EN") return `Open-Meteo Marine updated ${time}`;
  if (lang === "中文") return `Open-Meteo Marine ${time} 更新`;
  if (lang === "한국어") return `Open-Meteo Marine ${time} 업데이트`;
  return `Open-Meteo Marine ${time} 更新`;
};

const getWaveLabel = (waveHeight, lang) => {
  const labels = {
    JP: {
      calm: "穏やか",
      caution: "少し高め",
      high: "高め・注意",
    },
    EN: {
      calm: "Calm",
      caution: "Slightly high",
      high: "High / caution",
    },
    中文: {
      calm: "较平稳",
      caution: "稍高",
      high: "偏高・注意",
    },
    한국어: {
      calm: "잔잔한 편",
      caution: "조금 높음",
      high: "높음・주의",
    },
  };
  const langLabels = labels[lang] || labels.JP;
  if (!Number.isFinite(waveHeight)) return "今日の波";
  if (waveHeight >= 2) return langLabels.high;
  if (waveHeight >= 1) return langLabels.caution;
  return langLabels.calm;
};

const getSwellLabel = (swellHeight, lang) => {
  const labels = {
    JP: {
      calm: "小さめ",
      caution: "ややうねりあり",
      high: "うねり強め・注意",
    },
    EN: {
      calm: "Low swell",
      caution: "Some swell",
      high: "Strong swell",
    },
    中文: {
      calm: "涌浪较小",
      caution: "有些涌浪",
      high: "涌浪较强・注意",
    },
    한국어: {
      calm: "너울 약함",
      caution: "너울 조금 있음",
      high: "너울 강함・주의",
    },
  };
  const langLabels = labels[lang] || labels.JP;
  if (!Number.isFinite(swellHeight)) return "取得中";
  if (swellHeight >= 1.5) return langLabels.high;
  if (swellHeight >= 0.8) return langLabels.caution;
  return langLabels.calm;
};

const getDailyTidesFromSeaLevel = (times = [], levels = [], dateKey = "") => {
  const rawPoints = times
    .map((time, index) => ({
      time,
      date: new Date(time),
      level: Number(levels[index]),
    }))
    .filter(({ date, level }) => !Number.isNaN(date.getTime()) && Number.isFinite(level));

  if (rawPoints.length < 3) return defaultDailyMarineForecast.tides;

  // Collapse consecutive equal-level readings (a rounded plateau at the peak/trough)
  // into a single point, otherwise neither side of the plateau is strictly higher/lower
  // than its neighbor and the extremum is missed entirely.
  const points = rawPoints.filter((point, index) => {
    const previous = rawPoints[index - 1];
    return !previous || previous.level !== point.level;
  });

  // The hourly readings only sample the tide curve once an hour, so the real
  // high/low almost always falls between two readings. Fit a parabola through the
  // extremum and its immediate (raw, un-collapsed) hourly neighbors and use its
  // vertex to estimate the actual peak time (to the minute) and height, instead of
  // just reporting whichever whole hour happened to be sampled closest to it. This
  // only refines what's displayed — which calendar day a point belongs to is still
  // decided from the original on-the-hour reading, so a peak sampled at 00:00 stays
  // attributed to that day even though the interpolated time can drift a few
  // minutes to either side of it.
  const interpolateExtremum = (point) => {
    const rawIndex = rawPoints.findIndex((raw) => raw.time === point.time);
    const previous = rawIndex > 0 ? rawPoints[rawIndex - 1] : null;
    const next = rawIndex >= 0 && rawIndex < rawPoints.length - 1 ? rawPoints[rawIndex + 1] : null;
    if (!previous || !next) return { ...point, displayDate: point.date, displayLevel: point.level };

    const y0 = previous.level;
    const y1 = point.level;
    const y2 = next.level;
    const denominator = y0 - 2 * y1 + y2;
    if (!Number.isFinite(denominator) || Math.abs(denominator) < 1e-9) {
      return { ...point, displayDate: point.date, displayLevel: point.level };
    }

    const offsetHours = Math.max(-0.5, Math.min(0.5, (0.5 * (y0 - y2)) / denominator));

    return {
      ...point,
      displayDate: new Date(point.date.getTime() + offsetHours * 60 * 60 * 1000),
      displayLevel: y1 - (y0 - y2) ** 2 / (8 * denominator),
    };
  };

  const highExtrema = [];
  const lowExtrema = [];
  points.forEach((point, index) => {
    if (index === 0 || index === points.length - 1) return;
    const previous = points[index - 1];
    const next = points[index + 1];
    if (point.level > previous.level && point.level > next.level) {
      highExtrema.push(interpolateExtremum(point));
    }
    if (point.level < previous.level && point.level < next.level) {
      lowExtrema.push(interpolateExtremum(point));
    }
  });

  const completeTideExtrema = (items) => {
    // Only report extrema that actually fall on this calendar day (by their
    // original, un-interpolated hour). A day doesn't always contain a full pair of
    // highs/lows (the semi-diurnal cycle runs ~24.8h, not 24h), so borrowing a
    // point from the adjacent day to force a count of 2 just shows a mistimed,
    // out-of-day reading — better to show however many (0, 1, or 2) genuinely
    // occurred today.
    const sameDayItems = dateKey
      ? items.filter((point) => getDateKey(point.date) === dateKey)
      : items;
    return sameDayItems.slice(0, 2);
  };

  const formatTidePoint = (point) => [
    formatApiTime(point.displayDate),
    `${Math.round(point.displayLevel * 100)}cm`,
  ];

  return {
    high: completeTideExtrema(highExtrema).map(formatTidePoint),
    low: completeTideExtrema(lowExtrema).map(formatTidePoint),
  };
};

const getDaytimeHeightItems = (times = [], heights = [], dateKey = "") =>
  times
    .map((time, index) => {
      const date = new Date(time);
      const height = Number(heights[index]);
      return {
        dateKey: getDateKey(date),
        hour: date.getHours(),
        time: formatApiTime(time),
        value: Number.isFinite(height) ? `${height.toFixed(1)}m` : "--",
      };
    })
    .filter(
      (item) =>
        item.dateKey === dateKey &&
        item.hour >= 6 &&
        item.hour <= 18 &&
        item.hour % 3 === 0 &&
        item.time !== "--",
    );

const getDaytimeWaveItems = (times, waves, dateKey) =>
  getDaytimeHeightItems(times, waves, dateKey).map((item) => ({
    ...item,
    wave: item.value,
  }));

const getDaytimeSwellItems = (times, swells, dateKey) =>
  getDaytimeHeightItems(times, swells, dateKey).map((item) => ({
    ...item,
    swell: item.value,
  }));

const getNightWaveItems = (times = [], waves = [], dateKey = "") => {
  const selectedDate = new Date(dateKey);
  const nextDate = new Date(selectedDate);
  nextDate.setDate(selectedDate.getDate() + 1);
  const nextDateKey = getDateKey(nextDate);

  return times
    .map((time, index) => {
      const date = new Date(time);
      const wave = Number(waves[index]);
      return {
        date,
        dateKey: getDateKey(date),
        hour: date.getHours(),
        time: formatApiTime(time),
        wave: Number.isFinite(wave) ? `${wave.toFixed(1)}m` : "--",
      };
    })
    .filter((item) => (
      (item.dateKey === dateKey && item.hour >= 20) ||
      (item.dateKey === nextDateKey && item.hour <= 2)
    ))
    .filter((item) => item.time !== "--")
    .slice(0, 6);
};

// JMA's official 潮位表 (astronomical tide prediction) for 平良/Hirara gives exact
// high/low times to the minute, unlike Open-Meteo's marine model (a wave forecast,
// not a true tide prediction) — use it whenever it's available for the day, and only
// fall back to estimating from the hourly sea level model if it isn't.
const getJmaTideForDay = (tideData, dateKey) => {
  const dayTide = (tideData || []).find((item) => item.dateKey === dateKey);
  if (!dayTide) return null;

  const formatGroup = (items) =>
    (items || []).slice(0, 2).map(({ time, height }) => [time, `${height}cm`]);

  return {
    high: formatGroup(dayTide.high),
    low: formatGroup(dayTide.low),
  };
};

const getOpenMeteoDailyMarineForecast = (lang, data, tideData, updatedAt) => {
  const dailyTimes = data.daily?.time || [];
  // The cached raw data can be a bit stale by the time it's rendered (fetched a few
  // times a day, not on every visit), so index 0 of the API's daily arrays isn't
  // reliably "today" — it can still be yesterday if the cache hasn't refreshed past
  // midnight yet. Anchor on the entry that actually matches today's real date,
  // same as the weather forecast parser does.
  const startIndex = getForecastStartIndex(dailyTimes);
  const forecastDailyTimes = dailyTimes.slice(startIndex);
  const marineDays = forecastDailyTimes.map((time, relativeIndex) => {
    const index = startIndex + relativeIndex;
    const waveHeight = Number(data.daily?.wave_height_max?.[index]);
    const swellHeight = Number(data.daily?.swell_wave_height_max?.[index]);
    const wavePeriod = Number(data.daily?.wave_period_max?.[index]);

    return {
      dateKey: getDateKey(time),
      wave: Number.isFinite(waveHeight) ? `${waveHeight.toFixed(1)}m` : "--",
      waveLabel: getWaveLabel(waveHeight, lang),
      swell: Number.isFinite(swellHeight) ? `${swellHeight.toFixed(1)}m` : "--",
      swellLabel: getSwellLabel(swellHeight, lang),
      period: Number.isFinite(wavePeriod) ? `${wavePeriod.toFixed(0)}秒` : "--",
      nightWaves: getNightWaveItems(
        data.hourly?.time || [],
        data.hourly?.wave_height || [],
        getDateKey(time),
      ),
      daytimeWaves: getDaytimeWaveItems(
        data.hourly?.time || [],
        data.hourly?.wave_height || [],
        getDateKey(time),
      ),
      daytimeSwells: getDaytimeSwellItems(
        data.hourly?.time || [],
        data.hourly?.swell_wave_height || [],
        getDateKey(time),
      ),
      tides:
        getJmaTideForDay(tideData, getDateKey(time)) ||
        getDailyTidesFromSeaLevel(
          data.hourly?.time || [],
          data.hourly?.sea_level_height_msl || [],
          getDateKey(time),
        ),
    };
  });
  const todayMarine = marineDays[0] || defaultDailyMarineForecast;

  return {
    updated: formatOpenMeteoMarineUpdated(lang, updatedAt),
    ...todayMarine,
    days: marineDays,
  };
};

const getOpenMeteoHourlyWindForecast = (lang, data, updatedAt) => {
  const times = data.hourly?.time || [];
  const speeds = data.hourly?.wind_speed_10m || [];
  const directions = data.hourly?.wind_direction_10m || [];
  const gusts = data.hourly?.wind_gusts_10m || [];
  const buildItems = (targetDateKey) => times
    .map((time, dataIndex) => ({
      time,
      dataIndex,
      date: new Date(time),
    }))
    .filter(({ date }) =>
      getDateKey(date) === targetDateKey &&
      windDisplayHours.includes(date.getHours())
    )
    .map(({ time, dataIndex, date }) => {
    const speed = Number(speeds[dataIndex]);
    const directionDegree = Number(directions[dataIndex]);
    const gust = Number(gusts[dataIndex]);
    const [level, icon, status, risk] = getWindSafety(speed, lang, gust);

    return {
      time: `${date.getHours()}:00`,
      direction: getWindDirectionLabel(directionDegree, lang),
      degree: Number.isFinite(directionDegree) ? directionDegree : null,
      speed: Number.isFinite(speed) ? `${speed.toFixed(1)}m/s` : "--",
      gust: Number.isFinite(gust) ? `${gust.toFixed(1)}m/s` : null,
      level,
      icon,
      status,
      risk,
      avoidance: getWindAvoidanceAdvice(level, lang),
    };
  });
  const allDateKeys = [...new Set(times.map(getDateKey).filter(Boolean))];
  const startIndex = getForecastStartIndex(allDateKeys);
  const dateKeys = allDateKeys.slice(startIndex);
  const days = dateKeys.map((dateKey) => ({
    dateKey,
    items: buildItems(dateKey),
  }));

  return {
    updated: formatOpenMeteoUpdated(lang, updatedAt),
    source: "Open-Meteo",
    items: days[0]?.items || [],
    days,
  };
};

const getJmaTyphoonInfo = (lang, raw) => {
  const targets = raw?.targets;

  if (!Array.isArray(targets) || targets.length === 0) {
    return {
      updated: "",
      items: [],
      status: "empty",
    };
  }

  const categoryLabels = typhoonCategoryLabels[lang] || typhoonCategoryLabels.JP;
  const items = targets.slice(0, 5).map((target) => {
      const detail = raw?.details?.[target.tropicalCyclone] || [];
      const points = Array.isArray(detail)
        ? detail.filter((item) => Array.isArray(item.center))
        : [];
      const current = points.find((item) => item.advancedHours === 0) || points[0];
      const center = current?.center
        ? { lat: current.center[0], lng: current.center[1] }
        : null;
      const track = points.map((point) => {
        const coordinate = Array.isArray(point.center)
          ? { lat: point.center[0], lng: point.center[1] }
          : null;
        const probRadius = point.probabilityCircle?.radius;

        return {
          label:
            typeof point.part === "object"
              ? point.part.jp || point.part.en
              : String(point.part || ""),
          time: formatApiDateTime(point.validtime?.JST || point.validtime?.UTC),
          center: coordinate
            ? `${coordinate.lat.toFixed(1)}N / ${coordinate.lng.toFixed(1)}E`
            : "--",
          coordinate,
          distance: getDistanceKm(MIYAKO_MAP_CENTER, coordinate),
          probabilityCircleRadiusKm: Number.isFinite(probRadius)
            ? probRadius / 1000
            : null,
        };
      });
      const category = categoryLabels[target.category] || target.category;
      const impact = getTyphoonImpact(track, category, lang);
      const impactPeriod = getTyphoonImpactPeriod(track);
      const warningAreaStatus = getWarningAreaStatus(current, lang);
      const stormAreaProbability = getStormAreaProbability(track, current, lang, warningAreaStatus);
      const numberMatch = String(target.typhoonNumber || "").match(/(\d{1,2})$/);
      const downgradedNotice =
        target.category === "TD" && numberMatch
          ? (typhoonDowngradedNoticeTemplates[lang] || typhoonDowngradedNoticeTemplates.JP)
              .replace("{number}", numberMatch[1])
          : null;

      return {
        id: target.tropicalCyclone,
        number: target.typhoonNumber,
        category,
        downgradedNotice,
        issue: formatApiDateTime(target.issue),
        distance: getDistanceKm(MIYAKO_MAP_CENTER, center),
        movement: getTyphoonMovementSummary(track, lang),
        impact,
        impactPeriod,
        warningAreaStatus,
        stormAreaProbability,
        track,
      };
    }).filter((item) => {
      if (item.warningAreaStatus) return true;

      // Show only typhoons that actually approach Miyakojima (shared rule with
      // the daily Threads post). Distant systems near Ogasawara etc. are hidden.
      return item.track.some((point) =>
        isTyphoonPointNearMiyako(point.distance, point.probabilityCircleRadiusKm));
    });

  return {
    updated: items[0]?.issue || "",
    items,
    status: "ready",
  };
};

const getYahooRainRadarSnapshot = (radarWeatherByName) => {
  if (!radarWeatherByName) return null;

  const results = rainRadarPoints.map((point) => {
    const weatherData = radarWeatherByName[point.name] || {};
    const weatherList = weatherData.Feature?.[0]?.Property?.WeatherList?.Weather || [];
    const weatherIndex = getClosestIndex(weatherList.map((item) => item.Date));
    const current = weatherList[weatherIndex] || weatherList[0] || {};
    const rainfall = Number(current.Rainfall) || 0;
    const [level, label] = getRainLevel(rainfall);

    return {
      ...point,
      rainfall,
      level,
      label,
      timestamp: current.Date,
    };
  });

  return {
    updated: formatYahooWeatherTime(results[0]?.timestamp),
    points: results,
  };
};

const getYahooHourlyRainForecast = (centerWeatherData) => {
  if (!centerWeatherData) return null;

  const weatherData = centerWeatherData;
  const weatherList = weatherData.Feature?.[0]?.Property?.WeatherList?.Weather || [];
  if (weatherList.length === 0) return null;

  const startIndex = getClosestIndex(weatherList.map((item) => item.Date));
  const items = weatherList.slice(startIndex, startIndex + 8).map((item) => {
    const rainfall = Number(item.Rainfall) || 0;
    const [level, label] = getRainLevel(rainfall);

    return {
      time: formatYahooForecastTime(item.Date),
      rainfall,
      level,
      label,
    };
  });

  return {
    updated: formatYahooWeatherTime(weatherList[startIndex]?.Date),
    items,
  };
};

const fetchLatestJmaTyphoon = async () => {
  const targetResponse = await fetch(
    "https://www.jma.go.jp/bosai/typhoon/data/targetTc.json",
    { cache: "no-store" },
  );
  if (!targetResponse.ok) {
    throw new Error(`JMA typhoon list fetch failed: ${targetResponse.status}`);
  }

  const targets = await targetResponse.json();
  if (!Array.isArray(targets) || targets.length === 0) {
    return { targets: [], details: {} };
  }

  const detailEntries = await Promise.all(
    targets.slice(0, 5).map(async (target) => {
      const response = await fetch(
        `https://www.jma.go.jp/bosai/typhoon/data/${target.tropicalCyclone}/forecast.json`,
        { cache: "no-store" },
      );
      if (!response.ok) {
        throw new Error(`JMA typhoon detail fetch failed: ${response.status}`);
      }
      return [target.tropicalCyclone, await response.json()];
    }),
  );

  return { targets, details: Object.fromEntries(detailEntries) };
};

// Weather/marine/wind/rain data is fetched by a scheduled GitHub Action and
// published as this static file. Safety-sensitive typhoon data is refreshed
// directly from JMA once per page load, with this cache as the fallback.
const getCachedOceanData = async () => {
  const response = await fetch(`${process.env.PUBLIC_URL || ""}/ocean-data-cache.json`, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Ocean data cache fetch failed: ${response.status}`);
  }

  return response.json();
};

const getJmaPrimaryWeather = (text) => {
  const normalized = String(text || "")
    .replace(/[\s\u3000]+/g, "")
    .replace(/くもり/g, "曇り");
  const getKind = (value) => {
    const match = String(value || "").match(/晴れ|曇り|雨|雪/);
    return match?.[0] || "";
  };
  const toLabel = (kind) => ({ 晴れ: "晴れ", 曇り: "曇", 雨: "雨", 雪: "雪" }[kind] || "");
  const combine = (first, second, connector) => {
    const from = toLabel(first);
    const to = toLabel(second);
    if (!from || !to || from === to) return from || to;
    if (connector === "時々") {
      if (from === "晴れ" && to === "曇") return "晴時々曇";
      if (from === "曇" && to === "晴れ") return "曇時々晴";
      return `${from}時々${to}`;
    }
    return `${from}のち${to}`;
  };

  for (const connector of ["後", "時々"]) {
    const connectorIndex = normalized.indexOf(connector);
    if (connectorIndex > 0) {
      return combine(
        getKind(normalized.slice(0, connectorIndex)),
        getKind(normalized.slice(connectorIndex + connector.length)),
        connector,
      );
    }
  }
  return toLabel(getKind(normalized));
};

const getJmaWeatherByDate = (rawForecast) => {
  const shortForecast = Array.isArray(rawForecast) ? rawForecast[0] : null;
  const weatherSeries = shortForecast?.timeSeries?.find((series) =>
    series?.areas?.some((item) => Array.isArray(item?.weathers))
  );
  const area = weatherSeries?.areas?.find((item) => item?.area?.code === "473000")
    || weatherSeries?.areas?.find((item) => item?.area?.name?.includes("宮古島"));
  if (!area?.weathers) return {};

  return (weatherSeries.timeDefines || []).reduce((result, time, index) => {
    const label = getJmaPrimaryWeather(area.weathers[index]);
    if (label) result[getDateKey(time)] = label;
    return result;
  }, {});
};

const getJmaRainByDate = (rawForecast) => {
  const shortForecast = Array.isArray(rawForecast) ? rawForecast[0] : null;
  const rainSeries = shortForecast?.timeSeries?.find((series) =>
    series?.areas?.some((item) => Array.isArray(item?.pops))
  );
  const area = rainSeries?.areas?.find((item) => item?.area?.code === "473000")
    || rainSeries?.areas?.find((item) => item?.area?.name?.includes("宮古島"));
  if (!area?.pops) return {};

  return (rainSeries.timeDefines || []).reduce((result, time, index) => {
    const date = new Date(time);
    const probability = Number(area.pops[index]);
    if (Number.isNaN(date.getTime()) || !Number.isFinite(probability)) return result;
    const dateKey = getDateKey(date);
    const hour = date.getHours();
    result[dateKey] = {
      ...(result[dateKey] || {}),
      [`${hour}-${hour + 6}`]: probability,
    };
    return result;
  }, {});
};

const getJmaTemperaturesByDate = (rawForecast) => {
  const result = {};
  const forecastNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  for (const block of Array.isArray(rawForecast) ? rawForecast : []) {
    for (const series of block?.timeSeries || []) {
      const times = series?.timeDefines || [];
      for (const area of series?.areas || []) {
        const areaName = String(area?.area?.name || "");
        if (areaName && !areaName.includes("宮古島")) continue;

        times.forEach((time, index) => {
          const date = new Date(time);
          if (Number.isNaN(date.getTime())) return;
          const dateKey = getDateKey(date);
          const values = result[dateKey] || { high: null, low: null, regular: [] };
          const high = forecastNumber(area?.tempsMax?.[index]);
          const low = forecastNumber(area?.tempsMin?.[index]);
          const temperature = forecastNumber(area?.temps?.[index]);
          if (high !== null) values.high = high;
          if (low !== null) values.low = low;
          if (temperature !== null) {
            values.regular.push({ value: temperature, hour: date.getHours() });
          }
          result[dateKey] = values;
        });
      }
    }
  }

  return Object.fromEntries(Object.entries(result).map(([dateKey, values]) => {
    const daytime = values.regular.filter((item) => item.hour >= 9).map((item) => item.value);
    const earlyMorning = values.regular.filter((item) => item.hour < 9).map((item) => item.value);
    return [dateKey, {
      high: Number.isFinite(values.high)
        ? Math.round(values.high)
        : daytime.length
          ? Math.round(Math.max(...daytime))
          : null,
      low: Number.isFinite(values.low)
        ? Math.round(values.low)
        : earlyMorning.length
          ? Math.round(Math.min(...earlyMorning))
          : null,
    }];
  }));
};

const applyJmaMainWeather = (forecast, latestRawJma, frozenRawJma, lang) => {
  if (!forecast) return forecast;
  const latestByDate = getJmaWeatherByDate(latestRawJma);
  const frozenByDate = getJmaWeatherByDate(frozenRawJma);
  const latestRainByDate = getJmaRainByDate(latestRawJma);
  const frozenRainByDate = getJmaRainByDate(frozenRawJma);
  const latestTemperaturesByDate = getJmaTemperaturesByDate(latestRawJma);
  const frozenTemperaturesByDate = getJmaTemperaturesByDate(frozenRawJma);
  const dailyDetails = (forecast.dailyDetails || []).map((detail, index) => {
    const jmaWeather = index === 0
      ? frozenByDate[detail.dateKey] || latestByDate[detail.dateKey]
      : latestByDate[detail.dateKey];
    const hourlyWeather = detail.hourlyWeather || [];
    const hasSunnyHour = hourlyWeather.some((item) => /晴/.test(item?.weather || ""));
    const hasCloudOrRainHour = hourlyWeather.some((item) => /曇|雨/.test(item?.weather || ""));
    // Even when the broad-area JMA outlook is cloudy/rainy, show that the day
    // includes brighter weather if at least one of the displayed time slots does.
    const displayWeather = hasSunnyHour && hasCloudOrRainHour
      ? "晴れのち曇"
      : jmaWeather;
    const jmaRain = index === 0
      ? frozenRainByDate[detail.dateKey] || latestRainByDate[detail.dateKey]
      : latestRainByDate[detail.dateKey];
    const jmaTemperatures = index === 0
      ? frozenTemperaturesByDate[detail.dateKey] || latestTemperaturesByDate[detail.dateKey]
      : latestTemperaturesByDate[detail.dateKey];
    const rainByTime = (detail.rainByTime || []).map(([period, probability]) => [
      period,
      Number.isFinite(jmaRain?.[period]) ? `${jmaRain[period]}%` : probability,
    ]);
    if (!displayWeather && !jmaRain && !jmaTemperatures) return detail;
    const updated = {
      ...detail,
      weather: displayWeather || detail.weather,
      rainByTime,
      rainProbability: rainByTime.length
        ? Math.round(
            rainByTime.reduce(
              (total, [, probability]) => total + (Number.parseInt(probability, 10) || 0),
              0,
            ) / rainByTime.length,
          )
        : 0,
      high: Number.isFinite(jmaTemperatures?.high) ? jmaTemperatures.high : detail.high,
      low: Number.isFinite(jmaTemperatures?.low) ? jmaTemperatures.low : detail.low,
    };
    return {
      ...updated,
      summary: buildWeatherSummaryText(updated, rainByTime, lang, detail.dayLabel),
    };
  });
  const days = (forecast.days || []).map((day, index) => {
    const detail = dailyDetails[index];
    if (!detail || !Array.isArray(day)) return day;
    return [
      day[0],
      day[1],
      detail.weather,
      getForecastStatus(detail.weather, detail.rainProbability),
      detail.high,
      detail.low,
    ];
  });
  const today = dailyDetails[0];
  return {
    ...forecast,
    current: today
      ? { ...forecast.current, ...today, weather: today.weather, summary: today.summary }
      : forecast.current,
    days,
    dailyDetails,
    rainByTime: today?.rainByTime || forecast.rainByTime,
  };
};

const applyManualWeather = (forecast, guideSettings, lang) => {
  if (!forecast) return forecast;
  const keys = ["todayWeather", "tomorrowWeather", "dayAfterTomorrowWeather"];
  const dailyDetails = (forecast.dailyDetails || []).map((detail, index) => {
    const manualWeather = guideSettings?.[keys[index]];
    if (!manualWeather || manualWeather === "auto") return detail;
    const updated = { ...detail, weather: manualWeather };
    return {
      ...updated,
      summary: buildWeatherSummaryText(updated, detail.rainByTime || [], lang, detail.dayLabel),
    };
  });
  const days = (forecast.days || []).map((day, index) => {
    const detail = dailyDetails[index];
    if (!detail || !Array.isArray(day)) return day;
    return [day[0], day[1], detail.weather, getForecastStatus(detail.weather, detail.rainProbability), day[4], day[5]];
  });
  const today = dailyDetails[0];
  return {
    ...forecast,
    current: today ? { ...forecast.current, ...today } : forecast.current,
    days,
    dailyDetails,
  };
};

const keepTodayWeatherAtDaytimeSnapshot = (latest, frozen) => {
  if (!latest || !frozen) return latest;
  const latestToday = latest.dailyDetails?.[0] || {};
  const frozenToday = frozen.dailyDetails?.[0] || {};
  return {
    ...latest,
    current: {
      ...frozen.current,
      nightWeather: latest.current?.nightWeather,
      sunsetWeather: frozen.current?.sunsetWeather,
    },
    dailyDetails: [
      {
        ...frozenToday,
        nightCloudCover: latestToday.nightCloudCover,
      },
      ...(latest.dailyDetails || []).slice(1),
    ],
    days: [frozen.days?.[0], ...(latest.days || []).slice(1)].filter(Boolean),
  };
};

const keepTodayWindAtDaytimeSnapshot = (latest, frozen) => {
  if (!latest || !frozen) return latest;
  return {
    ...latest,
    items: frozen.items,
    days: [frozen.days?.[0], ...(latest.days || []).slice(1)].filter(Boolean),
  };
};

const keepTodayMarineAtDaytimeSnapshot = (latest, frozen) => {
  if (!latest || !frozen) return latest;
  const frozenToday = frozen.days?.[0] || {};
  return {
    ...latest,
    ...frozenToday,
    updated: latest.updated,
    days: [frozen.days?.[0], ...(latest.days || []).slice(1)].filter(Boolean),
  };
};

const Icon = ({ children }) => <span className={s.icon}>{children}</span>;

const turtleIcon = "𓆉";
const oceanGuideCategories = [
  ["snorkel", "🤿"],
  ["stars", "✩"],
  ["sunset", "☀"],
  ["yabiji", "🐠"],
  ["rainy", "☔"],
];

const mapGuideCategories = [
  ["snorkel", "🤿"],
  ["beach", "🏖"],
  ["stars", "✩"],
  ["sunset", "☀"],
  ["lunch", "🍴"],
  ["dinner", "☾"],
  ["nightlife", "🍸"],
  ["lateNightMeal", "🍜"],
  ["hotel", "🏨"],
];

const placeOnlyMapCategories = ["lunch", "dinner", "nightlife", "lateNightMeal", "hotel"];

const Stars = ({ value }) => (
  <span className={s.stars}>
    {"★".repeat(value)}
    <span>{"★".repeat(5 - value)}</span>
  </span>
);

const mapStarTourInstagramUrl =
  "https://www.instagram.com/p/DQAtNzskhDf/?utm_source=ig_web_copy_link&igsi=MzRlODBiNWFlZA==";
const starTourAccountUrl = "https://www.instagram.com/_key_photo/";
// 一時非表示。再掲載するときは true に戻す。
const SHOW_MAP_STAR_TOUR_PROMO = false;

const clampScore = (score) => Math.min(5, Math.max(1, Number(score) || 1));
const clampScoreZero = (score) => Math.min(5, Math.max(0, Number(score) || 0));

const getSpotScores = (spot) => {
  const categories = spot.categories || [];
  const isSnorkelSpot = categories.includes("snorkel");
  const isSunsetSpot = categories.includes("sunset");
  const isStarSpot = categories.includes("stars");
  const marineBase = isSnorkelSpot
    ? Math.max(spot.snorkel, spot.sup || 1)
    : spot.sup || (isSunsetSpot || isStarSpot ? 2 : 3);
  const photoBase = isStarSpot || isSunsetSpot
    ? 5
    : isSnorkelSpot
      ? 4
      : 4;

  return {
    snorkel: clampScoreZero(spot.snorkel),
    marine: clampScore(spot.marineActivity || marineBase),
    photo: clampScore(spot.photo || photoBase),
    dronePhoto: clampScore(spot.dronePhoto || spot.photo || photoBase),
    underwaterPhoto: clampScore(spot.underwaterPhoto || spot.snorkel),
    groundPhoto: clampScore(spot.photo || photoBase),
    starPhoto: clampScore(spot.milkyWay),
    sunsetPhoto: clampScore(spot.sunsetScore),
    beginner: clampScoreZero(spot.beginnerScore),
    turtle: clampScoreZero(spot.turtleScore),
    crowd: clampScoreZero(spot.crowdScore || crowdLevelStars[spot.crowdLevel] || 3),
  };
};

const getPhotogenicScore = (spot) => {
  const scores = getSpotScores(spot);
  const average =
    (scores.underwaterPhoto +
      scores.groundPhoto +
      scores.dronePhoto +
      scores.starPhoto +
      scores.sunsetPhoto) /
    5;
  return Math.round(average);
};

const getNumericValue = (value) => {
  const parsed = Number.parseFloat(String(value || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAverageRainProbability = (weather) => {
  const values = (weather?.rainByTime || [])
    .map(([, rain]) => getNumericValue(rain))
    .filter((rain) => Number.isFinite(rain));

  if (values.length > 0) {
    return values.reduce((total, rain) => total + rain, 0) / values.length;
  }

  return Number(weather?.rainProbability) || 0;
};

const getDaytimeRainProbability = (weather) => {
  const daytimePeriods = new Set(["6-12", "12-18"]);
  const values = (weather?.rainByTime || [])
    .filter(([period]) => daytimePeriods.has(period))
    .map(([, rain]) => getNumericValue(rain))
    .filter((rain) => Number.isFinite(rain));

  return values.length
    ? values.reduce((total, rain) => total + rain, 0) / values.length
    : getAverageRainProbability(weather);
};

const getNightRainProbability = (weather) => {
  const nightValue = weather?.rainByTime?.find(([time]) => time === "18-24")?.[1];
  return nightValue == null
    ? getAverageRainProbability(weather)
    : getNumericValue(nightValue);
};

const getMaxWindSpeed = (hourlyWindForecast, dayIndex = 0) =>
  Math.max(
    0,
    ...(hourlyWindForecast?.days?.[dayIndex]?.items || hourlyWindForecast?.items || []).map(
      (item) => getNumericValue(item.speed),
    ),
  );

const getDayOceanCondition = (dayIndex, oceanCondition, weatherForecast, hourlyWindForecast) => {
  const dayWindItems = hourlyWindForecast?.days?.[dayIndex]?.items || [];
  if (!dayIndex) return { ...oceanCondition, windItems: dayWindItems };

  const dayWeather = weatherForecast?.dailyDetails?.[dayIndex] || weatherForecast?.current || {};
  const representative =
    dayWindItems.find((item) => item.time === "12:00") || dayWindItems[0] || {};

  return {
    ...oceanCondition,
    weather: dayWeather.weather ?? oceanCondition.weather,
    windDirection: representative.direction ?? oceanCondition.windDirection,
    windSpeed: representative.speed ?? oceanCondition.windSpeed,
    windGust: representative.gust ?? oceanCondition.windGust,
    windDegree: representative.degree ?? oceanCondition.windDegree,
    windItems: dayWindItems,
  };
};

const isRainyWeather = (weather) =>
  /雨|rain|shower|비/i.test(String(weather || ""));

const isSunnyWeather = (weather) =>
  /晴|sun|clear|맑/i.test(String(weather || ""));

const isCloudyWeather = (weather) =>
  /曇|cloud|overcast|흐|구름/i.test(String(weather || ""));

const getForecastScores = (weather, marine, windItems) => {
  const wave = getNumericValue(marine.wave);
  const rainValues = (weather.rainByTime || []).map(([, rain]) => getNumericValue(rain));
  const averageRain = rainValues.length
    ? rainValues.reduce((total, rain) => total + rain, 0) / rainValues.length
    : 0;
  const windSpeeds = (windItems || []).map((item) => getNumericValue(item.speed));
  const maxWind = windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0;
  const windGusts = (windItems || []).map((item) => getNumericValue(item.gust));
  const maxGust = windGusts.length > 0 ? Math.max(...windGusts) : 0;
  const rainy = isRainyWeather(weather.weather);
  const cloudy = isCloudyWeather(weather.weather);
  const sunny = isSunnyWeather(weather.weather) && !rainy && !cloudy;
  const weatherPenalty = rainy ? 2 : cloudy ? 1 : 0;
  const rainPenalty = averageRain >= 70 ? 2 : averageRain >= 50 ? 1 : 0;
  const wavePenalty = wave >= 1.2 ? 2 : wave >= 0.8 ? 1 : 0;
  const windExtreme = maxWind >= 12 || maxGust >= 22;
  const windPenalty = windExtreme
    ? 5
    : maxWind >= 7 || maxGust >= 11
      ? 2
      : maxWind >= 5 || maxGust >= 8
        ? 1
        : 0;
  const photoWindPenalty = windExtreme ? 5 : maxWind >= 7 || maxGust >= 11 ? 2 : 0;

  if (sunny && wavePenalty === 0 && windPenalty === 0 && rainPenalty === 0) {
    return {
      snorkel: 5,
      marine: 5,
      photo: 5,
    };
  }

  const snorkelScore =
    5 -
    weatherPenalty -
    rainPenalty -
    wavePenalty -
    windPenalty;

  const marineScore =
    5 -
    weatherPenalty -
    rainPenalty -
    wavePenalty -
    windPenalty;

  const photoScore =
    5 -
    weatherPenalty -
    rainPenalty -
    photoWindPenalty;

  return {
    snorkel: clampScoreZero(snorkelScore),
    marine: clampScoreZero(marineScore),
    photo: clampScoreZero(photoScore),
  };
};

const getDisplayForecastScores = (scores, selectedForecastDay, guideSettings) => {
  const configuredScores = selectedForecastDay === 0
    ? guideSettings?.todayScores
    : selectedForecastDay === 1
      ? guideSettings?.tomorrowScores
      : selectedForecastDay === 2
        ? guideSettings?.dayAfterTomorrowScores
        : null;
  if (configuredScores) {
    return Object.fromEntries(
      Object.entries(scores).map(([key, calculated]) => [
        key,
        Number.isInteger(configuredScores[key]) && configuredScores[key] >= 0
          ? configuredScores[key]
          : calculated,
      ]),
    );
  }

  return scores;
};

const getScoreStatusType = (score) =>
  score === 0 ? "bad" : score <= 3 ? "caution" : "good";

const getConditionBestTimes = (labels, scores, marine, windItems, selectedDay, guideSettings) => {
  const base = labels.bestTimes || t.JP.bestTimes;
  const drone = base[0];
  const snorkel = base[1];
  const isPhotoPoor = scores.photo <= 3;
  const isSnorkelPoor = scores.snorkel <= 3;
  const isMarinePoor = scores.marine <= 3;
  const wave = getNumericValue(marine?.wave);
  const maxWind = Math.max(0, ...(windItems || []).map((item) => getNumericValue(item.speed)));
  const maxGust = Math.max(0, ...(windItems || []).map((item) => getNumericValue(item.gust)));
  const hasWaveOrWindImpact = wave >= 0.8 || maxWind >= 5 || maxGust >= 8;

  const automaticItems = [
    {
      key: "drone",
      title: drone[0],
      time: isPhotoPoor ? "" : drone[1],
      description: isPhotoPoor ? labels.bestTimePhotoBad : drone[2],
      caution: isPhotoPoor,
      type: getScoreStatusType(scores.photo),
    },
    {
      key: "snorkel",
      title: snorkel[0],
      time: isSnorkelPoor ? "" : snorkel[1],
      description: isSnorkelPoor
        ? (hasWaveOrWindImpact ? labels.bestTimeSnorkelMorning : labels.bestTimeSnorkelBad)
        : snorkel[2],
      caution: isSnorkelPoor,
      type: getScoreStatusType(scores.snorkel),
    },
    {
      key: "marine",
      title: labels.scoreLabels?.marine || t.JP.scoreLabels.marine,
      time: "",
      description: isMarinePoor ? labels.bestTimeMarineBad : labels.bestTimeMarineGood,
      caution: isMarinePoor,
      type: getScoreStatusType(scores.marine),
    },
  ];
  if (selectedDay > 2) return automaticItems;
  const day = selectedDay === 2
    ? "dayAfterTomorrow"
    : selectedDay === 1
      ? "tomorrow"
      : "today";
  const fieldNames = { drone: "Drone", snorkel: "Snorkel", marine: "Marine" };
  return automaticItems.map((item) => {
    const rating = guideSettings?.[`${day}BestTimeRatings`]?.[item.key];
    const type = rating && rating !== "auto" ? rating : item.type;
    return {
      ...item,
      type,
      caution: ["caution", "bad"].includes(type),
      time: getOceanGuideAdminText(guideSettings, `${day}${fieldNames[item.key]}Time`, labels.lang) || item.time,
      description: getOceanGuideAdminText(guideSettings, `${day}${fieldNames[item.key]}Text`, labels.lang) || item.description,
    };
  });
};

const scoreIcons = {
  snorkel: "🤿",
  marine: "☊",
  photo: "📷",
  dronePhoto: "🚁",
  underwaterPhoto: "🐠",
  groundPhoto: "📷",
  starPhoto: "✩",
  sunsetPhoto: "☀",
  beginner: "🔰",
  turtle: "🐢",
  crowd: "👥",
};

const SpotScoreList = ({
  spot,
  labels,
  compact = false,
  scores: scoreOverride,
  variant,
}) => {
  const scores = scoreOverride || getSpotScores(spot);
  const scoreLabels = labels.scoreLabels || t.JP.scoreLabels;
  const items =
    variant === "snorkelPhoto"
      ? [
          ["snorkel", scoreIcons.snorkel],
          ["dronePhoto", scoreIcons.dronePhoto],
          ["underwaterPhoto", scoreIcons.underwaterPhoto],
        ]
      : variant === "beginnerTurtle"
        ? [
            ["beginner", scoreIcons.beginner],
            ["turtle", scoreIcons.turtle],
            ["crowd", scoreIcons.crowd],
          ]
        : variant === "photogenic"
          ? [
              ["underwaterPhoto", scoreIcons.underwaterPhoto],
              ["groundPhoto", scoreIcons.groundPhoto],
              ["dronePhoto", scoreIcons.dronePhoto],
              ["starPhoto", scoreIcons.starPhoto],
              ["sunsetPhoto", scoreIcons.sunsetPhoto],
            ]
          : [
              ["snorkel", scoreIcons.snorkel],
              ["marine", scoreIcons.marine],
              ["photo", scoreIcons.photo],
            ];

  return (
    <div
      className={`${s.activityScores} ${compact ? s.compactScores : ""}`}
    >
      {items.map(([key, icon]) => (
        <div key={key}>
          <span className={s.scoreIcon}>{icon}</span>
          <span className={s.scoreLabel}>{scoreLabels[key]}</span>
          {key === "crowd" ? (
            <CrowdIndicator
              score={scores[key]}
              label={getCrowdScoreText(scores[key], labels)}
            />
          ) : spot[key] === "prohibited" ? (
            <span className={s.scoreProhibited}>×</span>
          ) : (
            <Stars value={scores[key]} />
          )}
        </div>
      ))}
    </div>
  );
};

const MoonPhaseIcon = ({ phase, size = 64 }) => {
  const r = size / 2 - 2;
  const c = size / 2;
  const path = getMoonPhasePath(phase, size);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className={s.moonPhaseIcon}
      aria-hidden="true"
    >
      <circle cx={c} cy={c} r={r} className={s.moonPhaseDark} />
      <path d={path} className={s.moonPhaseLight} />
      <circle cx={c} cy={c} r={r} className={s.moonPhaseOutline} fill="none" />
    </svg>
  );
};

const StarMoonGuide = ({ labels, moonData, weatherForecast, selectedDay = 0 }) => {
  const starLabels = labels.starSpotLabels || t.JP.starSpotLabels;
  const monthlyMoonDisplay = useMemo(
    () => getMonthlyMoonDisplay(labels.lang),
    [labels.lang],
  );
  const daySunset =
    selectedDay === 0
      ? weatherForecast?.current?.sunset
      : weatherForecast?.dailyDetails?.[selectedDay]?.sunset;

  return (
    <section className={s.starMoonGuide}>
      <div className={s.moonSnapshotGrid}>
        <article className={s.moonVisualArticle}>
          <MoonPhaseIcon phase={moonData.phase} size={64} />
          <div>
            <span>{starLabels.moonAgeLabel}</span>
            <strong>{moonData.ageDays}</strong>
          </div>
        </article>
        <div className={s.moonSnapshotDetails}>
          <article>
            <span>{starLabels.sunsetLabel}</span>
            <strong>{daySunset || "--:--"}</strong>
          </article>
          <article>
            <span>{starLabels.moonriseLabel}</span>
            <strong>{moonData.moonrise || "--:--"}</strong>
          </article>
          <article>
            <span>{starLabels.moonsetLabel}</span>
            <strong>{moonData.moonset || "--:--"}</strong>
          </article>
          <article>
            <span>{starLabels.milkyWayDirectionLabel}</span>
            <strong>{starLabels.milkyWayDirectionTonight}</strong>
          </article>
          <article>
            <span>{starLabels.cloudFactorLabel}</span>
            <strong>
              {Number.isFinite(moonData.tonightRating.nightCloudCover)
                ? `${moonData.tonightRating.nightCloudCover}%`
                : "--%"}
            </strong>
          </article>
          <article>
            <span>{starLabels.milkyWayVisibleLabel}</span>
            <strong>
              <StatusMark status={moonData.tonightRating.type} />
              {moonData.tonightRating.status}
            </strong>
          </article>
        </div>
      </div>

      <div className={s.tonightRating}>
        <div className={s.tonightRatingHeader}>
          <span>{starLabels.tonightRatingTitle}</span>
          <span className={s.tonightRatingBadge}>
            <StatusMark status={moonData.tonightRating.type} />
            <strong>{moonData.tonightRating.status}</strong>
          </span>
        </div>
        <div className={s.tonightRatingFactors}>
          <span>
            {starLabels.weatherFactorLabel}：
            {weatherIcons[moonData.tonightRating.weather.text] || "☁"}{" "}
            {translateWeatherLabel(moonData.tonightRating.weather.text, labels.lang)}
          </span>
          <span>
            {starLabels.cloudFactorLabel}：
            {Number.isFinite(moonData.tonightRating.nightCloudCover)
              ? `${moonData.tonightRating.nightCloudCover}%（${moonData.tonightRating.cloud?.label}）`
              : "--%"}
          </span>
          <span>
            {starLabels.windFactorLabel}：{moonData.tonightRating.wind.label}
          </span>
          <span>
            {starLabels.moonFactorLabel}：{moonData.tonightRating.moon.label}
          </span>
        </div>
      </div>
      <p className={s.moonGuideRecommend}>{moonData.recommendation}</p>
      {moonData.meteorShower && (
        <aside className={s.meteorShowerNotice} aria-label={moonData.meteorShower.title}>
          <span>🌠 METEOR SHOWER</span>
          <strong>{moonData.meteorShower.title}</strong>
          {moonData.meteorShower.detail && <p>{moonData.meteorShower.detail}</p>}
        </aside>
      )}

      <div className={s.moonMonthlySection}>
        <span className={s.moonMonthlyTitle}>{starLabels.monthlyInfoTitle}</span>
        <div className={s.moonMonthlyGrid}>
          <article>
            <span>{starLabels.newMoonTitle}</span>
            <strong>{monthlyMoonDisplay.newMoonDate}</strong>
          </article>
          <article>
            <span>{starLabels.monthlyBestTitle}</span>
            <strong>{monthlyMoonDisplay.bestDate}</strong>
          </article>
        </div>
        <p>{starLabels.moonNote}</p>
      </div>

      <div className={s.starConditions}>
        <span>{starLabels.starConditionsTitle}</span>
        <ul>
          {starLabels.starConditionsItems.map(([title, desc]) => (
            <li key={title}>
              <strong>{title}</strong>
              <p>{desc}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const getSpotYoutubeId = (spot) => spot?.youtubeId;

const SpotVideoButton = ({ spot, onPlayVideo, inlineMobile = false }) => {
  const youtubeId = getSpotYoutubeId(spot);
  if (!onPlayVideo || !youtubeId) return null;

  return (
    <button
      type="button"
      className={`${s.spotVideoButton} ${inlineMobile ? s.spotVideoButtonInlineMobile : ""}`}
      onClick={() => {
        trackEvent("video_play_click", { spot_name: spot?.name });
        onPlayVideo(youtubeId, spot);
      }}
      aria-label={`${spot?.name || "スポット"}の動画を再生`}
    >
      <span className={s.spotVideoIcon}>▶</span>
      <span className={s.spotVideoLabel}>動画で見る</span>
    </button>
  );
};

const OceanGuideVideoModal = ({ videoId, onClose }) => {
  if (!videoId) return null;

  return (
    <div className={s.videoModal} role="dialog" aria-modal="true" aria-label="Ocean Guide video">
      <button type="button" className={s.videoModalBackdrop} onClick={onClose} aria-label="動画を閉じる" />
      <div className={s.videoModalInner}>
        <button type="button" className={s.videoModalClose} onClick={onClose} aria-label="動画を閉じる">
          ×
        </button>
        <iframe
          title="Ocean Guide spot movie"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&rel=0&modestbranding=1&playsinline=1&vq=hd1080`}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
};

const StarSpotCard = ({ spot, labels, onOpen, moonData, onPlayVideo }) => {
  const starLabels = labels.starSpotLabels || t.JP.starSpotLabels;
  const hasVideo = Boolean(onPlayVideo && spot?.youtubeId);

  return (
    <article className={`${s.spotCard} ${s.starSpotCard}`}>
      <div className={s.spotHeroWrap}>
        <button type="button" className={s.spotHero} onClick={() => onOpen(spot)}>
          <img src={spot.image} alt="" loading="lazy" />
          <div className={hasVideo ? s.spotHeroTextWithVideo : ""}>
            <h3>{localizeSpotName(spot, labels.lang)}</h3>
            <p>{localizeSpotDescription(spot, labels.lang)}</p>
          </div>
          <aside>
            <span>{starLabels.photoScore}</span>
            <Stars value={clampScore(spot.photo || 5)} />
          </aside>
        </button>
        <SpotVideoButton spot={spot} onPlayVideo={onPlayVideo} inlineMobile />
      </div>

      <div className={s.starSpotScores}>
        <div>
          <span>{starLabels.photoScore}</span>
          <Stars value={clampScore(spot.photo || 5)} />
        </div>
        <div>
          <span>{starLabels.milkyWayScore}</span>
          <Stars value={clampScore(spot.milkyWay || 5)} />
        </div>
        <div>
          <span>{starLabels.darknessScore}</span>
          <Stars value={clampScore(spot.darkness || 5)} />
        </div>
        <div>
          <span>{starLabels.driveTimeLabel}</span>
          <strong>
            {spot.i18n?.[labels.lang]?.driveTimeText || spot.driveTimeText ||
              (spot.driveMinutes
                ? labels.approxMinutesTemplate.replace("{minutes}", spot.driveMinutes)
                : "--")}
          </strong>
        </div>
      </div>
    </article>
  );
};

const SunsetCondition = ({ labels, weatherForecast, selectedDay = 0, guideSettings }) => {
  const sunsetLabels = labels.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  const dayWeather = weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast.current;
  const sunsetTime = dayWeather.sunset || weatherForecast.current.sunset;
  const bestTime = getSunsetBestTimeRange(sunsetTime) || sunsetLabels.defaultBestTime;
  const weatherText = getSunsetWeatherForDay(weatherForecast, selectedDay);
  const rating = getSunsetConditionForSpot(
    { sunsetScore: 5 },
    labels,
    weatherForecast,
    selectedDay,
    guideSettings,
  );
  const burnJudgment = getSunsetBurnJudgment(dayWeather, sunsetLabels);
  const manualBurnText = getOceanGuideAdminText(
    guideSettings,
    selectedDay === 1 ? "tomorrowSunsetText" : "todaySunsetText",
    labels.lang,
  );
  const burnLevel = manualBurnText ? "high" : burnJudgment?.level;

  return (
    <section className={s.sunsetCondition}>
      {(manualBurnText || (burnJudgment && burnJudgment.level !== "low")) && (
        <article
          className={`${s.sunsetBurnBanner} ${
            burnLevel
              ? s[`sunsetBurn${burnLevel.charAt(0).toUpperCase()}${burnLevel.slice(1)}`] || ""
              : ""
          }`}
        >
          <strong>{manualBurnText || burnJudgment.label}</strong>
        </article>
      )}
      <article>
        <span>{sunsetLabels.bestTime}</span>
        <strong>{bestTime}</strong>
      </article>
      <article>
        <span>{sunsetLabels.weatherLabel}</span>
        <strong>{translateWeatherLabel(weatherText, labels.lang)}</strong>
      </article>
      <article className={s.sunsetRatingArticle}>
        <span>{sunsetLabels.ratingLabel}</span>
        <span className={s.sunsetRatingBadge}>
          <StatusMark status={rating.type} />
          <strong>{rating.status}</strong>
        </span>
      </article>
    </section>
  );
};

const SnorkelCondition = ({
  labels,
  dailyMarineForecast,
  oceanCondition,
  typhoonInfo,
  hourlyWindForecast,
  weatherForecast,
  selectedDay = 0,
  onOpenMap,
  onOpenRainForecast,
  dataUpdatedAt,
  guideSettings,
  spotOverrides,
}) => {
  const dayLabel = selectedDay === 1 ? labels.dayToggleTomorrow : labels.dayToggleToday;
  const dayDate = new Date();
  dayDate.setDate(dayDate.getDate() + selectedDay);
  const dayOceanCondition = getDayOceanCondition(selectedDay, oceanCondition, weatherForecast, hourlyWindForecast);
  const dayMarineForecast = dailyMarineForecast.days?.[selectedDay] || dailyMarineForecast;
  const dayWeather = weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current;
  const rainProbability = getDaytimeRainProbability(dayWeather);
  const hasHighRainProbability = rainProbability >= 50;
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, selectedDay);
  const rating = getSnorkelCondition(dayMarineForecast, dayOceanCondition, labels, maxWindSpeed);
  const manualRating = getManualOceanRating(selectedDay, labels, guideSettings);
  const displayRatingType = manualRating?.type || rating.type;
  const displayRatingStatus = manualRating?.status || rating.status;
  const manualClarity = getManualOceanClarity(selectedDay, labels, guideSettings);
  const displayClarityType = manualClarity?.type || (
    hasHighRainProbability ? "caution" : displayRatingType
  );
  const manualAssessmentText = getOceanGuideAdminText(
    guideSettings,
    selectedDay === 1 ? "tomorrowAssessmentText" : "todayAssessmentText",
    labels.lang,
  );
  const assessmentForSelectedDay = (text = "") => (
    labels.lang === "JP" && selectedDay === 1 ? text.replaceAll("今日", "明日") : text
  );
  const todayClarity = manualClarity?.label || (hasHighRainProbability ? labels.claritySlightlyLow : (
    isRainyWeather(dayOceanCondition.weather) ||
    rating.type === "bad" ||
    rating.type === "caution"
      ? labels.clarityLow
      : labels.clarityHigh
  ));
  const tideHigh = dayMarineForecast.tides?.high || [];
  const tideLow = dayMarineForecast.tides?.low || [];
  const windItems = hourlyWindForecast?.days?.[selectedDay]?.items || hourlyWindForecast?.items || [];
  const hourlyWeather = dayWeather?.hourlyWeather || [];
  const daytimeWaves = dayMarineForecast.daytimeWaves || [];
  const daytimeSwells = dayMarineForecast.daytimeSwells || [];
  const hourlyTimes = [
    ...new Set([
      ...hourlyWeather.map((item) => item.time),
      ...daytimeWaves.map((item) => item.time),
      ...daytimeSwells.map((item) => item.time),
      ...windItems.map((item) => item.time),
    ]),
  ];

  return (
    <section className={s.snorkelCondition}>
      <h3 className={s.snorkelConditionTitle}>{labels.snorkelConditionCardTitle.replace("{day}", dayLabel)}</h3>
      <article className={s.snorkelWeatherArticle}>
        <span className={s.snorkelWeatherIcon} aria-hidden="true">
          {weatherIcons[dayWeather?.weather] || "☁"}
        </span>
        <div>
          <span>
            {formatAppUpdatedDateTime(labels.lang, dataUpdatedAt)}
          </span>
          <h4>{translateWeatherLabel(dayWeather?.weather, labels.lang)}</h4>
          <strong>
            {labels.highTempLabel} {dayWeather?.high ?? "--"}℃ / {labels.lowTempLabel} {dayWeather?.low ?? "--"}℃
          </strong>
        </div>
      </article>
      <article className={s.snorkelRatingArticle}>
        <span>{labels.snorkelConditionTitle.replace("{day}", dayLabel)}</span>
        <span className={s.snorkelRatingBadge}>
          <StatusMark status={displayRatingType} />
          <strong>{displayRatingStatus}</strong>
        </span>
      </article>
      <article className={s.snorkelRatingArticle}>
        <span>{labels.oceanClarityLabel}</span>
        <span className={s.snorkelRatingBadge}>
          <StatusMark status={displayClarityType} />
          <strong>{todayClarity}</strong>
        </span>
      </article>
      <p className={s.snorkelAssessment}>
        {manualAssessmentText ? manualAssessmentText : labels.lang === "JP" && displayRatingType === "caution" ? (
          <>
            {assessmentForSelectedDay(labels.overallCautionBeforeMap)}
            <button
              type="button"
              onClick={() => {
                trackEvent("condition_caution_map_click", {});
                onOpenMap();
              }}
            >
              マップ
            </button>
            {assessmentForSelectedDay(labels.overallCautionAfterMap)}
          </>
        ) : assessmentForSelectedDay(labels.overallAssessment[displayRatingType])}
      </p>
      {selectedDay === 0 && hasHighRainProbability && (
        <button
          type="button"
          className={s.snorkelRainLink}
          onClick={() => {
            trackEvent("rain_forecast_link_click", {});
            onOpenRainForecast();
          }}
        >
          ☔ {labels.rainMovementLink} →
        </button>
      )}
      <article className={s.snorkelHourlyArticle}>
        <div className={s.snorkelHourlyScroll}>
          <div className={s.snorkelHourlyGrid}>
            <span className={s.snorkelHourlyRowLabel} aria-hidden="true" />
            {hourlyTimes.map((time) => (
              <span key={time} className={s.snorkelHourlyTimeLabel}>
                {time}
              </span>
            ))}

            <span className={s.snorkelHourlyRowLabel}>
              {labels.todayWeatherHourlyLabel}
            </span>
            {hourlyTimes.map((time) => {
              const item = hourlyWeather.find((w) => w.time === time);
              return (
                <span
                  key={time}
                  className={s.snorkelHourlyCell}
                  title={translateWeatherLabel(item?.weather, labels.lang)}
                >
                  {item
                    ? weatherIcons[item.weather] || translateWeatherLabel(item.weather, labels.lang)
                    : "–"}
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>
              {labels.todayWaveLabel}
            </span>
            {hourlyTimes.map((time) => {
              const item = daytimeWaves.find((w) => w.time === time);
              return (
                <span key={time} className={s.snorkelHourlyCell}>
                  {item ? item.wave : "–"}
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>{labels.swellLabel}</span>
            {hourlyTimes.map((time) => {
              const item = daytimeSwells.find((w) => w.time === time);
              return (
                <span key={time} className={s.snorkelHourlyCell}>
                  {item ? item.swell : "–"}
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>{labels.windLabel}</span>
            {hourlyTimes.map((time) => {
              const item = windItems.find((w) => w.time === time);
              return item ? (
                <span
                  key={time}
                  className={`${s.snorkelHourlyCell} ${s.snorkelHourlyWindCell} ${s[item.level] || ""}`}
                >
                  <strong>{item.direction} {item.speed}</strong>
                  <em>{item.icon}</em>
                </span>
              ) : (
                <span key={time} className={s.snorkelHourlyCell}>
                  –
                </span>
              );
            })}
          </div>
        </div>
        <div className={s.snorkelMeaningList}>
          <p className={s.snorkelMeaningNote}>{labels.waveMeaning}</p>
          <p className={s.snorkelMeaningNote}>{labels.swellMeaning}</p>
          <p className={s.snorkelMeaningNote}>{labels.hourlyWindNote}</p>
        </div>
      </article>

      {(tideHigh.length > 0 || tideLow.length > 0) && (
        <article className={s.snorkelWaveArticle}>
          <span>{labels.tideLabel}</span>
          <div className={s.snorkelChipList}>
            {tideHigh.map(([time, level]) => (
              <strong key={`high-${time}`}>
                {labels.tideHighShort} {time} <small>{level}</small>
              </strong>
            ))}
            {tideLow.map(([time, level]) => (
              <strong key={`low-${time}`}>
                {labels.tideLowShort} {time} <small>{level}</small>
              </strong>
            ))}
          </div>
        </article>
      )}
    </section>
  );
};

const YabijiPanel = ({ labels, guideSettings, selectedDay = 0 }) => (
  <section className={s.yabijiPanel}>
    <p className={s.yabijiNotice}>
      {getOceanGuideAdminText(
        guideSettings,
        selectedDay === 1 ? "tomorrowYabijiText" : "todayYabijiText",
        labels.lang,
      ) || labels.yabijiNotice}
    </p>

    <div className={s.yabijiTourPanel}>
      <span>{labels.yabijiTourTitle}</span>
      <p>{labels.yabijiTourLead}</p>
      <div className={s.yabijiTourGrid}>
        {yabijiTourAccounts.map((account) => (
          <div key={account.profileUrl} className={s.yabijiTourCard}>
            <a
              className={s.yabijiTourHandle}
              href={account.profileUrl}
              target="_blank"
              rel="noreferrer"
            >
              {getInstagramHandle(account.profileUrl)}
            </a>
            <div className={s.yabijiTourEmbed}>
              <InstagramEmbed
                url={account.postUrl}
                caption={getInstagramHandle(account.profileUrl)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const crowdLevelTypes = {
  crowdPrivate: "best",
  crowdQuiet: "best",
  crowdModerate: "caution",
  crowdBusy: "bad",
};

const crowdLevelStars = {
  crowdPrivate: 1,
  crowdQuiet: 1,
  crowdModerate: 3,
  crowdBusy: 5,
};

const getCrowdScoreText = (score, labels) => {
  if (score >= 5) return labels.crowdScoreVeryBusy;
  if (score >= 3) return labels.crowdScoreSlightlyBusy;
  if (score >= 2) return labels.crowdScoreFew;
  return labels.crowdScorePrivate;
};

const getCrowdScoreLevel = (score) => {
  if (score >= 5) return "crowdVeryBusy";
  if (score >= 3) return "crowdSlightlyBusy";
  if (score >= 2) return "crowdFew";
  return "crowdPrivate";
};

const CrowdIndicator = ({ score, label }) => {
  const level = getCrowdScoreLevel(score);
  const filledCount = Math.max(1, Math.min(5, Math.ceil(score)));

  return (
    <span className={`${s.crowdIndicator} ${s[level]}`}>
      <span className={s.crowdPeople} aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <span
            key={index}
            className={index < filledCount ? s.filledCrowdPerson : ""}
          />
        ))}
      </span>
      <span>
        {label} {filledCount}/5
      </span>
    </span>
  );
};

const SunsetSpotCard = ({ spot, labels, onOpen, weatherForecast, selectedDay = 0, onPlayVideo, guideSettings }) => {
  const sunsetLabels = labels.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  const crowdKey = spot.crowdLevel || "crowdModerate";
  const hasVideo = Boolean(onPlayVideo && spot?.youtubeId);
  const sunsetCondition = getSunsetConditionForSpot(
    spot,
    labels,
    weatherForecast,
    selectedDay,
    guideSettings,
  );

  return (
    <article className={`${s.spotCard} ${s.starSpotCard}`}>
      <div className={s.spotHeroWrap}>
        <button type="button" className={s.spotHero} onClick={() => onOpen(spot)}>
          <img src={spot.image} alt="" loading="lazy" />
          <div className={hasVideo ? s.spotHeroTextWithVideo : ""}>
            <h3>{localizeSpotName(spot, labels.lang)}</h3>
            <p>{localizeSpotDescription(spot, labels.lang)}</p>
          </div>
          <aside>
            <span>{sunsetLabels.sunsetScore}</span>
            <Stars value={sunsetCondition.score} />
          </aside>
        </button>
        <SpotVideoButton spot={spot} onPlayVideo={onPlayVideo} inlineMobile />
      </div>

      <div className={s.starSpotScores}>
        <div>
          <span>{sunsetLabels.sunsetScore}</span>
          <Stars value={sunsetCondition.score} />
        </div>
        <div>
          <span>{sunsetLabels.weatherLabel}</span>
          <strong className={s[sunsetCondition.type]}>
            {translateWeatherLabel(sunsetCondition.weatherText, labels.lang)}
          </strong>
        </div>
        <div>
          <span>{sunsetLabels.crowdLabel}</span>
          <strong className={s[crowdLevelTypes[crowdKey]]}>
            {sunsetLabels[crowdKey]}
          </strong>
        </div>
      </div>
    </article>
  );
};

const voiceMapCopy = {
  JP: { write: "今の様子を書く", post: "ひとことを投稿", open: "クリックして投稿を見る・書く →", title: "宮古島の海、いまどんな感じ？", lead: "吹き出しをタップして現地の声をチェック。今そこにいたら、あなたの一言も教えてください！", hint: "💬 気になる声をタップ" },
  EN: { write: "Share an update", post: "Post an update", open: "Click to read or post →", title: "What is Miyakojima's ocean like now?", lead: "Tap a bubble for local updates. If you are there, tell everyone what you see!", hint: "💬 Tap an update" },
  中文: { write: "分享现场情况", post: "发布一条动态", open: "点击查看或发布 →", title: "宫古岛的海现在怎么样？", lead: "点击气泡查看现场动态。如果你正在那里，也请分享一下！", hint: "💬 点击感兴趣的动态" },
  한국어: { write: "현재 상황 쓰기", post: "현장 소식 올리기", open: "클릭해서 읽고 작성하기 →", title: "미야코지마 바다, 지금 어때요?", lead: "말풍선을 눌러 현장 소식을 확인하세요. 지금 그곳에 있다면 한마디 남겨 주세요!", hint: "💬 궁금한 소식 누르기" },
};

const MapSpotOverlay = ({ labels, spot, oceanCondition, wave, isSelected, onSelect }) => {
  const isFoodSpot = spot.kind === "food";
  const isHotelSpot = spot.kind === "hotel";
  const isPlaceSpot = isFoodSpot || isHotelSpot;
  const spotName = isFoodSpot ? localizeFoodName(spot, labels.lang) : localizeSpotName(spot, labels.lang);
  const spotArea = localizeFoodArea(spot.area, labels.lang);
  const voiceCopy = voiceMapCopy[labels.lang] || voiceMapCopy.EN;

  if (spot.isVoiceSpot) {
    return (
      <OverlayViewF
        position={spot.location}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({ x: -72, y: -34 })}
      >
        <button
          type="button"
          className={`${s.mapVoiceBubble} ${spot.voiceComment ? s.mapVoiceBubblePosted : s.mapVoiceBubbleEmpty} ${isSelected ? s.mapVoiceBubbleSelected : ""}`}
          onClick={() => onSelect(spot)}
          aria-label={`${spotName} ${spot.voiceComment?.text || voiceCopy.post}`}
        >
          <span aria-hidden="true">{spot.voiceComment ? "💬" : "＋"}</span>
          <span>
            <strong>{spotName}</strong>
            <small>{spot.voiceComment?.text || voiceCopy.write}</small>
          </span>
          {spot.voiceComment?.text && (
            <span className={s.mapVoicePreview} aria-hidden="true">
              <b>{spotName}</b>
              <span className={s.mapVoicePreviewMessages}>
                {spot.voiceComments.map((item, index) => (
                  <span className={s.mapVoicePreviewMessage} key={item.id || `${item.text}-${index}`}>
                    <em>{item.text.length > 38 ? `${item.text.slice(0, 38)}…` : item.text}</em>
                  </span>
                ))}
              </span>
              <small>{voiceCopy.open}</small>
            </span>
          )}
        </button>
      </OverlayViewF>
    );
  }

  if (isHotelSpot) {
    return (
      <OverlayViewF
        position={spot.location}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() => ({
          x: -12,
          y: -12,
        })}
      >
        <div className={s.mapSpotWrap}>
          <button
            type="button"
            className={`${s.mapSpot} ${s.hotelNameOnly} ${isSelected ? s.mapSpotSelected : ""}`}
            aria-label={`${spot.name} ${spot.area}`}
            onClick={() => onSelect(spot)}
          >
            <strong>{spot.name}</strong>
          </button>
        </div>
      </OverlayViewF>
    );
  }

  return (
  <OverlayViewF
    position={spot.location}
    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    getPixelPositionOffset={() => ({
      x: -12,
      y: -12,
    })}
  >
    <div className={s.mapSpotWrap}>
      <button
        type="button"
        className={`${s.mapSpotPoint} ${s[spot.type]} ${isSelected ? s.mapSpotPointSelected : ""}`}
        aria-label={labels.mapSelectAriaLabel.replace(
          "{name}",
          spotName,
        )}
        onClick={() => onSelect(spot)}
      />
      <button
        type="button"
        className={`${s.mapSpot} ${s[spot.type]} ${isSelected ? s.mapSpotSelected : ""}`}
        aria-label={
          isFoodSpot
            ? `${spotName} ${localizeFoodGenre(spot, labels.lang) || localizeFoodField(spot.status, labels.lang)} ${spotArea}`
            : isHotelSpot
              ? `${spot.name} ${spot.genre} ${spot.area}`
            : `${localizeSpotName(spot, labels.lang)} ${spot.status} ${labels.todayWaveLabel} ${wave} ${labels.weatherLabel} ${translateWeatherLabel(oceanCondition.weather, labels.lang)} ${labels.windLabel} ${oceanCondition.windDirection} ${oceanCondition.windSpeed}`
        }
        onClick={() => onSelect(spot)}
      >
        <StatusMark status={spot.type} />
        <strong>{spotName}</strong>
        <span className={s.mapSpotStatus}>
          {isFoodSpot ? localizeFoodField(spot.status, labels.lang) : spot.status}
        </span>
        <dl className={s.mapSpotDetails}>
          {isPlaceSpot ? (
            <>
              <div>
                <dt>{labels.mapAreaLabel}</dt>
                <dd>{spotArea}</dd>
              </div>
              <div>
                <dt>{isHotelSpot ? labels.priceRangeLabel : labels.mapRecommendLabel}</dt>
                <dd>
                  {isHotelSpot
                    ? spot.priceRange || spot.genre
                    : localizeFoodMenuFood(spot, labels.lang) || localizeFoodGenre(spot, labels.lang)}
                </dd>
              </div>
              <div>
                <dt>{isHotelSpot ? labels.hotelViewLabel : spot.phone ? labels.mapPhoneLabel : labels.mapHoursLabel}</dt>
                <dd>{isHotelSpot ? spot.oceanView : spot.phone || localizeFoodField(spot.hours, labels.lang)}</dd>
              </div>
            </>
          ) : (
            <>
              <div>
                <dt>{labels.todayWaveLabel}</dt>
                <dd>{wave}</dd>
              </div>
              <div>
                <dt>{labels.weatherLabel}</dt>
                <dd>{translateWeatherLabel(oceanCondition.weather, labels.lang)}</dd>
              </div>
              <div>
                <dt>{labels.windLabel}</dt>
                <dd>
                  {oceanCondition.windDirection} {oceanCondition.windSpeed}
                </dd>
              </div>
            </>
          )}
        </dl>
      </button>
    </div>
  </OverlayViewF>
  );
};

const getCrowdFilterKey = (crowd = "") => {
  if (crowd.includes("予約不要") || crowd.includes("入りやすい")) return "easy";
  if (crowd.includes("予約")) return "reservation";
  if (crowd.includes("混雑")) return "busy";
  return "all";
};

const foodCrowdFilterOptions = [
  ["all", "•"],
  ["easy", "○"],
  ["reservation", "📅"],
];

const getSpotAreaLabel = (spot) => {
  const name = spot?.name || "";
  const query = spot?.mapQuery || "";
  const text = `${name} ${query}`;

  if (/17END|中の島|通り池|帯岩|白鳥崎|下地島/.test(text)) return "下地島";
  if (/伊良部|サバウツガー|渡口/.test(text)) return "伊良部島";
  if (/来間|長間浜|ムスヌン浜|パチャビーチ/.test(text)) return "来間島";
  if (/池間|フナクス|イキヅー/.test(text)) return "池間島";

  return "宮古島";
};

const getSpotDriveMinutes = (spot) => {
  if (spot?.driveMinutes) return spot.driveMinutes;

  const mapSpot = mapSpots.find((item) => item.name === spot?.name);
  if (!mapSpot?.location) return null;

  const distance = getDistanceKm(MIYAKO_MAP_CENTER, mapSpot.location);
  const minutes = Math.round((distance / 35) * 60 / 5) * 5;
  return Math.max(5, minutes);
};

const foodShopCardArticle = { mediaLayout: "split" };

const PlacePageLink = ({ spot, lang }) => {
  const href = getOceanPlacePath(spot?.name);
  if (!href) return null;
  const text = {
    JP: "写真・基本情報を詳細ページで見る",
    EN: "View place guide (Japanese)",
    中文: "查看地点详情（日语）",
    한국어: "상세 페이지 보기 (일본어)",
  }[lang] || "写真・基本情報を詳細ページで見る";
  return <a className={s.placePageLink} href={href} onClick={() => trackEvent("place_detail_click", {
    spot_name: spot.name, destination: href, placement: "ocean_guide_map",
  })}>{text} →</a>;
};

const FoodMapShopCard = ({ spot, labels }) => {
  const localizedFood = localizeFoodAsShop(spot, labels);

  return (
    <div className={blogArticleStyles.root}>
      <ShopCard
        shop={localizedFood}
        article={foodShopCardArticle}
        lang={labels.lang}
        stackedMedia
      />
    </div>
  );
};

const HotelMapDetailCard = ({ spot, labels }) => {
  const localizedHotel = localizeShop(spot, labels.lang);

  const hotelGuideUrl = "/ocean-guide/hotels";

  return (
    <div className={s.hotelMapEmbeddedCard}>
      <div className={blogArticleStyles.root}>
        <ShopCard
          shop={localizedHotel}
          article={hotelGuideArticle}
          lang={labels.lang}
          stackedMedia
        />
      </div>

      <div className={s.hotelGuideEmbedBlock}>
        <a href={hotelGuideUrl} className={s.hotelGuideEmbedLabel} onClick={() => trackEvent("hotel_guide_open", { placement: "ocean_guide_map", hotel_name: spot.name })}>
          {labels.hotelGuideEmbedLabel}
          <span aria-hidden="true"> →</span>
        </a>
      </div>
    </div>
  );
};

const statusMarkIcons = {
  caution: "△",
  best: "◎",
  bad: "×",
  food: "🍴",
  hotel: "🏨",
};

const StatusMark = ({ status }) => (
  <span className={`${s.statusMark} ${s[status]}`}>
    {statusMarkIcons[status] || "○"}
  </span>
);

const BeachLoungeStatusMark = ({ status }) => (
  <span className={`${s.statusMark} ${s[status]}`}>
    {status === "bad" ? "×" : status === "caution" ? "△" : "○"}
  </span>
);

const spotRatingStatusKey = {
  best: "snorkelConditionBest",
  good: "snorkelConditionGood",
  caution: "snorkelConditionCaution",
  bad: "snorkelConditionBad",
};

const spotCommentCopy = {
  JP: {
    title: "この場所の今を投稿",
    lead: "現地にいる人の情報が、次に行く人の参考になります",
    date: "日付",
    time: "時間",
    crowd: "混み具合",
    beach: "今日のビーチルおすすめ度",
    snorkel: "今日のシュノーケルおすすめ度",
    wind: "風の強さ",
    wave: "波の強さ",
    comment: "一言",
    name: "名前（任意）",
    placeholder: "例：空いていて、風も弱く穏やかです",
    submit: "この場所の情報を投稿",
    submitting: "投稿中...",
    anonymous: "匿名",
    empty: "まだ投稿はありません。現地の様子を教えてください。",
    sent: "投稿しました",
    cooldown: "少し時間をあけてから投稿してください",
    error: "投稿できませんでした。時間をおいてお試しください。",
  },
  EN: {
    title: "Share an update",
    lead: "Your local update helps the next visitor",
    date: "Date",
    time: "Time",
    crowd: "Crowd",
    beach: "Beach rating",
    snorkel: "Snorkeling rating",
    wind: "Wind strength",
    wave: "Wave strength",
    comment: "Comment",
    name: "Name (optional)",
    placeholder: "Example: Light wind and calm water",
    submit: "Post",
    submitting: "Posting...",
    anonymous: "Anonymous",
    empty: "No updates yet. Share what it is like there.",
    sent: "Posted",
    cooldown: "Please wait a moment before posting again.",
    error: "Could not post. Please try again later.",
  },
  中文: {
    title: "分享这里的最新情况",
    lead: "你的现场信息可以帮助下一位游客",
    date: "日期", time: "时间", crowd: "拥挤程度",
    beach: "今日海滩休闲推荐度", snorkel: "今日浮潜推荐度",
    wind: "风力", wave: "浪况", comment: "留言", name: "名字（选填）",
    placeholder: "例如：人不多，风小，海面平静",
    submit: "发布这里的信息", submitting: "发布中...", anonymous: "匿名",
    empty: "还没有留言，欢迎分享现场情况。", sent: "已发布",
    cooldown: "请稍等片刻再发布。", error: "发布失败，请稍后再试。",
  },
  한국어: {
    title: "이 장소의 현재 상황 공유",
    lead: "현장 정보가 다음 방문자에게 도움이 됩니다",
    date: "날짜", time: "시간", crowd: "혼잡도",
    beach: "오늘의 해변 추천도", snorkel: "오늘의 스노클링 추천도",
    wind: "바람 세기", wave: "파도 세기", comment: "한마디", name: "이름(선택)",
    placeholder: "예: 한적하고 바람이 약해 바다가 잔잔해요",
    submit: "이 장소 정보 올리기", submitting: "게시 중...", anonymous: "익명",
    empty: "아직 게시물이 없습니다. 현장 상황을 알려주세요.", sent: "게시했습니다",
    cooldown: "잠시 후 다시 게시해 주세요.", error: "게시하지 못했습니다. 잠시 후 다시 시도해 주세요.",
  },
};

const spotCommentCrowdOptions = [
  ["quiet", "🟢", { JP: "空いている", EN: "Quiet", 中文: "空闲", 한국어: "한적함" }],
  ["normal", "🟡", { JP: "普通", EN: "Moderate", 中文: "一般", 한국어: "보통" }],
  ["busy", "🔴", { JP: "混雑", EN: "Busy", 中文: "拥挤", 한국어: "혼잡" }],
];

const spotCommentRatingOptions = [
  ["best", "◎", { JP: "おすすめ", EN: "Recommended", 中文: "推荐", 한국어: "추천" }],
  ["good", "○", { JP: "良い", EN: "Good", 中文: "不错", 한국어: "좋음" }],
  ["caution", "△", { JP: "注意", EN: "Caution", 中文: "注意", 한국어: "주의" }],
  ["bad", "×", { JP: "おすすめしない", EN: "Not advised", 中文: "不推荐", 한국어: "비추천" }],
];

const spotCommentStrengthOptions = [
  [1, "1", { JP: "ほぼなし", EN: "None", 中文: "几乎没有", 한국어: "거의 없음" }],
  [2, "2", { JP: "弱い", EN: "Light", 中文: "较弱", 한국어: "약함" }],
  [3, "3", { JP: "普通", EN: "Moderate", 中文: "一般", 한국어: "보통" }],
  [4, "4", { JP: "強い", EN: "Strong", 中文: "较强", 한국어: "강함" }],
  [5, "5", { JP: "とても強い", EN: "Very strong", 中文: "很强", 한국어: "매우 강함" }],
];

const getJstInputParts = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const value = (type) => parts.find((item) => item.type === type)?.value || "";
  return {
    date: `${value("year")}-${value("month")}-${value("day")}`,
    time: `${value("hour")}:${value("minute")}`,
  };
};

const formatSpotCommentDateTime = (date, time, lang) => {
  if (!date) return time || "";
  if (lang !== "JP") return `${date.replaceAll("-", "/")} ${time || ""}`.trim();
  const [, month, day] = date.split("-").map(Number);
  return `${month}月${day}日 ${time || ""}`.trim();
};

const getSpotCommentTime = (comment) => {
  if (comment.observedDate) {
    const observedAt = Date.parse(`${comment.observedDate}T${comment.observedTime || "00:00"}:00+09:00`);
    if (Number.isFinite(observedAt)) return observedAt;
  }
  return comment.createdAt?.toMillis?.() || 0;
};

const sortSpotCommentsOldestFirst = (comments) => [...comments].sort(
  (left, right) => getSpotCommentTime(left) - getSpotCommentTime(right),
);

const SpotCommentChoices = ({ label, value, onChange, options, lang }) => (
  <fieldset className={`${s.spotCommentChoices} ${options.length === 5 ? s.spotCommentStrengthChoices : ""}`}>
    <legend>{label}</legend>
    <div>
      {options.map(([key, icon, text]) => (
        <button
          key={key}
          type="button"
          className={value === key ? s.activeSpotCommentChoice : ""}
          onClick={() => onChange(value === key ? (typeof key === "number" ? 0 : "none") : key)}
          aria-pressed={value === key}
        >
          <span>{icon}</span>
          <small>{text[lang] || text.EN}</small>
        </button>
      ))}
    </div>
  </fieldset>
);

const getSpotCommentClientId = () => {
  const storageKey = "oceanGuideSpotCommentClientId";
  const existing = window.localStorage.getItem(storageKey);
  if (existing) return existing;
  const id = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(storageKey, id);
  return id;
};

const SpotComments = ({ spot, labels }) => {
  const copy = spotCommentCopy[labels.lang] || spotCommentCopy.EN;
  const initialDateTime = useMemo(getJstInputParts, []);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [observedDate, setObservedDate] = useState(initialDateTime.date);
  const [observedTime, setObservedTime] = useState(initialDateTime.time);
  const [crowd, setCrowd] = useState("none");
  const [beachRating, setBeachRating] = useState("none");
  const [snorkelRating, setSnorkelRating] = useState("none");
  const [windStrength, setWindStrength] = useState(0);
  const [waveStrength, setWaveStrength] = useState(0);
  const [website, setWebsite] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const spotId = getSpotOverrideId(spot.name);

  useEffect(() => {
    const commentsQuery = query(
      collection(db, "oceanGuideSpotComments", spotId, "comments"),
      orderBy("createdAt", "desc"),
      limit(20),
    );
    return onSnapshot(commentsQuery, (snapshot) => {
      setComments(sortSpotCommentsOldestFirst(
        snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
      ));
    }, () => setComments([]));
  }, [spotId]);

  const submitComment = async (event) => {
    event.preventDefault();
    const text = comment.trim();
    if (!text || website) return;
    const cooldownKey = `oceanGuideSpotCommentAt:${spotId}`;
    const lastPostedAt = Number(window.localStorage.getItem(cooldownKey) || 0);
    if (Date.now() - lastPostedAt < 60000) {
      setMessage(copy.cooldown);
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      await addDoc(collection(db, "oceanGuideSpotComments", spotId, "comments"), {
        name: name.trim(),
        text,
        observedDate,
        observedTime,
        crowd,
        beachRating,
        snorkelRating,
        windStrength,
        waveStrength,
        clientId: getSpotCommentClientId(),
        createdAt: serverTimestamp(),
      });
      window.localStorage.setItem(cooldownKey, String(Date.now()));
      setComment("");
      setMessage(copy.sent);
      trackEvent("spot_comment_submit", { spot_name: spot.name });
    } catch (error) {
      setMessage(copy.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={s.spotComments}>
      <div className={s.spotCommentsHead}>
        <span aria-hidden="true">💬</span>
        <div>
          <h4>{copy.title}</h4>
          <p>{copy.lead}</p>
        </div>
      </div>
      <form onSubmit={submitComment}>
        <div className={s.spotCommentDateTime}>
          <label>
            <span>{copy.date}</span>
            <input type="date" required max={initialDateTime.date} value={observedDate} onChange={(event) => setObservedDate(event.target.value)} />
          </label>
          <label>
            <span>{copy.time}</span>
            <input type="time" required value={observedTime} onChange={(event) => setObservedTime(event.target.value)} />
          </label>
        </div>
        <SpotCommentChoices label={copy.crowd} value={crowd} onChange={setCrowd} options={spotCommentCrowdOptions} lang={labels.lang} />
        <SpotCommentChoices label={copy.beach} value={beachRating} onChange={setBeachRating} options={spotCommentRatingOptions} lang={labels.lang} />
        <SpotCommentChoices label={copy.snorkel} value={snorkelRating} onChange={setSnorkelRating} options={spotCommentRatingOptions} lang={labels.lang} />
        <SpotCommentChoices label={copy.wind} value={windStrength} onChange={setWindStrength} options={spotCommentStrengthOptions} lang={labels.lang} />
        <SpotCommentChoices label={copy.wave} value={waveStrength} onChange={setWaveStrength} options={spotCommentStrengthOptions} lang={labels.lang} />
        <label className={s.spotCommentTextLabel}>
          <span>{copy.comment}</span>
          <textarea
            required
            maxLength="120"
            rows="3"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={copy.placeholder}
          />
        </label>
        <input
          type="text"
          maxLength="20"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={copy.name}
          aria-label={copy.name}
        />
        <input
          className={s.spotCommentHoneypot}
          type="text"
          tabIndex="-1"
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          aria-hidden="true"
        />
        <div className={s.spotCommentSubmitRow}>
          <small>{comment.length}/120</small>
          <button type="submit" disabled={submitting || !comment.trim()}>
            {submitting ? copy.submitting : copy.submit}
          </button>
        </div>
        {message && <p className={s.spotCommentMessage}>{message}</p>}
      </form>
      <div className={s.spotCommentList}>
        {comments.length === 0 ? <p>{copy.empty}</p> : comments.map((item) => (
          <article key={item.id}>
            <div>
              <strong>{item.name || copy.anonymous}</strong>
              {(item.observedDate && item.observedTime) ? (
                <time>{formatSpotCommentDateTime(item.observedDate, item.observedTime, labels.lang)}</time>
              ) : item.createdAt?.toDate && (
                <time>{new Intl.DateTimeFormat(labels.lang === "JP" ? "ja-JP" : "en", {
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Asia/Tokyo",
                }).format(item.createdAt.toDate())}</time>
              )}
            </div>
            {((item.crowd && item.crowd !== "none")
              || (item.beachRating && item.beachRating !== "none")
              || (item.snorkelRating && item.snorkelRating !== "none")
              || item.windStrength > 0
              || item.waveStrength > 0) && (
              <div className={s.spotCommentBadges}>
                {item.crowd && item.crowd !== "none" && <span>{spotCommentCrowdOptions.find(([key]) => key === item.crowd)?.[1]} {copy.crowd}</span>}
                {item.beachRating && item.beachRating !== "none" && <span>{spotCommentRatingOptions.find(([key]) => key === item.beachRating)?.[1]} {copy.beach}</span>}
                {item.snorkelRating && item.snorkelRating !== "none" && <span>{spotCommentRatingOptions.find(([key]) => key === item.snorkelRating)?.[1]} {copy.snorkel}</span>}
                {item.windStrength > 0 && <span>💨 {copy.wind} {item.windStrength}/5</span>}
                {item.waveStrength > 0 && <span>🌊 {copy.wave} {item.waveStrength}/5</span>}
              </div>
            )}
            <p>{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
};

const SpotCard = ({
  spot,
  labels,
  onOpen,
  onPlayVideo,
  oceanCondition,
  dailyMarineForecast,
  maxWindSpeed,
  dayLabel,
  selectedDay = 0,
  rainProbability = 0,
  spotOverride,
  guideSettings,
  primaryCondition = "marine",
  showSpotComments = false,
  commentsOnly = false,
}) => {
  const resolvedDayLabel = dayLabel || labels.dayToggleToday;
  const calculatedRating = getWaveMapSpotCondition(spot, oceanCondition, dailyMarineForecast, maxWindSpeed);
  const manualMarineType = selectedDay === 0 ? spotOverride?.marineRating : "auto";
  const forcedCaution = selectedDay === 0 && spotOverride?.showCaution;
  const rating = applySpotConditionOverride(calculatedRating, manualMarineType, forcedCaution);
  const ratingStatusText = labels[spotRatingStatusKey[rating.type]];
  const { windImpact, waveImpact } = getWindWaveImpactTexts(
    spot,
    oceanCondition,
    dailyMarineForecast,
    labels,
  );
  const calculatedBeachLounge = getBeachLoungeCondition(
    spot,
    oceanCondition,
    labels,
  );
  const manualBeachType = selectedDay === 0 ? spotOverride?.beachRating : "auto";
  const beachLounge = applySpotConditionOverride(calculatedBeachLounge, manualBeachType, forcedCaution);
  const isBeachPrimary = primaryCondition === "beach" && spot.categories?.includes("beach");
  const primaryRatingType = isBeachPrimary ? beachLounge.type : rating.type;
  const beachReasonText = (beachLounge.reasons || []).map((reason) => {
    if (reason === "wave") {
      return labels.lang === "JP" ? `波${dailyMarineForecast?.wave || "高め"}` : `Waves ${dailyMarineForecast?.wave || "high"}`;
    }
    if (reason === "wind") {
      return labels.lang === "JP" ? `風${oceanCondition.windSpeed}` : `Wind ${oceanCondition.windSpeed}`;
    }
    return "";
  }).filter(Boolean).join("・");

  return (
  <article className={s.spotCard}>
    <div className={s.spotHeroWrap}>
      <button type="button" className={s.spotHero} onClick={() => onOpen(spot)} disabled={commentsOnly}>
        <img src={spot.image} alt="" loading="lazy" />
        <div>
          <h3>
            {localizeSpotName(spot, labels.lang)}
            {labels.lang === "JP" && spot.reading && <small>（{spot.reading}）</small>}
          </h3>
          <p>{localizeSpotDescription(spot, labels.lang)}</p>
          {!commentsOnly && (
            <strong>
              {primaryRatingType === "caution" || primaryRatingType === "bad" ? labels.caution : labels.ok}
            </strong>
          )}
        </div>
        {!commentsOnly && <div className={s.spotHeroBadges}>
          {spot.categories?.includes("beach") && (
            <aside className={s.spotHeroBeachBadge}>
              <span>{labels.todayBeachLoungeLabel.replace("{day}", resolvedDayLabel)}</span>
              <BeachLoungeStatusMark status={beachLounge.type} />
              {beachReasonText && ["caution", "bad"].includes(beachLounge.type) && (
                <small className={s.beachRainReason}>
                  {beachReasonText}
                </small>
              )}
            </aside>
          )}
          <aside>
            <span>{labels.overallRatingTitle.replace("{day}", resolvedDayLabel)}</span>
            <StatusMark status={rating.type} />
            <p>{ratingStatusText}</p>
          </aside>
        </div>}
      </button>
      {!commentsOnly && <SpotVideoButton spot={spot} onPlayVideo={onPlayVideo} inlineMobile />}
    </div>

    {!commentsOnly && <>
      <h4 className={s.metricsTitle}>{labels.todayConditionLabel.replace("{day}", resolvedDayLabel)}</h4>
      <div className={`${s.metrics} ${s.metricsThreeCol}`}>
      <div>
        <span>{labels.windImpactLabel}</span>
        <strong>{windImpact}</strong>
      </div>
      <div>
        <span>{labels.waveImpactLabel}</span>
        <strong>{waveImpact}</strong>
      </div>
      <div>
        <span>{labels.clarityLabel}</span>
        <strong>{getSpotTodayClarity(spot, rating.type, labels, selectedDay, rainProbability, guideSettings)}</strong>
      </div>
      </div>

      <h4 className={s.metricsTitle}>{labels.beachInfoLabel}</h4>
      <SpotScoreList spot={spot} labels={labels} variant="snorkelPhoto" />
      <SpotScoreList spot={spot} labels={labels} variant="beginnerTurtle" />

      {(spot.parking || spot.facilities) && (
        <div className={s.spotFacilities}>
        {spot.parking && (
          <div>
            <span>{labels.parkingLabel}</span>
            <p>{localizeSpotParking(spot, labels.lang)}</p>
          </div>
        )}
        {spot.facilities && (
          <div>
            <span>{labels.facilitiesLabel}</span>
            <p>{localizeSpotFacilities(spot, labels.lang)}</p>
          </div>
        )}
        </div>
      )}

      <div className={s.spotCardActions}>
      <button
        type="button"
        className={s.spotDetailButton}
        onClick={() => onOpen(spot)}
      >
        {labels.viewDetails}
      </button>
      <a
        className={s.spotMapLink}
        href={mapUrl(spot.mapQuery || `${spot.name} 宮古島`)}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackEvent("map_navigate_click", { spot_name: spot.name })}
      >
        {labels.navigate}
      </a>
      </div>
    </>}
    {showSpotComments && <SpotComments spot={spot} labels={labels} />}
  </article>
  );
};

const tourRecommendationText = {
  JP: {
    eyebrow: "今日参加できる体験",
    title: "1スポット撮影",
    condition: "その日、宮古島で最も映えるビーチと時間を選び、一眼レフ・ドローンで撮影します",
    price: "今だけモニター価格 1時間 ¥5,000 / 1名",
    detail: "ツアー詳細を見る",
    line: "LINEで空きを確認",
  },
  EN: {
    eyebrow: "Available experience today",
    title: "One-Spot Scenic Photo Tour",
    condition: "A DSLR and drone shoot at Miyakojima's most photogenic beach and time of the day.",
    price: "Limited monitor price · 1 hour · ¥5,000 / person",
    detail: "View tour details",
    line: "Check availability on LINE",
  },
  中文: {
    eyebrow: "今天可以参加的体验",
    title: "单景点绝景摄影之旅",
    condition: "选择当天宫古岛最上镜的海滩和时间，用单反与无人机拍摄。",
    price: "限时体验价 · 1小时 · 每人 ¥5,000",
    detail: "查看行程详情",
    line: "通过LINE确认是否有空位",
  },
  한국어: {
    eyebrow: "오늘 참여할 수 있는 체험",
    title: "원 스팟 절경 촬영 투어",
    condition: "당일 미야코지마에서 가장 아름다운 해변과 시간대를 골라 DSLR과 드론으로 촬영합니다.",
    price: "기간 한정 모니터 가격 · 1시간 · 1인 ¥5,000",
    detail: "투어 상세 보기",
    line: "LINE에서 예약 가능 여부 확인",
  },
};

const featuredRestaurantText = {
  JP: {
    eyebrow: "今日のご飯に",
    factLabels: ["ジャンル", "一人あたりの平均料金", "営業時間", "電話", "場所"],
    instagram: "Instagramで見る",
    map: "Google Mapで見る",
    otherRestaurants: "他の夜ご飯をマップで見る",
  },
  EN: {
    eyebrow: "DINNER PICK",
    factLabels: ["Cuisine", "Average per person", "Hours", "Phone", "Location"],
    instagram: "View on Instagram",
    map: "View on Google Maps",
    otherRestaurants: "See more dinner spots on the map",
  },
  中文: {
    eyebrow: "今天的晚餐推荐",
    factLabels: ["类型", "人均消费", "营业时间", "电话", "地点"],
    instagram: "在Instagram查看",
    map: "在Google地图查看",
    otherRestaurants: "在地图查看其他晚餐",
  },
  한국어: {
    eyebrow: "오늘의 저녁 추천",
    factLabels: ["종류", "1인 평균", "영업시간", "전화", "위치"],
    instagram: "Instagram에서 보기",
    map: "Google 지도에서 보기",
    otherRestaurants: "지도에서 다른 저녁 식당 보기",
  },
};

const partnerTourPlacementText = {
  JP: ["今日のご飯", "今夜、宮古島で何を食べる？", "Ocean Guideから今日の一軒を紹介します。"],
  EN: ["TODAY'S DINNER", "Where to eat in Miyakojima tonight", "Today's restaurant pick from Ocean Guide."],
  中文: ["今天的晚餐", "今晚在宫古岛吃什么？", "Ocean Guide今日餐厅推荐。"],
  한국어: ["오늘의 저녁", "오늘 밤 미야코지마에서 뭘 먹을까?", "Ocean Guide가 오늘의 한 곳을 소개합니다."],
};

const dinnerWeekdays = ["日", "月", "火", "水", "木", "金", "土"];

const getJstDateParts = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const date = new Date(`${values.year}-${values.month}-${values.day}T00:00:00+09:00`);
  return {
    dateKey: `${values.year}-${values.month}-${values.day}`,
    epochDay: Math.floor(date.getTime() / 86400000),
    weekday: dinnerWeekdays[date.getDay()],
  };
};

const isDinnerClosedToday = (restaurant, weekday) => {
  const closed = String(restaurant.closed || "").replace(/\s/g, "");
  if (!closed || /なし|無休|年中無休|不定休|要確認/.test(closed)) return false;
  return new RegExp(`${weekday}(?:曜(?:日)?)?`).test(closed);
};

const getDailyFeaturedRestaurant = () => {
  const { epochDay, weekday } = getJstDateParts();
  const candidates = mapSpots.filter((spot) =>
    spot.categories?.includes("dinner")
    && spot.instaPostUrl
    && spot.priceRange
    && spot.hours
    && spot.phone);
  if (!candidates.length) return null;
  const startIndex = Math.abs(epochDay) % candidates.length;
  const dailyOrder = [...candidates.slice(startIndex), ...candidates.slice(0, startIndex)];
  return dailyOrder.find((spot) => !isDinnerClosedToday(spot, weekday)) || dailyOrder[0];
};

const getInstagramEmbedUrl = (url) =>
  `${String(url || "").split("?")[0].replace(/\/?$/, "/")}embed`;

const getRestaurantAccessText = (restaurant, lang) => {
  const area = localizeFoodArea(restaurant.area, lang);
  if (!restaurant.location || !String(restaurant.area || "").includes("平良")) return area;
  const latDistance = (restaurant.location.lat - nightlifeMapCenter.lat) * 111000;
  const lngDistance = (restaurant.location.lng - nightlifeMapCenter.lng) * 101000;
  const walkingMinutes = Math.max(3, Math.round(Math.hypot(latDistance, lngDistance) / 75));
  const roundedMinutes = Math.max(5, Math.round(walkingMinutes / 5) * 5);
  const accessByLanguage = {
    JP: `${area}｜市街地中心部から徒歩約${roundedMinutes}分`,
    EN: `${area} · about ${roundedMinutes} min walk from downtown`,
    中文: `${area}｜距市中心步行约${roundedMinutes}分钟`,
    한국어: `${area} · 시내 중심에서 도보 약 ${roundedMinutes}분`,
  };
  return accessByLanguage[lang] || accessByLanguage.JP;
};

const FeaturedRestaurantOffer = ({ labels, onOpenDinnerMap }) => {
  const ref = useRef(null);
  const viewedRef = useRef(false);
  const copy = featuredRestaurantText[labels.lang] || featuredRestaurantText.JP;
  const featuredRestaurant = useMemo(
    () => getDailyFeaturedRestaurant() || mapSpots.find((spot) => spot.categories?.includes("dinner")),
    [],
  );
  const localizedName = localizeFoodName(featuredRestaurant, labels.lang);
  const localizedDescription = localizeFoodDetail(featuredRestaurant, labels.lang);
  const localizedGenre = localizeFoodGenre(featuredRestaurant, labels.lang);
  const localizedPrice = `${String(featuredRestaurant.priceRange).replace(/^1人あたり\s*/, "")} ${labels.lang === "JP" ? "目安" : ""}`.trim();
  const facts = [
    [copy.factLabels[0], localizedGenre],
    [copy.factLabels[1], localizedPrice],
    [copy.factLabels[2], featuredRestaurant.hours],
    [copy.factLabels[3], featuredRestaurant.phone],
    [copy.factLabels[4], getRestaurantAccessText(featuredRestaurant, labels.lang)],
  ];
  const instagramUrl = featuredRestaurant.instaPostUrl;
  const mapUrl = featuredRestaurant.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${featuredRestaurant.name} 宮古島`)}`;
  const eventParams = {
    restaurant_id: getSpotOverrideId(featuredRestaurant.name),
    restaurant_name: featuredRestaurant.name,
    placement: "ocean_guide_today_food",
  };
  const trackRestaurantCardClick = (clickTarget) => {
    trackEvent("restaurant_card_click", {
      ...eventParams,
      click_target: clickTarget,
    });
  };

  useEffect(() => {
    const node = ref.current;
    if (!node || viewedRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || viewedRef.current) return;
      viewedRef.current = true;
      trackEvent("restaurant_offer_view", {
        restaurant_id: getSpotOverrideId(featuredRestaurant.name),
        restaurant_name: featuredRestaurant.name,
        placement: "ocean_guide_today_food",
      });
      observer.disconnect();
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [featuredRestaurant.name]);

  return (
    <aside className={`${s.oceanTourRecommendation} ${s.partnerTourOffer}`} ref={ref}>
      <div
        className={s.partnerTourMedia}
      >
        <iframe
          src={getInstagramEmbedUrl(instagramUrl)}
          title={`${localizedName} Instagram`}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        />
      </div>
      <div className={s.oceanTourRecommendationBody}>
        <span>{copy.eyebrow}</span>
        <h3>{localizedName}</h3>
        <div className={s.oceanTourConditionLine}><p>{localizedDescription}</p></div>
        <dl className={s.featuredRestaurantFacts}>
          {facts.map(([label, value], index) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>
                {index === 3 ? (
                  <a
                    href={`tel:${featuredRestaurant.phone.replaceAll("-", "")}`}
                    onClick={() => {
                      trackRestaurantCardClick("phone");
                      trackPhoneBookingClick({ ...eventParams, booking_channel: "phone" });
                    }}
                  >
                    {value}
                  </a>
                ) : value}
              </dd>
            </div>
          ))}
        </dl>
        <div className={s.oceanTourRecommendationActions}>
          <a
            href={instagramUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              trackRestaurantCardClick("instagram");
              trackInstagramBookingClick({ ...eventParams, click_target: "instagram" });
            }}
          >
            {copy.instagram}
          </a>
          <a
            href={mapUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              trackRestaurantCardClick("google_map");
              trackRestaurantMapClick(eventParams);
            }}
          >
            {copy.map} →
          </a>
          <button
            type="button"
            onClick={() => {
              trackRestaurantCardClick("all_dinner_map");
              trackRestaurantMapClick({
                restaurant_id: "all_dinner_spots",
                restaurant_name: "Ocean Guide dinner map",
                placement: "ocean_guide_today_food",
                click_target: "all_dinner_map",
              });
              onOpenDinnerMap();
            }}
          >
            {copy.otherRestaurants} →
          </button>
        </div>
      </div>
    </aside>
  );
};

// 一時非表示。再表示するときは true に戻す。
const SHOW_OCEAN_TOUR_RECOMMENDATION = false;

const OceanTourRecommendation = ({ labels, spot }) => {
  const ref = useRef(null);
  const viewedRef = useRef(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const copy = tourRecommendationText[labels.lang] || tourRecommendationText.JP;

  useEffect(() => {
    const node = ref.current;
    if (!node || viewedRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || viewedRef.current) return;
      viewedRef.current = true;
      trackEvent("tour_recommendation_view", {
        tour_id: "oneSpotPhoto",
        placement: "ocean_guide_today_recommendation",
        spot_name: spot?.name,
      });
      observer.disconnect();
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [spot?.name]);

  return (
    <aside className={s.oceanTourRecommendation} ref={ref}>
      <div className={s.oceanTourRecommendationMedia}>
        {isVideoPlaying ? (
          <iframe
            src="https://www.youtube-nocookie.com/embed/mRwcLL6hzM8?autoplay=1&rel=0"
            title={copy.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            aria-label={`${copy.title}を再生`}
            onClick={() => {
              setIsVideoPlaying(true);
              trackEvent("tour_recommendation_video_play", {
                tour_id: "oneSpotPhoto",
                placement: "ocean_guide_today_recommendation",
                video_id: "mRwcLL6hzM8",
                spot_name: spot?.name,
              });
            }}
          >
            <img
              src="https://i.ytimg.com/vi/mRwcLL6hzM8/hqdefault.jpg"
              alt=""
              loading="lazy"
            />
            <span aria-hidden="true">▶</span>
          </button>
        )}
      </div>
      <div className={s.oceanTourRecommendationBody}>
        <span>{copy.eyebrow}</span>
        <h3>{copy.title}</h3>
        <div className={s.oceanTourConditionLine}>
          <p>{copy.condition}</p>
        </div>
        <strong>{copy.price}</strong>
        <div className={s.oceanTourRecommendationActions}>
          <a
            href="/tours/one-spot-photo-tour"
            onClick={() => trackEvent("tour_recommendation_click", {
              tour_id: "oneSpotPhoto",
              placement: "ocean_guide_today_recommendation",
              spot_name: spot?.name,
            })}
          >
            {copy.detail}
          </a>
          <a
            href={LINE_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              trackEvent("line_booking_click", {
                tour_id: "oneSpotPhoto",
                placement: "ocean_guide_today_recommendation",
                spot_name: spot?.name,
              });
              trackCtaClick({
                label: "ocean_guide_today_line_booking",
                placement: "ocean_guide_today_recommendation",
                destination: LINE_URL,
                destinationType: "line",
                value: "oneSpotPhoto",
              });
            }}
          >
            {copy.line} →
          </a>
        </div>
      </div>
    </aside>
  );
};

const WindyEmbed = ({ title, labels }) => {
  const [active, setActive] = useState(false);

  return (
    <div className={s.rainRadarFrame}>
      <iframe
        title={title}
        src={rainRadarUrl}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      {!active && (
        <button
          type="button"
          className={s.mapClickGuard}
          onClick={() => {
            trackEvent("rain_radar_engage_click", {});
            setActive(true);
          }}
        >
          <span>{labels.mapClickToInteract}</span>
        </button>
      )}
    </div>
  );
};

const RainRadarPanel = ({
  labels,
  rainRadarSnapshot,
  selectedForecastDay = 0,
  selectedWeather = null,
}) => {
  const isToday = selectedForecastDay === 0;
  const rainPoints =
    rainRadarSnapshot.points?.length > 0
      ? rainRadarSnapshot.points
      : defaultRainRadarSnapshot.points;
  const rainByTime = selectedWeather?.rainByTime || [];
  const maxRainChance = Math.max(
    0,
    ...rainByTime.map(([, rain]) => getNumericValue(rain)),
  );
  const strongestPoint = rainPoints.reduce(
    (strongest, point) =>
      point.rainfall > strongest.rainfall ? point : strongest,
    rainPoints[0] || { name: "宮古島", rainfall: 0 },
  );
  const hasRain = isToday
    ? rainPoints.some((point) => point.rainfall > 0)
    : maxRainChance >= 40;

  return (
  <section className={s.rainRadarPanel}>
    <div className={s.rainRadarHead}>
      <div>
        <span>Rain Radar</span>
        <h3>{labels.rainRadarTitle}</h3>
        {rainRadarSnapshot.updated && (
          <small>{rainRadarSnapshot.updated}</small>
        )}
      </div>
      <a href={yahooRainRadarUrl} target="_blank" rel="noreferrer">
        {labels.rainRadarButton}
      </a>
    </div>

    <div className={s.rainRadarSummaryCard}>
      <span>{hasRain ? "☔" : "🌤"}</span>
      <div>
        <strong>
          {isToday && hasRain
            ? labels.rainRadarNearAlert.replace("{name}", strongestPoint.name)
            : !isToday && hasRain
              ? labels.rainRadarDayAlert.replace(
                  "{day}",
                  selectedWeather?.dayLabel || labels.rainRadarSelectedDayFallback,
                )
            : labels.rainRadarLowSummary}
        </strong>
        <p>
          {isToday && hasRain
            ? labels.rainRadarCurrentNote.replace(
                "{rainfall}",
                strongestPoint.rainfall,
              )
            : !isToday && hasRain
              ? labels.rainRadarFutureHighNote
            : labels.rainRadarLowNote}
        </p>
      </div>
    </div>

    <WindyEmbed title={`${labels.rainRadarTitle} map`} labels={labels} />

    {isToday ? (
      <div className={s.rainRadarGrid}>
        {rainPoints.map((point) => (
          <article className={s[point.level]} key={point.name}>
            <span>{point.name}</span>
            <strong>{point.label}</strong>
            <small>{point.rainfall}mm/h</small>
          </article>
        ))}
      </div>
    ) : (
      <div className={s.rainRadarGrid}>
        {rainByTime.map(([time, rain]) => (
          <article
            className={getNumericValue(rain) >= 40 ? s.moderate : s.none}
            key={time}
          >
            <span>{time}</span>
            <strong>{labels.rainProbabilityLabel}</strong>
            <small>{rain}</small>
          </article>
        ))}
      </div>
    )}

    <div className={s.rainRadarGuide}>
      <p>{labels.rainRadarLead}</p>
      <strong>{labels.rainRadarAdvice}</strong>
    </div>
  </section>
  );
};

const SimpleRainRadarPanel = ({ labels }) => (
  <section className={s.simpleRainRadarPanel}>
    <WindyEmbed title={`${labels.rainRadarTitle} map`} labels={labels} />
    <p>{labels.rainRadarLead.split("。")[0]}。</p>
  </section>
);

const TyphoonPanel = ({ labels, typhoonInfo }) => (
  <section className={s.typhoonPanel}>
    <div className={s.typhoonHead}>
      <div>
        <span>Typhoon</span>
        <h3>{labels.typhoonTitle}</h3>
        {typhoonInfo.updated && (
          <small>
            {labels.typhoonUpdated} {typhoonInfo.updated}
          </small>
        )}
      </div>
      <a href={jmaTyphoonUrl} target="_blank" rel="noreferrer">
        {labels.typhoonOfficial}
      </a>
    </div>

    <p>{labels.typhoonLead}</p>

    {typhoonInfo.items.length === 0 ? (
      <article className={s.typhoonEmpty}>
        <strong>{labels.typhoonNone}</strong>
      </article>
    ) : (
      <div className={s.typhoonList}>
        {typhoonInfo.items.map((item) => (
          <article key={item.id}>
            <div className={s.typhoonSummary}>
              <span>{item.category}</span>
              <strong>{formatTyphoonDisplayName(item)}</strong>
              {item.distance && (
                <small>
                  {labels.typhoonDistance.replace("{distance}", item.distance)}
                </small>
              )}
            </div>
            {item.downgradedNotice && (
              <p className={s.typhoonDowngradedNotice}>{labels.typhoonAfterPassNote}</p>
            )}
            {!item.downgradedNotice && item.warningAreaStatus && (
              <p className={`${s.typhoonWarningArea} ${s[item.warningAreaStatus.level]}`}>
                {item.warningAreaStatus.text}
              </p>
            )}
            {!item.downgradedNotice && (
              <div className={`${s.typhoonImpact} ${s[item.impact.level]}`}>
                <div>
                  <span>{labels.typhoonMovementLabel}</span>
                  <strong>{item.movement}</strong>
                </div>
                <div>
                  <span>{labels.typhoonImpactLabel}</span>
                  <p>{item.impact.text}</p>
                </div>
                {item.impact.flightImpact && (
                  <div>
                    <span>{labels.typhoonFlightImpactLabel}</span>
                    <p>{item.impact.flightImpact.text}</p>
                  </div>
                )}
                {item.stormAreaProbability && (
                  <div>
                    <span>{item.stormAreaProbability.label}</span>
                    <strong>{item.stormAreaProbability.text}</strong>
                  </div>
                )}
                {item.impactPeriod && (
                  <div>
                    <span>{labels.typhoonPeriodLabel}</span>
                    <strong>
                      {labels.typhoonPeriodRange
                        .replace("{start}", item.impactPeriod.start)
                        .replace("{end}", item.impactPeriod.end)}
                    </strong>
                    <p className={s.typhoonPeriodCaveat}>
                      {labels.typhoonPeriodCaveat}
                    </p>
                    <p className={s.typhoonPeriodCaveat}>
                      {labels.typhoonAfterPassNote}
                    </p>
                  </div>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    )}
  </section>
);

const HomeScreen = ({
  labels,
  lang,
  setLang,
  activeCategory,
  setActiveCategory,
  onOpen,
  onPlayVideo,
  oceanCondition,
  dailyMarineForecast,
  weatherForecast,
  setScreen,
  onOpenDinnerMap,
  typhoonInfo,
  hourlyWindForecast,
  dataUpdatedAt,
  guideSettings,
  spotOverrides,
}) => {
  const [selectedDay, setSelectedDay] = useState(0);
  const dayLabel = selectedDay === 1 ? labels.dayToggleTomorrow : labels.dayToggleToday;
  const selectedDayDateKey = weatherForecast?.dailyDetails?.[selectedDay]?.dateKey;
  const typhoonAffectedDay = isTyphoonInfluenceForecastDay(
    typhoonInfo,
    selectedDayDateKey || getTodayDateKey(),
  );
  const dayOceanCondition = useMemo(() => ({
    ...getDayOceanCondition(selectedDay, oceanCondition, weatherForecast, hourlyWindForecast),
    typhoonAffected: typhoonAffectedDay,
  }), [selectedDay, oceanCondition, weatherForecast, hourlyWindForecast, typhoonAffectedDay]);
  const dayMarineForecast = dailyMarineForecast.days?.[selectedDay] || dailyMarineForecast;
  const dayRainProbability = getDaytimeRainProbability(
    weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current,
  );
  const dayWeatherLabel = String(dayOceanCondition?.weather || "");
  const dayWindItems =
    hourlyWindForecast?.days?.[selectedDay]?.items || hourlyWindForecast?.items || [];
  const dayWaveValues = [
    dayMarineForecast?.wave,
    ...(dayMarineForecast?.daytimeWaves || []).map((item) => item.wave),
  ].map(getNumericValue);
  const daySwellValues = [
    dayMarineForecast?.swell,
    ...(dayMarineForecast?.daytimeSwells || []).map((item) => item.swell),
  ].map(getNumericValue);
  const roughSeaDay =
    hasStrongWindSafetyStop({ ...dayOceanCondition, windItems: dayWindItems }) ||
    Math.max(0, ...dayWaveValues) >= 1.5 ||
    Math.max(0, ...daySwellValues) >= 1.3;
  // The tab also contains indoor alternatives. Keep it available when a typhoon
  // or rough sea makes outdoor plans unsuitable, even if it is not raining.
  const showRainyDayTab =
    dayRainProbability >= 50 ||
    (dayWeatherLabel.includes("雨") && dayWeatherLabel !== "雨なし") ||
    roughSeaDay ||
    typhoonAffectedDay;

  useEffect(() => {
    if (activeCategory === "rainy" && !showRainyDayTab) {
      setActiveCategory("snorkel");
    }
  }, [activeCategory, setActiveCategory, showRainyDayTab]);

  const visibleSpots = spots.filter((spot) =>
    spot.categories?.includes(activeCategory),
  );
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, selectedDay);
  const snorkelRating =
    activeCategory === "snorkel"
      ? getSnorkelCondition(dayMarineForecast, dayOceanCondition, labels, maxWindSpeed)
      : null;
  const windResistantSpots =
    snorkelRating?.type === "bad"
      ? spots.filter(
          (spot) =>
            spot.categories?.includes("snorkel") &&
            ["best", "good"].includes(
              getBeachLoungeCondition(
                spot,
                dayOceanCondition,
                labels,
              ).type,
            ),
        )
      : [];
  const shelteredSpots = windResistantSpots;
  const shelteredSpotsTitle = (
    snorkelRating?.windExtreme
      ? labels.windResistantSpotsTitle
      : labels.shelteredSpotsTitle
  ).replace("{day}", dayLabel);
  const shelteredSpotNames = shelteredSpots.map((spot) => spot.name);
  const mainListSpots = (
    shelteredSpots.length > 0
      ? visibleSpots.filter((spot) => !shelteredSpotNames.includes(spot.name))
      : visibleSpots
  ).filter((spot) => {
    if (activeCategory !== "snorkel") return true;
    if (
      !["good", "best"].includes(
        getWaveMapSpotCondition(spot, dayOceanCondition, dayMarineForecast, maxWindSpeed).type,
      )
    ) {
      return false;
    }
    // A spot rated "good" by wind speed alone can still be directly exposed to
    // today's wind (an onshore breeze feels rougher in person than the speed
    // number suggests) — don't recommend it as today's pick when that's the case.
    return !getWindWaveImpactTexts(spot, dayOceanCondition, dayMarineForecast, labels).onshore;
  });
  const recommendationRank = { best: 4, good: 3, caution: 2, bad: 1 };
  const getRecommendationCondition = (spot, category, targetDay = selectedDay) => {
    const targetOceanCondition = getDayOceanCondition(
      targetDay,
      oceanCondition,
      weatherForecast,
      hourlyWindForecast,
    );
    const targetMarineForecast = dailyMarineForecast.days?.[targetDay] || dailyMarineForecast;
    const targetMaxWindSpeed = getMaxWindSpeed(hourlyWindForecast, targetDay);
    const automatic = category === "beach"
      ? getBeachLoungeCondition(spot, targetOceanCondition, labels)
      : getWaveMapSpotCondition(spot, targetOceanCondition, targetMarineForecast, targetMaxWindSpeed);
    if (targetDay !== 0) return automatic;
    const override = getTodaySpotOverride(spotOverrides?.[getSpotOverrideId(spot.name)]);
    const manualType = category === "beach" ? override?.beachRating : override?.marineRating;
    return applySpotConditionOverride(automatic, manualType, override?.showCaution);
  };
  const rankRecommendedSpots = (category, excludedNames = new Set(), targetDay = selectedDay) => spots
    .filter((spot) => spot.categories?.includes(category) && !excludedNames.has(spot.name))
    .map((spot) => {
      const condition = getRecommendationCondition(spot, category, targetDay);
      const targetOceanCondition = getDayOceanCondition(
        targetDay,
        oceanCondition,
        weatherForecast,
        hourlyWindForecast,
      );
      const receivesOnshoreWind = category === "snorkel"
        && getWindWaveImpactTexts(
          spot,
          targetOceanCondition,
          dailyMarineForecast.days?.[targetDay] || dailyMarineForecast,
          labels,
        ).onshore;
      const suitability = category === "snorkel"
        ? Number(spot.snorkel || 0)
        : Number(spot.beginnerScore || spot.photo || 0);
      const crowdPenalty = Number(spot.crowdScore || 0) * 0.05;
      return {
        spot,
        conditionType: condition.type,
        receivesOnshoreWind,
        score: (recommendationRank[condition.type] || 0) * 100 + suitability - crowdPenalty,
      };
    })
    .filter(
      ({ conditionType, receivesOnshoreWind }) =>
        ["best", "good"].includes(conditionType) && !receivesOnshoreWind,
    )
    .sort((a, b) => b.score - a.score || a.spot.name.localeCompare(b.spot.name, "ja"))
    .slice(0, 3)
    .map(({ spot }) => spot);
  const fillRecommendedSpots = (category, preferredExcluded, requiredExcluded, targetDay) => {
    const preferred = rankRecommendedSpots(category, preferredExcluded, targetDay);
    if (preferred.length >= 3) return preferred;
    const selectedNames = new Set([...requiredExcluded, ...preferred.map((spot) => spot.name)]);
    return [
      ...preferred,
      ...rankRecommendedSpots(category, selectedNames, targetDay).slice(0, 3 - preferred.length),
    ];
  };
  // Give a spot with a strong beach-lounging score to the beach section first;
  // the snorkeling section then chooses its own best three without duplicates.
  const todayBeachRecommendations = rankRecommendedSpots("beach", new Set(), 0);
  const todayBeachNames = new Set(todayBeachRecommendations.map((spot) => spot.name));
  const todaySnorkelRecommendations = rankRecommendedSpots("snorkel", todayBeachNames, 0);
  const todayRecommendationNames = new Set([
    ...todayBeachRecommendations.map((spot) => spot.name),
    ...todaySnorkelRecommendations.map((spot) => spot.name),
  ]);
  const uniqueBeachRecommendations = selectedDay === 0
    ? todayBeachRecommendations
    : fillRecommendedSpots("beach", todayRecommendationNames, new Set(), 1);
  const beachRecommendationNames = new Set(uniqueBeachRecommendations.map((spot) => spot.name));
  const snorkelRecommendations = selectedDay === 0
    ? todaySnorkelRecommendations
    : fillRecommendedSpots(
        "snorkel",
        new Set([...todayRecommendationNames, ...beachRecommendationNames]),
        beachRecommendationNames,
        1,
      );
  const recommendationTitles = labels.lang === "JP"
    ? {
        snorkel: `${dayLabel}のシュノーケルおすすめスポット`,
        beach: `${dayLabel}のビーチルおすすめビーチ`,
      }
    : {
        snorkel: `${dayLabel}: Recommended snorkeling spots`,
        beach: `${dayLabel}: Recommended beach spots`,
      };
  const starLabels = labels.starSpotLabels || t.JP.starSpotLabels;
  const moonData = useMemo(
    () => {
      if (activeCategory !== "stars") return null;
      const data = buildMoonGuideData(
        starLabels,
        dayOceanCondition,
        labels,
        weatherForecast,
        selectedDay,
      );
      const manualType = selectedDay === 1
        ? guideSettings?.tomorrowStarRating
        : guideSettings?.todayStarRating;
      if (!manualType || manualType === "auto") return data;
      return {
        ...data,
        tonightRating: {
          ...data.tonightRating,
          type: manualType,
          status: {
            best: starLabels.mapConditionBest,
            good: starLabels.mapConditionGood,
            caution: starLabels.mapConditionCaution,
            bad: starLabels.mapConditionBad,
          }[manualType],
        },
      };
    },
    [activeCategory, starLabels, dayOceanCondition, labels, weatherForecast, selectedDay, guideSettings],
  );
  const heroWindSpeed = Math.max(
    getNumericValue(dayOceanCondition?.windSpeed),
    maxWindSpeed || 0,
  );
  const heroWaveHeight = getNumericValue(dayMarineForecast?.wave);
  const heroSwellHeight = getNumericValue(dayMarineForecast?.swell);
  const heroSeaRaised = heroWaveHeight >= 0.8 || heroSwellHeight >= 0.6;
  const heroWindRaised = heroWindSpeed >= 5;
  const autoOceanHeroText = {
    JP: {
      best: {
        title: "{day}の宮古島は海遊び日和です！",
        sub: heroWindSpeed <= 3.5 && heroWaveHeight <= 0.5
          ? "風と波が穏やかで、海遊びを楽しみやすい一日です。"
          : "比較的穏やかな海況です。海の変化を確認しながら楽しみましょう。",
      },
      good: {
        title: "{day}の宮古島は海遊びを楽しみやすい一日です！",
        sub: heroWindRaised && heroSeaRaised
          ? "少し風と波があります。影響を受けにくい場所を選びましょう。"
          : heroWindRaised
            ? "少し風があります。風の影響を受けにくい場所がおすすめです。"
            : "少し波があります。穏やかな場所を選べば海遊びを楽しめます。",
      },
      caution: {
        title: "{day}の宮古島は場所を選んで海を楽しみましょう",
        sub: heroWindRaised && heroSeaRaised
          ? "風と波が強めです。影響を受けにくい場所と時間を選びましょう。"
          : heroWindRaised
            ? "風が強めです。風をかわせる場所を選びましょう。"
            : "波とうねりが高めです。穏やかな場所を選び、沖へ出すぎないでください。",
      },
    },
    EN: {
      best: { title: "{day} is a great day for the ocean!", sub: "Wind and waves are calm, making ocean activities easy to enjoy." },
      good: { title: "Ocean activities should be enjoyable {day}.", sub: "There is some wind or swell, so choose a sheltered location." },
      caution: { title: "Choose your ocean location carefully {day}.", sub: "Wind or waves are elevated. Pick a sheltered place and a safer time." },
    },
    中文: {
      best: { title: "{day}的宫古岛很适合海上活动！", sub: "风浪较平稳，适合享受海上活动。" },
      good: { title: "{day}较适合享受海上活动！", sub: "有一些风浪，建议选择受影响较小的地点。" },
      caution: { title: "{day}请选好地点再进行海上活动", sub: "风浪偏强，请选择较避风、平稳的地点和时间。" },
    },
    한국어: {
      best: { title: "{day} 미야코지마는 바다놀이 하기 좋은 날입니다!", sub: "바람과 파도가 잔잔해 바다 활동을 즐기기 좋습니다." },
      good: { title: "{day}은 바다놀이를 즐기기 비교적 좋은 날입니다!", sub: "바람이나 파도가 조금 있어 영향이 적은 장소를 추천합니다." },
      caution: { title: "{day}은 장소를 잘 골라 바다를 즐겨 주세요", sub: "바람이나 파도가 강하므로 영향을 덜 받는 장소와 시간을 선택하세요." },
    },
  }[labels.lang] || {};
  const dynamicOceanHero = autoOceanHeroText[snorkelRating?.type]
    || autoOceanHeroText.good
    || { title: labels.heroTitle, sub: labels.heroSub };
  const baseActiveHero =
    activeCategory === "snorkel" && dayRainProbability >= 50
      ? { title: labels.heroHighRainTitle, sub: labels.heroHighRainSub }
      : activeCategory === "snorkel" && snorkelRating?.type === "bad"
      ? snorkelRating.windExtreme
        ? { title: labels.heroWindTitle, sub: labels.heroWindSub }
        : snorkelRating.rainy
          ? { title: labels.heroRainTitle, sub: labels.heroRainSub }
          : { title: labels.heroBadTitle, sub: labels.heroBadSub }
      : activeCategory === "snorkel"
        ? dynamicOceanHero
        : labels.heroByCategory?.[activeCategory] || {
            title: labels.heroTitle,
            sub: labels.heroSub,
          };
  const savedHeroNote = getOceanGuideAdminText(
    guideSettings,
    selectedDay === 1 ? "tomorrowNote" : "todayNote",
    labels.lang,
  );
  // Older settings sometimes saved the former generic default as a manual
  // override. Treat that exact value as automatic so the daily text can change.
  const manualHeroNote = savedHeroNote === labels.heroSub ? "" : savedHeroNote;
  const activeHero = activeCategory === "snorkel" && manualHeroNote
    ? { ...baseActiveHero, sub: manualHeroNote }
    : baseActiveHero;
  const activeHeroImage = heroImagesByCategory[activeCategory] || heroImage;
  const activeHeroYoutubeId = heroYoutubeIdsByCategory[activeCategory];
  const showHeroYoutube = Boolean(activeHeroYoutubeId);
  const savedHeroTitle = getOceanGuideAdminText(
    guideSettings,
    selectedDay === 1 ? "tomorrowTitle" : "todayTitle",
    labels.lang,
  );
  const formerDefaultHeroTitle = labels.heroTitle.replace("{day}", dayLabel);
  const manualHeroTitle = savedHeroTitle === formerDefaultHeroTitle ? "" : savedHeroTitle;
  const activeHeroTitle = activeCategory === "snorkel" && manualHeroTitle
    ? manualHeroTitle
    : activeHero.title.replace("{day}", dayLabel);
  const topAlertText = getOceanGuideAdminText(guideSettings, "topAlertText", labels.lang);
  const manualTopAlertEnabled = guideSettings?.topAlertEnabled && Boolean(topAlertText);
  const relevantTyphoon = typhoonInfo?.items?.[0];
  const automaticTyphoonAlertEnabled = guideSettings?.forecastTyphoonVisible !== false
    && Boolean(relevantTyphoon);
  const automaticTyphoonAlertText = relevantTyphoon
    ? {
        JP: `${formatTyphoonDisplayName(relevantTyphoon)}が発生しています。${relevantTyphoon.impact.text} 最新の進路と海況を確認してください。`,
        EN: `A typhoon that may affect Miyakojima has formed. ${relevantTyphoon.impact.text} Check the latest track and sea conditions.`,
        中文: `可能影响宫古岛的台风已经生成。${relevantTyphoon.impact.text} 请确认最新路径和海况。`,
        한국어: `미야코지마에 영향을 줄 수 있는 태풍이 발생했습니다. ${relevantTyphoon.impact.text} 최신 진로와 해상 상황을 확인하세요.`,
      }[labels.lang] || relevantTyphoon.impact.text
    : "";
  const displayedTopAlertText = manualTopAlertEnabled
    ? topAlertText
    : automaticTyphoonAlertText;
  const showTopAlert = activeCategory === "snorkel"
    && (manualTopAlertEnabled || automaticTyphoonAlertEnabled)
    && Boolean(displayedTopAlertText);
  const isSunsetHeroMovie = activeCategory === "sunset" && showHeroYoutube;

  return (
  <>
    <section className={`${s.appHero} ${activeCategory === "stars" ? s.appHeroStars : ""} ${showHeroYoutube ? s.appHeroMovie : ""} ${isSunsetHeroMovie ? s.appHeroSunsetMovie : ""} ${!activeHero.sub ? s.appHeroTitleOnly : ""}`}>
      {showHeroYoutube ? (
        <>
          {isSunsetHeroMovie && (
            <div
              className={s.appHeroMovieBackdropImage}
              style={{ backgroundImage: `url(${activeHeroImage})` }}
              aria-hidden="true"
            />
          )}
          <iframe
            className={s.appHeroVideo}
            title="Miyako Ocean Guide hero movie"
            src={`https://www.youtube.com/embed/${activeHeroYoutubeId}?autoplay=1&mute=1&loop=1&playlist=${activeHeroYoutubeId}&controls=0&disablekb=1&fs=0&iv_load_policy=3&playsinline=1&rel=0&modestbranding=1&vq=hd1080`}
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            tabIndex="-1"
            aria-hidden="true"
          />
        </>
      ) : activeCategory === "stars" ? (
        <div
          className={s.appHeroStarPhoto}
          style={{ backgroundImage: `url(${activeHeroImage})` }}
          aria-hidden="true"
        />
      ) : (
        <img src={activeHeroImage} alt="" />
      )}
      <div className={s.appHeroOverlay} />
      <div className={s.appHeroContent}>
        <div className={s.brandLine}>
          <div className={s.brandNameRow}>
            <span>{turtleIcon}</span>
            <strong>{labels.appName}</strong>
          </div>
          <div className={s.brandActions}>
            <label className={s.langSwitchWrap}>
              <span className={s.langSwitchIcon} aria-hidden="true">
                🌐
              </span>
              <select
                className={s.langSwitchSelect}
                value={lang}
                onChange={(event) => setLang(event.target.value)}
                aria-label="Language"
              >
                {oceanGuideLangOrder.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              <span className={s.langSwitchArrow} aria-hidden="true">
                ↓
              </span>
            </label>
            <button
              type="button"
              onClick={() => {
                trackEvent("home_hero_map_click", {});
                setScreen("map");
              }}
            >
              {labels.mapButton}
            </button>
          </div>
        </div>
        <div className={s.appHeroBottom}>
          <h1>{activeHeroTitle}</h1>
          {activeHero.sub && <p>{activeHero.sub}</p>}
        </div>
      </div>
    </section>

    <div className={s.categoryTabs}>
      {oceanGuideCategories
        .filter(([key]) => key !== "rainy" || showRainyDayTab)
        .map(([key, icon]) => (
        <button
          key={key}
          type="button"
          className={activeCategory === key ? s.activeCategory : ""}
          onClick={() => {
            trackEvent("home_category_click", { category: key });
            setActiveCategory(key);
          }}
        >
          <Icon>{icon}</Icon>
          {labels.tabs[key]}
        </button>
        ))}
    </div>

    {showTopAlert && (
      <aside className={s.topAlert} role="alert">
        <span className={s.topAlertIcon} aria-hidden="true">⚠</span>
        <div>
          <strong>{labels.lang === "JP" ? "台風に関するお知らせ" : "Weather alert"}</strong>
          <p>{displayedTopAlertText}</p>
        </div>
      </aside>
    )}

    {activeCategory !== "rainy" && (
      <div className={s.dayToggle}>
        <button
          type="button"
          className={selectedDay === 0 ? s.activeDayToggle : ""}
          onClick={() => {
            trackEvent("day_toggle_click", { day: "today", screen: "home" });
            setSelectedDay(0);
          }}
        >
          {labels.dayToggleToday}
        </button>
        <button
          type="button"
          className={selectedDay === 1 ? s.activeDayToggle : ""}
          onClick={() => {
            trackEvent("day_toggle_click", { day: "tomorrow", screen: "home" });
            setSelectedDay(1);
          }}
        >
          {labels.dayToggleTomorrow}
        </button>
      </div>
    )}

    {activeCategory === "stars" && (
      <StarMoonGuide
        labels={labels}
        moonData={moonData}
        weatherForecast={weatherForecast}
        selectedDay={selectedDay}
      />
    )}

    {activeCategory === "yabiji" ? (
      <YabijiPanel labels={labels} guideSettings={guideSettings} selectedDay={selectedDay} />
    ) : activeCategory === "rainy" ? (
      <section className={s.screenSection}>
        <RainyDayPlanPanel labels={labels} />
      </section>
    ) : (
    <section className={s.screenSection}>
      {activeCategory === "snorkel" && (
        <SnorkelCondition
          labels={labels}
          dailyMarineForecast={dailyMarineForecast}
          oceanCondition={oceanCondition}
          typhoonInfo={typhoonInfo}
          hourlyWindForecast={hourlyWindForecast}
          weatherForecast={weatherForecast}
          selectedDay={selectedDay}
          onOpenMap={() => setScreen("map")}
          onOpenRainForecast={() => {
            setScreen("forecast");
            window.setTimeout(() => {
              document.getElementById("rain-radar-forecast")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 150);
          }}
          dataUpdatedAt={dataUpdatedAt}
          guideSettings={guideSettings}
        />
      )}

      {activeCategory === "snorkel" && selectedDay === 0 && (
        <section className={s.partnerTourPlacement}>
          <div className={s.partnerTourPlacementTitle}>
            <span>{(partnerTourPlacementText[labels.lang] || partnerTourPlacementText.JP)[0]}</span>
            <h2>{(partnerTourPlacementText[labels.lang] || partnerTourPlacementText.JP)[1]}</h2>
            <p>{(partnerTourPlacementText[labels.lang] || partnerTourPlacementText.JP)[2]}</p>
          </div>
          <FeaturedRestaurantOffer labels={labels} onOpenDinnerMap={onOpenDinnerMap} />
        </section>
      )}

      {activeCategory === "snorkel" && (
        <div className={s.recommendationGroups}>
          {[
            ["snorkel", recommendationTitles.snorkel, snorkelRecommendations],
            ["beach", recommendationTitles.beach, uniqueBeachRecommendations],
          ]
            .filter(([, , recommendations]) => recommendations.length > 0)
            .map(([key, title, recommendations]) => (
            <section key={key}>
              <div className={s.sectionTitle}>
                <h2>{title}</h2>
                <span>{labels.lang === "JP" ? `${dayLabel}の予報と地点評価から上位3件を自動選出` : `Top 3 automatically selected for ${dayLabel}`}</span>
              </div>
              {recommendations.map((spot) => (
                <SpotCard
                  key={`${key}-recommend-${spot.name}`}
                  spot={spot}
                  labels={labels}
                  onOpen={onOpen}
                  onPlayVideo={onPlayVideo}
                  oceanCondition={dayOceanCondition}
                  dailyMarineForecast={dayMarineForecast}
                  maxWindSpeed={maxWindSpeed}
                  dayLabel={dayLabel}
                  selectedDay={selectedDay}
                  rainProbability={dayRainProbability}
                  spotOverride={getTodaySpotOverride(spotOverrides?.[getSpotOverrideId(spot.name)])}
                  guideSettings={guideSettings}
                />
              ))}
            </section>
          ))}
        </div>
      )}

      {SHOW_OCEAN_TOUR_RECOMMENDATION && activeCategory === "snorkel" && selectedDay === 0 && (
        <OceanTourRecommendation
          labels={labels}
          spot={snorkelRecommendations[0] || null}
        />
      )}

      {activeCategory !== "snorkel" && shelteredSpots.length > 0 && (
        <div className={s.shelteredSpotsSection}>
          <h3 className={s.shelteredSpotsTitle}>{shelteredSpotsTitle}</h3>
          {shelteredSpots.map((spot) => (
            <SpotCard
              key={`sheltered-${spot.name}`}
              spot={spot}
              labels={labels}
              onOpen={onOpen}
              onPlayVideo={onPlayVideo}
              oceanCondition={dayOceanCondition}
              dailyMarineForecast={dayMarineForecast}
              maxWindSpeed={maxWindSpeed}
              dayLabel={dayLabel}
              selectedDay={selectedDay}
              rainProbability={dayRainProbability}
              guideSettings={guideSettings}
            />
          ))}
        </div>
      )}

      {activeCategory !== "snorkel" && shelteredSpots.length === 0 && mainListSpots.length > 0 && (
        <>
          <div className={s.sectionTitle}>
            <h2>{labels.recommend.replace("{day}", dayLabel)}</h2>
            <span>{formatAppUpdatedTime(labels.lang, dataUpdatedAt)}</span>
          </div>

          {activeCategory === "sunset" && (
            <SunsetCondition
              labels={labels}
              weatherForecast={weatherForecast}
              selectedDay={selectedDay}
              guideSettings={guideSettings}
            />
          )}

          {mainListSpots.map((spot, index) => (
            activeCategory === "stars" ? (
              <StarSpotCard
                key={spot.name}
                spot={spot}
                labels={labels}
                onOpen={onOpen}
                onPlayVideo={onPlayVideo}
                moonData={moonData}
              />
            ) : activeCategory === "sunset" ? (
              <SunsetSpotCard
                key={spot.name}
                spot={spot}
                labels={labels}
                onOpen={onOpen}
                onPlayVideo={onPlayVideo}
                weatherForecast={weatherForecast}
                selectedDay={selectedDay}
                guideSettings={guideSettings}
              />
            ) : (
              <SpotCard
                key={spot.name}
                spot={spot}
                labels={labels}
                onOpen={onOpen}
                onPlayVideo={onPlayVideo}
                oceanCondition={dayOceanCondition}
                dailyMarineForecast={dayMarineForecast}
                maxWindSpeed={maxWindSpeed}
                dayLabel={dayLabel}
                selectedDay={selectedDay}
                rainProbability={dayRainProbability}
                guideSettings={guideSettings}
              />
            )
          ))}
        </>
      )}

      {activeCategory === "stars" && (
        <SimpleRainRadarPanel labels={labels} />
      )}
    </section>
    )}

    {SHOW_MAP_STAR_TOUR_PROMO && activeCategory === "stars" && (
      <>
        <section className={`${s.starTourPanel} ${s.starTourPanelSecondary}`}>
          <div className={s.starTourContent}>
            <span>{labels.mapStarTour.label}</span>
            <h2>{labels.mapStarTour.title}</h2>
            <strong className={s.starTourNotice}>
              {labels.mapStarTour.notice}
            </strong>
            <p>{labels.mapStarTour.description}</p>
            <ul>
              {labels.mapStarTour.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <a
              href={starTourAccountUrl}
              className={s.starTourButton}
              target="_blank"
              rel="noreferrer"
            >
              {labels.mapStarTour.button}
            </a>
          </div>
          <div className={s.starTourMedia}>
            <InstagramEmbed
              url={mapStarTourInstagramUrl}
              caption={labels.mapStarTour.title}
            />
          </div>
        </section>
      </>
    )}
    {labels.lang === "JP" && <OceanGuideSearchLinks compact />}
  </>
  );
};

const MapScreen = ({
  labels,
  lang,
  setLang,
  isMapLoaded,
  mapLoadError,
  oceanCondition,
  dailyMarineForecast,
  weatherForecast,
  typhoonInfo,
  hourlyWindForecast,
  setScreen,
  activeMapCategory,
  setActiveMapCategory,
  selectedSpotName,
  setSelectedSpotName,
  isDetailModalOpen,
  setIsDetailModalOpen,
  foodCrowdFilter,
  setFoodCrowdFilter,
  onPlayVideo,
  guideSettings,
  spotOverrides,
}) => {
  const voiceCopy = voiceMapCopy[labels.lang] || voiceMapCopy.EN;
  const [selectedDay, setSelectedDay] = useState(0);
  const dayLabel = selectedDay === 1 ? labels.dayToggleTomorrow : labels.dayToggleToday;
  const selectedDayDateKey = weatherForecast?.dailyDetails?.[selectedDay]?.dateKey;
  const typhoonAffectedDay = isTyphoonInfluenceForecastDay(
    typhoonInfo,
    selectedDayDateKey || getTodayDateKey(),
  );
  const dayOceanCondition = useMemo(() => ({
    ...getDayOceanCondition(selectedDay, oceanCondition, weatherForecast, hourlyWindForecast),
    typhoonAffected: typhoonAffectedDay,
  }), [selectedDay, oceanCondition, weatherForecast, hourlyWindForecast, typhoonAffectedDay]);
  const dayMarineForecast = dailyMarineForecast.days?.[selectedDay] || dailyMarineForecast;
  const dayRainProbability = getAverageRainProbability(
    weatherForecast?.dailyDetails?.[selectedDay] || weatherForecast?.current,
  );
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, selectedDay);
  const [foodLocations, setFoodLocations] = useState({});
  const [recentSpotComments, setRecentSpotComments] = useState({});
  const [voicePreviewSpotName, setVoicePreviewSpotName] = useState(null);
  const [mapEngaged, setMapEngaged] = useState(false);
  const [showDetailInModal, setShowDetailInModal] = useState(false);
  const [placeSearchQuery, setPlaceSearchQuery] = useState("");
  const starLabels = labels.starSpotLabels || t.JP.starSpotLabels;
  const sunsetLabels = labels.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  const showFoodCrowdFilter = ["lunch", "dinner"].includes(activeMapCategory);
  const isPlaceOnlyMapCategory = placeOnlyMapCategories.includes(activeMapCategory);
  const foodCrowdLabels = labels.foodCrowdFilters || t.JP.foodCrowdFilters;
  const moonData = useMemo(
    () =>
      activeMapCategory === "stars"
        ? buildMoonGuideData(starLabels, dayOceanCondition, labels, weatherForecast, selectedDay)
        : null,
    [activeMapCategory, starLabels, dayOceanCondition, labels, weatherForecast, selectedDay],
  );
  useEffect(() => {
    if (activeMapCategory !== "voices") return undefined;
    const voiceSpotIds = [...new Set(mapSpots
      .filter((spot) => spot.kind !== "food" && spot.kind !== "hotel" && spot.location)
      .filter((spot) => spot.categories?.some((category) => ["snorkel", "beach"].includes(category)))
      .map((spot) => getSpotOverrideId(spot.name)))];
    const unsubscribers = voiceSpotIds.map((spotId) => onSnapshot(
      query(
        collection(db, "oceanGuideSpotComments", spotId, "comments"),
        orderBy("createdAt", "desc"),
        limit(10),
      ),
      (snapshot) => {
        const comments = sortSpotCommentsOldestFirst(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
        setRecentSpotComments((current) => ({
          ...current,
          [spotId]: comments,
        }));
      },
      () => setRecentSpotComments((current) => ({ ...current, [spotId]: [] })),
    ));
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [activeMapCategory]);
  const voiceMapSpots = mapSpots
    .filter((spot) => spot.kind !== "food" && spot.kind !== "hotel" && spot.location)
    .filter((spot) => spot.categories?.some((category) => ["snorkel", "beach"].includes(category)))
    .map((spot) => {
      const voiceComments = recentSpotComments[getSpotOverrideId(spot.name)] || [];
      return {
        ...spot,
        type: "voice",
        isVoiceSpot: true,
        voiceComments,
        voiceComment: voiceComments[voiceComments.length - 1] || null,
      };
    });
  const categoryMapSpots = mapSpots
    .filter((spot) => spot.categories?.includes(activeMapCategory))
    .filter((spot) => {
      if (!showFoodCrowdFilter || foodCrowdFilter === "all") return true;
      return getCrowdFilterKey(spot.crowd) === foodCrowdFilter;
    })
    .map((spot) => ({
      ...spot,
      location: foodLocations[spot.name] || spot.location,
      ...getMapSpotCondition(
        spot,
        activeMapCategory,
        dayOceanCondition,
        labels,
        weatherForecast,
        dayMarineForecast,
        maxWindSpeed,
        selectedDay,
        guideSettings,
        spotOverrides,
      ),
    }));
  const visibleMapSpots = activeMapCategory === "voices" ? voiceMapSpots : categoryMapSpots;
  const selectedMapSpot =
    visibleMapSpots.find((spot) => spot.name === selectedSpotName) ||
    visibleMapSpots[0];
  const selectedSpotDetail = selectedMapSpot
    ? spots.find((spot) => spot.name === selectedMapSpot.name)
    : null;
  const currentWave =
    pickCurrentHourItem(dayMarineForecast.daytimeWaves)?.wave ||
    dayMarineForecast.wave;
  const placeSearchResults = useMemo(() => {
    const queryText = normalizePlaceSearch(placeSearchQuery);
    if (!queryText) return [];
    return oceanPlaces.filter((place) =>
      normalizePlaceSearch(`${place.name} ${place.area} ${place.genre || ""}`).includes(queryText),
    ).slice(0, 6);
  }, [placeSearchQuery]);

  useEffect(() => {
    if (
      !isMapLoaded ||
      !["cafe", "lunch", "dinner", "nightlife", "lateNightMeal", "hotel"].includes(activeMapCategory) ||
      !window.google?.maps?.Geocoder
    ) {
      return;
    }

    // Hotels rely entirely on the area-based fallback coordinates computed in
    // getHotelFallbackLocation — geocoding them here would just be 24+ extra
    // network requests on every visit to the hotel tab for no benefit, since
    // the current API key isn't authorized for the Geocoding API and every
    // one of those calls fails anyway (it was making the tab feel slow to load).
    const targets = mapSpots.filter(
      (spot) =>
        spot.kind === "food" &&
        spot.categories?.includes(activeMapCategory) &&
        !foodLocations[spot.name],
    );
    if (targets.length === 0) return;

    const geocoder = new window.google.maps.Geocoder();
    const bounds = new window.google.maps.LatLngBounds(
      { lat: MIYAKO_MAP_BOUNDS.south, lng: MIYAKO_MAP_BOUNDS.west },
      { lat: MIYAKO_MAP_BOUNDS.north, lng: MIYAKO_MAP_BOUNDS.east },
    );
    targets.forEach((spot) => {
      geocoder.geocode(
        {
          address: `${spot.name} ${spot.area || ""} 宮古島 沖縄県`,
          bounds,
          componentRestrictions: { country: "JP" },
          region: "jp",
        },
        (results, status) => {
          const location = results?.[0]?.geometry?.location;
          if (status !== "OK" || !location) return;

          const lat = location.lat();
          const lng = location.lng();
          const inMiyako =
            lat >= MIYAKO_MAP_BOUNDS.south &&
            lat <= MIYAKO_MAP_BOUNDS.north &&
            lng >= MIYAKO_MAP_BOUNDS.west &&
            lng <= MIYAKO_MAP_BOUNDS.east;

          if (!inMiyako) return;

          setFoodLocations((current) =>
            current[spot.name]
              ? current
              : {
                  ...current,
                  [spot.name]: {
                    lat,
                    lng,
                  },
                },
          );
        },
      );
    });
  }, [activeMapCategory, foodLocations, isMapLoaded]);

  useEffect(() => {
    setFoodCrowdFilter("all");
    setVoicePreviewSpotName(null);
    setSelectedSpotName(null);
    setIsDetailModalOpen(false);
    setShowDetailInModal(false);
  }, [
    activeMapCategory,
    setFoodCrowdFilter,
    setIsDetailModalOpen,
    setSelectedSpotName,
  ]);

  return (
  <section className={s.mapScreen}>
    <div className={s.mapHeader}>
      <button
        type="button"
        className={s.mapBack}
        onClick={() => setScreen("home")}
      >
        ‹ {labels.backButtonLabel}
      </button>
      <strong>🐢 {labels.appName}</strong>
      <label className={`${s.langSwitchWrap} ${s.mapLangSwitch}`}>
        <span className={s.langSwitchIcon} aria-hidden="true">🌐</span>
        <select className={s.langSwitchSelect} value={lang} onChange={(event) => setLang(event.target.value)} aria-label="Language">
          {oceanGuideLangOrder.map((code) => <option key={code} value={code}>{code}</option>)}
        </select>
        <span className={s.langSwitchArrow} aria-hidden="true">↓</span>
      </label>
    </div>

    <div className={s.mapTabs}>
      {mapGuideCategories.map(([key, icon]) => (
        <button
          type="button"
          className={activeMapCategory === key ? s.activeMapTab : ""}
          key={key}
          onClick={() => {
            trackEvent("map_category_click", { category: key });
            setActiveMapCategory(key);
          }}
        >
          <span>{icon}</span>
          {labels.tabs[key]}
        </button>
      ))}
    </div>

    <div className={s.mapPlaceSearch}>
      <label>
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          value={placeSearchQuery}
          onChange={(event) => setPlaceSearchQuery(event.target.value)}
          placeholder="場所・お店を検索（例：渡口の浜）"
          aria-label="場所・お店を検索"
        />
      </label>
      {placeSearchResults.length > 0 && (
        <div className={s.mapPlaceSearchResults} role="listbox" aria-label="検索結果">
          {placeSearchResults.map((place) => (
            <a
              key={place.path}
              href={place.path}
              role="option"
              aria-selected={false}
              onClick={() => trackEvent("place_search_select", {
                query: placeSearchQuery,
                place_name: place.name,
                destination: place.path,
                placement: "ocean_guide_map_search",
              })}
            >
              <small>{place.collection === "restaurants" ? "飲食店" : place.area}</small>
              <strong>{place.name}</strong>
              <span>詳細ページを見る →</span>
            </a>
          ))}
        </div>
      )}
    </div>

    {activeMapCategory === "voices" && (
      <aside className={s.mapVoiceIntro}>
        <span aria-hidden="true">👀💬</span>
        <div>
          <strong>{voiceCopy.title}</strong>
          <p>{voiceCopy.lead}</p>
        </div>
      </aside>
    )}

    {!isPlaceOnlyMapCategory && (
      <div className={s.dayToggle}>
        <button
          type="button"
          className={selectedDay === 0 ? s.activeDayToggle : ""}
          onClick={() => {
            trackEvent("day_toggle_click", { day: "today", screen: "map" });
            setSelectedDay(0);
          }}
        >
          {labels.dayToggleToday}
        </button>
        <button
          type="button"
          className={selectedDay === 1 ? s.activeDayToggle : ""}
          onClick={() => {
            trackEvent("day_toggle_click", { day: "tomorrow", screen: "map" });
            setSelectedDay(1);
          }}
        >
          {labels.dayToggleTomorrow}
        </button>
      </div>
    )}

    {showFoodCrowdFilter && (
      <div className={s.foodFilterBar} aria-label="混雑度で絞り込み">
        {foodCrowdFilterOptions.map(([key, icon]) => (
          <button
            type="button"
            key={key}
            className={foodCrowdFilter === key ? s.activeFoodFilter : ""}
            onClick={() => {
              trackEvent("crowd_filter_click", { filter: key });
              setFoodCrowdFilter(key);
              setSelectedSpotName(null);
              setIsDetailModalOpen(false);
              setShowDetailInModal(false);
            }}
          >
            <span>{icon}</span>
            {foodCrowdLabels[key]}
          </button>
        ))}
      </div>
    )}

    <div className={s.mapCanvas}>
      {isMapLoaded ? (
        <GoogleMap
          mapContainerStyle={oceanGuideMapStyle}
          center={
            ["nightlife", "lateNightMeal", "dinner"].includes(activeMapCategory)
              ? nightlifeMapCenter
              : MIYAKO_MAP_CENTER
          }
          zoom={
            ["nightlife", "lateNightMeal", "dinner"].includes(activeMapCategory)
              ? 15
              : 10
          }
          options={oceanGuideMapOptions}
        >
          {visibleMapSpots.map((spot) => (
            <MapSpotOverlay
              key={`overlay-${spot.name}`}
              labels={labels}
              spot={spot}
              oceanCondition={dayOceanCondition}
              wave={currentWave}
              isSelected={spot.name === selectedMapSpot?.name}
              onSelect={(selected) => {
                const needsTouchPreview = selected.isVoiceSpot
                  && selected.voiceComment
                  && window.matchMedia("(hover: none), (pointer: coarse)").matches
                  && voicePreviewSpotName !== selected.name;
                setSelectedSpotName(selected.name);
                setIsDetailModalOpen(!needsTouchPreview);
                setShowDetailInModal(false);
                if (needsTouchPreview) {
                  setVoicePreviewSpotName(selected.name);
                  trackEvent("spot_comment_preview", { spot_name: selected.name, placement: "map" });
                } else {
                  setVoicePreviewSpotName(null);
                  trackSpotView(selected, { placement: "map" });
                }
              }}
            />
          ))}
        </GoogleMap>
      ) : (
        <div className={s.mapFallback}>
          {mapLoadError
            ? "Google Mapを読み込めませんでした。時間をおいて再読み込みしてください。"
            : "Google Mapを読み込んでいます。"}
        </div>
      )}

      {!isPlaceOnlyMapCategory && (
        <div className={s.mapTop}>
          <article className={s.conditionLegend}>
            <span>{labels.mapLegendTitle}</span>
            {activeMapCategory === "stars" ? (
              <>
                <small>◎ {starLabels.mapConditionBest}</small>
                <small>○ {starLabels.mapConditionGood}</small>
                <small>△ {starLabels.mapConditionCaution}</small>
                <small>× {starLabels.mapConditionBad}</small>
              </>
            ) : activeMapCategory === "sunset" ? (
              <>
                <small>◎ {sunsetLabels.mapConditionBest}</small>
                <small>○ {sunsetLabels.mapConditionGood}</small>
                <small>△ {sunsetLabels.mapConditionCaution}</small>
                <small>× {sunsetLabels.mapConditionBad}</small>
              </>
            ) : (
              <>
                <small>◎ {labels.mapConditionBest}</small>
                <small>○ {labels.mapConditionGood}</small>
                <small>△ {labels.mapConditionCaution}</small>
                <small>× {labels.mapConditionBad}</small>
              </>
            )}
          </article>
        </div>
      )}

      {isMapLoaded && !mapEngaged && (
        <button
          type="button"
          className={s.mapClickGuard}
          onClick={() => {
            trackEvent("map_engage_click", { category: activeMapCategory });
            setMapEngaged(true);
          }}
        >
          <span>{labels.mapClickToInteract}</span>
        </button>
      )}
    </div>

    {isDetailModalOpen && selectedMapSpot && (
      <div
        className={s.mapDetailModalOverlay}
        onClick={() => {
          setIsDetailModalOpen(false);
          setShowDetailInModal(false);
        }}
      >
        <div
          className={s.mapDetailModal}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className={s.mapDetailModalClose}
            onClick={() => {
              setIsDetailModalOpen(false);
              setShowDetailInModal(false);
            }}
            aria-label={labels.closeButtonLabel}
          >
            ×
          </button>
          <div className={s.mapDetailModalScroll}>
            {selectedSpotDetail ? (
              showDetailInModal ? (
                <DetailScreen
                  spot={selectedSpotDetail}
                  labels={labels}
                  setScreen={() => setShowDetailInModal(false)}
                  oceanCondition={dayOceanCondition}
                  dailyMarineForecast={dayMarineForecast}
                  hourlyWindForecast={hourlyWindForecast}
                />
              ) : activeMapCategory === "stars" ? (
                <StarSpotCard
                  spot={selectedSpotDetail}
                  labels={labels}
                  onOpen={() => {
                    setShowDetailInModal(true);
                    trackConditionDetailView(selectedSpotDetail, { placement: "map" });
                  }}
                  onPlayVideo={onPlayVideo}
                  moonData={moonData}
                />
              ) : activeMapCategory === "sunset" ? (
                <SunsetSpotCard
                  spot={selectedSpotDetail}
                  labels={labels}
                  onOpen={() => {
                    setShowDetailInModal(true);
                    trackConditionDetailView(selectedSpotDetail, { placement: "map" });
                  }}
                  onPlayVideo={onPlayVideo}
                  weatherForecast={weatherForecast}
                  selectedDay={selectedDay}
                  guideSettings={guideSettings}
                />
              ) : (
                <SpotCard
                  spot={selectedSpotDetail}
                  labels={labels}
                  onOpen={() => {
                    setShowDetailInModal(true);
                    trackConditionDetailView(selectedSpotDetail, { placement: "map" });
                  }}
                  onPlayVideo={onPlayVideo}
                  oceanCondition={dayOceanCondition}
                  dailyMarineForecast={dayMarineForecast}
                  maxWindSpeed={maxWindSpeed}
                  dayLabel={dayLabel}
                  selectedDay={selectedDay}
                  rainProbability={dayRainProbability}
                  guideSettings={guideSettings}
                  spotOverride={getTodaySpotOverride(spotOverrides?.[getSpotOverrideId(selectedSpotDetail.name)])}
                  primaryCondition={activeMapCategory === "beach" ? "beach" : "marine"}
                />
              )
            ) : selectedMapSpot.kind === "food" ? (
              <FoodMapShopCard spot={selectedMapSpot} labels={labels} />
            ) : selectedMapSpot.kind === "hotel" ? (
              <HotelMapDetailCard spot={selectedMapSpot} labels={labels} />
            ) : null}
            {!showDetailInModal && <PlacePageLink spot={selectedMapSpot} lang={labels.lang} />}
          </div>
        </div>
      </div>
    )}

    {SHOW_MAP_STAR_TOUR_PROMO && activeMapCategory === "stars" && (
      <section className={`${s.starTourPanel} ${s.mapStarTourPanel}`}>
        <div className={s.starTourContent}>
          <span>{labels.mapStarTour.label}</span>
          <h2>{labels.mapStarTour.title}</h2>
          <strong className={s.starTourNotice}>
            {labels.mapStarTour.notice}
          </strong>
          <p>{labels.mapStarTour.description}</p>
          <ul>
            {labels.mapStarTour.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <a
            href={starTourAccountUrl}
            className={s.starTourButton}
            target="_blank"
            rel="noreferrer"
          >
            {labels.mapStarTour.button}
          </a>
        </div>
        <div className={s.starTourMedia}>
          <InstagramEmbed
            url={mapStarTourInstagramUrl}
            caption={labels.mapStarTour.title}
          />
        </div>
      </section>
    )}
  </section>
  );
};

const ForecastScreen = ({
  labels,
  rainRadarSnapshot,
  hourlyWindForecast,
  dailyMarineForecast,
  weatherForecast,
  typhoonInfo,
  selectedForecastDay,
  setSelectedForecastDay,
  setScreen,
  guideSettings,
}) => {
  const availableDetailDays = Math.min(
    weatherForecast.dailyDetails?.length || 0,
    dailyMarineForecast.days?.length || 0,
    hourlyWindForecast.days?.length || 0,
  );
  const selectedWeather =
    weatherForecast.dailyDetails[selectedForecastDay] ||
    weatherForecast.current;
  const selectedMarine =
    dailyMarineForecast.days[selectedForecastDay] ||
    dailyMarineForecast;
  const selectedWindItems =
    hourlyWindForecast.days[selectedForecastDay]?.items ||
    hourlyWindForecast.items;
  const selectedDayLabel = selectedWeather.dayLabel || "今日";
  const forecastScores = getDisplayForecastScores(getForecastScores(
    selectedWeather,
    selectedMarine,
    selectedWindItems,
  ), selectedForecastDay, guideSettings);
  const conditionBestTimes = getConditionBestTimes(
    labels,
    forecastScores,
    selectedMarine,
    selectedWindItems,
    selectedForecastDay,
    guideSettings,
  );
  const manualWeatherSummary = selectedForecastDay <= 2
    ? getOceanGuideAdminText(
        guideSettings,
        selectedForecastDay === 2
          ? "dayAfterTomorrowWeatherSummary"
          : selectedForecastDay === 1
            ? "tomorrowWeatherSummary"
            : "todayWeatherSummary",
        labels.lang,
      )
    : "";
  // Rebuild from the currently selected day's values. Stored summaries can be
  // carried across a data refresh and otherwise leave every tab showing the
  // same date, temperatures and advice.
  const generatedWeatherSummary = buildWeatherSummaryText(
    selectedWeather,
    selectedWeather.rainByTime || [],
    labels.lang,
    selectedDayLabel,
  );
  const forecastWaveValues = [
    selectedMarine.wave,
    ...(selectedMarine.daytimeWaves || []).map((item) => item.wave),
  ].map(getNumericValue).filter(Number.isFinite);
  const forecastSwellValues = [
    selectedMarine.swell,
    ...(selectedMarine.daytimeSwells || []).map((item) => item.swell),
  ].map(getNumericValue).filter(Number.isFinite);
  const forecastWindValues = selectedWindItems
    .map((item) => item.speed)
    .map(getNumericValue)
    .filter(Number.isFinite);
  const forecastGustValues = selectedWindItems
    .map((item) => item.gust)
    .map(getNumericValue)
    .filter(Number.isFinite);
  const hasTyphoonLevelMarineRisk =
    Math.max(0, ...forecastWaveValues) >= 0.6
    || Math.max(0, ...forecastSwellValues) >= 0.5
    || Math.max(0, ...forecastWindValues) >= 5
    || Math.max(0, ...forecastGustValues) >= 8;
  const shouldShowTyphoonWeatherSummary = isTyphoonInfluenceForecastDay(
    typhoonInfo,
    selectedWeather.dateKey,
  ) && hasTyphoonLevelMarineRisk;
  const automaticWeatherSummary = shouldShowTyphoonWeatherSummary
    ? `${generatedWeatherSummary}${labels.typhoonWeatherSummary}`
    : generatedWeatherSummary;
  const rawWeatherSummary = manualWeatherSummary || automaticWeatherSummary;
  const weatherSummary = labels.lang === "JP"
    ? rawWeatherSummary
        .replace(/^(今日|明日|明後日|あさって)は/, `${selectedDayLabel}は`)
        .replace(/^(今日|明日|明後日|あさって)の宮古島は/, `${selectedDayLabel}の宮古島は`)
        .replace(/^\d{1,2}\/\d{1,2}\([^)]*\)は/, `${selectedDayLabel}は`)
        .replace(/^\d{1,2}\/\d{1,2}\([^)]*\)の宮古島は/, `${selectedDayLabel}の宮古島は`)
    : rawWeatherSummary;
  const tideRecommendations = buildVisibleTideRecommendations({
    labels,
    tides: selectedMarine.tides,
    weather: selectedWeather,
    marine: selectedMarine,
    windItems: selectedWindItems,
  });
  const selectedDaytimeWaves = selectedMarine.daytimeWaves || [];
  const selectedDaytimeSwells = selectedMarine.daytimeSwells || [];
  const selectedHourlyWeather = selectedWeather.hourlyWeather || [];
  const forecastHourlyTimes = [
    ...new Set([
      ...selectedHourlyWeather.map((item) => item.time),
      ...selectedWindItems.map((item) => item.time),
      ...selectedDaytimeWaves.map((item) => item.time),
      ...selectedDaytimeSwells.map((item) => item.time),
    ]),
  ];

  return (
  <section className={s.forecastScreen}>
    <h2>{labels.forecastTitle}</h2>
    <div className={s.forecastCards}>
      {(weatherForecast.days.length > 0 ? weatherForecast.days : buildForecastDays(labels.lang)).map(([day, date, label, status, high, low], index) => {
        const isDetailAvailable = index < availableDetailDays;
        return (
          <button
            type="button"
            key={`${day}-${date}`}
            className={`${index === selectedForecastDay ? s.activeForecast : ""} ${isDetailAvailable ? "" : s.forecastNoDetail}`}
            disabled={!isDetailAvailable}
            onClick={() => {
              if (!isDetailAvailable) return;
              trackEvent("forecast_day_click", { day_index: index });
              setSelectedForecastDay(index);
            }}
          >
            <strong>{day}</strong>
            {day !== date && <span>{date}</span>}
            <b className={s.weatherIcon}>{weatherIcons[label] || "☁"}</b>
            <p>{translateWeatherLabel(label, labels.lang)}</p>
            <small>
              {high}℃ / {low}℃
            </small>
          </button>
        );
      })}
    </div>
    <div className={s.todayWeatherPanel}>
      <div className={s.todayWeatherMain}>
        <span className={s.todayWeatherIcon}>
          {weatherIcons[selectedWeather.weather] || "☁"}
        </span>
        <div>
          <p>{selectedWeather.dateLabel || formatForecastDate(new Date(), labels.lang)}</p>
          <h3>{translateWeatherLabel(selectedWeather.weather, labels.lang)}</h3>
          <strong>
            {labels.highTempLabel} {selectedWeather.high}℃ / {labels.lowTempLabel} {selectedWeather.low}℃
          </strong>
        </div>
      </div>

      <p className={s.todayWeatherSummary}>
        {weatherSummary}
      </p>

      <section className={s.forecastScorePanel}>
        <h3>{labels.forecastScoreTitle}</h3>
        <SpotScoreList
          spot={spots[0]}
          labels={labels}
          compact
          scores={forecastScores}
        />
      </section>

      <div className={s.todayWeatherFacts}>
        <div>
          <span>{labels.sunriseLabel}</span>
          <strong>{selectedWeather.sunrise}</strong>
        </div>
        <div>
          <span>{labels.sunsetLabel}</span>
          <strong>{selectedWeather.sunset}</strong>
        </div>
      </div>

      {forecastHourlyTimes.length > 0 && (
        <div className={s.snorkelHourlyScroll}>
          <div className={s.snorkelHourlyGrid}>
            <span className={s.snorkelHourlyRowLabel} aria-hidden="true" />
            {forecastHourlyTimes.map((time) => (
              <span key={time} className={s.snorkelHourlyTimeLabel}>
                {time}
              </span>
            ))}

            <span className={s.snorkelHourlyRowLabel}>
              {labels.todayWeatherHourlyLabel}
            </span>
            {forecastHourlyTimes.map((time) => {
              const item = selectedHourlyWeather.find((w) => w.time === time);
              return (
                <span
                  key={time}
                  className={s.snorkelHourlyCell}
                  title={translateWeatherLabel(item?.weather, labels.lang)}
                >
                  {item
                    ? weatherIcons[item.weather] || translateWeatherLabel(item.weather, labels.lang)
                    : "–"}
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>{labels.windLabel}</span>
            {forecastHourlyTimes.map((time) => {
              const item = selectedWindItems.find((w) => w.time === time);
              return item ? (
                <span
                  key={time}
                  className={`${s.snorkelHourlyCell} ${s.snorkelHourlyWindCell} ${s[item.level] || ""}`}
                >
                  <strong>{item.direction} {item.speed}</strong>
                  <em>{item.icon}</em>
                </span>
              ) : (
                <span key={time} className={s.snorkelHourlyCell}>
                  –
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>
              {labels.waveHeightLabel}
            </span>
            {forecastHourlyTimes.map((time) => {
              const item = selectedDaytimeWaves.find((w) => w.time === time);
              return (
                <span key={time} className={s.snorkelHourlyCell}>
                  {item ? item.wave : "–"}
                </span>
              );
            })}

            <span className={s.snorkelHourlyRowLabel}>{labels.swellLabel}</span>
            {forecastHourlyTimes.map((time) => {
              const item = selectedDaytimeSwells.find((w) => w.time === time);
              return (
                <span key={time} className={s.snorkelHourlyCell}>
                  {item ? item.swell : "–"}
                </span>
              );
            })}
          </div>
        </div>
      )}
      <small className={s.hourlyUpdated}>{weatherForecast.updated}</small>
    </div>

    <section className={s.waveMeaningPanel}>
      <article>
        <strong>{labels.waveHeightLabel}</strong>
        <p>{labels.waveMeaning}</p>
      </article>
      <article>
        <strong>{labels.swellLabel}</strong>
        <p>{labels.swellMeaning}</p>
      </article>
    </section>

    <div id="rain-radar-forecast" className={s.rainRadarAnchor}>
      <RainRadarPanel
        labels={labels}
        rainRadarSnapshot={rainRadarSnapshot}
        selectedForecastDay={selectedForecastDay}
        selectedWeather={selectedWeather}
      />
    </div>

    <section className={s.hourlyPanel}>
      <h3>{labels.rainProbabilitySectionTitle.replace("{day}", selectedDayLabel)}</h3>
      <div className={s.rainTimeGrid}>
        {(selectedWeather.rainByTime?.length > 0 ? selectedWeather.rainByTime : defaultWeatherForecast.rainByTime).map(([time, rain]) => (
          <div key={time}>
            <span>{time}</span>
            <strong>{rain}</strong>
          </div>
        ))}
      </div>
    </section>

    {guideSettings?.forecastTyphoonVisible !== false && typhoonInfo.items.length > 0 && (
      <TyphoonPanel labels={labels} typhoonInfo={typhoonInfo} />
    )}

    <section className={s.hourlyPanel}>
      <h3>{labels.tideSectionTitle.replace("{day}", selectedDayLabel)}</h3>
      <div className={s.tideGrid}>
        <div>
          <span>{labels.highTideLabel}</span>
          {(selectedMarine.tides?.high || []).map(([time, height]) => (
            <strong key={time}>
              {time} <small>{height}</small>
            </strong>
          ))}
        </div>
        <div>
          <span>{labels.lowTideLabel}</span>
          {(selectedMarine.tides?.low || []).map(([time, height]) => (
            <strong key={time}>
              {time} <small>{height}</small>
            </strong>
          ))}
        </div>
      </div>
      {tideRecommendations.length > 0 && (
        <div className={s.tideRecommendation}>
          <h4>{labels.tideRecommendTitle}</h4>
          {tideRecommendations.map(({ timing, spot, description, condition }) => (
            <article
              key={`${timing}-${spot}`}
              className={condition === "caution" ? s.tideCaution : ""}
            >
              <span>{timing}</span>
              <strong>{spot}</strong>
              <p>{description}</p>
            </article>
          ))}
        </div>
      )}
    </section>

    <section className={s.hourlyPanel}>
      <h3>{labels.hourlyWindTitle}</h3>
      <p className={s.hourlyNote}>{labels.hourlyWindNote}</p>
      <div className={s.hourlyWindGrid}>
        {selectedWindItems.length > 0 ? (
          selectedWindItems.map((item) => (
            <article className={s[item.level]} key={`${item.time}-${item.direction}`}>
              <span>{item.time}</span>
              <strong>
                <b className={s.windStrengthIcon}>{item.icon}</b>
                {item.speed}
              </strong>
              <small>
                {item.direction}
                {item.gust && (
                  <b>
                    {" "}
                    {labels.windGustLabel} {item.gust}
                  </b>
                )}
              </small>
              <em>{item.status}</em>
              {item.risk && <p>{item.risk}</p>}
              {item.avoidance && item.level !== "windDanger" && (
                <p className={s.windAvoidance}>
                  <strong>{labels.windAvoidanceLabel}</strong>
                  {item.avoidance}
                </p>
              )}
            </article>
          ))
        ) : (
          <article>
            <span>--:--</span>
            <strong>{labels.windForecastLoading}</strong>
            <small>Open-Meteo</small>
            <em>{labels.fetchingStatusLabel}</em>
            <p>{labels.windForecastLoadingNote}</p>
          </article>
        )}
      </div>

      {selectedWindItems.some((item) => item.level === "windDanger") && (
        <div className={s.typhoonAvoidBox}>
          <p>
            <strong>{labels.typhoonAvoidTitle}</strong>
            {labels.typhoonAvoidSeparator}
            {labels.typhoonAvoidMapPrefix}
            <button
              type="button"
              onClick={() => {
                trackEvent("typhoon_avoid_map_click", {});
                setScreen("map");
              }}
            >
              {labels.typhoonAvoidMapButtonLabel}
            </button>
            {labels.typhoonAvoidMapSuffix}
          </p>
        </div>
      )}

      <small className={s.hourlyUpdated}>{hourlyWindForecast.updated}</small>
    </section>

    <div className={s.bestTime}>
      <h3>{labels.bestTimeTitle}</h3>
      {conditionBestTimes.map(({ title, time, description, caution, type }) => (
        <p key={title} className={caution ? s.bestTimeCaution : ""}>
          <StatusMark status={type} />
          <strong>{title}</strong>
          {time && <span>{time}</span>}
          {description && <small>{description}</small>}
        </p>
      ))}
      </div>
    </section>
  );
};

const GuideScreen = ({ labels }) => (
  <section className={s.guideScreen}>
    <h2>{labels.guideTitle}</h2>
    <div className={s.guideHero}>
      <img src={heroImage} alt="" />
      <div>
        <h3>{labels.guideIntroTitle}</h3>
        <p>{labels.guideIntroSub}</p>
      </div>
    </div>
    <div className={s.guideTiles}>
      {guideItems.map(([icon, title, description]) => (
        <article key={title}>
          <Icon>{icon}</Icon>
          <h3>{localizeGuideTitle(title, labels.lang)}</h3>
          <p>{localizeGuideDescription(title, description, labels.lang)}</p>
        </article>
      ))}
    </div>
    <h3 className={s.guideSectionTitle}>{labels.guideActivitySectionTitle}</h3>
    <div className={s.activityList}>
      {activities.map(([title, description, tag, link]) => (
        <article key={title}>
          <img src={activityImages[title]?.() || starImage} alt="" />
          <div>
            <h4>{localizeGuideTitle(title, labels.lang)}</h4>
            <p>{localizeGuideDescription(title, description, labels.lang)}</p>
            {localizeGuideTag(title, tag, labels.lang) && (
              <span>{localizeGuideTag(title, tag, labels.lang)}</span>
            )}
            {link && (
              <a className={s.activityLink} href={link}>
                {labels.tourLinkLabel}
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
    <h3 className={s.guideSectionTitle}>{labels.safetyTitle}</h3>
    <div className={s.safetyList}>
      {safetyGuides.map(([title, description]) => (
        <article key={title}>
          <Icon>!</Icon>
          <div>
            <h4>{localizeGuideTitle(title, labels.lang)}</h4>
            <p>{localizeGuideDescription(title, description, labels.lang)}</p>
          </div>
        </article>
      ))}
    </div>
    <h3 className={s.guideSectionTitle}>{labels.natureTitle}</h3>
    <div className={s.natureGrid}>
      {natureGuides.map(([title, description, image]) => (
        <article key={title}>
          <img src={image} alt="" loading="lazy" />
          <h4>{localizeGuideTitle(title, labels.lang)}</h4>
          <p>{localizeGuideDescription(title, description, labels.lang)}</p>
        </article>
      ))}
    </div>
  </section>
);

const RainyDayPlanPanel = ({ labels }) => {
  const localizedPlan = localizeArticle(rainyDayPlan, labels.lang);

  return (
  <>
    <p className={s.rainSectionLead}>{localizedPlan.leadText}</p>
    {localizedPlan.points.map((point, pointIndex) => {
      const rawPoint = rainyDayPlan.points[pointIndex];
      return (
      <section className={s.rainPointCard} key={rawPoint.title}>
        <div className={s.rainPointHead}>
          <span className={s.rainPointIcon}>
            {rainPointIcons[rawPoint.title] || "✦"}
          </span>
          <div>
            <h3>{point.title}</h3>
            <p>{point.text}</p>
          </div>
        </div>
        {point.shops.map((shop, shopIndex) => {
          const rawShop = rawPoint.shops[shopIndex];
          return (
          <article className={s.rainShop} key={rawShop.name}>
            {shop.instagramPostUrl && (
              <div className={s.rainShopMedia}>
                <InstagramEmbed url={shop.instagramPostUrl} caption={shop.name} />
              </div>
            )}
            <div className={s.rainShopBody}>
              <div className={s.rainShopHead}>
                <strong>{shop.name}</strong>
                <span>{shop.genre}</span>
              </div>
              <p>{shop.text}</p>
              {shop.rows?.length > 0 && (
                <div className={s.rainShopRows}>
                  {shop.rows.map(([label, value], rowIndex) => (
                    <div key={label}>
                      <em>{rainRowIcons[rawShop.rows[rowIndex]?.[0]] || "・"}</em>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              )}
              {shop.mapUrl && (
                <a
                  className={s.rainShopMapLink}
                  href={shop.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackRestaurantMapClick({
                    restaurant_name: rawShop.name,
                    placement: "ocean_guide_rainy_day",
                    destination: shop.mapUrl,
                  })}
                >
                  {labels.viewOnMapLabel}
                </a>
              )}
            </div>
          </article>
          );
        })}
      </section>
      );
    })}
  </>
  );
};

const RainScreen = ({ labels, rainRadarSnapshot }) => (
  <section className={s.rainScreen}>
    <div className={s.rainHero}>
      <img src={heroImage} alt="" />
      <div>
        <span>{labels.appName}</span>
        <h1>
          {labels.rainHeroTitle1}
          <br />
          {labels.rainHeroTitle2}
        </h1>
        <p>{labels.rainHeroSub}</p>
      </div>
    </div>
    <div className={s.weatherStrip}>
      <div>
        {labels.rainProbabilityLabel} <strong>80%</strong>
      </div>
      <div>
        {labels.windLabel} <strong>6m/s</strong>
      </div>
      <div>
        {labels.outdoorActivityLabel} <strong>{labels.outdoorActivityLevelLow}</strong>
      </div>
    </div>
    <p className={s.rainLead}>{labels.rainLead}</p>
    <RainRadarPanel labels={labels} rainRadarSnapshot={rainRadarSnapshot} />
    <h2>{labels.rainyRecommendTitle}</h2>
    <RainyDayPlanPanel labels={labels} />
  </section>
);

const MenuScreen = ({ labels, setScreen }) => (
  <section className={s.menuScreen}>
    <div className={s.menuHero}>
      <span>Menu</span>
      <h2>{labels.menuHeroTitle}</h2>
      <p>{labels.menuHeroSub}</p>
    </div>

    <div className={s.menuList}>
      {[
        ["map", "⌖"],
        ["forecast", "▣"],
        ["guide", "▤"],
        ["rain", "☔"],
      ].map(([key, icon]) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            trackEvent("menu_link_click", { screen: key });
            setScreen(key);
          }}
        >
          <span>{icon}</span>
          <div>
            <strong>{labels.menuItems[key].title}</strong>
            <small>{labels.menuItems[key].description}</small>
          </div>
        </button>
      ))}
      <a href="/ocean-guide/hotels" onClick={() => trackEvent("hotel_guide_open", { placement: "ocean_guide_menu" })}>
        <span aria-hidden="true">⌂</span>
        <div><strong>{labels.hotelGuideEmbedLabel}</strong><small>{labels.lang === "JP" ? "エリア・予算で探して、気になるホテルを比較" : "Hotel & Villa Guide · 日本語"}</small></div>
      </a>
    </div>

    <a
      className={s.menuTourButton}
      href="/tours"
      onClick={() => {
        trackCtaClick({
          label: "ocean_guide_tours_menu",
          placement: "ocean_guide_menu",
          destination: "/tours",
          destinationType: "internal_tours",
        });
        trackEvent("tour_click", { placement: "ocean_guide_menu" });
      }}
    >
      {labels.photoTourLinkLabel}
    </a>

    <div className={s.menuAbout}>
      <section>
        <h3>{labels.termsTitle}</h3>
        <p>{labels.termsText}</p>
      </section>
      <section>
        <h3>{labels.aboutWhyTitle}</h3>
        <p>{labels.aboutWhyText}</p>
      </section>
      <section>
        <h3>{labels.aboutFeedbackTitle}</h3>
        <p>{labels.aboutFeedbackText}</p>
      </section>
      <p className={s.menuDeveloperCredit}>{labels.developerCredit}</p>
    </div>
  </section>
);

const DetailScreen = ({
  labels,
  spot,
  setScreen,
  originScreen = "home",
  oceanCondition,
  dailyMarineForecast,
  hourlyWindForecast,
}) => {
  const currentSpot = spot || spots[0];
  const photogenicScore = getPhotogenicScore(currentSpot);
  const areaLabel = getSpotAreaLabel(currentSpot);
  const driveMinutes = getSpotDriveMinutes(currentSpot);
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast);
  const rating = getWaveMapSpotCondition(
    currentSpot,
    oceanCondition,
    dailyMarineForecast,
    maxWindSpeed,
  );
  const ratingStatusText = labels[spotRatingStatusKey[rating.type]];
  const { windImpact, waveImpact } = getWindWaveImpactTexts(
    currentSpot,
    oceanCondition,
    dailyMarineForecast,
    labels,
  );
  const detailFacts = [
    [labels.overallRatingTitle.replace("{day}", labels.dayToggleToday), ratingStatusText, <StatusMark status={rating.type} />],
    [labels.weatherLabel, translateWeatherLabel(oceanCondition.weather, labels.lang)],
    [labels.todayWaveFactLabel, dailyMarineForecast.wave],
    [labels.swellLabel, dailyMarineForecast.swell],
    [
      labels.windStrengthLabel,
      `${oceanCondition.windDirection} ${oceanCondition.windSpeed}`,
    ],
    [labels.windImpactLabel, windImpact],
    [labels.waveImpactLabel, waveImpact],
    [labels.clarityLabel, getSpotTodayClarity(currentSpot, rating.type, labels)],
    [labels.beginnerSuitabilityLabel, localizeSpotBeginner(currentSpot, labels.lang)],
    [labels.parkingLabel, localizeSpotParking(currentSpot, labels.lang)],
    [labels.facilitiesLabel, localizeSpotFacilities(currentSpot, labels.lang)],
  ].filter(([, value]) => value);

  return (
    <section className={s.detailScreen}>
      <div className={s.detailNav}>
        <button type="button" onClick={() => setScreen(originScreen)}>
          {labels.backButtonLabel}
        </button>
        <h2>{labels.detailTitle}</h2>
      </div>
      <div className={s.detailHero}>
        <img src={currentSpot.image} alt="" />
        <div>
          <h1>{localizeSpotName(currentSpot, labels.lang)}</h1>
          <p className={s.detailMetaPills}>
            <span>{areaLabel}</span>
            {driveMinutes && (
              <span>{labels.droneTourDriveTime.replace("{minutes}", driveMinutes)}</span>
            )}
          </p>
        </div>
      </div>
      <div className={s.scorePanel}>
        <h3>{labels.photogenicScoreTitle}</h3>
        <strong>{photogenicScore}.0 / 5.0</strong>
        <Stars value={photogenicScore} />
        <SpotScoreList spot={currentSpot} labels={labels} compact variant="photogenic" />
      </div>
      <div className={s.detailInfoPanel}>
        <h3>{labels.spotInfoTitle}</h3>
        <dl>
          {detailFacts.map(([label, value, icon]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>
                {icon}
                <span>{value}</span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className={s.detailActions}>
        <a
          href="/tours"
          className={s.bookButton}
          onClick={() => {
            trackCtaClick({
              label: "ocean_guide_spot_book",
              placement: "ocean_guide_spot_detail",
              destination: "/tours",
              destinationType: "internal_tours",
              value: currentSpot?.name,
            });
            trackEvent("tour_click", {
              placement: "ocean_guide_spot_detail",
              spot_name: currentSpot?.name,
            });
          }}
        >
          {labels.book}
        </a>
        <a
          href={mapUrl(currentSpot.mapQuery)}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            trackEvent("map_navigate_click", { spot_name: currentSpot?.name })
          }
        >
          {labels.navigate}
        </a>
      </div>
      <PlacePageLink spot={currentSpot} lang={labels.lang} />
    </section>
  );
};

const oceanGuideLangOrder = ["JP", "EN", "中文", "한국어"];

const getOperatorRecommendations = ({
  labels,
  oceanCondition,
  weatherForecast,
  hourlyWindForecast,
  dailyMarineForecast,
  spotOverrides,
  typhoonInfo,
  dayIndex = 0,
}) => {
  const recommendationRank = { best: 4, good: 3, caution: 2, bad: 1 };
  const selectedDateKey = weatherForecast?.dailyDetails?.[dayIndex]?.dateKey;
  const dayOceanCondition = {
    ...getDayOceanCondition(dayIndex, oceanCondition, weatherForecast, hourlyWindForecast),
    typhoonAffected: isTyphoonInfluenceForecastDay(
      typhoonInfo,
      selectedDateKey || getTodayDateKey(),
    ),
  };
  const dayMarineForecast = dailyMarineForecast.days?.[dayIndex] || dailyMarineForecast;
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, dayIndex);
  const rankSpots = (category, excludedNames = new Set()) => spots
    .filter((spot) => spot.categories?.includes(category) && !excludedNames.has(spot.name))
    .map((spot) => {
      const automatic = category === "beach"
        ? getBeachLoungeCondition(spot, dayOceanCondition, labels)
        : getWaveMapSpotCondition(spot, dayOceanCondition, dayMarineForecast, maxWindSpeed);
      const override = dayIndex === 0
        ? getTodaySpotOverride(spotOverrides?.[getSpotOverrideId(spot.name)])
        : null;
      const manualType = category === "beach" ? override?.beachRating : override?.marineRating;
      const condition = applySpotConditionOverride(automatic, manualType, override?.showCaution);
      const receivesOnshoreWind = category === "snorkel"
        && getWindWaveImpactTexts(
          spot,
          dayOceanCondition,
          dayMarineForecast,
          labels,
        ).onshore;
      const suitability = category === "snorkel"
        ? Number(spot.snorkel || 0)
        : Number(spot.beginnerScore || spot.photo || 0);
      const crowdPenalty = Number(spot.crowdScore || 0) * 0.05;
      return {
        spot,
        condition,
        receivesOnshoreWind,
        score: (recommendationRank[condition.type] || 0) * 100 + suitability - crowdPenalty,
      };
    })
    .filter(({ condition, receivesOnshoreWind }) =>
      ["best", "good"].includes(condition.type) && !receivesOnshoreWind)
    .sort((a, b) => b.score - a.score || a.spot.name.localeCompare(b.spot.name, "ja"))
    .slice(0, 3)
    .map(({ spot, condition }) => ({ ...spot, operatorCondition: condition }));

  const beach = rankSpots("beach");
  const beachNames = new Set(beach.map((spot) => spot.name));
  return {
    beach,
    snorkel: rankSpots("snorkel", beachNames),
  };
};

const getTomorrowOperatorPlan = ({
  labels,
  oceanCondition,
  weatherForecast,
  hourlyWindForecast,
  dailyMarineForecast,
  spotOverrides,
  typhoonInfo,
}) => {
  const dayIndex = 1;
  const weather = weatherForecast?.dailyDetails?.[dayIndex];
  const marine = dailyMarineForecast.days?.[dayIndex];
  const windItems = hourlyWindForecast.days?.[dayIndex]?.items || [];
  if (!weather || !marine || windItems.length === 0) return { available: false };

  const dayOceanCondition = getDayOceanCondition(
    dayIndex,
    oceanCondition,
    weatherForecast,
    hourlyWindForecast,
  );
  const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, dayIndex);
  const representativeWind = windItems.find((item) => item.time === "12:00") || windItems[0] || {};
  const recommendations = getOperatorRecommendations({
    labels,
    oceanCondition,
    weatherForecast,
    hourlyWindForecast,
    dailyMarineForecast,
    spotOverrides,
    typhoonInfo,
    dayIndex,
  });
  const seaRating = getSnorkelCondition(
    marine,
    dayOceanCondition,
    labels,
    maxWindSpeed,
  );

  return {
    available: true,
    weather,
    marine,
    maxWindSpeed,
    windDirection: representativeWind.direction || dayOceanCondition.windDirection,
    rainProbability: Math.round(getDaytimeRainProbability(weather)),
    recommendations,
    seaRating,
  };
};

const getTodayOperatorSkyInsights = ({
  labels,
  oceanCondition,
  weatherForecast,
  guideSettings,
}) => {
  const starLabels = labels.starSpotLabels || t.JP.starSpotLabels;
  const sunsetLabels = labels.sunsetSpotLabels || t.JP.sunsetSpotLabels;
  const manualStarType = guideSettings?.todayStarRating;
  const rawMoonData = buildMoonGuideData(
    starLabels,
    oceanCondition,
    labels,
    weatherForecast,
    0,
  );
  const moonData = manualStarType && manualStarType !== "auto"
    ? {
        ...rawMoonData,
        tonightRating: {
          ...rawMoonData.tonightRating,
          type: manualStarType,
          status: {
            best: starLabels.mapConditionBest,
            good: starLabels.mapConditionGood,
            caution: starLabels.mapConditionCaution,
            bad: starLabels.mapConditionBad,
          }[manualStarType],
        },
      }
    : rawMoonData;
  const starSpots = spots
    .filter((spot) => spot.categories?.includes("stars"))
    .sort((a, b) => {
      const score = (spot) =>
        Number(spot.milkyWay || 0) * 3
        + Number(spot.darkness || 0) * 2
        + Number(spot.photo || 0);
      return score(b) - score(a) || a.name.localeCompare(b.name, "ja");
    })
    .slice(0, 3);

  const sunsetRating = getSunsetConditionForSpot(
    { sunsetScore: 5 },
    labels,
    weatherForecast,
    0,
    guideSettings,
  );
  const sunsetSpots = spots
    .filter((spot) => spot.categories?.includes("sunset"))
    .map((spot) => ({
      ...spot,
      operatorCondition: getSunsetConditionForSpot(
        spot,
        labels,
        weatherForecast,
        0,
        guideSettings,
      ),
    }))
    .filter((spot) => ["best", "good"].includes(spot.operatorCondition.type))
    .sort((a, b) =>
      ({ best: 2, good: 1 }[b.operatorCondition.type] || 0)
      - ({ best: 2, good: 1 }[a.operatorCondition.type] || 0)
      || Number(b.sunsetScore || 0) - Number(a.sunsetScore || 0))
    .slice(0, 3);
  const sunsetTime = weatherForecast?.current?.sunset;

  return {
    stars: {
      ...moonData,
      spots: starSpots,
    },
    sunset: {
      rating: sunsetRating,
      weather: getSunsetWeatherForDay(weatherForecast, 0),
      sunsetTime,
      bestTime: getSunsetBestTimeRange(sunsetTime) || sunsetLabels.defaultBestTime,
      spots: sunsetSpots,
    },
  };
};

const PWA_DISMISS_KEY = "ocean-guide-pwa-dismissed-at";
const PWA_DISMISS_DAYS = 30;

const pwaInstallCopy = {
  JP: {
    title: "ホーム画面からすぐ開けます",
    text: "Ocean Guideをアプリとして追加",
    action: "追加",
    close: "閉じる",
    ios: "Safari下部の共有ボタンを押し、「ホーム画面に追加」を選んでください。",
  },
  EN: {
    title: "Open Ocean Guide in one tap",
    text: "Add Ocean Guide to your home screen",
    action: "Add",
    close: "Close",
    ios: "Tap Share in Safari, then choose Add to Home Screen.",
  },
  中文: {
    title: "从主屏幕快速打开",
    text: "将 Ocean Guide 添加为应用",
    action: "添加",
    close: "关闭",
    ios: "点击 Safari 的分享按钮，然后选择“添加到主屏幕”。",
  },
  한국어: {
    title: "홈 화면에서 바로 열기",
    text: "Ocean Guide를 앱으로 추가",
    action: "추가",
    close: "닫기",
    ios: "Safari의 공유 버튼을 누른 뒤 홈 화면에 추가를 선택하세요.",
  },
};

const OceanGuideInstallBar = ({ lang }) => {
  const copy = pwaInstallCopy[lang] || pwaInstallCopy.JP;
  const [installPrompt, setInstallPrompt] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    const isMobile = window.matchMedia("(max-width: 720px)").matches;
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const dismissedAt = Number(window.localStorage.getItem(PWA_DISMISS_KEY));
    const recentlyDismissed = Number.isFinite(dismissedAt)
      && Date.now() - dismissedAt < PWA_DISMISS_DAYS * 86_400_000;

    if (standalone || !isMobile || recentlyDismissed) return undefined;
    if (isIos) setPlatform("ios");

    const handlePrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setPlatform("installable");
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setPlatform(null);
    };
    window.addEventListener("beforeinstallprompt", handlePrompt);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  if (!platform) return null;

  const dismiss = () => {
    window.localStorage.setItem(PWA_DISMISS_KEY, String(Date.now()));
    setPlatform(null);
  };

  const install = async () => {
    trackEvent("pwa_install_click", { platform });
    if (platform === "ios") {
      setShowIosHelp((value) => !value);
      return;
    }
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    trackEvent("pwa_install_result", { outcome: choice.outcome });
    if (choice.outcome === "accepted") setPlatform(null);
    setInstallPrompt(null);
  };

  return (
    <aside className={s.pwaInstallBar} aria-label={copy.text}>
      <div className={s.pwaInstallIcon} aria-hidden="true">OG</div>
      <div className={s.pwaInstallText}>
        <strong>{copy.title}</strong>
        <span>{copy.text}</span>
        {showIosHelp && <small>{copy.ios}</small>}
      </div>
      <button type="button" className={s.pwaInstallAction} onClick={install}>
        {copy.action}
      </button>
      <button
        type="button"
        className={s.pwaInstallClose}
        onClick={dismiss}
        aria-label={copy.close}
      >
        ×
      </button>
    </aside>
  );
};

const OceanGuidePage = () => {
  const { lang, setLang } = useLanguage();
  const labels = t[lang] || t.JP;
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [screen, setScreen] = useState("home");
  const [activeCategory, setActiveCategory] = useState("snorkel");
  const [selectedSpot, setSelectedSpot] = useState(spots[0]);
  const [oceanCondition, setOceanCondition] = useState(currentOceanCondition);
  const [rainRadarSnapshot, setRainRadarSnapshot] = useState(
    defaultRainRadarSnapshot,
  );
  const [, setHourlyRainForecast] = useState(
    defaultHourlyRainForecast,
  );
  const [hourlyWindForecast, setHourlyWindForecast] = useState(
    defaultHourlyWindForecast,
  );
  const [dailyMarineForecast, setDailyMarineForecast] = useState(
    defaultDailyMarineForecast,
  );
  const [weatherForecast, setWeatherForecast] = useState(
    defaultWeatherForecast,
  );
  const [typhoonInfo, setTyphoonInfo] = useState(defaultTyphoonInfo);
  const [dataUpdatedAt, setDataUpdatedAt] = useState(null);
  const [rawOceanData, setRawOceanData] = useState(null);
  const [latestTyphoonRaw, setLatestTyphoonRaw] = useState(null);
  const [activeSpotVideoId, setActiveSpotVideoId] = useState(null);
  const [selectedForecastDay, setSelectedForecastDay] = useState(0);
  const [detailOriginScreen, setDetailOriginScreen] = useState("home");
  const [mapActiveCategory, setMapActiveCategory] = useState("snorkel");
  const [mapSelectedSpotName, setMapSelectedSpotName] = useState(null);
  const [mapIsDetailModalOpen, setMapIsDetailModalOpen] = useState(false);
  const [mapFoodCrowdFilter, setMapFoodCrowdFilter] = useState("all");
  const [guideSettings, setGuideSettings] = useState(defaultOceanGuideSettings);
  const [spotOverrides, setSpotOverrides] = useState({});
  const appBodyRef = useRef(null);

  const operatorRecommendations = useMemo(
    () => getOperatorRecommendations({
      labels,
      oceanCondition,
      weatherForecast,
      hourlyWindForecast,
      dailyMarineForecast,
      spotOverrides,
      typhoonInfo,
    }),
    [
      labels,
      oceanCondition,
      weatherForecast,
      hourlyWindForecast,
      dailyMarineForecast,
      spotOverrides,
      typhoonInfo,
    ],
  );
  const operatorSpots = useMemo(() => {
    const maxWindSpeed = getMaxWindSpeed(hourlyWindForecast, 0);
    const todayCondition = {
      ...oceanCondition,
      typhoonAffected: isTyphoonInfluenceForecastDay(
        typhoonInfo,
        weatherForecast?.dailyDetails?.[0]?.dateKey || getTodayDateKey(),
      ),
    };
    return spots.map((spot) => ({
      ...spot,
      operatorCondition: getMapSpotCondition(
        spot,
        spot.categories?.includes("snorkel") ? "snorkel" : "beach",
        todayCondition,
        labels,
        weatherForecast,
        dailyMarineForecast,
        maxWindSpeed,
        0,
        guideSettings,
        spotOverrides,
      ),
    }));
  }, [
    hourlyWindForecast,
    oceanCondition,
    labels,
    weatherForecast,
    dailyMarineForecast,
    guideSettings,
    spotOverrides,
    typhoonInfo,
  ]);
  const operatorSkyInsights = useMemo(
    () => getTodayOperatorSkyInsights({
      labels,
      oceanCondition,
      weatherForecast,
      guideSettings,
    }),
    [labels, oceanCondition, weatherForecast, guideSettings],
  );
  const operatorTomorrowPlan = useMemo(
    () => getTomorrowOperatorPlan({
      labels,
      oceanCondition,
      weatherForecast,
      hourlyWindForecast,
      dailyMarineForecast,
      spotOverrides,
      typhoonInfo,
    }),
    [
      labels,
      oceanCondition,
      weatherForecast,
      hourlyWindForecast,
      dailyMarineForecast,
      spotOverrides,
      typhoonInfo,
    ],
  );

  useEffect(
    () => subscribeOceanGuideSettings(setGuideSettings, () => {}),
    [],
  );
  useEffect(
    () => subscribeOceanGuideSpotOverrides(setSpotOverrides, () => {}),
    [],
  );

  // Fetched once per page load — the actual weather/wind/marine/typhoon/rain data
  // behind this file is refreshed 4x/day (0:05/8:00/16:00/22:00 JST) by a scheduled
  // GitHub Action (scripts/fetch-ocean-data.mjs), not on every visit.
  useEffect(() => {
    let isActive = true;

    getCachedOceanData()
      .then((raw) => {
        if (!isActive || !raw) return;
        setRawOceanData(raw);
        // Always show when the complete cache was last refreshed. The daytime
        // assessment may still use its 16:00 snapshot, but stars, night weather,
        // and future forecasts continue to update later in the day.
        setDataUpdatedAt(raw.fetchedAt ? new Date(raw.fetchedAt) : new Date());
      })
      .catch(() => {
        if (!isActive) return;
        setDataUpdatedAt(new Date());
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    fetchLatestJmaTyphoon()
      .then((raw) => {
        if (isActive) setLatestTyphoonRaw(raw);
      })
      .catch(() => {
        // Keep using the scheduled static cache when JMA is temporarily unavailable.
      });

    return () => {
      isActive = false;
    };
  }, []);

  // Re-parses the cached raw payload whenever it arrives or the language changes.
  // This is pure/local computation (no network call), so switching languages is instant.
  useEffect(() => {
    if (!rawOceanData) return;
    const fetchedAt = rawOceanData.fetchedAt || new Date();
    const daytimeRaw = isCurrentJstDaytimeSnapshot(rawOceanData.daytimeSnapshot)
      ? rawOceanData.daytimeSnapshot
      : null;
    const daytimeFetchedAt = daytimeRaw?.fetchedAt || fetchedAt;

    if (rawOceanData.weather) {
      const latestForecast = getOpenMeteoWeatherForecast(lang, rawOceanData.weather, fetchedAt);
      const daytimeForecast = daytimeRaw?.weather
        ? getOpenMeteoWeatherForecast(lang, daytimeRaw.weather, daytimeFetchedAt)
        : null;
      const daytimeMergedForecast = keepTodayWeatherAtDaytimeSnapshot(
        latestForecast,
        daytimeForecast,
      );
      const automaticForecast = applyJmaMainWeather(
        daytimeMergedForecast,
        rawOceanData.jmaForecast,
        daytimeRaw?.jmaForecast,
        lang,
      );
      const forecast = applyManualWeather(automaticForecast, guideSettings, lang);
      setWeatherForecast(forecast);
      setOceanCondition((current) => ({
        ...current,
        weather: forecast.current.weather,
      }));
    }

    const rainRadarSnapshot = getYahooRainRadarSnapshot(rawOceanData.yahooRadar);
    if (rainRadarSnapshot) setRainRadarSnapshot(rainRadarSnapshot);

    const hourlyRain = getYahooHourlyRainForecast(rawOceanData.yahooCenter);
    if (hourlyRain) setHourlyRainForecast(hourlyRain);

    if (rawOceanData.wind) {
      const latestWindForecast = getOpenMeteoHourlyWindForecast(lang, rawOceanData.wind, fetchedAt);
      const daytimeWindForecast = daytimeRaw?.wind
        ? getOpenMeteoHourlyWindForecast(lang, daytimeRaw.wind, daytimeFetchedAt)
        : null;
      const windForecast = keepTodayWindAtDaytimeSnapshot(latestWindForecast, daytimeWindForecast);
      setHourlyWindForecast(windForecast);
      const currentWind = pickCurrentHourItem(windForecast.items);
      if (currentWind) {
        setOceanCondition((current) => ({
          ...current,
          windDirection: currentWind.direction,
          windDegree: currentWind.degree,
          windSpeed: currentWind.speed,
          windGust: currentWind.gust,
        }));
      }
    }

    if (rawOceanData.marine) {
      const latestMarineForecast = getOpenMeteoDailyMarineForecast(
        lang, rawOceanData.marine, rawOceanData.tide, fetchedAt,
      );
      const daytimeMarineForecast = daytimeRaw?.marine
        ? getOpenMeteoDailyMarineForecast(
            lang, daytimeRaw.marine, daytimeRaw.tide || rawOceanData.tide, daytimeFetchedAt,
          )
        : null;
      setDailyMarineForecast(
        keepTodayMarineAtDaytimeSnapshot(latestMarineForecast, daytimeMarineForecast),
      );
    }

    const typhoonRaw = latestTyphoonRaw || rawOceanData.typhoon;
    if (typhoonRaw) {
      setTyphoonInfo(getJmaTyphoonInfo(lang, typhoonRaw));
    }
  }, [lang, rawOceanData, guideSettings, latestTyphoonRaw]);

  useEffect(() => {
    const top =
      (appBodyRef.current?.getBoundingClientRect().top || 0) +
      window.scrollY -
      86;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "auto",
    });
  }, [screen]);

  const activeScreen = useMemo(() => {
    if (screen === "map") {
      return (
        <MapScreen
          labels={labels}
          lang={lang}
          setLang={setLang}
          isMapLoaded={isMapLoaded}
          mapLoadError={mapLoadError}
          oceanCondition={oceanCondition}
          dailyMarineForecast={dailyMarineForecast}
          weatherForecast={weatherForecast}
          typhoonInfo={typhoonInfo}
          hourlyWindForecast={hourlyWindForecast}
          setScreen={setScreen}
          activeMapCategory={mapActiveCategory}
          setActiveMapCategory={setMapActiveCategory}
          selectedSpotName={mapSelectedSpotName}
          setSelectedSpotName={setMapSelectedSpotName}
          isDetailModalOpen={mapIsDetailModalOpen}
          setIsDetailModalOpen={setMapIsDetailModalOpen}
          foodCrowdFilter={mapFoodCrowdFilter}
          setFoodCrowdFilter={setMapFoodCrowdFilter}
          onPlayVideo={(videoId) => setActiveSpotVideoId(videoId)}
          guideSettings={guideSettings}
          spotOverrides={spotOverrides}
        />
      );
    }
    if (screen === "forecast") {
      return (
        <ForecastScreen
          labels={labels}
          rainRadarSnapshot={rainRadarSnapshot}
          hourlyWindForecast={hourlyWindForecast}
          dailyMarineForecast={dailyMarineForecast}
          weatherForecast={weatherForecast}
          typhoonInfo={typhoonInfo}
          selectedForecastDay={selectedForecastDay}
          setSelectedForecastDay={setSelectedForecastDay}
          setScreen={setScreen}
          guideSettings={guideSettings}
        />
      );
    }
    if (screen === "guide") return <GuideScreen labels={labels} />;
    if (screen === "rain") {
      return (
        <RainScreen labels={labels} rainRadarSnapshot={rainRadarSnapshot} />
      );
    }
    if (screen === "menu") {
      return <MenuScreen labels={labels} setScreen={setScreen} />;
    }
    if (screen === "detail") {
      return (
        <DetailScreen
          labels={labels}
          spot={selectedSpot}
          setScreen={setScreen}
          originScreen={detailOriginScreen}
          oceanCondition={oceanCondition}
          dailyMarineForecast={dailyMarineForecast}
          hourlyWindForecast={hourlyWindForecast}
        />
      );
    }

    return (
      <HomeScreen
        labels={labels}
        lang={lang}
        setLang={setLang}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        onOpen={(spot) => {
          setSelectedSpot(spot);
          setDetailOriginScreen("home");
          setScreen("detail");
          trackSpotView(spot, { placement: "home" });
          trackConditionDetailView(spot, { placement: "home" });
        }}
        onPlayVideo={(videoId) => setActiveSpotVideoId(videoId)}
        oceanCondition={oceanCondition}
        dailyMarineForecast={dailyMarineForecast}
        weatherForecast={weatherForecast}
        setScreen={setScreen}
        onOpenDinnerMap={() => {
          setMapActiveCategory("dinner");
          setMapSelectedSpotName(null);
          setMapIsDetailModalOpen(false);
          setScreen("map");
        }}
        typhoonInfo={typhoonInfo}
        hourlyWindForecast={hourlyWindForecast}
        dataUpdatedAt={dataUpdatedAt}
        guideSettings={guideSettings}
        spotOverrides={spotOverrides}
      />
    );
  }, [
    activeCategory,
    isMapLoaded,
    labels,
    lang,
    dataUpdatedAt,
    setLang,
    mapLoadError,
    hourlyWindForecast,
    dailyMarineForecast,
    weatherForecast,
    typhoonInfo,
    selectedForecastDay,
    oceanCondition,
    rainRadarSnapshot,
    screen,
    selectedSpot,
    detailOriginScreen,
    mapActiveCategory,
    mapSelectedSpotName,
    mapIsDetailModalOpen,
    mapFoodCrowdFilter,
    guideSettings,
    spotOverrides,
  ]);

  return (
    <>
      <div className={s.siteHeader}>
        <Header logoColor="dark" />
      </div>
      <main className={s.root}>
        <OceanGuideInstallBar lang={lang} />
        <section className={s.pageHero}>
          <div>
            <p>Ocean Condition App</p>
            <h1>{labels.appName}</h1>
            <span>{labels.pageHeroSubtitle}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              trackEvent("rainy_day_view", {});
              setScreen("rain");
            }}
          >
            {labels.rainButton}
          </button>
        </section>

        <section className={s.appShell} ref={appBodyRef}>
          <div className={s.appBody}>{activeScreen}</div>

          <OceanOperator
            oceanCondition={oceanCondition}
            weatherForecast={weatherForecast}
            dailyMarineForecast={dailyMarineForecast}
            hourlyWindForecast={hourlyWindForecast}
            typhoonInfo={typhoonInfo}
            spots={operatorSpots}
            places={mapSpots.filter((spot) => spot.kind === "food" || spot.kind === "hotel")}
            recommendations={operatorRecommendations}
            skyInsights={operatorSkyInsights}
            tomorrowPlan={operatorTomorrowPlan}
            setScreen={setScreen}
          />

          <nav className={s.bottomNav}>
            {[
              ["home", labels.nav[0], "⌂"],
              ["map", labels.nav[1], "⌖"],
              ["forecast", labels.nav[2], "▣"],
              ["guide", labels.nav[3], "▤"],
              ["menu", labels.nav[4], "☰"],
            ].map(([key, label, icon]) => (
              <button
                key={key}
                type="button"
                className={screen === key ? s.activeNav : ""}
                onClick={() => {
                  setScreen(key);
                  trackEvent("bottom_nav_click", { tab: key });
                  if (key === "forecast") {
                    trackEvent("weather_tab_click", {});
                  }
                }}
              >
                <span>{icon}</span>
                {label}
              </button>
            ))}
          </nav>
        </section>
      </main>
      <OceanGuideVideoModal
        videoId={activeSpotVideoId}
        onClose={() => setActiveSpotVideoId(null)}
      />
      <Footer />
    </>
  );
};

export default OceanGuidePage;
